/**
 * Miroir CLIENT des définitions d'objets - cf. server/services/generation/itemTypes.js,
 * la vraie source de vérité pour ce qui TOMBE (quel objet, où, à quelle
 * fréquence). `statBonus`/`effect`/`price` sont recopiés ici aussi
 * désormais (utilisés par equipment.js pour les bonus de stats, par
 * l'utilisation des consommables, et par la vente en boutique - cf.
 * MainScene.sellItem) - le serveur reste seul décideur de ce qui tombe,
 * mais une fois l'objet en inventaire, appliquer son effet ou calculer
 * son prix de revente est une opération purement locale, pas besoin
 * d'aller-retour serveur.
 *
 * Les deux fichiers doivent rester synchronisés à la main (mêmes clés,
 * mêmes statBonus/effect) - exactement la même discipline que
 * spriteRegistry.js (clés client) vs enemyStats.js/biomeConfig.js (clés
 * serveur) déjà en place ailleurs dans le projet.
 *
 * Les objets présents ici si ils ne sont pas présents dans le serveur ne pourront
 * jamais être looter ou achetés mais peuvent exister dans l'inventaire local
 * et etre vendu localement dans une boutique et racheter dans cette même boutique.
 */
export const ITEM_DEFS = {
  woodenDagger: {
    id: "woodenDagger",
    category: "equipment",
    slot: "mainHand", // une main - peut cohabiter avec un bouclier (offHand) ou une seconde arme (double armement automatique si offHand est libre, cf. MainScene.equipItem)
    twoHanded: false,
    grantsRanged: false, // explicite plutot qu'absent - purement melee, ne debloque jamais l'attaque a distance seule (cf. MainScene.canUseRangedAttack)
    name: "Dague en bois",
    description:
      "+1 dégât au corps à corps et -2 à la distance d'attaque. Arme d'entraînement de départ.",
    statBonus: { meleeDamage: 1, meleeRange: -2 },
    stackable: false,
    // unique: true,
    archetypes: ["voleur"],
    price: 2,
  },

  copperDagger: {
    id: "copperDagger",
    category: "equipment",
    slot: "mainHand",
    name: "Dague en cuivre",
    description: "+5 dégât au corps à corps.",
    statBonus: { meleeDamage: 5 },
    stackable: false,
    archetypes: ["voleur", "assassin"],
    price: 50,
  },
  ironDagger: {
    id: "ironDagger",
    category: "equipment",
    slot: "mainHand",
    name: "Dague en fer",
    description: "+7 dégât au corps à corps.",
    statBonus: { meleeDamage: 7 },
    stackable: false,
    archetypes: ["voleur", "assassin"],
    price: 70,
  },
  silverDagger: {
    id: "silverDagger",
    category: "equipment",
    slot: "mainHand",
    name: "Dague en argent",
    description: "+9 dégât au corps à corps.",
    statBonus: { meleeDamage: 9 },
    stackable: false,
    archetypes: ["voleur", "assassin"],
    price: 90,
  },
  steelDagger: {
    id: "steelDagger",
    category: "equipment",
    slot: "mainHand",
    name: "Dague en acier",
    description: "+11 dégât au corps à corps.",
    statBonus: { meleeDamage: 11 },
    stackable: false,
    archetypes: ["voleur", "assassin"],
    price: 110,
  },
  goldDagger: {
    id: "goldDagger",
    category: "equipment",
    slot: "mainHand",
    name: "Dague en or",
    description: "+13 dégât au corps à corps.",
    statBonus: { meleeDamage: 13 },
    stackable: false,
    archetypes: ["voleur", "assassin"],
    price: 130,
  },
  platiniumDagger: {
    id: "platiniumDagger",
    category: "equipment",
    slot: "mainHand",
    name: "Dague en platine",
    description: "+15 dégât au corps à corps.",
    statBonus: { meleeDamage: 15 },
    stackable: false,
    archetypes: ["voleur", "assassin"],
    price: 150,
  },
  cobaltDagger: {
    id: "cobaltDagger",
    category: "equipment",
    slot: "mainHand",
    name: "Dague en cobalt",
    description: "+17 dégât au corps à corps.",
    statBonus: { meleeDamage: 17 },
    stackable: false,
    archetypes: ["voleur", "assassin"],
    price: 170,
  },
  adamantineDagger: {
    id: "adamantineDagger",
    category: "equipment",
    slot: "mainHand",
    name: "Dague en adamantine",
    description: "+19 dégât au corps à corps.",
    statBonus: { meleeDamage: 19 },
    stackable: false,
    archetypes: ["voleur", "assassin"],
    price: 200,
  },
  angelicDagger: {
    id: "angelicDagger",
    category: "equipment",
    slot: "mainHand",
    name: "Dague angélique",
    description: "+21 dégât au corps à corps.",
    statBonus: { meleeDamage: 21 },
    stackable: false,
    archetypes: ["voleur", "assassin"],
    price: 230,
  },
  fatefulDagger: {
    id: "fatefulDagger",
    category: "equipment",
    slot: "mainHand",
    name: "Dague fatale",
    description: "+23 dégât au corps à corps.",
    statBonus: { meleeDamage: 23 },
    stackable: false,
    archetypes: ["voleur", "assassin"],
    price: 260,
  },
  novaDagger: {
    id: "novaDagger",
    category: "equipment",
    slot: "mainHand",
    name: "Dague de nova",
    description: "+25 dégât au corps à corps.",
    statBonus: { meleeDamage: 25 },
    stackable: false,
    archetypes: ["voleur", "assassin"],
    price: 300,
  },
  sharpCopperDagger: {
    id: "sharpCopperDagger",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Dague en cuivre aiguisée",
    description:
      "Inflige un saignement à l'ennemi. +4 dégâts au corps à corps.",
    statBonus: { meleeDamage: 4 },
    stackable: false,
    archetypes: ["voleur", "guerrier", "archer", "mage"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot", // damage over time
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 50,
  },
  sharpIronDagger: {
    id: "sharpIronDagger",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Dague en fer aiguisée",
    description:
      "Inflige un saignement à l'ennemi. +5 dégâts au corps à corps.",
    statBonus: { meleeDamage: 5 },
    stackable: false,
    archetypes: ["voleur", "guerrier", "archer", "mage"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot", // damage over time
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 60,
  },
  sharpSteelDagger: {
    id: "sharpSteelDagger",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Dague en acier aiguisée",
    description:
      "Inflige un saignement à l'ennemi. +6 dégâts au corps à corps.",
    statBonus: { meleeDamage: 6 },
    stackable: false,
    archetypes: ["voleur", "guerrier", "archer", "mage"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot", // damage over time
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 70,
  },
  sharpSilverDagger: {
    id: "sharpSilverDagger",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Dague en argent aiguisée",
    description:
      "Inflige un saignement à l'ennemi. +7 dégâts au corps à corps.",
    statBonus: { meleeDamage: 7 },
    stackable: false,
    archetypes: ["voleur", "guerrier", "archer", "mage"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot", // damage over time
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 80,
  },
  sharpGoldDagger: {
    id: "sharpGoldDagger",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Dague en or aiguisée",
    description:
      "Inflige un saignement à l'ennemi. +8 dégâts au corps à corps.",
    statBonus: { meleeDamage: 8 },
    stackable: false,
    archetypes: ["voleur", "guerrier", "archer", "mage"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot", // damage over time
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 90,
  },
  sharpPlatiniumDagger: {
    id: "sharpPlatiniumDagger",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Dague en platine aiguisée",
    description:
      "Inflige un saignement à l'ennemi. +9 dégâts au corps à corps.",
    statBonus: { meleeDamage: 9 },
    stackable: false,
    archetypes: ["voleur", "guerrier", "archer", "mage"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot", // damage over time
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 100,
  },
  sharpCobaltDagger: {
    id: "sharpCobaltDagger",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Dague en cobalt aiguisée",
    description:
      "Inflige un saignement à l'ennemi. +10 dégâts au corps à corps.",
    statBonus: { meleeDamage: 10 },
    stackable: false,
    archetypes: ["voleur", "guerrier", "archer", "mage"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot", // damage over time
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 110,
  },
  sharpAdamantineDagger: {
    id: "sharpAdamantineDagger",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Dague en adamantine aiguisée",
    description:
      "Inflige un saignement à l'ennemi. +11 dégâts au corps à corps.",
    statBonus: { meleeDamage: 11 },
    stackable: false,
    archetypes: ["voleur", "guerrier", "archer", "mage"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot", // damage over time
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 120,
  },
  sharpCrimsonDagger: {
    id: "sharpCrimsonDagger",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Dague en cramoisi aiguisée",
    description:
      "Inflige un saignement à l'ennemi. +12 dégâts au corps à corps.",
    statBonus: { meleeDamage: 12 },
    stackable: false,
    archetypes: ["voleur", "guerrier", "archer", "mage"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot", // damage over time
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 130,
  },
  sharpAngelicDagger: {
    id: "sharpAngelicDagger",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Dague angélique aiguisée",
    description:
      "Inflige un saignement à l'ennemi. +13 dégâts au corps à corps.",
    statBonus: { meleeDamage: 13 },
    stackable: false,
    archetypes: ["voleur", "guerrier", "archer", "mage"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot", // damage over time
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 140,
  },
  sharpFatefulDagger: {
    id: "sharpFatefulDagger",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Dague fatidique aiguisée",
    description:
      "Inflige un saignement à l'ennemi. +14 dégâts au corps à corps.",
    statBonus: { meleeDamage: 14 },
    stackable: false,
    archetypes: ["voleur", "guerrier", "archer", "mage"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot", // damage over time
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 150,
  },
  sharpNovaDagger: {
    id: "sharpNovaDagger",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Inflige un saignement à l'ennemi.",
    description: "+15 dégâts au corps à corps.",
    statBonus: { meleeDamage: 15 },
    stackable: false,
    archetypes: ["voleur", "guerrier", "archer", "mage"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot", // damage over time
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 160,
  },
  reinforcedWoodenDagger: {
    id: "reinforcedWoodenDagger",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Dague en bois renforcée",
    description: "+4 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 4 },
    stackable: false,
    archetypes: ["voleur", "guerrier", "archer", "mage"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier", // <-- le champ qui manquait, decide TOUT le comportement
      chance: 0.2,
      statModifiers: { stunned: true }, // effet de stun
      durationMs: 2000, // duree en ms
    },
    price: 50,
  },
  reinforcedCopperDagger: {
    id: "reinforcedCopperDagger",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Dague en cuivre renforcée",
    description: "+5 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 5 },
    stackable: false,
    archetypes: ["voleur", "guerrier", "archer", "mage"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier", // <-- le champ qui manquait, decide TOUT le comportement
      chance: 0.25,
      statModifiers: { stunned: true }, // effet de stun
      durationMs: 2000, // duree en ms
    },
    price: 65,
  },
  reinforcedIronDagger: {
    id: "reinforcedIronDagger",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Dague en fer renforcée ",
    description: "+6 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 6 },
    stackable: false,
    archetypes: ["voleur", "guerrier", "archer", "mage"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier", // <-- le champ qui manquait, decide TOUT le comportement
      chance: 0.3,
      statModifiers: { stunned: true }, // effet de stun
      durationMs: 2000, // duree en ms
    },
    price: 80,
  },
  reinforcedSteelDagger: {
    id: "reinforcedSteelDagger",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Dague en acier renforcée",
    description: "+8 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 8 },
    stackable: false,
    archetypes: ["voleur", "guerrier", "archer", "mage"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier", // <-- le champ qui manquait, decide TOUT le comportement
      chance: 0.4,
      statModifiers: { stunned: true }, // effet de stun
      durationMs: 2000, // duree en ms
    },
    price: 120,
  },
  reinforcedSilverDagger: {
    id: "reinforcedSilverDagger",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Dague en argent renforcée",
    description: "+10 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 10 },
    stackable: false,
    archetypes: ["voleur", "guerrier", "archer", "mage"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier", // <-- le champ qui manquait, decide TOUT le comportement
      chance: 0.5,
      statModifiers: { stunned: true }, // effet de stun
      durationMs: 2000, // duree en ms
    },
    price: 180,
  },
  reinforcedGoldDagger: {
    id: "reinforcedGoldDagger",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Dague en or renforcée",
    description: "+12 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 12 },
    stackable: false,
    archetypes: ["voleur", "guerrier", "archer", "mage"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier", // <-- le champ qui manquait, decide TOUT le comportement
      chance: 0.6,
      statModifiers: { stunned: true }, // effet de stun
      durationMs: 2000, // duree en ms
    },
    price: 250,
  },
  reinforcedPlatiniumDagger: {
    id: "reinforcedPlatiniumDagger",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Dague en platine renforcée",
    description: "+14 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 14 },
    stackable: false,
    archetypes: ["voleur", "guerrier", "archer", "mage"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier", // <-- le champ qui manquait, decide TOUT le comportement
      chance: 0.7,
      statModifiers: { stunned: true }, // effet de stun
      durationMs: 2000, // duree en ms
    },
    price: 350,
  },
  reinforcedCobaltDagger: {
    id: "reinforcedCobaltDagger",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Dague en cobalt renforcée",
    description: "+16 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 16 },
    stackable: false,
    archetypes: ["voleur", "guerrier", "archer", "mage"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier", // <-- le champ qui manquait, decide TOUT le comportement
      chance: 0.8,
      statModifiers: { stunned: true }, // effet de stun
      durationMs: 2000, // duree en ms
    },
    price: 450,
  },
  reinforcedAdamantineDagger: {
    id: "reinforcedAdamantineDagger",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Dague en adamantine renforcée",
    description: "+18 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 18 },
    stackable: false,
    archetypes: ["voleur", "guerrier", "archer", "mage"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier", // <-- le champ qui manquait, decide TOUT le comportement
      chance: 0.9,
      statModifiers: { stunned: true }, // effet de stun
      durationMs: 2000, // duree en ms
    },
    price: 550,
  },
  reinfircedCrimsoonDagger: {
    id: "reinfircedCrimsoonDagger",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Dague en cramoisi renforcée",
    description: "+20 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 20 },
    stackable: false,
    archetypes: ["voleur", "guerrier", "archer", "mage"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier", // <-- le champ qui manquait, decide TOUT le comportement
      chance: 1.0,
      statModifiers: { stunned: true }, // effet de stun
      durationMs: 2000, // duree en ms
    },
    price: 650,
  },
  reinforcedAngelicDagger: {
    id: "reinforcedAngelicDagger",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Dague en angélique renforcée",
    description: "+22 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 22 },
    stackable: false,
    archetypes: ["voleur", "guerrier", "archer", "mage"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier", // <-- le champ qui manquait, decide TOUT le comportement
      chance: 1.0,
      statModifiers: { stunned: true }, // effet de stun
      durationMs: 2000, // duree en ms
    },
    price: 750,
  },
  reinforcedFatefulDagger: {
    id: "reinforcedFatefulDagger",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Dague fatale renforcée",
    description: "+25 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 25 },
    stackable: false,
    archetypes: ["voleur", "guerrier", "archer", "mage"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier", // <-- le champ qui manquait, decide TOUT le comportement
      chance: 1.0,
      statModifiers: { stunned: true }, // effet de stun
      durationMs: 2000, // duree en ms
    },
    price: 850,
  },
  reinforcedNovaDagger: {
    id: "reinforcedNovaDagger",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Dague nova renforcée",
    description: "+28 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 28 },
    stackable: false,
    archetypes: ["voleur", "guerrier", "archer", "mage"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier", // <-- le champ qui manquait, decide TOUT le comportement
      chance: 1.0,
      statModifiers: { stunned: true }, // effet de stun
      durationMs: 2000, // duree en ms
    },
    price: 950,
  },

  woodenSword: {
    id: "woodenSword",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Épée en bois",
    description: "+1 dégât au corps à corps. Arme d'entraînement de départ.",
    statBonus: { meleeDamage: 1 },
    stackable: false,
    archetypes: ["guerrier"],
    price: 2,
  },
  copperSword: {
    id: "copperSword",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Épée en cuivre",
    description: "+3 dégâts au corps à corps.",
    statBonus: { meleeDamage: 3 },
    stackable: false,
    archetypes: ["guerrier"],
    price: 30,
  },
  ironSword: {
    id: "ironSword",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Épée de fer",
    description: "+5 dégâts au corps à corps.",
    statBonus: { meleeDamage: 5 },
    stackable: false,
    archetypes: ["guerrier"],
    price: 60,
  },
  steelSword: {
    id: "steelSword",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Épée en acier",
    description: "+7 dégâts au corps à corps.",
    statBonus: { meleeDamage: 7 },
    stackable: false,
    archetypes: ["guerrier"],
    price: 100,
  },
  silverSword: {
    id: "silverSword",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Épée en argent",
    description: "+9 dégâts au corps à corps.",
    statBonus: { meleeDamage: 9 },
    stackable: false,
    archetypes: ["guerrier"],
    price: 150,
  },
  goldSword: {
    id: "goldSword",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Épée en or",
    description: "+11 dégâts au corps à corps.",
    statBonus: { meleeDamage: 11 },
    stackable: false,
    archetypes: ["guerrier"],
    price: 200,
  },
  platiniumSword: {
    id: "platiniumSword",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Épée en platine",
    description: "+13 dégâts au corps à corps.",
    statBonus: { meleeDamage: 13 },
    stackable: false,
    archetypes: ["guerrier"],
    price: 300,
  },
  cobaltSword: {
    id: "cobaltSword",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Épée en cobalt",
    description: "+15 dégâts au corps à corps.",
    statBonus: { meleeDamage: 15 },
    stackable: false,
    archetypes: ["guerrier"],
    price: 400,
  },
  adamantineSword: {
    id: "adamantineSword",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Épée en adamantine",
    description: "+17 dégâts au corps à corps.",
    statBonus: { meleeDamage: 17 },
    stackable: false,
    archetypes: ["guerrier"],
    price: 500,
  },
  crimsonSword: {
    id: "crimsonSword",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Épée cramoisie",
    description: "+19 dégâts au corps à corps.",
    statBonus: { meleeDamage: 19 },
    stackable: false,
    archetypes: ["guerrier"],
    price: 600,
  },
  angelicSword: {
    id: "angelicSword",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Épée angélique",
    description: "+21 dégâts au corps à corps.",
    statBonus: { meleeDamage: 21 },
    stackable: false,
    archetypes: ["guerrier"],
    price: 700,
  },
  fatefulSword: {
    id: "fatefulSword",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Épée fatale",
    description: "+23 dégâts au corps à corps.",
    statBonus: { meleeDamage: 23 },
    stackable: false,
    archetypes: ["guerrier"],
    price: 800,
  },
  novaSword: {
    id: "novaSword",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Épée nova",
    description: "+25 dégâts au corps à corps.",
    statBonus: { meleeDamage: 25 },
    stackable: false,
    archetypes: ["guerrier"],
    price: 900,
  },
  sharpCopperSword: {
    id: "sharpCopperSword",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Épée en cuivre aiguisée. Inflige un saignement à l'ennemi.",
    description: "+4 dégâts au corps à corps.",
    statBonus: { meleeDamage: 4 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 50,
  },
  sharpIronSword: {
    id: "sharpIronSword",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Épée en fer aiguisée. Inflige un saignement à l'ennemi.",
    description: "+5 dégâts au corps à corps.",
    statBonus: { meleeDamage: 5 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 60,
  },
  sharpSteelSword: {
    id: "sharpSteelSword",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Épée en acier aiguisée. Inflige un saignement à l'ennemi.",
    description: "+6 dégâts au corps à corps.",
    statBonus: { meleeDamage: 6 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 70,
  },
  sharpSilverSword: {
    id: "sharpSilverSword",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Épée en argent aiguisée. Inflige un saignement à l'ennemi.",
    description: "+7 dégâts au corps à corps.",
    statBonus: { meleeDamage: 7 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 80,
  },
  sharpGoldSword: {
    id: "sharpGoldSword",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Épée en or aiguisée. Inflige un saignement à l'ennemi.",
    description: "+8 dégâts au corps à corps.",
    statBonus: { meleeDamage: 8 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 90,
  },
  sharpPlatiniumSword: {
    id: "sharpPlatiniumSword",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Épée en platine aiguisée. Inflige un saignement à l'ennemi.",
    description: "+9 dégâts au corps à corps.",
    statBonus: { meleeDamage: 9 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 100,
  },
  sharpCobaltSword: {
    id: "sharpCobaltSword",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Épée en cobalt aiguisée. Inflige un saignement à l'ennemi.",
    description: "+10 dégâts au corps à corps.",
    statBonus: { meleeDamage: 10 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 110,
  },
  sharpAdamantineSword: {
    id: "sharpAdamantineSword",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Épée en adamantine aiguisée. Inflige un saignement à l'ennemi.",
    description: "+11 dégâts au corps à corps.",
    statBonus: { meleeDamage: 11 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 120,
  },
  sharpCrimsonSword: {
    id: "sharpCrimsonSword",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Épée en cramoisi aiguisée. Inflige un saignement à l'ennemi.",
    description: "+12 dégâts au corps à corps.",
    statBonus: { meleeDamage: 12 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 130,
  },
  sharpAngelicSword: {
    id: "sharpAngelicSword",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Épée en angélique aiguisée. Inflige un saignement à l'ennemi.",
    description: "+13 dégâts au corps à corps.",
    statBonus: { meleeDamage: 13 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 140,
  },
  sharpFatefulSword: {
    id: "sharpFatefulSword",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Épée en fatidique aiguisée. Inflige un saignement à l'ennemi.",
    description: "+14 dégâts au corps à corps.",
    statBonus: { meleeDamage: 14 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 150,
  },
  sharpNovaSword: {
    id: "sharpNovaSword",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Épée en nova aiguisée. Inflige un saignement à l'ennemi.",
    description: "+15 dégâts au corps à corps.",
    statBonus: { meleeDamage: 15 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 160,
  },

  // ----- Épée renforcée (reinforced) - branche METAL -----
  reinforcedWoodenSword: {
    id: "reinforcedWoodenSword",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Épée en bois renforcée",
    description: "+4 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 4 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.2,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 50,
  },
  reinforcedCopperSword: {
    id: "reinforcedCopperSword",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Épée en cuivre renforcée",
    description: "+5 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 5 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.25,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 65,
  },
  reinforcedIronSword: {
    id: "reinforcedIronSword",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Épée en fer renforcée",
    description: "+6 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 6 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.3,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 80,
  },
  reinforcedSteelSword: {
    id: "reinforcedSteelSword",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Épée en acier renforcée",
    description: "+8 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 8 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.4,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 120,
  },
  reinforcedSilverSword: {
    id: "reinforcedSilverSword",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Épée en argent renforcée",
    description: "+10 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 10 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.5,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 180,
  },
  reinforcedGoldSword: {
    id: "reinforcedGoldSword",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Épée en or renforcée",
    description: "+12 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 12 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.6,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 250,
  },
  reinforcedPlatiniumSword: {
    id: "reinforcedPlatiniumSword",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Épée en platine renforcée",
    description: "+14 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 14 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.7,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 350,
  },
  reinforcedCobaltSword: {
    id: "reinforcedCobaltSword",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Épée en cobalt renforcée",
    description: "+16 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 16 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.8,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 450,
  },
  reinforcedAdamantineSword: {
    id: "reinforcedAdamantineSword",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Épée en adamantine renforcée",
    description: "+18 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 18 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.9,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 550,
  },
  reinforcedCrimsonSword: {
    id: "reinforcedCrimsonSword",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Épée en cramoisi renforcée",
    description: "+20 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 20 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 650,
  },
  reinforcedAngelicSword: {
    id: "reinforcedAngelicSword",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Épée en angélique renforcée",
    description: "+22 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 22 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 750,
  },
  reinforcedFatefulSword: {
    id: "reinforcedFatefulSword",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Épée en fatidique renforcée",
    description: "+25 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 25 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 850,
  },
  reinforcedNovaSword: {
    id: "reinforcedNovaSword",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Épée en nova renforcée",
    description: "+28 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 28 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 950,
  },

  woodenBow: {
    id: "woodenBow",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true, // occupe les DEUX mains - equiper libere mainHand ET offHand (cf. MainScene.equipItem)
    grantsRanged: true, // sans arme marquee ainsi equipee (ici ou en offHand), l'attaque a distance est indisponible - cf. MainScene.canUseRangedAttack
    requiresAmmo: ["woodenArrow", "bigWoodenArrow"], // itemId EXACT requis (pas juste un booleen) - un carreau ne peut pas alimenter un arc, cf. MainScene.performRangedAttack
    name: "Arc en bois",
    description:
      "+1 dégât à distance. Nécessite des flèches. Arme d'entraînement de départ.",
    statBonus: { rangedDamage: 1 },
    stackable: false,
    archetypes: ["archer"],
    price: 2,
  },
  oakBow: {
    id: "oakBow",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: ["woodenArrow", "bigWoodenArrow", "ironArrow", "acidArrow"],
    name: "Arc en bois chêne",
    description: "+3 dégâts à distance. +2 de distance d'attaque.",
    statBonus: { rangedDamage: 3, rangedRange: 2 },
    stackable: false,
    archetypes: ["archer"],
    price: 30,
  },
  ashBow: {
    id: "ashBow",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: ["woodenArrow", "bigWoodenArrow", "ironArrow", "acidArrow"],
    name: "Arc en bois frêne",
    description: "+5 dégâts à distance. +3 de distance d'attaque.",
    statBonus: { rangedDamage: 5, rangedRange: 3 },
    stackable: false,
    archetypes: ["archer"],
    price: 60,
  },
  yewBow: {
    id: "yewBow",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: ["woodenArrow", "bigWoodenArrow", "ironArrow", "acidArrow"],
    name: "Arc en bois if",
    description: "+7 dégâts à distance. +4 de distance d'attaque.",
    statBonus: { rangedDamage: 7, rangedRange: 4 },
    stackable: false,
    archetypes: ["archer"],
    price: 100,
  },
  ebonyBow: {
    id: "ebonyBow",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: ["woodenArrow", "bigWoodenArrow", "ironArrow", "acidArrow"],
    name: "Arc en bois ébène",
    description: "+9 dégâts à distance. +5 de distance d'attaque.",
    statBonus: { rangedDamage: 9, rangedRange: 5 },
    stackable: false,
    archetypes: ["archer"],
    price: 150,
  },
  petrifiedBow: {
    id: "petrifiedBow",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: ["woodenArrow", "bigWoodenArrow", "ironArrow", "acidArrow"],
    name: "Arc en bois pétrifié",
    description: "+11 dégâts à distance. +6 de distance d'attaque.",
    statBonus: { rangedDamage: 11, rangedRange: 6 },
    stackable: false,
    archetypes: ["archer"],
    price: 200,
  },
  mistwoodBow: {
    id: "mistwoodBow",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: ["woodenArrow", "bigWoodenArrow", "ironArrow", "acidArrow"],
    name: "Arc en bois de brume",
    description: "+13 dégâts à distance. +7 de distance d'attaque.",
    statBonus: { rangedDamage: 13, rangedRange: 7 },
    stackable: false,
    archetypes: ["archer"],
    price: 300,
  },
  runewoodBow: {
    id: "runewoodBow",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: ["woodenArrow", "bigWoodenArrow", "ironArrow", "acidArrow"],
    name: "Arc en bois runique",
    description: "+15 dégâts à distance. +8 de distance d'attaque.",
    statBonus: { rangedDamage: 15, rangedRange: 8 },
    stackable: false,
    archetypes: ["archer"],
    price: 400,
  },
  skywoodBow: {
    id: "skywoodBow",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: ["woodenArrow", "bigWoodenArrow", "ironArrow", "acidArrow"],
    name: "Arc en bois céleste",
    description: "+17 dégâts à distance. +9 de distance d'attaque.",
    statBonus: { rangedDamage: 17, rangedRange: 9 },
    stackable: false,
    archetypes: ["archer"],
    price: 500,
  },
  scarletwoodBow: {
    id: "scarletwoodBow",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: ["woodenArrow", "bigWoodenArrow", "ironArrow", "acidArrow"],
    name: "Arc en bois écarlate",
    description: "+19 dégâts à distance. +10 de distance d'attaque.",
    statBonus: { rangedDamage: 19, rangedRange: 10 },
    stackable: false,
    archetypes: ["archer"],
    price: 600,
  },
  sacredBow: {
    id: "sacredBow",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: ["woodenArrow", "bigWoodenArrow", "ironArrow", "acidArrow"],
    name: "Arc en bois sacré",
    description: "+21 dégâts à distance. +11 de distance d'attaque.",
    statBonus: { rangedDamage: 21, rangedRange: 11 },
    stackable: false,
    archetypes: ["archer"],
    price: 700,
  },
  eternalBow: {
    id: "eternalBow",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: ["woodenArrow", "bigWoodenArrow", "ironArrow", "acidArrow"],
    name: "Arc en bois éternel",
    description: "+23 dégâts à distance. +12 de distance d'attaque.",
    statBonus: { rangedDamage: 23, rangedRange: 12 },
    stackable: false,
    archetypes: ["archer"],
    price: 800,
  },
  starwoodBow: {
    id: "starwoodBow",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: ["woodenArrow", "bigWoodenArrow", "ironArrow", "acidArrow"],
    name: "Arc en bois stellaire",
    description: "+25 dégâts à distance. +13 de distance d'attaque.",
    statBonus: { rangedDamage: 25, rangedRange: 13 },
    stackable: false,
    archetypes: ["archer"],
    price: 900,
  },
  reinforcedWoodenBow: {
    id: "reinforcedWoodenBow",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: ["woodenArrow", "bigWoodenArrow"],
    name: "Arc en bois renforcé",
    description: "+4 dégâts à distance (chance d'infliger stun).",
    statBonus: { rangedDamage: 4 },
    stackable: false,
    archetypes: ["archer"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.2,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 50,
  },
  reinforcedOakBow: {
    id: "reinforcedOakBow",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: ["woodenArrow", "bigWoodenArrow", "ironArrow", "acidArrow"],
    name: "Arc en chêne renforcé",
    description:
      "+5 dégâts à distance. +2 de distance d'attaque (chance d'infliger stun).",
    statBonus: { rangedDamage: 5, rangedRange: 2 },
    stackable: false,
    archetypes: ["archer"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.25,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 65,
  },
  reinforcedAshBow: {
    id: "reinforcedAshBow",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: ["woodenArrow", "bigWoodenArrow", "ironArrow", "acidArrow"],
    name: "Arc en frêne renforcé",
    description:
      "+6 dégâts à distance. +3 de distance d'attaque (chance d'infliger stun).",
    statBonus: { rangedDamage: 6, rangedRange: 3 },
    stackable: false,
    archetypes: ["archer"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.3,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 80,
  },
  reinforcedYewBow: {
    id: "reinforcedYewBow",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: ["woodenArrow", "bigWoodenArrow", "ironArrow", "acidArrow"],
    name: "Arc en if renforcé",
    description:
      "+8 dégâts à distance. +4 de distance d'attaque (chance d'infliger stun).",
    statBonus: { rangedDamage: 8, rangedRange: 4 },
    stackable: false,
    archetypes: ["archer"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.4,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 120,
  },
  reinforcedEbonyBow: {
    id: "reinforcedEbonyBow",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: ["woodenArrow", "bigWoodenArrow", "ironArrow", "acidArrow"],
    name: "Arc en ébène renforcé",
    description:
      "+10 dégâts à distance. +5 de distance d'attaque (chance d'infliger stun).",
    statBonus: { rangedDamage: 10, rangedRange: 5 },
    stackable: false,
    archetypes: ["archer"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.5,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 180,
  },
  reinforcedPetrifiedBow: {
    id: "reinforcedPetrifiedBow",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: ["woodenArrow", "bigWoodenArrow", "ironArrow", "acidArrow"],
    name: "Arc en bois pétrifié renforcé",
    description:
      "+12 dégâts à distance. +6 de distance d'attaque (chance d'infliger stun).",
    statBonus: { rangedDamage: 12, rangedRange: 6 },
    stackable: false,
    archetypes: ["archer"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.6,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 250,
  },
  reinforcedMistwoodBow: {
    id: "reinforcedMistwoodBow",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: ["woodenArrow", "bigWoodenArrow", "ironArrow", "acidArrow"],
    name: "Arc en bois de brume renforcé",
    description:
      "+14 dégâts à distance. +7 de distance d'attaque (chance d'infliger stun).",
    statBonus: { rangedDamage: 14, rangedRange: 7 },
    stackable: false,
    archetypes: ["archer"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.7,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 350,
  },
  reinforcedRunewoodBow: {
    id: "reinforcedRunewoodBow",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: ["woodenArrow", "bigWoodenArrow", "ironArrow", "acidArrow"],
    name: "Arc en bois runique renforcé",
    description:
      "+16 dégâts à distance. +8 de distance d'attaque (chance d'infliger stun).",
    statBonus: { rangedDamage: 16, rangedRange: 8 },
    stackable: false,
    archetypes: ["archer"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.8,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 450,
  },
  reinforcedSkywoodBow: {
    id: "reinforcedSkywoodBow",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: ["woodenArrow", "bigWoodenArrow", "ironArrow", "acidArrow"],
    name: "Arc en bois céleste renforcé",
    description:
      "+18 dégâts à distance. +9 de distance d'attaque (chance d'infliger stun).",
    statBonus: { rangedDamage: 18, rangedRange: 9 },
    stackable: false,
    archetypes: ["archer"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.9,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 550,
  },
  reinforcedScarletwoodBow: {
    id: "reinforcedScarletwoodBow",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: ["woodenArrow", "bigWoodenArrow", "ironArrow", "acidArrow"],
    name: "Arc en bois écarlate renforcé",
    description:
      "+20 dégâts à distance. +10 de distance d'attaque (chance d'infliger stun).",
    statBonus: { rangedDamage: 20, rangedRange: 10 },
    stackable: false,
    archetypes: ["archer"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 650,
  },
  reinforcedSacredBow: {
    id: "reinforcedSacredBow",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: ["woodenArrow", "bigWoodenArrow", "ironArrow", "acidArrow"],
    name: "Arc en bois sacré renforcé",
    description:
      "+22 dégâts à distance. +11 de distance d'attaque (chance d'infliger stun).",
    statBonus: { rangedDamage: 22, rangedRange: 11 },
    stackable: false,
    archetypes: ["archer"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 750,
  },
  reinforcedEternalBow: {
    id: "reinforcedEternalBow",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: ["woodenArrow", "bigWoodenArrow", "ironArrow", "acidArrow"],
    name: "Arc en bois éternel renforcé",
    description:
      "+25 dégâts à distance. +12 de distance d'attaque (chance d'infliger stun).",
    statBonus: { rangedDamage: 25, rangedRange: 12 },
    stackable: false,
    archetypes: ["archer"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 850,
  },
  reinforcedStarwoodBow: {
    id: "reinforcedStarwoodBow",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: ["woodenArrow", "bigWoodenArrow", "ironArrow", "acidArrow"],
    name: "Arc en bois stellaire renforcé",
    description:
      "+28 dégâts à distance. +13 de distance d'attaque (chance d'infliger stun).",
    statBonus: { rangedDamage: 28, rangedRange: 13 },
    stackable: false,
    archetypes: ["archer"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 950,
  },

  woodenCrossbow: {
    id: "woodenCrossbow",
    category: "equipment",
    slot: "offHand",
    twoHanded: false,
    grantsRanged: true,
    requiresAmmo: ["woodenCrossbowBolt"],
    name: "Arbalète en bois",
    description:
      "+1 dégât à distance, Nécessite des carreaux. +1 de distance d'attaque. Arme d'entraînement de départ.",
    statBonus: { rangedDamage: 1, rangedRange: 1 },
    archetypes: ["archer", "voleur"],
    stackable: false,
    price: 2,
  },
  oakCrossbow: {
    id: "oakCrossbow",
    category: "equipment",
    slot: "offHand",
    twoHanded: false,
    grantsRanged: true,
    requiresAmmo: [
      "woodenCrossbowBolt",
      "ironCrossbowBolt",
      "acidCrossbowBolt",
    ],
    name: "Arbalète en bois chêne",
    description: "+2 dégâts à distance. +1 de distance d'attaque.",
    statBonus: { rangedDamage: 2, rangedRange: 1 },
    stackable: false,
    archetypes: ["archer", "voleur"],
    price: 30,
  },
  ashCrossbow: {
    id: "ashCrossbow",
    category: "equipment",
    slot: "offHand",
    twoHanded: false,
    grantsRanged: true,
    requiresAmmo: [
      "woodenCrossbowBolt",
      "ironCrossbowBolt",
      "acidCrossbowBolt",
    ],
    name: "Arbalète en bois frêne",
    description: "+4 dégâts à distance. +2 de distance d'attaque.",
    statBonus: { rangedDamage: 4, rangedRange: 2 },
    stackable: false,
    archetypes: ["archer", "voleur"],
    price: 60,
  },
  yewCrossbow: {
    id: "yewCrossbow",
    category: "equipment",
    slot: "offHand",
    twoHanded: false,
    grantsRanged: true,
    requiresAmmo: [
      "woodenCrossbowBolt",
      "ironCrossbowBolt",
      "acidCrossbowBolt",
    ],
    name: "Arbalète en bois if",
    description: "+6 dégâts à distance. +3 de distance d'attaque.",
    statBonus: { rangedDamage: 6, rangedRange: 3 },
    stackable: false,
    archetypes: ["archer", "voleur"],
    price: 100,
  },
  ebonyCrossbow: {
    id: "ebonyCrossbow",
    category: "equipment",
    slot: "offHand",
    twoHanded: false,
    grantsRanged: true,
    requiresAmmo: [
      "woodenCrossbowBolt",
      "ironCrossbowBolt",
      "acidCrossbowBolt",
    ],
    name: "Arbalète en bois ébène",
    description: "+8 dégâts à distance. +4 de distance d'attaque.",
    statBonus: { rangedDamage: 8, rangedRange: 4 },
    stackable: false,
    archetypes: ["archer", "voleur"],
    price: 150,
  },
  petrifiedCrossbow: {
    id: "petrifiedCrossbow",
    category: "equipment",
    slot: "offHand",
    twoHanded: false,
    grantsRanged: true,
    requiresAmmo: [
      "woodenCrossbowBolt",
      "ironCrossbowBolt",
      "acidCrossbowBolt",
    ],
    name: "Arbalète en bois pétrifié",
    description: "+10 dégâts à distance. +5 de distance d'attaque.",
    statBonus: { rangedDamage: 10, rangedRange: 5 },
    stackable: false,
    archetypes: ["archer", "voleur"],
    price: 200,
  },
  mistwoodCrossbow: {
    id: "mistwoodCrossbow",
    category: "equipment",
    slot: "offHand",
    twoHanded: false,
    grantsRanged: true,
    requiresAmmo: [
      "woodenCrossbowBolt",
      "ironCrossbowBolt",
      "acidCrossbowBolt",
    ],
    name: "Arbalète en bois de brume",
    description: "+12 dégâts à distance. +6 de distance d'attaque.",
    statBonus: { rangedDamage: 12, rangedRange: 6 },
    stackable: false,
    archetypes: ["archer", "voleur"],
    price: 300,
  },
  runewoodCrossbow: {
    id: "runewoodCrossbow",
    category: "equipment",
    slot: "offHand",
    twoHanded: false,
    grantsRanged: true,
    requiresAmmo: [
      "woodenCrossbowBolt",
      "ironCrossbowBolt",
      "acidCrossbowBolt",
    ],
    name: "Arbalète en bois runique",
    description: "+14 dégâts à distance. +7 de distance d'attaque.",
    statBonus: { rangedDamage: 14, rangedRange: 7 },
    stackable: false,
    archetypes: ["archer", "voleur"],
    price: 400,
  },
  skywoodCrossbow: {
    id: "skywoodCrossbow",
    category: "equipment",
    slot: "offHand",
    twoHanded: false,
    grantsRanged: true,
    requiresAmmo: [
      "woodenCrossbowBolt",
      "ironCrossbowBolt",
      "acidCrossbowBolt",
    ],
    name: "Arbalète en bois céleste",
    description: "+16 dégâts à distance. +8 de distance d'attaque.",
    statBonus: { rangedDamage: 16, rangedRange: 8 },
    stackable: false,
    archetypes: ["archer", "voleur"],
    price: 500,
  },
  scarletwoodCrossbow: {
    id: "scarletwoodCrossbow",
    category: "equipment",
    slot: "offHand",
    twoHanded: false,
    grantsRanged: true,
    requiresAmmo: [
      "woodenCrossbowBolt",
      "ironCrossbowBolt",
      "acidCrossbowBolt",
    ],
    name: "Arbalète en bois écarlate",
    description: "+18 dégâts à distance. +9 de distance d'attaque.",
    statBonus: { rangedDamage: 18, rangedRange: 9 },
    stackable: false,
    archetypes: ["archer", "voleur"],
    price: 600,
  },
  sacredCrossbow: {
    id: "sacredCrossbow",
    category: "equipment",
    slot: "offHand",
    twoHanded: false,
    grantsRanged: true,
    requiresAmmo: [
      "woodenCrossbowBolt",
      "ironCrossbowBolt",
      "acidCrossbowBolt",
    ],
    name: "Arbalète en bois sacré",
    description: "+20 dégâts à distance. +10 de distance d'attaque.",
    statBonus: { rangedDamage: 20, rangedRange: 10 },
    stackable: false,
    archetypes: ["archer", "voleur"],
    price: 700,
  },
  eternalCrossbow: {
    id: "eternalCrossbow",
    category: "equipment",
    slot: "offHand",
    twoHanded: false,
    grantsRanged: true,
    requiresAmmo: [
      "woodenCrossbowBolt",
      "ironCrossbowBolt",
      "acidCrossbowBolt",
    ],
    name: "Arbalète en bois éternel",
    description: "+22 dégâts à distance. +11 de distance d'attaque.",
    statBonus: { rangedDamage: 22, rangedRange: 11 },
    stackable: false,
    archetypes: ["archer", "voleur"],
    price: 800,
  },
  starwoodCrossbow: {
    id: "starwoodCrossbow",
    category: "equipment",
    slot: "offHand",
    twoHanded: false,
    grantsRanged: true,
    requiresAmmo: [
      "woodenCrossbowBolt",
      "ironCrossbowBolt",
      "acidCrossbowBolt",
    ],
    name: "Arbalète en bois stellaire",
    description: "+24 dégâts à distance. +12 de distance d'attaque.",
    statBonus: { rangedDamage: 24, rangedRange: 12 },
    stackable: false,
    archetypes: ["archer", "voleur"],
    price: 900,
  },
  // ----- Arbalète renforcée - branche BOIS uniquement (pas de version aiguisée) -----
  reinforcedWoodenCrossbow: {
    id: "reinforcedWoodenCrossbow",
    category: "equipment",
    slot: "offHand",
    twoHanded: false,
    grantsRanged: true,
    requiresAmmo: ["woodenCrossbowBolt"],
    name: "Arbalète en bois renforcée",
    description: "+4 dégâts à distance (chance d'infliger stun).",
    statBonus: { rangedDamage: 4 },
    stackable: false,
    archetypes: ["archer", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.2,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 50,
  },
  reinforcedOakCrossbow: {
    id: "reinforcedOakCrossbow",
    category: "equipment",
    slot: "offHand",
    twoHanded: false,
    grantsRanged: true,
    requiresAmmo: [
      "woodenCrossbowBolt",
      "ironCrossbowBolt",
      "acidCrossbowBolt",
    ],
    name: "Arbalète en chêne renforcée",
    description:
      "+5 dégâts à distance. +1 de distance d'attaque (chance d'infliger stun).",
    statBonus: { rangedDamage: 5, rangedRange: 1 },
    stackable: false,
    archetypes: ["archer", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.25,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 65,
  },
  reinforcedAshCrossbow: {
    id: "reinforcedAshCrossbow",
    category: "equipment",
    slot: "offHand",
    twoHanded: false,
    grantsRanged: true,
    requiresAmmo: [
      "woodenCrossbowBolt",
      "ironCrossbowBolt",
      "acidCrossbowBolt",
    ],
    name: "Arbalète en frêne renforcée",
    description:
      "+6 dégâts à distance. +2 de distance d'attaque (chance d'infliger stun).",
    statBonus: { rangedDamage: 6, rangedRange: 2 },
    stackable: false,
    archetypes: ["archer", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.3,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 80,
  },
  reinforcedYewCrossbow: {
    id: "reinforcedYewCrossbow",
    category: "equipment",
    slot: "offHand",
    twoHanded: false,
    grantsRanged: true,
    requiresAmmo: [
      "woodenCrossbowBolt",
      "ironCrossbowBolt",
      "acidCrossbowBolt",
    ],
    name: "Arbalète en if renforcée",
    description:
      "+8 dégâts à distance. +3 de distance d'attaque (chance d'infliger stun).",
    statBonus: { rangedDamage: 8, rangedRange: 3 },
    stackable: false,
    archetypes: ["archer", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.4,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 120,
  },
  reinforcedEbonyCrossbow: {
    id: "reinforcedEbonyCrossbow",
    category: "equipment",
    slot: "offHand",
    twoHanded: false,
    grantsRanged: true,
    requiresAmmo: [
      "woodenCrossbowBolt",
      "ironCrossbowBolt",
      "acidCrossbowBolt",
    ],
    name: "Arbalète en ébène renforcée",
    description:
      "+10 dégâts à distance. +4 de distance d'attaque (chance d'infliger stun).",
    statBonus: { rangedDamage: 10, rangedRange: 4 },
    stackable: false,
    archetypes: ["archer", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.5,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 180,
  },
  reinforcedPetrifiedCrossbow: {
    id: "reinforcedPetrifiedCrossbow",
    category: "equipment",
    slot: "offHand",
    twoHanded: false,
    grantsRanged: true,
    requiresAmmo: [
      "woodenCrossbowBolt",
      "ironCrossbowBolt",
      "acidCrossbowBolt",
    ],
    name: "Arbalète en bois pétrifié renforcée",
    description:
      "+12 dégâts à distance. +5 de distance d'attaque (chance d'infliger stun).",
    statBonus: { rangedDamage: 12, rangedRange: 5 },
    stackable: false,
    archetypes: ["archer", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.6,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 250,
  },
  reinforcedMistwoodCrossbow: {
    id: "reinforcedMistwoodCrossbow",
    category: "equipment",
    slot: "offHand",
    twoHanded: false,
    grantsRanged: true,
    requiresAmmo: [
      "woodenCrossbowBolt",
      "ironCrossbowBolt",
      "acidCrossbowBolt",
    ],
    name: "Arbalète en bois de brume renforcée",
    description:
      "+14 dégâts à distance. +6 de distance d'attaque (chance d'infliger stun).",
    statBonus: { rangedDamage: 14, rangedRange: 6 },
    stackable: false,
    archetypes: ["archer", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.7,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 350,
  },
  reinforcedRunewoodCrossbow: {
    id: "reinforcedRunewoodCrossbow",
    category: "equipment",
    slot: "offHand",
    twoHanded: false,
    grantsRanged: true,
    requiresAmmo: [
      "woodenCrossbowBolt",
      "ironCrossbowBolt",
      "acidCrossbowBolt",
    ],
    name: "Arbalète en bois runique renforcée",
    description:
      "+16 dégâts à distance. +7 de distance d'attaque (chance d'infliger stun).",
    statBonus: { rangedDamage: 16, rangedRange: 7 },
    stackable: false,
    archetypes: ["archer", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.8,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 450,
  },
  reinforcedSkywoodCrossbow: {
    id: "reinforcedSkywoodCrossbow",
    category: "equipment",
    slot: "offHand",
    twoHanded: false,
    grantsRanged: true,
    requiresAmmo: [
      "woodenCrossbowBolt",
      "ironCrossbowBolt",
      "acidCrossbowBolt",
    ],
    name: "Arbalète en bois céleste renforcée",
    description:
      "+18 dégâts à distance. +8 de distance d'attaque (chance d'infliger stun).",
    statBonus: { rangedDamage: 18, rangedRange: 8 },
    stackable: false,
    archetypes: ["archer", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.9,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 550,
  },
  reinforcedScarletwoodCrossbow: {
    id: "reinforcedScarletwoodCrossbow",
    category: "equipment",
    slot: "offHand",
    twoHanded: false,
    grantsRanged: true,
    requiresAmmo: [
      "woodenCrossbowBolt",
      "ironCrossbowBolt",
      "acidCrossbowBolt",
    ],
    name: "Arbalète en bois écarlate renforcée",
    description:
      "+20 dégâts à distance. +9 de distance d'attaque (chance d'infliger stun).",
    statBonus: { rangedDamage: 20, rangedRange: 9 },
    stackable: false,
    archetypes: ["archer", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 650,
  },
  reinforcedSacredCrossbow: {
    id: "reinforcedSacredCrossbow",
    category: "equipment",
    slot: "offHand",
    twoHanded: false,
    grantsRanged: true,
    requiresAmmo: [
      "woodenCrossbowBolt",
      "ironCrossbowBolt",
      "acidCrossbowBolt",
    ],
    name: "Arbalète en bois sacré renforcée",
    description:
      "+22 dégâts à distance. +10 de distance d'attaque (chance d'infliger stun).",
    statBonus: { rangedDamage: 22, rangedRange: 10 },
    stackable: false,
    archetypes: ["archer", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 750,
  },
  reinforcedEternalCrossbow: {
    id: "reinforcedEternalCrossbow",
    category: "equipment",
    slot: "offHand",
    twoHanded: false,
    grantsRanged: true,
    requiresAmmo: [
      "woodenCrossbowBolt",
      "ironCrossbowBolt",
      "acidCrossbowBolt",
    ],
    name: "Arbalète en bois éternel renforcée",
    description:
      "+25 dégâts à distance. +11 de distance d'attaque (chance d'infliger stun).",
    statBonus: { rangedDamage: 25, rangedRange: 11 },
    stackable: false,
    archetypes: ["archer", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 850,
  },
  reinforcedStarwoodCrossbow: {
    id: "reinforcedStarwoodCrossbow",
    category: "equipment",
    slot: "offHand",
    twoHanded: false,
    grantsRanged: true,
    requiresAmmo: [
      "woodenCrossbowBolt",
      "ironCrossbowBolt",
      "acidCrossbowBolt",
    ],
    name: "Arbalète en bois stellaire renforcée",
    description:
      "+28 dégâts à distance. +12 de distance d'attaque (chance d'infliger stun).",
    statBonus: { rangedDamage: 28, rangedRange: 12 },
    stackable: false,
    archetypes: ["archer", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 950,
  },

  woodenSpear: {
    id: "woodenSpear",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Lance en bois",
    description:
      "+1 dégât au corps à corps. +2 de distance d'attaque. Arme d'entraînement de départ.",
    statBonus: { meleeDamage: 1, meleeRange: 2 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    price: 2,
  },
  copperSpear: {
    id: "copperSpear",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Lance en cuivre",
    description:
      "+2 dégât au corps à corps. +2 de distance d'attaque. Arme d'entraînement de départ.",
    statBonus: { meleeDamage: 2, meleeRange: 2 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    price: 2,
  },
  ironSpear: {
    id: "ironSpear",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Lance en fer",
    description: "+4 dégât au corps à corps. +3 de distance d'attaque.",
    statBonus: { meleeDamage: 4, meleeRange: 3 },
    stackable: false,
    archetypes: ["guerrier"],
    price: 55,
  },
  steelSpear: {
    id: "steelSpear",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Lance en acier",
    description: "+6 dégât au corps à corps. +3 de distance d'attaque.",
    statBonus: { meleeDamage: 6, meleeRange: 3 },
    stackable: false,
    archetypes: ["guerrier"],
    price: 120,
  },
  silverSpear: {
    id: "silverSpear",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Lance en argent",
    description: "+8 dégât au corps à corps. +3 de distance d'attaque.",
    statBonus: { meleeDamage: 8, meleeRange: 3 },
    stackable: false,
    archetypes: ["guerrier"],
    price: 200,
  },
  goldSpear: {
    id: "goldSpear",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Lance en or",
    description: "+10 dégât au corps à corps. +3 de distance d'attaque.",
    statBonus: { meleeDamage: 10, meleeRange: 3 },
    stackable: false,
    archetypes: ["guerrier"],
    price: 300,
  },
  platinumSpear: {
    id: "platinumSpear",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Lance en platine",
    description: "+12 dégât au corps à corps. +3 de distance d'attaque.",
    statBonus: { meleeDamage: 12, meleeRange: 3 },
    stackable: false,
    archetypes: ["guerrier"],
    price: 400,
  },
  cobaltSpear: {
    id: "cobaltSpear",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Lance en cobalt",
    description: "+14 dégât au corps à corps. +3 de distance d'attaque.",
    statBonus: { meleeDamage: 14, meleeRange: 3 },
    stackable: false,
    archetypes: ["guerrier"],
    price: 500,
  },
  adamantineSpear: {
    id: "adamantineSpear",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Lance en adamantine",
    description: "+16 dégât au corps à corps. +3 de distance d'attaque.",
    statBonus: { meleeDamage: 16, meleeRange: 3 },
    stackable: false,
    archetypes: ["guerrier"],
    price: 600,
  },
  crimsonSpear: {
    id: "crimsonSpear",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Lance en cramoisi",
    description: "+18 dégât au corps à corps. +3 de distance d'attaque.",
    statBonus: { meleeDamage: 18, meleeRange: 3 },
    stackable: false,
    archetypes: ["guerrier"],
    price: 700,
  },
  angelicSpear: {
    id: "angelicSpear",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Lance angélique",
    description: "+20 dégât au corps à corps. +3 de distance d'attaque.",
    statBonus: { meleeDamage: 20, meleeRange: 3 },
    stackable: false,
    archetypes: ["guerrier"],
    price: 800,
  },
  fatefulSpear: {
    id: "fatefulSpear",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Lance fatidique",
    description: "+22 dégât au corps à corps. +3 de distance d'attaque.",
    statBonus: { meleeDamage: 22, meleeRange: 3 },
    stackable: false,
    archetypes: ["guerrier"],
    price: 900,
  },
  novaSpear: {
    id: "novaSpear",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Lance nova",
    description: "+24 dégât au corps à corps. +3 de distance d'attaque.",
    statBonus: { meleeDamage: 24, meleeRange: 3 },
    stackable: false,
    archetypes: ["guerrier"],
    price: 1000,
  },
  sharpCopperSpear: {
    id: "sharpCopperSpear",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Lance en cuivre aiguisée. Inflige un saignement à l'ennemi.",
    description: "+4 dégâts au corps à corps. +3 de distance d'attaque.",
    statBonus: { meleeDamage: 4, meleeRange: 3 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 50,
  },
  sharpIronSpear: {
    id: "sharpIronSpear",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Lance en fer aiguisée. Inflige un saignement à l'ennemi.",
    description: "+5 dégâts au corps à corps. +3 de distance d'attaque.",
    statBonus: { meleeDamage: 5, meleeRange: 3 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 60,
  },
  sharpSteelSpear: {
    id: "sharpSteelSpear",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Lance en acier aiguisée. Inflige un saignement à l'ennemi.",
    description: "+6 dégâts au corps à corps. +3 de distance d'attaque.",
    statBonus: { meleeDamage: 6, meleeRange: 3 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 70,
  },
  sharpSilverSpear: {
    id: "sharpSilverSpear",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Lance en argent aiguisée. Inflige un saignement à l'ennemi.",
    description: "+7 dégâts au corps à corps. +3 de distance d'attaque.",
    statBonus: { meleeDamage: 7, meleeRange: 3 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 80,
  },
  sharpGoldSpear: {
    id: "sharpGoldSpear",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Lance en or aiguisée. Inflige un saignement à l'ennemi.",
    description: "+8 dégâts au corps à corps. +3 de distance d'attaque.",
    statBonus: { meleeDamage: 8, meleeRange: 3 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 90,
  },
  sharpPlatiniumSpear: {
    id: "sharpPlatiniumSpear",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Lance en platine aiguisée. Inflige un saignement à l'ennemi.",
    description: "+9 dégâts au corps à corps. +3 de distance d'attaque.",
    statBonus: { meleeDamage: 9, meleeRange: 3 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 100,
  },
  sharpCobaltSpear: {
    id: "sharpCobaltSpear",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Lance en cobalt aiguisée. Inflige un saignement à l'ennemi.",
    description: "+10 dégâts au corps à corps. +3 de distance d'attaque.",
    statBonus: { meleeDamage: 10, meleeRange: 3 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 110,
  },
  sharpAdamantineSpear: {
    id: "sharpAdamantineSpear",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Lance en adamantine aiguisée. Inflige un saignement à l'ennemi.",
    description: "+11 dégâts au corps à corps. +3 de distance d'attaque.",
    statBonus: { meleeDamage: 11, meleeRange: 3 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 120,
  },
  sharpCrimsonSpear: {
    id: "sharpCrimsonSpear",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Lance en cramoisi aiguisée. Inflige un saignement à l'ennemi.",
    description: "+12 dégâts au corps à corps. +3 de distance d'attaque.",
    statBonus: { meleeDamage: 12, meleeRange: 3 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 130,
  },
  sharpAngelicSpear: {
    id: "sharpAngelicSpear",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Lance en angélique aiguisée. Inflige un saignement à l'ennemi.",
    description: "+13 dégâts au corps à corps. +3 de distance d'attaque.",
    statBonus: { meleeDamage: 13, meleeRange: 3 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 140,
  },
  sharpFatefulSpear: {
    id: "sharpFatefulSpear",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Lance en fatidique aiguisée. Inflige un saignement à l'ennemi.",
    description: "+14 dégâts au corps à corps. +3 de distance d'attaque.",
    statBonus: { meleeDamage: 14, meleeRange: 3 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 150,
  },
  sharpNovaSpear: {
    id: "sharpNovaSpear",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Lance en nova aiguisée. Inflige un saignement à l'ennemi.",
    description: "+15 dégâts au corps à corps. +3 de distance d'attaque.",
    statBonus: { meleeDamage: 15, meleeRange: 3 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 160,
  },

  // ----- Lance renforcée (reinforced) - branche METAL -----
  reinforcedWoodenSpear: {
    id: "reinforcedWoodenSpear",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Lance en bois renforcée",
    description:
      "+4 dégât au corps à corps (chance d'infliger stun). +2 de distance d'attaque.",
    statBonus: { meleeDamage: 4, meleeRange: 2 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.2,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 50,
  },
  reinforcedCopperSpear: {
    id: "reinforcedCopperSpear",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Lance en cuivre renforcée",
    description:
      "+5 dégâts au corps à corps (chance d'infliger stun). +3 de distance d'attaque.",
    statBonus: { meleeDamage: 5, meleeRange: 3 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.25,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 65,
  },
  reinforcedIronSpear: {
    id: "reinforcedIronSpear",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Lance en fer renforcée",
    description:
      "+6 dégâts au corps à corps (chance d'infliger stun). +3 de distance d'attaque.",
    statBonus: { meleeDamage: 6, meleeRange: 3 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.3,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 80,
  },
  reinforcedSteelSpear: {
    id: "reinforcedSteelSpear",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Lance en acier renforcée",
    description:
      "+8 dégâts au corps à corps (chance d'infliger stun). +3 de distance d'attaque.",
    statBonus: { meleeDamage: 8, meleeRange: 3 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.4,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 120,
  },
  reinforcedSilverSpear: {
    id: "reinforcedSilverSpear",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Lance en argent renforcée",
    description:
      "+10 dégâts au corps à corps (chance d'infliger stun). +3 de distance d'attaque.",
    statBonus: { meleeDamage: 10, meleeRange: 3 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.5,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 180,
  },
  reinforcedGoldSpear: {
    id: "reinforcedGoldSpear",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Lance en or renforcée",
    description:
      "+12 dégâts au corps à corps (chance d'infliger stun). +3 de distance d'attaque.",
    statBonus: { meleeDamage: 12, meleeRange: 3 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.6,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 250,
  },
  reinforcedPlatiniumSpear: {
    id: "reinforcedPlatiniumSpear",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Lance en platine renforcée",
    description:
      "+14 dégâts au corps à corps (chance d'infliger stun). +3 de distance d'attaque.",
    statBonus: { meleeDamage: 14, meleeRange: 3 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.7,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 350,
  },
  reinforcedCobaltSpear: {
    id: "reinforcedCobaltSpear",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Lance en cobalt renforcée",
    description:
      "+16 dégâts au corps à corps (chance d'infliger stun). +3 de distance d'attaque.",
    statBonus: { meleeDamage: 16, meleeRange: 3 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.8,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 450,
  },
  reinforcedAdamantineSpear: {
    id: "reinforcedAdamantineSpear",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Lance en adamantine renforcée",
    description:
      "+18 dégâts au corps à corps (chance d'infliger stun). +3 de distance d'attaque.",
    statBonus: { meleeDamage: 18, meleeRange: 3 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.9,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 550,
  },
  reinforcedCrimsonSpear: {
    id: "reinforcedCrimsonSpear",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Lance en cramoisi renforcée",
    description:
      "+20 dégâts au corps à corps (chance d'infliger stun). +3 de distance d'attaque.",
    statBonus: { meleeDamage: 20, meleeRange: 3 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 650,
  },
  reinforcedAngelicSpear: {
    id: "reinforcedAngelicSpear",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Lance en angélique renforcée",
    description:
      "+22 dégâts au corps à corps (chance d'infliger stun). +3 de distance d'attaque.",
    statBonus: { meleeDamage: 22, meleeRange: 3 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 750,
  },
  reinforcedFatefulSpear: {
    id: "reinforcedFatefulSpear",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Lance en fatidique renforcée",
    description:
      "+25 dégâts au corps à corps (chance d'infliger stun). +3 de distance d'attaque.",
    statBonus: { meleeDamage: 25, meleeRange: 3 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 850,
  },
  reinforcedNovaSpear: {
    id: "reinforcedNovaSpear",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Lance en nova renforcée",
    description:
      "+28 dégâts au corps à corps (chance d'infliger stun). +3 de distance d'attaque.",
    statBonus: { meleeDamage: 28, meleeRange: 3 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 950,
  },

  woodenMallet: {
    id: "woodenMallet",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Masse en bois",
    description: "+1 dégât au corps à corps. Arme d'entraînement de départ.",
    statBonus: { meleeDamage: 1 },
    stackable: false,
    archetypes: ["guerrier"],
    price: 2,
  },
  copperMallet: {
    id: "copperMallet",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Masse en cuivre",
    description: "+3 dégât au corps à corps.",
    statBonus: { meleeDamage: 3 },
    stackable: false,
    archetypes: ["guerrier"],
    price: 30,
  },
  ironMallet: {
    id: "ironMallet",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Masse en fer",
    description: "+5 dégât au corps à corps.",
    statBonus: { meleeDamage: 5 },
    stackable: false,
    archetypes: ["guerrier"],
    price: 60,
  },
  steelMallet: {
    id: "steelMallet",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Masse en acier",
    description: "+7 dégât au corps à corps.",
    statBonus: { meleeDamage: 7 },
    stackable: false,
    archetypes: ["guerrier"],
    price: 100,
  },
  silverMallet: {
    id: "silverMallet",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Masse en argent",
    description: "+9 dégât au corps à corps.",
    statBonus: { meleeDamage: 9 },
    stackable: false,
    archetypes: ["guerrier"],
    price: 150,
  },
  goldMallet: {
    id: "goldMallet",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Masse en or",
    description: "+11 dégât au corps à corps.",
    statBonus: { meleeDamage: 11 },
    stackable: false,
    archetypes: ["guerrier"],
    price: 200,
  },
  platiniumMallet: {
    id: "platiniumMallet",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Masse en platine",
    description: "+13 dégât au corps à corps.",
    statBonus: { meleeDamage: 13 },
    stackable: false,
    archetypes: ["guerrier"],
    price: 300,
  },
  cobaltMallet: {
    id: "cobaltMallet",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Masse en cobalt",
    description: "+15 dégât au corps à corps.",
    statBonus: { meleeDamage: 15 },
    stackable: false,
    archetypes: ["guerrier"],
    price: 400,
  },
  adamantineMallet: {
    id: "adamantineMallet",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Masse en adamantine",
    description: "+17 dégât au corps à corps.",
    statBonus: { meleeDamage: 17 },
    stackable: false,
    archetypes: ["guerrier"],
    price: 500,
  },
  crimsonMallet: {
    id: "crimsonMallet",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Masse en cramoisi",
    description: "+19 dégât au corps à corps.",
    statBonus: { meleeDamage: 19 },
    stackable: false,
    archetypes: ["guerrier"],
    price: 600,
  },
  angelicMallet: {
    id: "angelicMallet",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Masse en angélique",
    description: "+21 dégât au corps à corps.",
    statBonus: { meleeDamage: 21 },
    stackable: false,
    archetypes: ["guerrier"],
    price: 700,
  },
  fatefulMallet: {
    id: "fatefulMallet",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Masse en fatidique",
    description: "+23 dégât au corps à corps.",
    statBonus: { meleeDamage: 23 },
    stackable: false,
    archetypes: ["guerrier"],
    price: 800,
  },
  novaMallet: {
    id: "novaMallet",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Masse en nova",
    description: "+25 dégât au corps à corps.",
    statBonus: { meleeDamage: 25 },
    stackable: false,
    archetypes: ["guerrier"],
    price: 900,
  },

  // ----- Masse aiguisée (sharp) - branche METAL uniquement -----
  sharpCopperMallet: {
    id: "sharpCopperMallet",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Masse en cuivre aiguisée. Inflige un saignement à l'ennemi.",
    description: "+4 dégâts au corps à corps.",
    statBonus: { meleeDamage: 4 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 50,
  },
  sharpIronMallet: {
    id: "sharpIronMallet",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Masse en fer aiguisée. Inflige un saignement à l'ennemi.",
    description: "+5 dégâts au corps à corps.",
    statBonus: { meleeDamage: 5 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 60,
  },
  sharpSteelMallet: {
    id: "sharpSteelMallet",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Masse en acier aiguisée. Inflige un saignement à l'ennemi.",
    description: "+6 dégâts au corps à corps.",
    statBonus: { meleeDamage: 6 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 70,
  },
  sharpSilverMallet: {
    id: "sharpSilverMallet",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Masse en argent aiguisée. Inflige un saignement à l'ennemi.",
    description: "+7 dégâts au corps à corps.",
    statBonus: { meleeDamage: 7 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 80,
  },
  sharpGoldMallet: {
    id: "sharpGoldMallet",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Masse en or aiguisée. Inflige un saignement à l'ennemi.",
    description: "+8 dégâts au corps à corps.",
    statBonus: { meleeDamage: 8 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 90,
  },
  sharpPlatiniumMallet: {
    id: "sharpPlatiniumMallet",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Masse en platine aiguisée. Inflige un saignement à l'ennemi.",
    description: "+9 dégâts au corps à corps.",
    statBonus: { meleeDamage: 9 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 100,
  },
  sharpCobaltMallet: {
    id: "sharpCobaltMallet",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Masse en cobalt aiguisée. Inflige un saignement à l'ennemi.",
    description: "+10 dégâts au corps à corps.",
    statBonus: { meleeDamage: 10 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 110,
  },
  sharpAdamantineMallet: {
    id: "sharpAdamantineMallet",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Masse en adamantine aiguisée. Inflige un saignement à l'ennemi.",
    description: "+11 dégâts au corps à corps.",
    statBonus: { meleeDamage: 11 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 120,
  },
  sharpCrimsonMallet: {
    id: "sharpCrimsonMallet",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Masse en cramoisi aiguisée. Inflige un saignement à l'ennemi.",
    description: "+12 dégâts au corps à corps.",
    statBonus: { meleeDamage: 12 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 130,
  },
  sharpAngelicMallet: {
    id: "sharpAngelicMallet",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Masse en angélique aiguisée. Inflige un saignement à l'ennemi.",
    description: "+13 dégâts au corps à corps.",
    statBonus: { meleeDamage: 13 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 140,
  },
  sharpFatefulMallet: {
    id: "sharpFatefulMallet",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Masse en fatidique aiguisée. Inflige un saignement à l'ennemi.",
    description: "+14 dégâts au corps à corps.",
    statBonus: { meleeDamage: 14 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 150,
  },
  sharpNovaMallet: {
    id: "sharpNovaMallet",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Masse en nova aiguisée. Inflige un saignement à l'ennemi.",
    description: "+15 dégâts au corps à corps.",
    statBonus: { meleeDamage: 15 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 160,
  },

  // ----- Masse renforcée (reinforced) - branche METAL -----
  reinforcedWoodenMallet: {
    id: "reinforcedWoodenMallet",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Masse en bois renforcée",
    description: "+4 dégât au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 4 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.2,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 50,
  },
  reinforcedCopperMallet: {
    id: "reinforcedCopperMallet",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Masse en cuivre renforcée",
    description: "+5 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 5 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.25,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 65,
  },
  reinforcedIronMallet: {
    id: "reinforcedIronMallet",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Masse en fer renforcée",
    description: "+6 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 6 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.3,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 80,
  },
  reinforcedSteelMallet: {
    id: "reinforcedSteelMallet",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Masse en acier renforcée",
    description: "+8 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 8 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.4,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 120,
  },
  reinforcedSilverMallet: {
    id: "reinforcedSilverMallet",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Masse en argent renforcée",
    description: "+10 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 10 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.5,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 180,
  },
  reinforcedGoldMallet: {
    id: "reinforcedGoldMallet",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Masse en or renforcée",
    description: "+12 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 12 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.6,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 250,
  },
  reinforcedPlatiniumMallet: {
    id: "reinforcedPlatiniumMallet",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Masse en platine renforcée",
    description: "+14 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 14 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.7,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 350,
  },
  reinforcedCobaltMallet: {
    id: "reinforcedCobaltMallet",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Masse en cobalt renforcée",
    description: "+16 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 16 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.8,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 450,
  },
  reinforcedAdamantineMallet: {
    id: "reinforcedAdamantineMallet",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Masse en adamantine renforcée",
    description: "+18 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 18 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.9,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 550,
  },
  reinforcedCrimsonMallet: {
    id: "reinforcedCrimsonMallet",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Masse en cramoisi renforcée",
    description: "+20 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 20 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 650,
  },
  reinforcedAngelicMallet: {
    id: "reinforcedAngelicMallet",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Masse en angélique renforcée",
    description: "+22 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 22 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 750,
  },
  reinforcedFatefulMallet: {
    id: "reinforcedFatefulMallet",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Masse en fatidique renforcée",
    description: "+25 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 25 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 850,
  },
  reinforcedNovaMallet: {
    id: "reinforcedNovaMallet",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Masse en nova renforcée",
    description: "+28 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 28 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 950,
  },

  woodenShovel: {
    id: "woodenShovel",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Pelle en bois",
    description:
      "+1 dégât au corps à corps. +1 de distance d'attaque. Arme d'entraînement de départ.",
    statBonus: { meleeDamage: 1, meleeRange: 1 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    price: 2,
  },
  copperShovel: {
    id: "copperShovel",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Pelle en cuivre",
    description: "+2 dégât au corps à corps. +1 de distance d'attaque.",
    statBonus: { meleeDamage: 2, meleeRange: 1 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    price: 30,
  },
  ironShovel: {
    id: "ironShovel",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Pelle en fer",
    description: "+4 dégât au corps à corps. +1 de distance d'attaque.",
    statBonus: { meleeDamage: 4, meleeRange: +1 },
    stackable: false,
    archetypes: ["guerrier"],
    price: 55,
  },
  steelShovel: {
    id: "steelShovel",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Pelle en acier",
    description: "+6 dégât au corps à corps. +1 de distance d'attaque.",
    statBonus: { meleeDamage: 6, meleeRange: 1 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    price: 100,
  },
  silverShovel: {
    id: "silverShovel",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Pelle en argent",
    description: "+8 dégât au corps à corps. +1 de distance d'attaque.",
    statBonus: { meleeDamage: 8, meleeRange: 1 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    price: 150,
  },
  goldShovel: {
    id: "goldShovel",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Pelle en or",
    description: "+10 dégât au corps à corps. +1 de distance d'attaque.",
    statBonus: { meleeDamage: 10, meleeRange: 1 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    price: 200,
  },
  platiniumShovel: {
    id: "platiniumShovel",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Pelle en platine",
    description: "+12 dégât au corps à corps. +1 de distance d'attaque.",
    statBonus: { meleeDamage: 12, meleeRange: 1 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    price: 300,
  },
  cobaltShovel: {
    id: "cobaltShovel",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Pelle en cobalt",
    description: "+14 dégât au corps à corps. +1 de distance d'attaque.",
    statBonus: { meleeDamage: 14, meleeRange: 1 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    price: 400,
  },
  adamantineShovel: {
    id: "adamantineShovel",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Pelle en adamantine",
    description: "+16 dégât au corps à corps. +1 de distance d'attaque.",
    statBonus: { meleeDamage: 16, meleeRange: 1 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    price: 500,
  },
  crimsonShovel: {
    id: "crimsonShovel",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Pelle en cramoisi",
    description: "+18 dégât au corps à corps. +1 de distance d'attaque.",
    statBonus: { meleeDamage: 18, meleeRange: 1 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    price: 600,
  },
  angelicShovel: {
    id: "angelicShovel",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Pelle en angélique",
    description: "+20 dégât au corps à corps. +1 de distance d'attaque.",
    statBonus: { meleeDamage: 20, meleeRange: 1 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    price: 700,
  },
  fatefulShovel: {
    id: "fatefulShovel",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Pelle en fatidique",
    description: "+22 dégât au corps à corps. +1 de distance d'attaque.",
    statBonus: { meleeDamage: 22, meleeRange: 1 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    price: 800,
  },
  novaShovel: {
    id: "novaShovel",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Pelle en nova",
    description: "+24 dégât au corps à corps. +1 de distance d'attaque.",
    statBonus: { meleeDamage: 24, meleeRange: 1 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    price: 900,
  },
  // ----- Pelle aiguisée (sharp) - branche METAL uniquement -----
  sharpCopperShovel: {
    id: "sharpCopperShovel",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Pelle en cuivre aiguisée. Inflige un saignement à l'ennemi.",
    description: "+4 dégâts au corps à corps. +1 de distance d'attaque.",
    statBonus: { meleeDamage: 4, meleeRange: 1 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 50,
  },
  sharpIronShovel: {
    id: "sharpIronShovel",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Pelle en fer aiguisée. Inflige un saignement à l'ennemi.",
    description: "+5 dégâts au corps à corps. +1 de distance d'attaque.",
    statBonus: { meleeDamage: 5, meleeRange: 1 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 60,
  },
  sharpSteelShovel: {
    id: "sharpSteelShovel",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Pelle en acier aiguisée. Inflige un saignement à l'ennemi.",
    description: "+6 dégâts au corps à corps. +1 de distance d'attaque.",
    statBonus: { meleeDamage: 6, meleeRange: 1 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 70,
  },
  sharpSilverShovel: {
    id: "sharpSilverShovel",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Pelle en argent aiguisée. Inflige un saignement à l'ennemi.",
    description: "+7 dégâts au corps à corps. +1 de distance d'attaque.",
    statBonus: { meleeDamage: 7, meleeRange: 1 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 80,
  },
  sharpGoldShovel: {
    id: "sharpGoldShovel",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Pelle en or aiguisée. Inflige un saignement à l'ennemi.",
    description: "+8 dégâts au corps à corps. +1 de distance d'attaque.",
    statBonus: { meleeDamage: 8, meleeRange: 1 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 90,
  },
  sharpPlatiniumShovel: {
    id: "sharpPlatiniumShovel",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Pelle en platine aiguisée. Inflige un saignement à l'ennemi.",
    description: "+9 dégâts au corps à corps. +1 de distance d'attaque.",
    statBonus: { meleeDamage: 9, meleeRange: 1 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 100,
  },
  sharpCobaltShovel: {
    id: "sharpCobaltShovel",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Pelle en cobalt aiguisée. Inflige un saignement à l'ennemi.",
    description: "+10 dégâts au corps à corps. +1 de distance d'attaque.",
    statBonus: { meleeDamage: 10, meleeRange: 1 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 110,
  },
  sharpAdamantineShovel: {
    id: "sharpAdamantineShovel",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Pelle en adamantine aiguisée. Inflige un saignement à l'ennemi.",
    description: "+11 dégâts au corps à corps. +1 de distance d'attaque.",
    statBonus: { meleeDamage: 11, meleeRange: 1 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 120,
  },
  sharpCrimsonShovel: {
    id: "sharpCrimsonShovel",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Pelle en cramoisi aiguisée. Inflige un saignement à l'ennemi.",
    description: "+12 dégâts au corps à corps. +1 de distance d'attaque.",
    statBonus: { meleeDamage: 12, meleeRange: 1 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 130,
  },
  sharpAngelicShovel: {
    id: "sharpAngelicShovel",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Pelle en angélique aiguisée. Inflige un saignement à l'ennemi.",
    description: "+13 dégâts au corps à corps. +1 de distance d'attaque.",
    statBonus: { meleeDamage: 13, meleeRange: 1 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 140,
  },
  sharpFatefulShovel: {
    id: "sharpFatefulShovel",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Pelle en fatidique aiguisée. Inflige un saignement à l'ennemi.",
    description: "+14 dégâts au corps à corps. +1 de distance d'attaque.",
    statBonus: { meleeDamage: 14, meleeRange: 1 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 150,
  },
  sharpNovaShovel: {
    id: "sharpNovaShovel",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Pelle en nova aiguisée. Inflige un saignement à l'ennemi.",
    description: "+15 dégâts au corps à corps. +1 de distance d'attaque.",
    statBonus: { meleeDamage: 15, meleeRange: 1 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 160,
  },

  // ----- Pelle renforcée (reinforced) - branche METAL -----
  reinforcedWoodenShovel: {
    id: "reinforcedWoodenShovel",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Pelle en bois renforcée",
    description:
      "+4 dégât au corps à corps (chance d'infliger stun). +1 de distance d'attaque.",
    statBonus: { meleeDamage: 4, meleeRange: 1 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.2,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 50,
  },
  reinforcedCopperShovel: {
    id: "reinforcedCopperShovel",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Pelle en cuivre renforcée",
    description:
      "+5 dégâts au corps à corps (chance d'infliger stun). +1 de distance d'attaque.",
    statBonus: { meleeDamage: 5, meleeRange: 1 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.25,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 65,
  },
  reinforcedIronShovel: {
    id: "reinforcedIronShovel",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Pelle en fer renforcée",
    description:
      "+6 dégâts au corps à corps (chance d'infliger stun). +1 de distance d'attaque.",
    statBonus: { meleeDamage: 6, meleeRange: 1 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.3,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 80,
  },
  reinforcedSteelShovel: {
    id: "reinforcedSteelShovel",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Pelle en acier renforcée",
    description:
      "+8 dégâts au corps à corps (chance d'infliger stun). +1 de distance d'attaque.",
    statBonus: { meleeDamage: 8, meleeRange: 1 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.4,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 120,
  },
  reinforcedSilverShovel: {
    id: "reinforcedSilverShovel",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Pelle en argent renforcée",
    description:
      "+10 dégâts au corps à corps (chance d'infliger stun). +1 de distance d'attaque.",
    statBonus: { meleeDamage: 10, meleeRange: 1 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.5,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 180,
  },
  reinforcedGoldShovel: {
    id: "reinforcedGoldShovel",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Pelle en or renforcée",
    description:
      "+12 dégâts au corps à corps (chance d'infliger stun). +1 de distance d'attaque.",
    statBonus: { meleeDamage: 12, meleeRange: 1 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.6,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 250,
  },
  reinforcedPlatiniumShovel: {
    id: "reinforcedPlatiniumShovel",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Pelle en platine renforcée",
    description:
      "+14 dégâts au corps à corps (chance d'infliger stun). +1 de distance d'attaque.",
    statBonus: { meleeDamage: 14, meleeRange: 1 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.7,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 350,
  },
  reinforcedCobaltShovel: {
    id: "reinforcedCobaltShovel",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Pelle en cobalt renforcée",
    description:
      "+16 dégâts au corps à corps (chance d'infliger stun). +1 de distance d'attaque.",
    statBonus: { meleeDamage: 16, meleeRange: 1 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.8,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 450,
  },
  reinforcedAdamantineShovel: {
    id: "reinforcedAdamantineShovel",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Pelle en adamantine renforcée",
    description:
      "+18 dégâts au corps à corps (chance d'infliger stun). +1 de distance d'attaque.",
    statBonus: { meleeDamage: 18, meleeRange: 1 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.9,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 550,
  },
  reinforcedCrimsonShovel: {
    id: "reinforcedCrimsonShovel",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Pelle en cramoisi renforcée",
    description:
      "+20 dégâts au corps à corps (chance d'infliger stun). +1 de distance d'attaque.",
    statBonus: { meleeDamage: 20, meleeRange: 1 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 650,
  },
  reinforcedAngelicShovel: {
    id: "reinforcedAngelicShovel",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Pelle en angélique renforcée",
    description:
      "+22 dégâts au corps à corps (chance d'infliger stun). +1 de distance d'attaque.",
    statBonus: { meleeDamage: 22, meleeRange: 1 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 750,
  },
  reinforcedFatefulShovel: {
    id: "reinforcedFatefulShovel",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Pelle en fatidique renforcée",
    description:
      "+25 dégâts au corps à corps (chance d'infliger stun). +1 de distance d'attaque.",
    statBonus: { meleeDamage: 25, meleeRange: 1 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 850,
  },
  reinforcedNovaShovel: {
    id: "reinforcedNovaShovel",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Pelle en nova renforcée",
    description:
      "+28 dégâts au corps à corps (chance d'infliger stun). +1 de distance d'attaque.",
    statBonus: { meleeDamage: 28, meleeRange: 1 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 950,
  },

  woodenHammer: {
    id: "woodenHammer",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Marteau de guerre en bois",
    description: "+1 dégât au corps à corps. Arme d'entraînement de départ.",
    statBonus: { meleeDamage: 1 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    price: 2,
  },
  copperHammer: {
    id: "copperHammer",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Marteau de guerre en cuivre",
    description: "+2 dégât au corps à corps.",
    statBonus: { meleeDamage: 2 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    price: 30,
  },
  ironHammer: {
    id: "ironHammer",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Marteau de guerre en fer",
    description: "+4 dégât au corps à corps.",
    statBonus: { meleeDamage: 4 },
    stackable: false,
    archetypes: ["guerrier"],
    price: 50,
  },
  steelHammer: {
    id: "steelHammer",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Marteau de guerre en acier",
    description: "+6 dégât au corps à corps.",
    statBonus: { meleeDamage: 6 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    price: 100,
  },
  silverHammer: {
    id: "silverHammer",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Marteau de guerre en argent",
    description: "+8 dégât au corps à corps.",
    statBonus: { meleeDamage: 8 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    price: 150,
  },
  goldHammer: {
    id: "goldHammer",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Marteau de guerre en or",
    description: "+10 dégât au corps à corps.",
    statBonus: { meleeDamage: 10 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    price: 200,
  },
  platiniumHammer: {
    id: "platiniumHammer",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Marteau de guerre en platine",
    description: "+12 dégât au corps à corps.",
    statBonus: { meleeDamage: 12 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    price: 300,
  },
  cobaltHammer: {
    id: "cobaltHammer",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Marteau de guerre en cobalt",
    description: "+14 dégât au corps à corps.",
    statBonus: { meleeDamage: 14 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    price: 400,
  },
  adamantineHammer: {
    id: "adamantineHammer",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Marteau de guerre en adamantine",
    description: "+16 dégât au corps à corps.",
    statBonus: { meleeDamage: 16 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    price: 500,
  },
  crimsonHammer: {
    id: "crimsonHammer",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Marteau de guerre en cramoisi",
    description: "+18 dégât au corps à corps.",
    statBonus: { meleeDamage: 18 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    price: 600,
  },
  angelicHammer: {
    id: "angelicHammer",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Marteau de guerre en angélique",
    description: "+20 dégât au corps à corps.",
    statBonus: { meleeDamage: 20 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    price: 700,
  },
  fatefulHammer: {
    id: "fatefulHammer",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Marteau de guerre en fatidique",
    description: "+22 dégât au corps à corps.",
    statBonus: { meleeDamage: 22 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    price: 800,
  },
  novaHammer: {
    id: "novaHammer",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Marteau de guerre en nova",
    description: "+24 dégât au corps à corps.",
    statBonus: { meleeDamage: 24 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    price: 900,
  },
  // ----- Marteau de guerre aiguisée (sharp) - branche METAL uniquement -----
  sharpCopperHammer: {
    id: "sharpCopperHammer",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Marteau de guerre en cuivre aiguisée. Inflige un saignement à l'ennemi.",
    description: "+4 dégâts au corps à corps.",
    statBonus: { meleeDamage: 4 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 50,
  },
  sharpIronHammer: {
    id: "sharpIronHammer",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Marteau de guerre en fer aiguisée. Inflige un saignement à l'ennemi.",
    description: "+5 dégâts au corps à corps.",
    statBonus: { meleeDamage: 5 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 60,
  },
  sharpSteelHammer: {
    id: "sharpSteelHammer",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Marteau de guerre en acier aiguisée. Inflige un saignement à l'ennemi.",
    description: "+6 dégâts au corps à corps.",
    statBonus: { meleeDamage: 6 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 70,
  },
  sharpSilverHammer: {
    id: "sharpSilverHammer",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Marteau de guerre en argent aiguisée. Inflige un saignement à l'ennemi.",
    description: "+7 dégâts au corps à corps.",
    statBonus: { meleeDamage: 7 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 80,
  },
  sharpGoldHammer: {
    id: "sharpGoldHammer",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Marteau de guerre en or aiguisée. Inflige un saignement à l'ennemi.",
    description: "+8 dégâts au corps à corps.",
    statBonus: { meleeDamage: 8 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 90,
  },
  sharpPlatiniumHammer: {
    id: "sharpPlatiniumHammer",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Marteau de guerre en platine aiguisée. Inflige un saignement à l'ennemi.",
    description: "+9 dégâts au corps à corps.",
    statBonus: { meleeDamage: 9 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 100,
  },
  sharpCobaltHammer: {
    id: "sharpCobaltHammer",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Marteau de guerre en cobalt aiguisée. Inflige un saignement à l'ennemi.",
    description: "+10 dégâts au corps à corps.",
    statBonus: { meleeDamage: 10 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 110,
  },
  sharpAdamantineHammer: {
    id: "sharpAdamantineHammer",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Marteau de guerre en adamantine aiguisée. Inflige un saignement à l'ennemi.",
    description: "+11 dégâts au corps à corps.",
    statBonus: { meleeDamage: 11 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 120,
  },
  sharpCrimsonHammer: {
    id: "sharpCrimsonHammer",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Marteau de guerre en cramoisi aiguisée. Inflige un saignement à l'ennemi.",
    description: "+12 dégâts au corps à corps.",
    statBonus: { meleeDamage: 12 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 130,
  },
  sharpAngelicHammer: {
    id: "sharpAngelicHammer",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Marteau de guerre en angélique aiguisée. Inflige un saignement à l'ennemi.",
    description: "+13 dégâts au corps à corps.",
    statBonus: { meleeDamage: 13 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 140,
  },
  sharpFatefulHammer: {
    id: "sharpFatefulHammer",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Marteau de guerre en fatidique aiguisée. Inflige un saignement à l'ennemi.",
    description: "+14 dégâts au corps à corps.",
    statBonus: { meleeDamage: 14 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 150,
  },
  sharpNovaHammer: {
    id: "sharpNovaHammer",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Marteau de guerre en nova aiguisée. Inflige un saignement à l'ennemi.",
    description: "+15 dégâts au corps à corps.",
    statBonus: { meleeDamage: 15 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 160,
  },

  // ----- Marteau de guerre renforcée (reinforced) - branche METAL -----
  reinforcedWoodenHammer: {
    id: "reinforcedWoodenHammer",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Marteau de guerre en bois renforcée",
    description: "+4 dégât au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 4 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.2,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 50,
  },
  reinforcedCopperHammer: {
    id: "reinforcedCopperHammer",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Marteau de guerre en cuivre renforcée",
    description: "+5 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 5 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.25,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 65,
  },
  reinforcedIronHammer: {
    id: "reinforcedIronHammer",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Marteau de guerre en fer renforcée",
    description: "+6 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 6 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.3,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 80,
  },
  reinforcedSteelHammer: {
    id: "reinforcedSteelHammer",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Marteau de guerre en acier renforcée",
    description: "+8 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 8 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.4,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 120,
  },
  reinforcedSilverHammer: {
    id: "reinforcedSilverHammer",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Marteau de guerre en argent renforcée",
    description: "+10 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 10 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.5,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 180,
  },
  reinforcedGoldHammer: {
    id: "reinforcedGoldHammer",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Marteau de guerre en or renforcée",
    description: "+12 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 12 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.6,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 250,
  },
  reinforcedPlatiniumHammer: {
    id: "reinforcedPlatiniumHammer",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Marteau de guerre en platine renforcée",
    description: "+14 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 14 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.7,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 350,
  },
  reinforcedCobaltHammer: {
    id: "reinforcedCobaltHammer",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Marteau de guerre en cobalt renforcée",
    description: "+16 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 16 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.8,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 450,
  },
  reinforcedAdamantineHammer: {
    id: "reinforcedAdamantineHammer",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Marteau de guerre en adamantine renforcée",
    description: "+18 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 18 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.9,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 550,
  },
  reinforcedCrimsonHammer: {
    id: "reinforcedCrimsonHammer",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Marteau de guerre en cramoisi renforcée",
    description: "+20 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 20 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 650,
  },
  reinforcedAngelicHammer: {
    id: "reinforcedAngelicHammer",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Marteau de guerre en angélique renforcée",
    description: "+22 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 22 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 750,
  },
  reinforcedFatefulHammer: {
    id: "reinforcedFatefulHammer",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Marteau de guerre en fatidique renforcée",
    description: "+25 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 25 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 850,
  },
  reinforcedNovaHammer: {
    id: "reinforcedNovaHammer",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Marteau de guerre en nova renforcée",
    description: "+28 dégâts au corps à corps (chance d'infliger stun).",
    statBonus: { meleeDamage: 28 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 950,
  },

  woodenSickle: {
    id: "woodenSickle",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Faucille en bois",
    description:
      "+1 dégât au corps à corps. +1 de distance d'attaque  Arme d'entraînement de départ.",
    statBonus: { meleeDamage: 1, meleeRange: 1 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    price: 2,
  },
  copperSickle: {
    id: "copperSickle",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Faucille en cuivre",
    description: "+3 dégât au corps à corps. +2 de distance d'attaque.",
    statBonus: { meleeDamage: 3, meleeRange: 2 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    price: 30,
  },
  ironSickle: {
    id: "ironSickle",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Faucille en fer",
    description: "+5 dégât au corps à corps. +2 de distance d'attaque.",
    statBonus: { meleeDamage: 5, meleeRange: 2 },
    stackable: false,
    archetypes: ["guerrier"],
    price: 60,
  },
  steelSickle: {
    id: "steelSickle",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Faucille en acier",
    description: "+7 dégât au corps à corps. +2 de distance d'attaque.",
    statBonus: { meleeDamage: 7, meleeRange: 2 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    price: 100,
  },
  silverSickle: {
    id: "silverSickle",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Faucille en argent",
    description: "+9 dégât au corps à corps. +2 de distance d'attaque.",
    statBonus: { meleeDamage: 9, meleeRange: 2 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    price: 150,
  },
  goldSickle: {
    id: "goldSickle",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Faucille en or",
    description: "+11 dégât au corps à corps. +2 de distance d'attaque.",
    statBonus: { meleeDamage: 11, meleeRange: 2 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    price: 200,
  },
  platiniumSickle: {
    id: "platiniumSickle",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Faucille en platine",
    description: "+13 dégât au corps à corps. +2 de distance d'attaque.",
    statBonus: { meleeDamage: 13, meleeRange: 2 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    price: 300,
  },
  cobaltSickle: {
    id: "cobaltSickle",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Faucille en cobalt",
    description: "+15 dégât au corps à corps. +2 de distance d'attaque.",
    statBonus: { meleeDamage: 15, meleeRange: 2 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    price: 400,
  },
  adamantineSickle: {
    id: "adamantineSickle",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Faucille en adamantine",
    description: "+17 dégât au corps à corps. +2 de distance d'attaque.",
    statBonus: { meleeDamage: 17, meleeRange: 2 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    price: 500,
  },
  crimsonSickle: {
    id: "crimsonSickle",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Faucille en cramoisi",
    description: "+19 dégât au corps à corps. +2 de distance d'attaque.",
    statBonus: { meleeDamage: 19, meleeRange: 2 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    price: 600,
  },
  angelicSickle: {
    id: "angelicSickle",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Faucille en angélique",
    description: "+21 dégât au corps à corps. +2 de distance d'attaque.",
    statBonus: { meleeDamage: 21, meleeRange: 2 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    price: 700,
  },
  fatefulSickle: {
    id: "fatefulSickle",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Faucille en fatidique",
    description: "+23 dégât au corps à corps. +2 de distance d'attaque.",
    statBonus: { meleeDamage: 23, meleeRange: 2 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    price: 800,
  },
  novaSickle: {
    id: "novaSickle",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Faucille en nova",
    description: "+25 dégât au corps à corps. +2 de distance d'attaque.",
    statBonus: { meleeDamage: 25, meleeRange: 2 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    price: 900,
  },
  // ----- Faucille aiguisée (sharp) - branche METAL uniquement -----
  sharpCopperSickle: {
    id: "sharpCopperSickle",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Faucille en cuivre aiguisée. Inflige un saignement à l'ennemi.",
    description: "+4 dégâts au corps à corps. +2 de distance d'attaque.",
    statBonus: { meleeDamage: 4, meleeRange: 2 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 50,
  },
  sharpIronSickle: {
    id: "sharpIronSickle",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Faucille en fer aiguisée. Inflige un saignement à l'ennemi.",
    description: "+5 dégâts au corps à corps. +2 de distance d'attaque.",
    statBonus: { meleeDamage: 5, meleeRange: 2 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 60,
  },
  sharpSteelSickle: {
    id: "sharpSteelSickle",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Faucille en acier aiguisée. Inflige un saignement à l'ennemi.",
    description: "+6 dégâts au corps à corps. +2 de distance d'attaque.",
    statBonus: { meleeDamage: 6, meleeRange: 2 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 70,
  },
  sharpSilverSickle: {
    id: "sharpSilverSickle",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Faucille en argent aiguisée. Inflige un saignement à l'ennemi.",
    description: "+7 dégâts au corps à corps. +2 de distance d'attaque.",
    statBonus: { meleeDamage: 7, meleeRange: 2 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 80,
  },
  sharpGoldSickle: {
    id: "sharpGoldSickle",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Faucille en or aiguisée. Inflige un saignement à l'ennemi.",
    description: "+8 dégâts au corps à corps. +2 de distance d'attaque.",
    statBonus: { meleeDamage: 8, meleeRange: 2 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 90,
  },
  sharpPlatiniumSickle: {
    id: "sharpPlatiniumSickle",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Faucille en platine aiguisée. Inflige un saignement à l'ennemi.",
    description: "+9 dégâts au corps à corps. +2 de distance d'attaque.",
    statBonus: { meleeDamage: 9, meleeRange: 2 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 100,
  },
  sharpCobaltSickle: {
    id: "sharpCobaltSickle",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Faucille en cobalt aiguisée. Inflige un saignement à l'ennemi.",
    description: "+10 dégâts au corps à corps. +2 de distance d'attaque.",
    statBonus: { meleeDamage: 10, meleeRange: 2 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 110,
  },
  sharpAdamantineSickle: {
    id: "sharpAdamantineSickle",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Faucille en adamantine aiguisée. Inflige un saignement à l'ennemi.",
    description: "+11 dégâts au corps à corps. +2 de distance d'attaque.",
    statBonus: { meleeDamage: 11, meleeRange: 2 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 120,
  },
  sharpCrimsonSickle: {
    id: "sharpCrimsonSickle",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Faucille en cramoisi aiguisée. Inflige un saignement à l'ennemi.",
    description: "+12 dégâts au corps à corps. +2 de distance d'attaque.",
    statBonus: { meleeDamage: 12, meleeRange: 2 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 130,
  },
  sharpAngelicSickle: {
    id: "sharpAngelicSickle",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Faucille en angélique aiguisée. Inflige un saignement à l'ennemi.",
    description: "+13 dégâts au corps à corps. +2 de distance d'attaque.",
    statBonus: { meleeDamage: 13, meleeRange: 2 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 140,
  },
  sharpFatefulSickle: {
    id: "sharpFatefulSickle",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Faucille en fatidique aiguisée. Inflige un saignement à l'ennemi.",
    description: "+14 dégâts au corps à corps. +2 de distance d'attaque.",
    statBonus: { meleeDamage: 14, meleeRange: 2 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 150,
  },
  sharpNovaSickle: {
    id: "sharpNovaSickle",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Faucille en nova aiguisée. Inflige un saignement à l'ennemi.",
    description: "+15 dégâts au corps à corps. +2 de distance d'attaque.",
    statBonus: { meleeDamage: 15, meleeRange: 2 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 160,
  },

  // ----- Faucille renforcée (reinforced) - branche METAL -----
  reinforcedWoodenSickle: {
    id: "reinforcedWoodenSickle",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Faucille en bois renforcée",
    description:
      "+4 dégât au corps à corps (chance d'infliger stun). +1 de distance d'attaque.",
    statBonus: { meleeDamage: 4, meleeRange: 1 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.2,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 50,
  },
  reinforcedCopperSickle: {
    id: "reinforcedCopperSickle",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Faucille en cuivre renforcée",
    description:
      "+5 dégâts au corps à corps (chance d'infliger stun). +2 de distance d'attaque.",
    statBonus: { meleeDamage: 5, meleeRange: 2 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.25,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 65,
  },
  reinforcedIronSickle: {
    id: "reinforcedIronSickle",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Faucille en fer renforcée",
    description:
      "+6 dégâts au corps à corps (chance d'infliger stun). +2 de distance d'attaque.",
    statBonus: { meleeDamage: 6, meleeRange: 2 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.3,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 80,
  },
  reinforcedSteelSickle: {
    id: "reinforcedSteelSickle",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Faucille en acier renforcée",
    description:
      "+8 dégâts au corps à corps (chance d'infliger stun). +2 de distance d'attaque.",
    statBonus: { meleeDamage: 8, meleeRange: 2 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.4,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 120,
  },
  reinforcedSilverSickle: {
    id: "reinforcedSilverSickle",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Faucille en argent renforcée",
    description:
      "+10 dégâts au corps à corps (chance d'infliger stun). +2 de distance d'attaque.",
    statBonus: { meleeDamage: 10, meleeRange: 2 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.5,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 180,
  },
  reinforcedGoldSickle: {
    id: "reinforcedGoldSickle",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Faucille en or renforcée",
    description:
      "+12 dégâts au corps à corps (chance d'infliger stun). +2 de distance d'attaque.",
    statBonus: { meleeDamage: 12, meleeRange: 2 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.6,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 250,
  },
  reinforcedPlatiniumSickle: {
    id: "reinforcedPlatiniumSickle",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Faucille en platine renforcée",
    description:
      "+14 dégâts au corps à corps (chance d'infliger stun). +2 de distance d'attaque.",
    statBonus: { meleeDamage: 14, meleeRange: 2 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.7,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 350,
  },
  reinforcedCobaltSickle: {
    id: "reinforcedCobaltSickle",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Faucille en cobalt renforcée",
    description:
      "+16 dégâts au corps à corps (chance d'infliger stun). +2 de distance d'attaque.",
    statBonus: { meleeDamage: 16, meleeRange: 2 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.8,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 450,
  },
  reinforcedAdamantineSickle: {
    id: "reinforcedAdamantineSickle",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Faucille en adamantine renforcée",
    description:
      "+18 dégâts au corps à corps (chance d'infliger stun). +2 de distance d'attaque.",
    statBonus: { meleeDamage: 18, meleeRange: 2 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.9,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 550,
  },
  reinforcedCrimsonSickle: {
    id: "reinforcedCrimsonSickle",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Faucille en cramoisi renforcée",
    description:
      "+20 dégâts au corps à corps (chance d'infliger stun). +2 de distance d'attaque.",
    statBonus: { meleeDamage: 20, meleeRange: 2 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 650,
  },
  reinforcedAngelicSickle: {
    id: "reinforcedAngelicSickle",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Faucille en angélique renforcée",
    description:
      "+22 dégâts au corps à corps (chance d'infliger stun). +2 de distance d'attaque.",
    statBonus: { meleeDamage: 22, meleeRange: 2 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 750,
  },
  reinforcedFatefulSickle: {
    id: "reinforcedFatefulSickle",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Faucille en fatidique renforcée",
    description:
      "+25 dégâts au corps à corps (chance d'infliger stun). +2 de distance d'attaque.",
    statBonus: { meleeDamage: 25, meleeRange: 2 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 850,
  },
  reinforcedNovaSickle: {
    id: "reinforcedNovaSickle",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Faucille en nova renforcée",
    description:
      "+28 dégâts au corps à corps (chance d'infliger stun). +2 de distance d'attaque.",
    statBonus: { meleeDamage: 28, meleeRange: 2 },
    stackable: false,
    archetypes: ["guerrier", "voleur"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 950,
  },

  woodenShield: {
    id: "woodenShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Bouclier en bois",
    description: "+1 défense. Équipement d'entraînement de départ.",
    statBonus: { defense: 1 },
    archetypes: ["guerrier"],
    stackable: false,
    price: 2,
  },
  copperShield: {
    id: "copperShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Bouclier en cuivre",
    description: "+2 défense.",
    statBonus: { defense: 2 },
    archetypes: ["guerrier"],
    stackable: false,
    price: 30,
  },

  ironShield: {
    id: "ironShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Bouclier en fer",
    description: "+3 défense.",
    statBonus: { defense: 3 },
    archetypes: ["guerrier"],
    stackable: false,
    price: 60,
  },

  steelShield: {
    id: "steelShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Bouclier en acier",
    description: "+4 défense.",
    statBonus: { defense: 4 },
    archetypes: ["guerrier"],
    stackable: false,
    price: 100,
  },

  silverShield: {
    id: "silverShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Bouclier en argent",
    description: "+5 défense.",
    statBonus: { defense: 5 },
    archetypes: ["guerrier"],
    stackable: false,
    price: 150,
  },

  goldShield: {
    id: "goldShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Bouclier en or",
    description: "+6 défense.",
    statBonus: { defense: 6 },
    archetypes: ["guerrier"],
    stackable: false,
    price: 200,
  },

  platiniumShield: {
    id: "platiniumShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Bouclier en platine",
    description: "+7 défense.",
    statBonus: { defense: 7 },
    archetypes: ["guerrier"],
    stackable: false,
    price: 300,
  },

  cobaltShield: {
    id: "cobaltShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Bouclier en cobalt",
    description: "+8 défense.",
    statBonus: { defense: 8 },
    archetypes: ["guerrier"],
    stackable: false,
    price: 400,
  },

  adamantineShield: {
    id: "adamantineShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Bouclier en adamantine",
    description: "+9 défense.",
    statBonus: { defense: 9 },
    archetypes: ["guerrier"],
    stackable: false,
    price: 500,
  },

  crimsonShield: {
    id: "crimsonShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Bouclier en cramoisi",
    description: "+10 défense.",
    statBonus: { defense: 10 },
    archetypes: ["guerrier"],
    stackable: false,
    price: 600,
  },

  angelicShield: {
    id: "angelicShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Bouclier en angélique",
    description: "+11 défense.",
    statBonus: { defense: 11 },
    archetypes: ["guerrier"],
    stackable: false,
    price: 700,
  },

  fatefulShield: {
    id: "fatefulShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Bouclier en fatidique",
    description: "+12 défense.",
    statBonus: { defense: 12 },
    archetypes: ["guerrier"],
    stackable: false,
    price: 800,
  },

  novaShield: {
    id: "novaShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Bouclier en nova",
    description: "+13 défense.",
    statBonus: { defense: 13 },
    archetypes: ["guerrier"],
    stackable: false,
    price: 900,
  },

  // ===== Bouclier renforcé - branche METAL (copper -> nova) - riposte étourdissante au blocage (meme convention que les dagues renforcées) - pas d'unlockLevel (craftable uniquement) =====

  reinforcedCopperShield: {
    id: "reinforcedCopperShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Bouclier en cuivre renforcé",
    description: "+4 défense (chance d'étourdir l'ennemi au blocage).",
    statBonus: { defense: 4 },
    archetypes: ["guerrier"],
    stackable: false,
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.25,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 65,
  },

  reinforcedIronShield: {
    id: "reinforcedIronShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Bouclier en fer renforcé",
    description: "+5 défense (chance d'étourdir l'ennemi au blocage).",
    statBonus: { defense: 5 },
    archetypes: ["guerrier"],
    stackable: false,
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.3,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 80,
  },

  reinforcedSteelShield: {
    id: "reinforcedSteelShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Bouclier en acier renforcé",
    description: "+6 défense (chance d'étourdir l'ennemi au blocage).",
    statBonus: { defense: 6 },
    archetypes: ["guerrier"],
    stackable: false,
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.4,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 120,
  },

  reinforcedSilverShield: {
    id: "reinforcedSilverShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Bouclier en argent renforcé",
    description: "+7 défense (chance d'étourdir l'ennemi au blocage).",
    statBonus: { defense: 7 },
    archetypes: ["guerrier"],
    stackable: false,
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.5,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 180,
  },

  reinforcedGoldShield: {
    id: "reinforcedGoldShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Bouclier en or renforcé",
    description: "+8 défense (chance d'étourdir l'ennemi au blocage).",
    statBonus: { defense: 8 },
    archetypes: ["guerrier"],
    stackable: false,
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.6,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 250,
  },

  reinforcedPlatiniumShield: {
    id: "reinforcedPlatiniumShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Bouclier en platine renforcé",
    description: "+9 défense (chance d'étourdir l'ennemi au blocage).",
    statBonus: { defense: 9 },
    archetypes: ["guerrier"],
    stackable: false,
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.7,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 350,
  },

  reinforcedCobaltShield: {
    id: "reinforcedCobaltShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Bouclier en cobalt renforcé",
    description: "+10 défense (chance d'étourdir l'ennemi au blocage).",
    statBonus: { defense: 10 },
    archetypes: ["guerrier"],
    stackable: false,
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.8,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 450,
  },

  reinforcedAdamantineShield: {
    id: "reinforcedAdamantineShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Bouclier en adamantine renforcé",
    description: "+11 défense (chance d'étourdir l'ennemi au blocage).",
    statBonus: { defense: 11 },
    archetypes: ["guerrier"],
    stackable: false,
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.9,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 550,
  },

  reinforcedCrimsonShield: {
    id: "reinforcedCrimsonShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Bouclier en cramoisi renforcé",
    description: "+12 défense (chance d'étourdir l'ennemi au blocage).",
    statBonus: { defense: 12 },
    archetypes: ["guerrier"],
    stackable: false,
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 650,
  },

  reinforcedAngelicShield: {
    id: "reinforcedAngelicShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Bouclier en angélique renforcé",
    description: "+13 défense (chance d'étourdir l'ennemi au blocage).",
    statBonus: { defense: 13 },
    archetypes: ["guerrier"],
    stackable: false,
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 750,
  },

  reinforcedFatefulShield: {
    id: "reinforcedFatefulShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Bouclier en fatidique renforcé",
    description: "+14 défense (chance d'étourdir l'ennemi au blocage).",
    statBonus: { defense: 14 },
    archetypes: ["guerrier"],
    stackable: false,
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 850,
  },

  reinforcedNovaShield: {
    id: "reinforcedNovaShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Bouclier en nova renforcé",
    description: "+15 défense (chance d'étourdir l'ennemi au blocage).",
    statBonus: { defense: 15 },
    archetypes: ["guerrier"],
    stackable: false,
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 950,
  },
  // ===== Buckler - évolution classique (wood -> nova) - pas d'unlockLevel (craftable uniquement) =====
  woodenBuckler: {
    id: "woodenBuckler",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Buckler en bois",
    description: "+1 défense. Équipement d'entraînement de départ.",
    statBonus: { defense: 1 },
    archetypes: ["guerrier"],
    stackable: false,
    price: 2,
  },
  oakBuckler: {
    id: "oakBuckler",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Buckler en chêne",
    description: "+1 défense.",
    statBonus: { defense: 1 },
    archetypes: ["guerrier"],
    stackable: false,
    price: 30,
  },
  ashBuckler: {
    id: "ashBuckler",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Buckler en frêne",
    description: "+2 défense.",
    statBonus: { defense: 2 },
    archetypes: ["guerrier"],
    stackable: false,
    price: 60,
  },
  yewBuckler: {
    id: "yewBuckler",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Buckler en if",
    description: "+3 défense.",
    statBonus: { defense: 3 },
    archetypes: ["guerrier"],
    stackable: false,
    price: 100,
  },
  ebonyBuckler: {
    id: "ebonyBuckler",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Buckler en ébène",
    description: "+4 défense.",
    statBonus: { defense: 4 },
    archetypes: ["guerrier"],
    stackable: false,
    price: 150,
  },
  petrifiedBuckler: {
    id: "petrifiedBuckler",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Buckler de bois pétrifié",
    description: "+5 défense.",
    statBonus: { defense: 5 },
    archetypes: ["guerrier"],
    stackable: false,
    price: 200,
  },
  mistwoodBuckler: {
    id: "mistwoodBuckler",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Buckler en bois de brume",
    description: "+6 défense.",
    statBonus: { defense: 6 },
    archetypes: ["guerrier"],
    stackable: false,
    price: 300,
  },
  runewoodBuckler: {
    id: "runewoodBuckler",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Buckler en bois runique",
    description: "+7 défense.",
    statBonus: { defense: 7 },
    archetypes: ["guerrier"],
    stackable: false,
    price: 400,
  },
  skywoodBuckler: {
    id: "skywoodBuckler",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Buckler en bois céleste",
    description: "+8 défense.",
    statBonus: { defense: 8 },
    archetypes: ["guerrier"],
    stackable: false,
    price: 500,
  },
  scarletwoodBuckler: {
    id: "scarletwoodBuckler",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Buckler en bois écarlate",
    description: "+9 défense.",
    statBonus: { defense: 9 },
    archetypes: ["guerrier"],
    stackable: false,
    price: 600,
  },
  sacredBuckler: {
    id: "sacredBuckler",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Buckler en bois sacré",
    description: "+10 défense.",
    statBonus: { defense: 10 },
    archetypes: ["guerrier"],
    stackable: false,
    price: 700,
  },
  eternalBuckler: {
    id: "eternalBuckler",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Buckler en bois éternel",
    description: "+11 défense.",
    statBonus: { defense: 11 },
    archetypes: ["guerrier"],
    stackable: false,
    price: 800,
  },
  starwoodBuckler: {
    id: "starwoodBuckler",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Buckler en bois étoilé",
    description: "+12 défense.",
    statBonus: { defense: 12 },
    archetypes: ["guerrier"],
    stackable: false,
    price: 900,
  },

  // ===== Buckler renforcé - évolution (wood -> nova) - riposte étourdissante au blocage - pas d'unlockLevel (craftable uniquement) =====
  reinforcedWoodenBuckler: {
    id: "reinforcedWoodenBuckler",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Buckler en bois renforcé",
    description: "+2 défense (chance d'étourdir l'ennemi au blocage).",
    statBonus: { defense: 2 },
    archetypes: ["guerrier"],
    stackable: false,
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.2,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 50,
  },
  reinforcedOakBuckler: {
    id: "reinforcedOakBuckler",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Buckler en chêne renforcé",
    description: "+3 défense (chance d'étourdir l'ennemi au blocage).",
    statBonus: { defense: 3 },
    archetypes: ["guerrier"],
    stackable: false,
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.25,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 65,
  },
  reinforcedAshBuckler: {
    id: "reinforcedAshBuckler",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Buckler en frêne renforcé",
    description: "+4 défense (chance d'étourdir l'ennemi au blocage).",
    statBonus: { defense: 4 },
    archetypes: ["guerrier"],
    stackable: false,
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.3,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 80,
  },
  reinforcedYewBuckler: {
    id: "reinforcedYewBuckler",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Buckler en if renforcé",
    description: "+5 défense (chance d'étourdir l'ennemi au blocage).",
    statBonus: { defense: 5 },
    archetypes: ["guerrier"],
    stackable: false,
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.4,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 120,
  },
  reinforcedEbonyBuckler: {
    id: "reinforcedEbonyBuckler",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Buckler en ébène renforcé",
    description: "+6 défense (chance d'étourdir l'ennemi au blocage).",
    statBonus: { defense: 6 },
    archetypes: ["guerrier"],
    stackable: false,
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.5,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 180,
  },
  reinforcedPetrifiedBuckler: {
    id: "reinforcedPetrifiedBuckler",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Buckler en bois pétrifié renforcé",
    description: "+7 défense (chance d'étourdir l'ennemi au blocage).",
    statBonus: { defense: 7 },
    archetypes: ["guerrier"],
    stackable: false,
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.6,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 250,
  },
  reinforcedMistwoodBuckler: {
    id: "reinforcedMistwoodBuckler",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Buckler en bois de brume renforcé",
    description: "+8 défense (chance d'étourdir l'ennemi au blocage).",
    statBonus: { defense: 8 },
    archetypes: ["guerrier"],
    stackable: false,
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.7,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 350,
  },
  reinforcedRunewoodBuckler: {
    id: "reinforcedRunewoodBuckler",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Buckler en bois runique renforcé",
    description: "+9 défense (chance d'étourdir l'ennemi au blocage).",
    statBonus: { defense: 9 },
    archetypes: ["guerrier"],
    stackable: false,
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.8,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 450,
  },
  reinforcedSkywoodBuckler: {
    id: "reinforcedSkywoodBuckler",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Buckler en bois céleste renforcé",
    description: "+10 défense (chance d'étourdir l'ennemi au blocage).",
    statBonus: { defense: 10 },
    archetypes: ["guerrier"],
    stackable: false,
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.9,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 550,
  },
  reinforcedScarletwoodBuckler: {
    id: "reinforcedScarletwoodBuckler",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Buckler en bois écarlate renforcé",
    description: "+11 défense (chance d'étourdir l'ennemi au blocage).",
    statBonus: { defense: 11 },
    archetypes: ["guerrier"],
    stackable: false,
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 650,
  },
  reinforcedSacredBuckler: {
    id: "reinforcedSacredBuckler",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Buckler en sacré renforcé",
    description: "+12 défense (chance d'étourdir l'ennemi au blocage).",
    statBonus: { defense: 12 },
    archetypes: ["guerrier"],
    stackable: false,
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 750,
  },
  reinforcedEternalBuckler: {
    id: "reinforcedEternalBuckler",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Buckler en éternel renforcé",
    description: "+13 défense (chance d'étourdir l'ennemi au blocage).",
    statBonus: { defense: 13 },
    archetypes: ["guerrier"],
    stackable: false,
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 850,
  },
  reinforcedStarwoodBuckler: {
    id: "reinforcedStarwoodBuckler",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Buckler en bois étoilé renforcé",
    description: "+14 défense (chance d'étourdir l'ennemi au blocage).",
    statBonus: { defense: 14 },
    archetypes: ["guerrier"],
    stackable: false,
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 950,
  },

  // ===== Grand bouclier - évolution classique (wood -> nova) - pas d'unlockLevel (craftable uniquement) =====
  woodenGreatShield: {
    id: "woodenGreatShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Grand bouclier en bois",
    description: "+2 défense. Équipement d'entraînement de départ.",
    statBonus: { defense: 2 },
    archetypes: ["guerrier"],
    stackable: false,
    price: 2,
  },
  copperGreatShield: {
    id: "copperGreatShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Grand bouclier en cuivre",
    description: "+3 défense.",
    statBonus: { defense: 3 },
    archetypes: ["guerrier"],
    stackable: false,
    price: 30,
  },
  ironGreatShield: {
    id: "ironGreatShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Grand bouclier en fer",
    description: "+4 défense.",
    statBonus: { defense: 4 },
    archetypes: ["guerrier"],
    stackable: false,
    price: 60,
  },
  steelGreatShield: {
    id: "steelGreatShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Grand bouclier en acier",
    description: "+5 défense.",
    statBonus: { defense: 5 },
    archetypes: ["guerrier"],
    stackable: false,
    price: 100,
  },
  silverGreatShield: {
    id: "silverGreatShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Grand bouclier en argent",
    description: "+6 défense.",
    statBonus: { defense: 6 },
    archetypes: ["guerrier"],
    stackable: false,
    price: 150,
  },
  goldGreatShield: {
    id: "goldGreatShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Grand bouclier en or",
    description: "+7 défense.",
    statBonus: { defense: 7 },
    archetypes: ["guerrier"],
    stackable: false,
    price: 200,
  },
  platiniumGreatShield: {
    id: "platiniumGreatShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Grand bouclier en platine",
    description: "+8 défense.",
    statBonus: { defense: 8 },
    archetypes: ["guerrier"],
    stackable: false,
    price: 300,
  },
  cobaltGreatShield: {
    id: "cobaltGreatShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Grand bouclier en cobalt",
    description: "+9 défense.",
    statBonus: { defense: 9 },
    archetypes: ["guerrier"],
    stackable: false,
    price: 400,
  },
  adamantineGreatShield: {
    id: "adamantineGreatShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Grand bouclier en adamantine",
    description: "+10 défense.",
    statBonus: { defense: 10 },
    archetypes: ["guerrier"],
    stackable: false,
    price: 500,
  },
  crimsonGreatShield: {
    id: "crimsonGreatShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Grand bouclier en cramoisi",
    description: "+11 défense.",
    statBonus: { defense: 11 },
    archetypes: ["guerrier"],
    stackable: false,
    price: 600,
  },
  angelicGreatShield: {
    id: "angelicGreatShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Grand bouclier en angélique",
    description: "+12 défense.",
    statBonus: { defense: 12 },
    archetypes: ["guerrier"],
    stackable: false,
    price: 700,
  },
  fatefulGreatShield: {
    id: "fatefulGreatShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Grand bouclier en fatidique",
    description: "+13 défense.",
    statBonus: { defense: 13 },
    archetypes: ["guerrier"],
    stackable: false,
    price: 800,
  },
  novaGreatShield: {
    id: "novaGreatShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Grand bouclier en nova",
    description: "+14 défense.",
    statBonus: { defense: 14 },
    archetypes: ["guerrier"],
    stackable: false,
    price: 900,
  },

  // ===== Grand bouclier renforcé - évolution (wood -> nova) - riposte étourdissante au blocage - pas d'unlockLevel (craftable uniquement) =====
  reinforcedWoodenGreatShield: {
    id: "reinforcedWoodenGreatShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Grand bouclier en bois renforcé",
    description: "+4 défense (chance d'étourdir l'ennemi au blocage).",
    statBonus: { defense: 4 },
    archetypes: ["guerrier"],
    stackable: false,
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.2,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 55,
  },
  reinforcedCopperGreatShield: {
    id: "reinforcedCopperGreatShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Grand bouclier en cuivre renforcé",
    description: "+5 défense (chance d'étourdir l'ennemi au blocage).",
    statBonus: { defense: 5 },
    archetypes: ["guerrier"],
    stackable: false,
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.25,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 65,
  },
  reinforcedIronGreatShield: {
    id: "reinforcedIronGreatShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Grand bouclier en fer renforcé",
    description: "+6 défense (chance d'étourdir l'ennemi au blocage).",
    statBonus: { defense: 6 },
    archetypes: ["guerrier"],
    stackable: false,
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.3,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 80,
  },
  reinforcedSteelGreatShield: {
    id: "reinforcedSteelGreatShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Grand bouclier en acier renforcé",
    description: "+7 défense (chance d'étourdir l'ennemi au blocage).",
    statBonus: { defense: 7 },
    archetypes: ["guerrier"],
    stackable: false,
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.4,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 120,
  },
  reinforcedSilverGreatShield: {
    id: "reinforcedSilverGreatShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Grand bouclier en argent renforcé",
    description: "+8 défense (chance d'étourdir l'ennemi au blocage).",
    statBonus: { defense: 8 },
    archetypes: ["guerrier"],
    stackable: false,
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.5,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 180,
  },
  reinforcedGoldGreatShield: {
    id: "reinforcedGoldGreatShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Grand bouclier en or renforcé",
    description: "+9 défense (chance d'étourdir l'ennemi au blocage).",
    statBonus: { defense: 9 },
    archetypes: ["guerrier"],
    stackable: false,
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.6,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 250,
  },
  reinforcedPlatiniumGreatShield: {
    id: "reinforcedPlatiniumGreatShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Grand bouclier en platine renforcé",
    description: "+10 défense (chance d'étourdir l'ennemi au blocage).",
    statBonus: { defense: 10 },
    archetypes: ["guerrier"],
    stackable: false,
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.7,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 350,
  },
  reinforcedCobaltGreatShield: {
    id: "reinforcedCobaltGreatShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Grand bouclier en cobalt renforcé",
    description: "+11 défense (chance d'étourdir l'ennemi au blocage).",
    statBonus: { defense: 11 },
    archetypes: ["guerrier"],
    stackable: false,
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.8,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 450,
  },
  reinforcedAdamantineGreatShield: {
    id: "reinforcedAdamantineGreatShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Grand bouclier en adamantine renforcé",
    description: "+12 défense (chance d'étourdir l'ennemi au blocage).",
    statBonus: { defense: 12 },
    archetypes: ["guerrier"],
    stackable: false,
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.9,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 550,
  },
  reinforcedCrimsonGreatShield: {
    id: "reinforcedCrimsonGreatShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Grand bouclier en cramoisi renforcé",
    description: "+13 défense (chance d'étourdir l'ennemi au blocage).",
    statBonus: { defense: 13 },
    archetypes: ["guerrier"],
    stackable: false,
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 650,
  },
  reinforcedAngelicGreatShield: {
    id: "reinforcedAngelicGreatShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Grand bouclier en angélique renforcé",
    description: "+14 défense (chance d'étourdir l'ennemi au blocage).",
    statBonus: { defense: 14 },
    archetypes: ["guerrier"],
    stackable: false,
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 750,
  },
  reinforcedFatefulGreatShield: {
    id: "reinforcedFatefulGreatShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Grand bouclier en fatidique renforcé",
    description: "+15 défense (chance d'étourdir l'ennemi au blocage).",
    statBonus: { defense: 15 },
    archetypes: ["guerrier"],
    stackable: false,
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 850,
  },
  reinforcedNovaGreatShield: {
    id: "reinforcedNovaGreatShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Grand bouclier en nova renforcé",
    description: "+16 défense (chance d'étourdir l'ennemi au blocage).",
    statBonus: { defense: 16 },
    archetypes: ["guerrier"],
    stackable: false,
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 950,
  },

  woodenStaff: {
    id: "woodenStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 1,
    name: "Bâton en bois",
    description:
      "+1 dégât à distance. Canalise la magie (1 mana par tir), sans munitions. Arme d'entraînement de départ.",
    statBonus: { rangedDamage: 1 },
    stackable: false,
    archetypes: ["mage"],
    price: 2,
  },
  // ===== Bâton - branche METAL (copper -> nova) =====
  copperStaff: {
    id: "copperStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 1,
    name: "Bâton en cuivre",
    description:
      "+3 dégâts à distance. Canalise la magie (1 mana par tir), sans munitions.",
    statBonus: { rangedDamage: 3 },
    stackable: false,
    archetypes: ["mage"],
    unlockLevel: 1,
    price: 30,
  },
  ironStaff: {
    id: "ironStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 1,
    name: "Bâton en fer",
    description:
      "+5 dégâts à distance. Canalise la magie (1 mana par tir), sans munitions.",
    statBonus: { rangedDamage: 5 },
    stackable: false,
    archetypes: ["mage"],
    unlockLevel: 2,
    price: 60,
  },
  steelStaff: {
    id: "steelStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 2,
    name: "Bâton en acier",
    description:
      "+7 dégâts à distance. Canalise la magie (2 mana par tir), sans munitions.",
    statBonus: { rangedDamage: 7 },
    stackable: false,
    archetypes: ["mage"],
    unlockLevel: 3,
    price: 100,
  },
  silverStaff: {
    id: "silverStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 2,
    name: "Bâton en argent",
    description:
      "+9 dégâts à distance. Canalise la magie (2 mana par tir), sans munitions.",
    statBonus: { rangedDamage: 9 },
    stackable: false,
    archetypes: ["mage"],
    unlockLevel: 4,
    price: 150,
  },
  goldStaff: {
    id: "goldStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 3,
    name: "Bâton en or",
    description:
      "+11 dégâts à distance. Canalise la magie (3 mana par tir), sans munitions.",
    statBonus: { rangedDamage: 11 },
    stackable: false,
    archetypes: ["mage"],
    unlockLevel: 5,
    price: 200,
  },
  platiniumStaff: {
    id: "platiniumStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 3,
    name: "Bâton en platine",
    description:
      "+13 dégâts à distance. Canalise la magie (3 mana par tir), sans munitions.",
    statBonus: { rangedDamage: 13 },
    stackable: false,
    archetypes: ["mage"],
    unlockLevel: 6,
    price: 300,
  },
  cobaltStaff: {
    id: "cobaltStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 4,
    name: "Bâton en cobalt",
    description:
      "+15 dégâts à distance. Canalise la magie (4 mana par tir), sans munitions.",
    statBonus: { rangedDamage: 15 },
    stackable: false,
    archetypes: ["mage"],
    unlockLevel: 7,
    price: 400,
  },
  adamantineStaff: {
    id: "adamantineStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 4,
    name: "Bâton en adamantine",
    description:
      "+17 dégâts à distance. Canalise la magie (4 mana par tir), sans munitions.",
    statBonus: { rangedDamage: 17 },
    stackable: false,
    archetypes: ["mage"],
    unlockLevel: 8,
    price: 500,
  },
  crimsonStaff: {
    id: "crimsonStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 5,
    name: "Bâton en cramoisi",
    description:
      "+19 dégâts à distance. Canalise la magie (5 mana par tir), sans munitions.",
    statBonus: { rangedDamage: 19 },
    stackable: false,
    archetypes: ["mage"],
    unlockLevel: 9,
    price: 600,
  },
  angelicStaff: {
    id: "angelicStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 5,
    name: "Bâton en angélique",
    description:
      "+21 dégâts à distance. Canalise la magie (5 mana par tir), sans munitions.",
    statBonus: { rangedDamage: 21 },
    stackable: false,
    archetypes: ["mage"],
    unlockLevel: 10,
    price: 700,
  },
  fatefulStaff: {
    id: "fatefulStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 6,
    name: "Bâton en fatidique",
    description:
      "+23 dégâts à distance. Canalise la magie (6 mana par tir), sans munitions.",
    statBonus: { rangedDamage: 23 },
    stackable: false,
    archetypes: ["mage"],
    unlockLevel: 11,
    price: 800,
  },
  novaStaff: {
    id: "novaStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 6,
    name: "Bâton en nova",
    description:
      "+25 dégâts à distance. Canalise la magie (6 mana par tir), sans munitions.",
    statBonus: { rangedDamage: 25 },
    stackable: false,
    archetypes: ["mage"],
    unlockLevel: 12,
    price: 900,
  },

  // ----- Bâton aiguisé (sharp) - branche METAL uniquement -----
  sharpCopperStaff: {
    id: "sharpCopperStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 1,
    name: "Bâton en cuivre aiguisé. Inflige un saignement à l'ennemi.",
    description: "+4 dégâts à distance. Canalise la magie (1 mana par tir).",
    statBonus: { rangedDamage: 4 },
    stackable: false,
    archetypes: ["mage"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 50,
  },
  sharpIronStaff: {
    id: "sharpIronStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 1,
    name: "Bâton en fer aiguisé. Inflige un saignement à l'ennemi.",
    description: "+5 dégâts à distance. Canalise la magie (1 mana par tir).",
    statBonus: { rangedDamage: 5 },
    stackable: false,
    archetypes: ["mage"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 60,
  },
  sharpSteelStaff: {
    id: "sharpSteelStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 2,
    name: "Bâton en acier aiguisé. Inflige un saignement à l'ennemi.",
    description: "+6 dégâts à distance. Canalise la magie (2 mana par tir).",
    statBonus: { rangedDamage: 6 },
    stackable: false,
    archetypes: ["mage"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 70,
  },
  sharpSilverStaff: {
    id: "sharpSilverStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 2,
    name: "Bâton en argent aiguisé. Inflige un saignement à l'ennemi.",
    description: "+7 dégâts à distance. Canalise la magie (2 mana par tir).",
    statBonus: { rangedDamage: 7 },
    stackable: false,
    archetypes: ["mage"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 80,
  },
  sharpGoldStaff: {
    id: "sharpGoldStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 3,
    name: "Bâton en or aiguisé. Inflige un saignement à l'ennemi.",
    description: "+8 dégâts à distance. Canalise la magie (3 mana par tir).",
    statBonus: { rangedDamage: 8 },
    stackable: false,
    archetypes: ["mage"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 90,
  },
  sharpPlatiniumStaff: {
    id: "sharpPlatiniumStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 3,
    name: "Bâton en platine aiguisé. Inflige un saignement à l'ennemi.",
    description: "+9 dégâts à distance. Canalise la magie (3 mana par tir).",
    statBonus: { rangedDamage: 9 },
    stackable: false,
    archetypes: ["mage"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 100,
  },
  sharpCobaltStaff: {
    id: "sharpCobaltStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 4,
    name: "Bâton en cobalt aiguisé. Inflige un saignement à l'ennemi.",
    description: "+10 dégâts à distance. Canalise la magie (4 mana par tir).",
    statBonus: { rangedDamage: 10 },
    stackable: false,
    archetypes: ["mage"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 110,
  },
  sharpAdamantineStaff: {
    id: "sharpAdamantineStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 4,
    name: "Bâton en adamantine aiguisé. Inflige un saignement à l'ennemi.",
    description: "+11 dégâts à distance. Canalise la magie (4 mana par tir).",
    statBonus: { rangedDamage: 11 },
    stackable: false,
    archetypes: ["mage"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 120,
  },
  sharpCrimsonStaff: {
    id: "sharpCrimsonStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 5,
    name: "Bâton en cramoisi aiguisé. Inflige un saignement à l'ennemi.",
    description: "+12 dégâts à distance. Canalise la magie (5 mana par tir).",
    statBonus: { rangedDamage: 12 },
    stackable: false,
    archetypes: ["mage"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 130,
  },
  sharpAngelicStaff: {
    id: "sharpAngelicStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 5,
    name: "Bâton en angélique aiguisé. Inflige un saignement à l'ennemi.",
    description: "+13 dégâts à distance. Canalise la magie (5 mana par tir).",
    statBonus: { rangedDamage: 13 },
    stackable: false,
    archetypes: ["mage"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 140,
  },
  sharpFatefulStaff: {
    id: "sharpFatefulStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 6,
    name: "Bâton en fatidique aiguisé. Inflige un saignement à l'ennemi.",
    description: "+14 dégâts à distance. Canalise la magie (6 mana par tir).",
    statBonus: { rangedDamage: 14 },
    stackable: false,
    archetypes: ["mage"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 150,
  },
  sharpNovaStaff: {
    id: "sharpNovaStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 6,
    name: "Bâton en nova aiguisé. Inflige un saignement à l'ennemi.",
    description: "+15 dégâts à distance. Canalise la magie (6 mana par tir).",
    statBonus: { rangedDamage: 15 },
    stackable: false,
    archetypes: ["mage"],
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 1,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    price: 160,
  },

  // ----- Bâton renforcé (reinforced) - branche METAL -----
  reinforcedWoodenStaff: {
    id: "reinforcedWoodenStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 1,
    name: "Bâton en bois renforcé",
    description:
      "+4 dégâts à distance. Canalise la magie (1 mana par tir) (chance d'infliger stun).",
    statBonus: { rangedDamage: 4 },
    stackable: false,
    archetypes: ["mage"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.2,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 50,
  },
  reinforcedCopperStaff: {
    id: "reinforcedCopperStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 1,
    name: "Bâton en cuivre renforcé",
    description:
      "+5 dégâts à distance. Canalise la magie (1 mana par tir) (chance d'infliger stun).",
    statBonus: { rangedDamage: 5 },
    stackable: false,
    archetypes: ["mage"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.25,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 65,
  },
  reinforcedIronStaff: {
    id: "reinforcedIronStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 1,
    name: "Bâton en fer renforcé",
    description:
      "+6 dégâts à distance. Canalise la magie (1 mana par tir) (chance d'infliger stun).",
    statBonus: { rangedDamage: 6 },
    stackable: false,
    archetypes: ["mage"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.3,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 80,
  },
  reinforcedSteelStaff: {
    id: "reinforcedSteelStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 2,
    name: "Bâton en acier renforcé",
    description:
      "+8 dégâts à distance. Canalise la magie (2 mana par tir) (chance d'infliger stun).",
    statBonus: { rangedDamage: 8 },
    stackable: false,
    archetypes: ["mage"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.4,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 120,
  },
  reinforcedSilverStaff: {
    id: "reinforcedSilverStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 2,
    name: "Bâton en argent renforcé",
    description:
      "+10 dégâts à distance. Canalise la magie (2 mana par tir) (chance d'infliger stun).",
    statBonus: { rangedDamage: 10 },
    stackable: false,
    archetypes: ["mage"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.5,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 180,
  },
  reinforcedGoldStaff: {
    id: "reinforcedGoldStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 3,
    name: "Bâton en or renforcé",
    description:
      "+12 dégâts à distance. Canalise la magie (3 mana par tir) (chance d'infliger stun).",
    statBonus: { rangedDamage: 12 },
    stackable: false,
    archetypes: ["mage"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.6,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 250,
  },
  reinforcedPlatiniumStaff: {
    id: "reinforcedPlatiniumStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 3,
    name: "Bâton en platine renforcé",
    description:
      "+14 dégâts à distance. Canalise la magie (3 mana par tir) (chance d'infliger stun).",
    statBonus: { rangedDamage: 14 },
    stackable: false,
    archetypes: ["mage"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.7,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 350,
  },
  reinforcedCobaltStaff: {
    id: "reinforcedCobaltStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 4,
    name: "Bâton en cobalt renforcé",
    description:
      "+16 dégâts à distance. Canalise la magie (4 mana par tir) (chance d'infliger stun).",
    statBonus: { rangedDamage: 16 },
    stackable: false,
    archetypes: ["mage"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.8,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 450,
  },
  reinforcedAdamantineStaff: {
    id: "reinforcedAdamantineStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 4,
    name: "Bâton en adamantine renforcé",
    description:
      "+18 dégâts à distance. Canalise la magie (4 mana par tir) (chance d'infliger stun).",
    statBonus: { rangedDamage: 18 },
    stackable: false,
    archetypes: ["mage"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.9,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 550,
  },
  reinforcedCrimsonStaff: {
    id: "reinforcedCrimsonStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 5,
    name: "Bâton en cramoisi renforcé",
    description:
      "+20 dégâts à distance. Canalise la magie (5 mana par tir) (chance d'infliger stun).",
    statBonus: { rangedDamage: 20 },
    stackable: false,
    archetypes: ["mage"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 650,
  },
  reinforcedAngelicStaff: {
    id: "reinforcedAngelicStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 5,
    name: "Bâton en angélique renforcé",
    description:
      "+22 dégâts à distance. Canalise la magie (5 mana par tir) (chance d'infliger stun).",
    statBonus: { rangedDamage: 22 },
    stackable: false,
    archetypes: ["mage"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 750,
  },
  reinforcedFatefulStaff: {
    id: "reinforcedFatefulStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 6,
    name: "Bâton en fatidique renforcé",
    description:
      "+25 dégâts à distance. Canalise la magie (6 mana par tir) (chance d'infliger stun).",
    statBonus: { rangedDamage: 25 },
    stackable: false,
    archetypes: ["mage"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 850,
  },
  reinforcedNovaStaff: {
    id: "reinforcedNovaStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 6,
    name: "Bâton en nova renforcé",
    description:
      "+28 dégâts à distance. Canalise la magie (6 mana par tir) (chance d'infliger stun).",
    statBonus: { rangedDamage: 28 },
    stackable: false,
    archetypes: ["mage"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 950,
  },

  // ===== Bâton - branche BOIS (oak -> starwood, alternative a la branche metal ci-dessus) =====
  oakStaff: {
    id: "oakStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 1,
    name: "Bâton en bois chêne",
    description:
      "+3 dégâts à distance. Canalise la magie (1 mana par tir), sans munitions.",
    statBonus: { rangedDamage: 3 },
    stackable: false,
    archetypes: ["mage"],
    unlockLevel: 1,
    price: 30,
  },
  ashStaff: {
    id: "ashStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 1,
    name: "Bâton en bois frêne",
    description:
      "+5 dégâts à distance. Canalise la magie (1 mana par tir), sans munitions.",
    statBonus: { rangedDamage: 5 },
    stackable: false,
    archetypes: ["mage"],
    unlockLevel: 2,
    price: 60,
  },
  yewStaff: {
    id: "yewStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 2,
    name: "Bâton en bois if",
    description:
      "+7 dégâts à distance. Canalise la magie (2 mana par tir), sans munitions.",
    statBonus: { rangedDamage: 7 },
    stackable: false,
    archetypes: ["mage"],
    unlockLevel: 3,
    price: 100,
  },
  ebonyStaff: {
    id: "ebonyStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 2,
    name: "Bâton en bois ébène",
    description:
      "+9 dégâts à distance. Canalise la magie (2 mana par tir), sans munitions.",
    statBonus: { rangedDamage: 9 },
    stackable: false,
    archetypes: ["mage"],
    unlockLevel: 4,
    price: 150,
  },
  petrifiedStaff: {
    id: "petrifiedStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 3,
    name: "Bâton en bois pétrifié",
    description:
      "+11 dégâts à distance. Canalise la magie (3 mana par tir), sans munitions.",
    statBonus: { rangedDamage: 11 },
    stackable: false,
    archetypes: ["mage"],
    unlockLevel: 5,
    price: 200,
  },
  mistwoodStaff: {
    id: "mistwoodStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 3,
    name: "Bâton en bois de brume",
    description:
      "+13 dégâts à distance. Canalise la magie (3 mana par tir), sans munitions.",
    statBonus: { rangedDamage: 13 },
    stackable: false,
    archetypes: ["mage"],
    unlockLevel: 6,
    price: 300,
  },
  runewoodStaff: {
    id: "runewoodStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 4,
    name: "Bâton en bois runique",
    description:
      "+15 dégâts à distance. Canalise la magie (4 mana par tir), sans munitions.",
    statBonus: { rangedDamage: 15 },
    stackable: false,
    archetypes: ["mage"],
    unlockLevel: 7,
    price: 400,
  },
  skywoodStaff: {
    id: "skywoodStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 4,
    name: "Bâton en bois céleste",
    description:
      "+17 dégâts à distance. Canalise la magie (4 mana par tir), sans munitions.",
    statBonus: { rangedDamage: 17 },
    stackable: false,
    archetypes: ["mage"],
    unlockLevel: 8,
    price: 500,
  },
  scarletwoodStaff: {
    id: "scarletwoodStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 5,
    name: "Bâton en bois écarlate",
    description:
      "+19 dégâts à distance. Canalise la magie (5 mana par tir), sans munitions.",
    statBonus: { rangedDamage: 19 },
    stackable: false,
    archetypes: ["mage"],
    unlockLevel: 9,
    price: 600,
  },
  sacredStaff: {
    id: "sacredStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 5,
    name: "Bâton en bois sacré",
    description:
      "+21 dégâts à distance. Canalise la magie (5 mana par tir), sans munitions.",
    statBonus: { rangedDamage: 21 },
    stackable: false,
    archetypes: ["mage"],
    unlockLevel: 10,
    price: 700,
  },
  eternalStaff: {
    id: "eternalStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 6,
    name: "Bâton en bois éternel",
    description:
      "+23 dégâts à distance. Canalise la magie (6 mana par tir), sans munitions.",
    statBonus: { rangedDamage: 23 },
    stackable: false,
    archetypes: ["mage"],
    unlockLevel: 11,
    price: 800,
  },
  starwoodStaff: {
    id: "starwoodStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 6,
    name: "Bâton en bois stellaire",
    description:
      "+25 dégâts à distance. Canalise la magie (6 mana par tir), sans munitions.",
    statBonus: { rangedDamage: 25 },
    stackable: false,
    archetypes: ["mage"],
    unlockLevel: 12,
    price: 900,
  },
  // ----- Bâton renforcé - branche BOIS (alternative a la branche metal ci-dessus) -----
  reinforcedOakStaff: {
    id: "reinforcedOakStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 1,
    name: "Bâton en chêne renforcé",
    description:
      "+5 dégâts à distance. Canalise la magie (1 mana par tir) (chance d'infliger stun).",
    statBonus: { rangedDamage: 5 },
    stackable: false,
    archetypes: ["mage"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.25,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 65,
  },
  reinforcedAshStaff: {
    id: "reinforcedAshStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 1,
    name: "Bâton en frêne renforcé",
    description:
      "+6 dégâts à distance. Canalise la magie (1 mana par tir) (chance d'infliger stun).",
    statBonus: { rangedDamage: 6 },
    stackable: false,
    archetypes: ["mage"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.3,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 80,
  },
  reinforcedYewStaff: {
    id: "reinforcedYewStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 2,
    name: "Bâton en if renforcé",
    description:
      "+8 dégâts à distance. Canalise la magie (2 mana par tir) (chance d'infliger stun).",
    statBonus: { rangedDamage: 8 },
    stackable: false,
    archetypes: ["mage"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.4,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 120,
  },
  reinforcedEbonyStaff: {
    id: "reinforcedEbonyStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 2,
    name: "Bâton en ébène renforcé",
    description:
      "+10 dégâts à distance. Canalise la magie (2 mana par tir) (chance d'infliger stun).",
    statBonus: { rangedDamage: 10 },
    stackable: false,
    archetypes: ["mage"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.5,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 180,
  },
  reinforcedPetrifiedStaff: {
    id: "reinforcedPetrifiedStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 3,
    name: "Bâton en bois pétrifié renforcé",
    description:
      "+12 dégâts à distance. Canalise la magie (3 mana par tir) (chance d'infliger stun).",
    statBonus: { rangedDamage: 12 },
    stackable: false,
    archetypes: ["mage"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.6,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 250,
  },
  reinforcedMistwoodStaff: {
    id: "reinforcedMistwoodStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 3,
    name: "Bâton en bois de brume renforcé",
    description:
      "+14 dégâts à distance. Canalise la magie (3 mana par tir) (chance d'infliger stun).",
    statBonus: { rangedDamage: 14 },
    stackable: false,
    archetypes: ["mage"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.7,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 350,
  },
  reinforcedRunewoodStaff: {
    id: "reinforcedRunewoodStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 4,
    name: "Bâton en bois runique renforcé",
    description:
      "+16 dégâts à distance. Canalise la magie (4 mana par tir) (chance d'infliger stun).",
    statBonus: { rangedDamage: 16 },
    stackable: false,
    archetypes: ["mage"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.8,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 450,
  },
  reinforcedSkywoodStaff: {
    id: "reinforcedSkywoodStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 4,
    name: "Bâton en bois céleste renforcé",
    description:
      "+18 dégâts à distance. Canalise la magie (4 mana par tir) (chance d'infliger stun).",
    statBonus: { rangedDamage: 18 },
    stackable: false,
    archetypes: ["mage"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.9,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 550,
  },
  reinforcedScarletwoodStaff: {
    id: "reinforcedScarletwoodStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 5,
    name: "Bâton en bois écarlate renforcé",
    description:
      "+20 dégâts à distance. Canalise la magie (5 mana par tir) (chance d'infliger stun).",
    statBonus: { rangedDamage: 20 },
    stackable: false,
    archetypes: ["mage"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 650,
  },
  reinforcedSacredStaff: {
    id: "reinforcedSacredStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 5,
    name: "Bâton en bois sacré renforcé",
    description:
      "+22 dégâts à distance. Canalise la magie (5 mana par tir) (chance d'infliger stun).",
    statBonus: { rangedDamage: 22 },
    stackable: false,
    archetypes: ["mage"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 750,
  },
  reinforcedEternalStaff: {
    id: "reinforcedEternalStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 6,
    name: "Bâton en bois éternel renforcé",
    description:
      "+25 dégâts à distance. Canalise la magie (6 mana par tir) (chance d'infliger stun).",
    statBonus: { rangedDamage: 25 },
    stackable: false,
    archetypes: ["mage"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 850,
  },
  reinforcedStarwoodStaff: {
    id: "reinforcedStarwoodStaff",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: true,
    requiresAmmo: false,
    manaCost: 6,
    name: "Bâton en bois stellaire renforcé",
    description:
      "+28 dégâts à distance. Canalise la magie (6 mana par tir) (chance d'infliger stun).",
    statBonus: { rangedDamage: 28 },
    stackable: false,
    archetypes: ["mage"],
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 1.0,
      statModifiers: { stunned: true },
      durationMs: 2000,
    },
    price: 950,
  },

  // ===== Armure - évolution classique (wood -> nova) - pas d'unlockLevel (craftable uniquement) =====
  woodenArmor: {
    id: "woodenArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure en bois",
    description: "+1 défense. Armure d'entraînement de départ.",
    statBonus: { defense: 1 },
    stackable: false,
    price: 2,
  },
  copperArmor: {
    id: "copperArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure en cuivre",
    description: "+2 défense.",
    statBonus: { defense: 2 },
    stackable: false,
    price: 30,
  },
  ironArmor: {
    id: "ironArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure en fer",
    description: "+3 défense.",
    statBonus: { defense: 3 },
    stackable: false,
    price: 60,
  },
  steelArmor: {
    id: "steelArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure en acier",
    description: "+4 défense.",
    statBonus: { defense: 4 },
    stackable: false,
    price: 100,
  },
  silverArmor: {
    id: "silverArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure en argent",
    description: "+5 défense.",
    statBonus: { defense: 5 },
    stackable: false,
    price: 150,
  },
  goldArmor: {
    id: "goldArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure en or",
    description: "+6 défense.",
    statBonus: { defense: 6 },
    stackable: false,
    price: 200,
  },
  platiniumArmor: {
    id: "platiniumArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure en platine",
    description: "+7 défense.",
    statBonus: { defense: 7 },
    stackable: false,
    price: 300,
  },
  cobaltArmor: {
    id: "cobaltArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure en cobalt",
    description: "+8 défense.",
    statBonus: { defense: 8 },
    stackable: false,
    price: 400,
  },
  adamantineArmor: {
    id: "adamantineArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure en adamantine",
    description: "+9 défense.",
    statBonus: { defense: 9 },
    stackable: false,
    price: 500,
  },
  crimsonArmor: {
    id: "crimsonArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure en cramoisi",
    description: "+10 défense.",
    statBonus: { defense: 10 },
    stackable: false,
    price: 600,
  },
  angelicArmor: {
    id: "angelicArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure en angélique",
    description: "+11 défense.",
    statBonus: { defense: 11 },
    stackable: false,
    price: 700,
  },
  fatefulArmor: {
    id: "fatefulArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure en fatidique",
    description: "+12 défense.",
    statBonus: { defense: 12 },
    stackable: false,
    price: 800,
  },
  novaArmor: {
    id: "novaArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure en nova",
    description: "+13 défense.",
    statBonus: { defense: 13 },
    stackable: false,
    price: 900,
  },

  // ===== Armure renforcée - évolution (wood -> nova) - pas d'unlockLevel (craftable uniquement) =====
  reinforcedWoodenArmor: {
    id: "reinforcedWoodenArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure en bois renforcée",
    description: "+3 défense.",
    statBonus: { defense: 3 },
    stackable: false,
    price: 50,
  },
  reinforcedCopperArmor: {
    id: "reinforcedCopperArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure en cuivre renforcée",
    description: "+4 défense.",
    statBonus: { defense: 4 },
    stackable: false,
    price: 65,
  },
  reinforcedIronArmor: {
    id: "reinforcedIronArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure en fer renforcée",
    description: "+5 défense.",
    statBonus: { defense: 5 },
    stackable: false,
    price: 80,
  },
  reinforcedSteelArmor: {
    id: "reinforcedSteelArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure en acier renforcée",
    description: "+6 défense.",
    statBonus: { defense: 6 },
    stackable: false,
    price: 120,
  },
  reinforcedSilverArmor: {
    id: "reinforcedSilverArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure en argent renforcée",
    description: "+7 défense.",
    statBonus: { defense: 7 },
    stackable: false,
    price: 180,
  },
  reinforcedGoldArmor: {
    id: "reinforcedGoldArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure en or renforcée",
    description: "+8 défense.",
    statBonus: { defense: 8 },
    stackable: false,
    price: 250,
  },
  reinforcedPlatiniumArmor: {
    id: "reinforcedPlatiniumArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure en platine renforcée",
    description: "+9 défense.",
    statBonus: { defense: 9 },
    stackable: false,
    price: 350,
  },
  reinforcedCobaltArmor: {
    id: "reinforcedCobaltArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure en cobalt renforcée",
    description: "+10 défense.",
    statBonus: { defense: 10 },
    stackable: false,
    price: 450,
  },
  reinforcedAdamantineArmor: {
    id: "reinforcedAdamantineArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure en adamantine renforcée",
    description: "+11 défense.",
    statBonus: { defense: 11 },
    stackable: false,
    price: 550,
  },
  reinforcedCrimsonArmor: {
    id: "reinforcedCrimsonArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure en cramoisi renforcée",
    description: "+12 défense.",
    statBonus: { defense: 12 },
    stackable: false,
    price: 650,
  },
  reinforcedAngelicArmor: {
    id: "reinforcedAngelicArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure en angélique renforcée",
    description: "+13 défense.",
    statBonus: { defense: 13 },
    stackable: false,
    price: 750,
  },
  reinforcedFatefulArmor: {
    id: "reinforcedFatefulArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure en fatidique renforcée",
    description: "+14 défense.",
    statBonus: { defense: 14 },
    stackable: false,
    price: 850,
  },
  reinforcedNovaArmor: {
    id: "reinforcedNovaArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure en nova renforcée",
    description: "+15 défense.",
    statBonus: { defense: 15 },
    stackable: false,
    price: 950,
  },
  // ===== Robe de mage - évolution classique (matériaux de monstres) - pas d'unlockLevel (craftable uniquement) =====
  mageRobe: {
    id: "mageRobe",
    category: "equipment",
    slot: "armor",
    name: "Robe de mage de base",
    description: "+1 défense. Équipement d'entraînement de départ.",
    statBonus: { defense: 1 },
    archetypes: ["mage"],
    stackable: false,
    price: 2,
  },
  slimeBlobMageRobe: {
    id: "slimeBlobMageRobe",
    category: "equipment",
    slot: "armor",
    name: "Robe de mage en bave de slime",
    description: "+1 défense.",
    statBonus: { defense: 1 },
    archetypes: ["mage"],
    stackable: false,
    price: 30,
  },
  bearPeltMageRobe: {
    id: "bearPeltMageRobe",
    category: "equipment",
    slot: "armor",
    name: "Robe de mage en peau d'ours",
    description: "+2 défense.",
    statBonus: { defense: 2 },
    archetypes: ["mage"],
    stackable: false,
    price: 60,
  },
  spiderLegMageRobe: {
    id: "spiderLegMageRobe",
    category: "equipment",
    slot: "armor",
    name: "Robe de mage en patte d'araignée",
    description: "+3 défense.",
    statBonus: { defense: 3 },
    archetypes: ["mage"],
    stackable: false,
    price: 100,
  },
  greyMonsterScaleMageRobe: {
    id: "greyMonsterScaleMageRobe",
    category: "equipment",
    slot: "armor",
    name: "Robe de mage en écaille de monstre grise",
    description: "+4 défense.",
    statBonus: { defense: 4 },
    archetypes: ["mage"],
    stackable: false,
    price: 150,
  },
  crabClawMageRobe: {
    id: "crabClawMageRobe",
    category: "equipment",
    slot: "armor",
    name: "Robe de mage en pince de crabe",
    description: "+5 défense.",
    statBonus: { defense: 5 },
    archetypes: ["mage"],
    stackable: false,
    price: 200,
  },
  blackBearPeltMageRobe: {
    id: "blackBearPeltMageRobe",
    category: "equipment",
    slot: "armor",
    name: "Robe de mage en peau d'ours noir",
    description: "+6 défense.",
    statBonus: { defense: 6 },
    archetypes: ["mage"],
    stackable: false,
    price: 300,
  },
  turtleShellMageRobe: {
    id: "turtleShellMageRobe",
    category: "equipment",
    slot: "armor",
    name: "Robe de mage en carapace de tortue",
    description: "+7 défense.",
    statBonus: { defense: 7 },
    archetypes: ["mage"],
    stackable: false,
    price: 400,
  },
  greenMonsterScaleMageRobe: {
    id: "greenMonsterScaleMageRobe",
    category: "equipment",
    slot: "armor",
    name: "Robe de mage en écaille de monstre verte",
    description: "+8 défense.",
    statBonus: { defense: 8 },
    archetypes: ["mage"],
    stackable: false,
    price: 500,
  },
  batWingsMageRobe: {
    id: "batWingsMageRobe",
    category: "equipment",
    slot: "armor",
    name: "Robe de mage en ailes de chauve-souris",
    description: "+9 défense.",
    statBonus: { defense: 9 },
    archetypes: ["mage"],
    stackable: false,
    price: 600,
  },
  dragonScaleMageRobe: {
    id: "dragonScaleMageRobe",
    category: "equipment",
    slot: "armor",
    name: "Robe de mage en écaille de dragon",
    description: "+10 défense.",
    statBonus: { defense: 10 },
    archetypes: ["mage"],
    stackable: false,
    price: 700,
  },
  ghostEctoplasmMageRobe: {
    id: "ghostEctoplasmMageRobe",
    category: "equipment",
    slot: "armor",
    name: "Robe de mage en ectoplasme de fantôme",
    description: "+11 défense.",
    statBonus: { defense: 11 },
    archetypes: ["mage"],
    stackable: false,
    price: 800,
  },
  monsterCoreMageRobe: {
    id: "monsterCoreMageRobe",
    category: "equipment",
    slot: "armor",
    name: "Robe de mage en noyau de monstre",
    description: "+12 défense.",
    statBonus: { defense: 12 },
    archetypes: ["mage"],
    stackable: false,
    price: 900,
  },

  // ===== Robe de mage renforcé(e) - évolution (matériaux de monstres) - pas d'unlockLevel (craftable uniquement) =====
  reinforcedmageRobe: {
    id: "reinforcedmageRobe",
    category: "equipment",
    slot: "armor",
    name: "Robe de mage renforcé(e) de base",
    description: "+3 défense.",
    statBonus: { defense: 3 },
    archetypes: ["mage"],
    stackable: false,
    price: 50,
  },
  reinforcedSlimeBlobMageRobe: {
    id: "reinforcedSlimeBlobMageRobe",
    category: "equipment",
    slot: "armor",
    name: "Robe de mage renforcé(e) en bave de slime",
    description: "+3 défense.",
    statBonus: { defense: 3 },
    archetypes: ["mage"],
    stackable: false,
    price: 65,
  },
  reinforcedBearPeltMageRobe: {
    id: "reinforcedBearPeltMageRobe",
    category: "equipment",
    slot: "armor",
    name: "Robe de mage renforcé(e) en peau d'ours",
    description: "+4 défense.",
    statBonus: { defense: 4 },
    archetypes: ["mage"],
    stackable: false,
    price: 80,
  },
  reinforcedSpiderLegMageRobe: {
    id: "reinforcedSpiderLegMageRobe",
    category: "equipment",
    slot: "armor",
    name: "Robe de mage renforcé(e) en patte d'araignée",
    description: "+5 défense.",
    statBonus: { defense: 5 },
    archetypes: ["mage"],
    stackable: false,
    price: 120,
  },
  reinforcedGreyMonsterScaleMageRobe: {
    id: "reinforcedGreyMonsterScaleMageRobe",
    category: "equipment",
    slot: "armor",
    name: "Robe de mage renforcé(e) en écaille de monstre grise",
    description: "+6 défense.",
    statBonus: { defense: 6 },
    archetypes: ["mage"],
    stackable: false,
    price: 180,
  },
  reinforcedCrabClawMageRobe: {
    id: "reinforcedCrabClawMageRobe",
    category: "equipment",
    slot: "armor",
    name: "Robe de mage renforcé(e) en pince de crabe",
    description: "+7 défense.",
    statBonus: { defense: 7 },
    archetypes: ["mage"],
    stackable: false,
    price: 250,
  },
  reinforcedBlackBearPeltMageRobe: {
    id: "reinforcedBlackBearPeltMageRobe",
    category: "equipment",
    slot: "armor",
    name: "Robe de mage renforcé(e) en peau d'ours noir",
    description: "+8 défense.",
    statBonus: { defense: 8 },
    archetypes: ["mage"],
    stackable: false,
    price: 350,
  },
  reinforcedTurtleShellMageRobe: {
    id: "reinforcedTurtleShellMageRobe",
    category: "equipment",
    slot: "armor",
    name: "Robe de mage renforcé(e) en carapace de tortue",
    description: "+9 défense.",
    statBonus: { defense: 9 },
    archetypes: ["mage"],
    stackable: false,
    price: 450,
  },
  reinforcedGreenMonsterScaleMageRobe: {
    id: "reinforcedGreenMonsterScaleMageRobe",
    category: "equipment",
    slot: "armor",
    name: "Robe de mage renforcé(e) en écaille de monstre verte",
    description: "+10 défense.",
    statBonus: { defense: 10 },
    archetypes: ["mage"],
    stackable: false,
    price: 550,
  },
  reinforcedBatWingsMageRobe: {
    id: "reinforcedBatWingsMageRobe",
    category: "equipment",
    slot: "armor",
    name: "Robe de mage renforcé(e) en ailes de chauve-souris",
    description: "+11 défense.",
    statBonus: { defense: 11 },
    archetypes: ["mage"],
    stackable: false,
    price: 650,
  },
  reinforcedDragonScaleMageRobe: {
    id: "reinforcedDragonScaleMageRobe",
    category: "equipment",
    slot: "armor",
    name: "Robe de mage renforcé(e) en écaille de dragon",
    description: "+12 défense.",
    statBonus: { defense: 12 },
    archetypes: ["mage"],
    stackable: false,
    price: 750,
  },
  reinforcedGhostEctoplasmMageRobe: {
    id: "reinforcedGhostEctoplasmMageRobe",
    category: "equipment",
    slot: "armor",
    name: "Robe de mage renforcé(e) en ectoplasme de fantôme",
    description: "+13 défense.",
    statBonus: { defense: 13 },
    archetypes: ["mage"],
    stackable: false,
    price: 850,
  },
  reinforcedMonsterCoreMageRobe: {
    id: "reinforcedMonsterCoreMageRobe",
    category: "equipment",
    slot: "armor",
    name: "Robe de mage renforcé(e) en noyau de monstre",
    description: "+14 défense.",
    statBonus: { defense: 14 },
    archetypes: ["mage"],
    stackable: false,
    price: 950,
  },

  // ===== Armure - évolution classique (matériaux de monstres) - pas d'unlockLevel (craftable uniquement) =====
  slimeBlobLeatherArmor: {
    id: "slimeBlobLeatherArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure en bave de slime",
    description: "+4 défense.",
    statBonus: { defense: 4 },
    stackable: false,
    price: 30,
  },
  bearPeltLeatherArmor: {
    id: "bearPeltLeatherArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure en peau d'ours",
    description: "+5 défense.",
    statBonus: { defense: 5 },
    stackable: false,
    price: 60,
  },
  spiderLegLeatherArmor: {
    id: "spiderLegLeatherArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure en patte d'araignée",
    description: "+6 défense.",
    statBonus: { defense: 6 },
    stackable: false,
    price: 100,
  },
  greyMonsterScaleLeatherArmor: {
    id: "greyMonsterScaleLeatherArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure en écaille de monstre grise",
    description: "+7 défense.",
    statBonus: { defense: 7 },
    stackable: false,
    price: 150,
  },
  crabClawLeatherArmor: {
    id: "crabClawLeatherArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure en pince de crabe",
    description: "+8 défense.",
    statBonus: { defense: 8 },
    stackable: false,
    price: 200,
  },
  blackBearPeltLeatherArmor: {
    id: "blackBearPeltLeatherArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure en peau d'ours noir",
    description: "+9 défense.",
    statBonus: { defense: 9 },
    stackable: false,
    price: 300,
  },
  turtleShellLeatherArmor: {
    id: "turtleShellLeatherArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure en carapace de tortue",
    description: "+10 défense.",
    statBonus: { defense: 10 },
    stackable: false,
    price: 400,
  },
  greenMonsterScaleLeatherArmor: {
    id: "greenMonsterScaleLeatherArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure en écaille de monstre verte",
    description: "+11 défense.",
    statBonus: { defense: 11 },
    stackable: false,
    price: 500,
  },
  batWingsLeatherArmor: {
    id: "batWingsLeatherArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure en ailes de chauve-souris",
    description: "+12 défense.",
    statBonus: { defense: 12 },
    stackable: false,
    price: 600,
  },
  dragonScaleLeatherArmor: {
    id: "dragonScaleLeatherArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure en écaille de dragon",
    description: "+13 défense.",
    statBonus: { defense: 13 },
    stackable: false,
    price: 700,
  },
  ghostEctoplasmLeatherArmor: {
    id: "ghostEctoplasmLeatherArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure en ectoplasme de fantôme",
    description: "+14 défense.",
    statBonus: { defense: 14 },
    stackable: false,
    price: 800,
  },
  monsterCoreLeatherArmor: {
    id: "monsterCoreLeatherArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure en noyau de monstre",
    description: "+15 défense.",
    statBonus: { defense: 15 },
    stackable: false,
    price: 900,
  },

  // ===== Armure renforcé(e) - évolution (matériaux de monstres) - pas d'unlockLevel (craftable uniquement) =====
  reinforcedLeatherArmor: {
    id: "reinforcedLeatherArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure renforcé(e) de base",
    description: "+5 défense.",
    statBonus: { defense: 5 },
    stackable: false,
    price: 50,
  },
  reinforcedSlimeBlobLeatherArmor: {
    id: "reinforcedSlimeBlobLeatherArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure renforcé(e) en bave de slime",
    description: "+6 défense.",
    statBonus: { defense: 6 },
    stackable: false,
    price: 65,
  },
  reinforcedBearPeltLeatherArmor: {
    id: "reinforcedBearPeltLeatherArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure renforcé(e) en peau d'ours",
    description: "+7 défense.",
    statBonus: { defense: 7 },
    stackable: false,
    price: 80,
  },
  reinforcedSpiderLegLeatherArmor: {
    id: "reinforcedSpiderLegLeatherArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure renforcé(e) en patte d'araignée",
    description: "+8 défense.",
    statBonus: { defense: 8 },
    stackable: false,
    price: 120,
  },
  reinforcedGreyMonsterScaleLeatherArmor: {
    id: "reinforcedGreyMonsterScaleLeatherArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure renforcé(e) en écaille de monstre grise",
    description: "+9 défense.",
    statBonus: { defense: 9 },
    stackable: false,
    price: 180,
  },
  reinforcedCrabClawLeatherArmor: {
    id: "reinforcedCrabClawLeatherArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure renforcé(e) en pince de crabe",
    description: "+10 défense.",
    statBonus: { defense: 10 },
    stackable: false,
    price: 250,
  },
  reinforcedBlackBearPeltLeatherArmor: {
    id: "reinforcedBlackBearPeltLeatherArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure renforcé(e) en peau d'ours noir",
    description: "+11 défense.",
    statBonus: { defense: 11 },
    stackable: false,
    price: 350,
  },
  reinforcedTurtleShellLeatherArmor: {
    id: "reinforcedTurtleShellLeatherArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure renforcé(e) en carapace de tortue",
    description: "+12 défense.",
    statBonus: { defense: 12 },
    stackable: false,
    price: 450,
  },
  reinforcedGreenMonsterScaleLeatherArmor: {
    id: "reinforcedGreenMonsterScaleLeatherArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure renforcé(e) en écaille de monstre verte",
    description: "+13 défense.",
    statBonus: { defense: 13 },
    stackable: false,
    price: 550,
  },
  reinforcedBatWingsLeatherArmor: {
    id: "reinforcedBatWingsLeatherArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure renforcé(e) en ailes de chauve-souris",
    description: "+14 défense.",
    statBonus: { defense: 14 },
    stackable: false,
    price: 650,
  },
  reinforcedDragonScaleLeatherArmor: {
    id: "reinforcedDragonScaleLeatherArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure renforcé(e) en écaille de dragon",
    description: "+15 défense.",
    statBonus: { defense: 15 },
    stackable: false,
    price: 750,
  },
  reinforcedGhostEctoplasmLeatherArmor: {
    id: "reinforcedGhostEctoplasmLeatherArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure renforcé(e) en ectoplasme de fantôme",
    description: "+16 défense.",
    statBonus: { defense: 16 },
    stackable: false,
    price: 850,
  },
  reinforcedMonsterCoreLeatherArmor: {
    id: "reinforcedMonsterCoreLeatherArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure renforcé(e) en noyau de monstre",
    description: "+17 défense.",
    statBonus: { defense: 17 },
    stackable: false,
    price: 950,
  },

  // ===== Bottes - évolution classique (matériaux de monstres) - défense + vitesse (bonus PLAT, comme tous les autres bonus d'équipement) - pas d'unlockLevel =====
  boots: {
    id: "boots",
    category: "equipment",
    slot: "boots",
    name: "Bottes",
    description:
      "+1 défense, +2 vitesse de déplacement. Équipement d'entraînement de départ.",
    statBonus: { defense: 1, moveSpeed: 2 },
    stackable: false,
    price: 2,
  },
  slimeBlobBoots: {
    id: "slimeBlobBoots",
    category: "equipment",
    slot: "boots",
    name: "Bottes en bave de slime",
    description: "+1 défense, +4 vitesse de déplacement.",
    statBonus: { defense: 1, moveSpeed: 4 },
    stackable: false,
    price: 30,
  },
  bearPeltBoots: {
    id: "bearPeltBoots",
    category: "equipment",
    slot: "boots",
    name: "Bottes en peau d'ours",
    description: "+2 défense, +6 vitesse de déplacement.",
    statBonus: { defense: 2, moveSpeed: 6 },
    stackable: false,
    price: 60,
  },
  spiderLegBoots: {
    id: "spiderLegBoots",
    category: "equipment",
    slot: "boots",
    name: "Bottes en patte d'araignée",
    description: "+3 défense, +8 vitesse de déplacement.",
    statBonus: { defense: 3, moveSpeed: 8 },
    stackable: false,
    price: 100,
  },
  greyMonsterScaleBoots: {
    id: "greyMonsterScaleBoots",
    category: "equipment",
    slot: "boots",
    name: "Bottes en écaille de monstre grise",
    description: "+4 défense, +10 vitesse de déplacement.",
    statBonus: { defense: 4, moveSpeed: 10 },
    stackable: false,
    price: 150,
  },
  crabClawBoots: {
    id: "crabClawBoots",
    category: "equipment",
    slot: "boots",
    name: "Bottes en pince de crabe",
    description: "+5 défense, +12 vitesse de déplacement.",
    statBonus: { defense: 5, moveSpeed: 12 },
    stackable: false,
    price: 200,
  },
  blackBearPeltBoots: {
    id: "blackBearPeltBoots",
    category: "equipment",
    slot: "boots",
    name: "Bottes en peau d'ours noir",
    description: "+6 défense, +14 vitesse de déplacement.",
    statBonus: { defense: 6, moveSpeed: 14 },
    stackable: false,
    price: 300,
  },
  turtleShellBoots: {
    id: "turtleShellBoots",
    category: "equipment",
    slot: "boots",
    name: "Bottes en carapace de tortue",
    description: "+7 défense, +16 vitesse de déplacement.",
    statBonus: { defense: 7, moveSpeed: 16 },
    stackable: false,
    price: 400,
  },
  greenMonsterScaleBoots: {
    id: "greenMonsterScaleBoots",
    category: "equipment",
    slot: "boots",
    name: "Bottes en écaille de monstre verte",
    description: "+8 défense, +18 vitesse de déplacement.",
    statBonus: { defense: 8, moveSpeed: 18 },
    stackable: false,
    price: 500,
  },
  batWingsBoots: {
    id: "batWingsBoots",
    category: "equipment",
    slot: "boots",
    name: "Bottes en ailes de chauve-souris",
    description: "+9 défense, +20 vitesse de déplacement.",
    statBonus: { defense: 9, moveSpeed: 20 },
    stackable: false,
    price: 600,
  },
  dragonScaleBoots: {
    id: "dragonScaleBoots",
    category: "equipment",
    slot: "boots",
    name: "Bottes en écaille de dragon",
    description: "+10 défense, +22 vitesse de déplacement.",
    statBonus: { defense: 10, moveSpeed: 22 },
    stackable: false,
    price: 700,
  },
  ghostEctoplasmBoots: {
    id: "ghostEctoplasmBoots",
    category: "equipment",
    slot: "boots",
    name: "Bottes en ectoplasme de fantôme",
    description: "+11 défense, +24 vitesse de déplacement.",
    statBonus: { defense: 11, moveSpeed: 24 },
    stackable: false,
    price: 800,
  },
  monsterCoreBoots: {
    id: "monsterCoreBoots",
    category: "equipment",
    slot: "boots",
    name: "Bottes en noyau de monstre",
    description: "+12 défense, +26 vitesse de déplacement.",
    statBonus: { defense: 12, moveSpeed: 26 },
    stackable: false,
    price: 900,
  },

  // ===== Bottes renforcées - évolution (matériaux de monstres) - défense + vitesse accrues - pas d'unlockLevel =====
  reinforcedBoots: {
    id: "reinforcedBoots",
    category: "equipment",
    slot: "boots",
    name: "Bottes renforcées",
    description: "+3 défense, +8 vitesse de déplacement.",
    statBonus: { defense: 3, moveSpeed: 8 },
    stackable: false,
    price: 50,
  },
  reinforcedSlimeBlobBoots: {
    id: "reinforcedSlimeBlobBoots",
    category: "equipment",
    slot: "boots",
    name: "Bottes en bave de slime renforcées",
    description: "+3 défense, +10 vitesse de déplacement.",
    statBonus: { defense: 3, moveSpeed: 10 },
    stackable: false,
    price: 65,
  },
  reinforcedBearPeltBoots: {
    id: "reinforcedBearPeltBoots",
    category: "equipment",
    slot: "boots",
    name: "Bottes en peau d'ours renforcées",
    description: "+4 défense, +12 vitesse de déplacement.",
    statBonus: { defense: 4, moveSpeed: 12 },
    stackable: false,
    price: 80,
  },
  reinforcedSpiderLegBoots: {
    id: "reinforcedSpiderLegBoots",
    category: "equipment",
    slot: "boots",
    name: "Bottes en patte d'araignée renforcées",
    description: "+5 défense, +14 vitesse de déplacement.",
    statBonus: { defense: 5, moveSpeed: 14 },
    stackable: false,
    price: 120,
  },
  reinforcedGreyMonsterScaleBoots: {
    id: "reinforcedGreyMonsterScaleBoots",
    category: "equipment",
    slot: "boots",
    name: "Bottes en écaille de monstre grise renforcées",
    description: "+6 défense, +16 vitesse de déplacement.",
    statBonus: { defense: 6, moveSpeed: 16 },
    stackable: false,
    price: 180,
  },
  reinforcedCrabClawBoots: {
    id: "reinforcedCrabClawBoots",
    category: "equipment",
    slot: "boots",
    name: "Bottes en pince de crabe renforcées",
    description: "+7 défense, +18 vitesse de déplacement.",
    statBonus: { defense: 7, moveSpeed: 18 },
    stackable: false,
    price: 250,
  },
  reinforcedBlackBearPeltBoots: {
    id: "reinforcedBlackBearPeltBoots",
    category: "equipment",
    slot: "boots",
    name: "Bottes en peau d'ours noir renforcées",
    description: "+8 défense, +20 vitesse de déplacement.",
    statBonus: { defense: 8, moveSpeed: 20 },
    stackable: false,
    price: 350,
  },
  reinforcedTurtleShellBoots: {
    id: "reinforcedTurtleShellBoots",
    category: "equipment",
    slot: "boots",
    name: "Bottes en carapace de tortue renforcées",
    description: "+9 défense, +22 vitesse de déplacement.",
    statBonus: { defense: 9, moveSpeed: 22 },
    stackable: false,
    price: 450,
  },
  reinforcedGreenMonsterScaleBoots: {
    id: "reinforcedGreenMonsterScaleBoots",
    category: "equipment",
    slot: "boots",
    name: "Bottes en écaille de monstre verte renforcées",
    description: "+10 défense, +24 vitesse de déplacement.",
    statBonus: { defense: 10, moveSpeed: 24 },
    stackable: false,
    price: 550,
  },
  reinforcedBatWingsBoots: {
    id: "reinforcedBatWingsBoots",
    category: "equipment",
    slot: "boots",
    name: "Bottes en ailes de chauve-souris renforcées",
    description: "+11 défense, +26 vitesse de déplacement.",
    statBonus: { defense: 11, moveSpeed: 26 },
    stackable: false,
    price: 650,
  },
  reinforcedDragonScaleBoots: {
    id: "reinforcedDragonScaleBoots",
    category: "equipment",
    slot: "boots",
    name: "Bottes en écaille de dragon renforcées",
    description: "+12 défense, +28 vitesse de déplacement.",
    statBonus: { defense: 12, moveSpeed: 28 },
    stackable: false,
    price: 750,
  },
  reinforcedGhostEctoplasmBoots: {
    id: "reinforcedGhostEctoplasmBoots",
    category: "equipment",
    slot: "boots",
    name: "Bottes en ectoplasme de fantôme renforcées",
    description: "+13 défense, +30 vitesse de déplacement.",
    statBonus: { defense: 13, moveSpeed: 30 },
    stackable: false,
    price: 850,
  },
  reinforcedMonsterCoreBoots: {
    id: "reinforcedMonsterCoreBoots",
    category: "equipment",
    slot: "boots",
    name: "Bottes en noyau de monstre renforcées",
    description: "+14 défense, +32 vitesse de déplacement.",
    statBonus: { defense: 14, moveSpeed: 32 },
    stackable: false,
    price: 950,
  },

  // ligne 1 - metal classic
  armorBelt: {
    id: "armorBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture d'armure",
    description: "+1 défense, +2 endurance max, +0.1 régén. endurance/s.",
    statBonus: { defense: 1, stamina: 2, staminaRegen: 0.1 },
    stackable: false,
    price: 5,
  },
  copperArmorBelt: {
    id: "copperArmorBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture d'armure en copper",
    description: "+2 défense, +4 endurance max, +0.2 régén. endurance/s.",
    statBonus: { defense: 2, stamina: 4, staminaRegen: 0.2 },
    stackable: false,
    price: 10,
  },
  ironArmorBelt: {
    id: "ironArmorBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture d'armure en iron",
    description: "+3 défense, +6 endurance max, +0.3 régén. endurance/s.",
    statBonus: { defense: 3, stamina: 6, staminaRegen: 0.3 },
    stackable: false,
    price: 15,
  },
  steelArmorBelt: {
    id: "steelArmorBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture d'armure en steel",
    description: "+4 défense, +8 endurance max, +0.4 régén. endurance/s.",
    statBonus: { defense: 4, stamina: 8, staminaRegen: 0.4 },
    stackable: false,
    price: 20,
  },
  silverArmorBelt: {
    id: "silverArmorBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture d'armure en silver",
    description: "+5 défense, +10 endurance max, +0.5 régén. endurance/s.",
    statBonus: { defense: 5, stamina: 10, staminaRegen: 0.5 },
    stackable: false,
    price: 25,
  },
  goldArmorBelt: {
    id: "goldArmorBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture d'armure en gold",
    description: "+6 défense, +12 endurance max, +0.6 régén. endurance/s.",
    statBonus: { defense: 6, stamina: 12, staminaRegen: 0.6 },
    stackable: false,
    price: 30,
  },
  platiniumArmorBelt: {
    id: "platiniumArmorBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture d'armure en platinium",
    description: "+7 défense, +14 endurance max, +0.7 régén. endurance/s.",
    statBonus: { defense: 7, stamina: 14, staminaRegen: 0.7 },
    stackable: false,
    price: 35,
  },
  cobaltArmorBelt: {
    id: "cobaltArmorBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture d'armure en cobalt",
    description: "+8 défense, +16 endurance max, +0.8 régén. endurance/s.",
    statBonus: { defense: 8, stamina: 16, staminaRegen: 0.8 },
    stackable: false,
    price: 40,
  },
  adamantineArmorBelt: {
    id: "adamantineArmorBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture d'armure en adamantine",
    description: "+9 défense, +18 endurance max, +0.9 régén. endurance/s.",
    statBonus: { defense: 9, stamina: 18, staminaRegen: 0.9 },
    stackable: false,
    price: 45,
  },
  crimsonArmorBelt: {
    id: "crimsonArmorBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture d'armure en crimson",
    description: "+10 défense, +20 endurance max, +1.0 régén. endurance/s.",
    statBonus: { defense: 10, stamina: 20, staminaRegen: 1.0 },
    stackable: false,
    price: 50,
  },
  angelicArmorBelt: {
    id: "angelicArmorBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture d'armure en angelic",
    description: "+11 défense, +22 endurance max, +1.1 régén. endurance/s.",
    statBonus: { defense: 11, stamina: 22, staminaRegen: 1.1 },
    stackable: false,
    price: 55,
  },
  fatefulArmorBelt: {
    id: "fatefulArmorBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture d'armure en fateful",
    description: "+12 défense, +24 endurance max, +1.2 régén. endurance/s.",
    statBonus: { defense: 12, stamina: 24, staminaRegen: 1.2 },
    stackable: false,
    price: 60,
  },
  novaArmorBelt: {
    id: "novaArmorBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture d'armure en nova",
    description: "+13 défense, +26 endurance max, +1.3 régén. endurance/s.",
    statBonus: { defense: 13, stamina: 26, staminaRegen: 1.3 },
    stackable: false,
    price: 65,
  },

  // ligne 1 - reinforced (metal)
  reinforcedArmorBelt: {
    id: "reinforcedArmorBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture d'armure renforcée",
    description: "+3 défense, +6 endurance max, +0.3 régén. endurance/s.",
    statBonus: { defense: 3, stamina: 6, staminaRegen: 0.3 },
    stackable: false,
    price: 8,
  },
  reinforcedCopperArmorBelt: {
    id: "reinforcedCopperArmorBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture d'armure en copper renforcée",
    description: "+4 défense, +8 endurance max, +0.4 régén. endurance/s.",
    statBonus: { defense: 4, stamina: 8, staminaRegen: 0.4 },
    stackable: false,
    price: 16,
  },
  reinforcedIronArmorBelt: {
    id: "reinforcedIronArmorBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture d'armure en iron renforcée",
    description: "+5 défense, +10 endurance max, +0.5 régén. endurance/s.",
    statBonus: { defense: 5, stamina: 10, staminaRegen: 0.5 },
    stackable: false,
    price: 24,
  },
  reinforcedSteelArmorBelt: {
    id: "reinforcedSteelArmorBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture d'armure en steel renforcée",
    description: "+6 défense, +12 endurance max, +0.6 régén. endurance/s.",
    statBonus: { defense: 6, stamina: 12, staminaRegen: 0.6 },
    stackable: false,
    price: 32,
  },
  reinforcedSilverArmorBelt: {
    id: "reinforcedSilverArmorBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture d'armure en silver renforcée",
    description: "+7 défense, +14 endurance max, +0.7 régén. endurance/s.",
    statBonus: { defense: 7, stamina: 14, staminaRegen: 0.7 },
    stackable: false,
    price: 40,
  },
  reinforcedGoldArmorBelt: {
    id: "reinforcedGoldArmorBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture d'armure en gold renforcée",
    description: "+8 défense, +16 endurance max, +0.8 régén. endurance/s.",
    statBonus: { defense: 8, stamina: 16, staminaRegen: 0.8 },
    stackable: false,
    price: 48,
  },
  reinforcedPlatiniumArmorBelt: {
    id: "reinforcedPlatiniumArmorBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture d'armure en platinium renforcée",
    description: "+9 défense, +18 endurance max, +0.9 régén. endurance/s.",
    statBonus: { defense: 9, stamina: 18, staminaRegen: 0.9 },
    stackable: false,
    price: 56,
  },
  reinforcedCobaltArmorBelt: {
    id: "reinforcedCobaltArmorBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture d'armure en cobalt renforcée",
    description: "+10 défense, +20 endurance max, +1.0 régén. endurance/s.",
    statBonus: { defense: 10, stamina: 20, staminaRegen: 1.0 },
    stackable: false,
    price: 64,
  },
  reinforcedAdamantineArmorBelt: {
    id: "reinforcedAdamantineArmorBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture d'armure en adamantine renforcée",
    description: "+11 défense, +22 endurance max, +1.1 régén. endurance/s.",
    statBonus: { defense: 11, stamina: 22, staminaRegen: 1.1 },
    stackable: false,
    price: 72,
  },
  reinforcedCrimsonArmorBelt: {
    id: "reinforcedCrimsonArmorBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture d'armure en crimson renforcée",
    description: "+12 défense, +24 endurance max, +1.2 régén. endurance/s.",
    statBonus: { defense: 12, stamina: 24, staminaRegen: 1.2 },
    stackable: false,
    price: 80,
  },
  reinforcedAngelicArmorBelt: {
    id: "reinforcedAngelicArmorBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture d'armure en angelic renforcée",
    description: "+13 défense, +26 endurance max, +1.3 régén. endurance/s.",
    statBonus: { defense: 13, stamina: 26, staminaRegen: 1.3 },
    stackable: false,
    price: 88,
  },
  reinforcedFatefulArmorBelt: {
    id: "reinforcedFatefulArmorBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture d'armure en fateful renforcée",
    description: "+14 défense, +28 endurance max, +1.4 régén. endurance/s.",
    statBonus: { defense: 14, stamina: 28, staminaRegen: 1.4 },
    stackable: false,
    price: 96,
  },
  reinforcedNovaArmorBelt: {
    id: "reinforcedNovaArmorBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture d'armure en nova renforcée",
    description: "+15 défense, +30 endurance max, +1.5 régén. endurance/s.",
    statBonus: { defense: 15, stamina: 30, staminaRegen: 1.5 },
    stackable: false,
    price: 104,
  },

  // ligne 2 - normal (textile/cuir)
  belt: {
    id: "belt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture",
    description: "+1 défense, +2 endurance max, +0.1 régén. endurance/s.",
    statBonus: { defense: 1, stamina: 2, staminaRegen: 0.1 },
    stackable: false,
    price: 5,
  },
  slimeBlobBelt: {
    id: "slimeBlobBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture en slimeBlob",
    description: "+2 défense, +4 endurance max, +0.2 régén. endurance/s.",
    statBonus: { defense: 2, stamina: 4, staminaRegen: 0.2 },
    stackable: false,
    price: 10,
  },
  bearPeltBelt: {
    id: "bearPeltBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture en bearPelt",
    description: "+3 défense, +6 endurance max, +0.3 régén. endurance/s.",
    statBonus: { defense: 3, stamina: 6, staminaRegen: 0.3 },
    stackable: false,
    price: 15,
  },
  spiderLegBelt: {
    id: "spiderLegBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture en spiderLeg",
    description: "+4 défense, +8 endurance max, +0.4 régén. endurance/s.",
    statBonus: { defense: 4, stamina: 8, staminaRegen: 0.4 },
    stackable: false,
    price: 20,
  },
  greyMonsterScaleBelt: {
    id: "greyMonsterScaleBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture en greyMonsterScale",
    description: "+5 défense, +10 endurance max, +0.5 régén. endurance/s.",
    statBonus: { defense: 5, stamina: 10, staminaRegen: 0.5 },
    stackable: false,
    price: 25,
  },
  crabClawBelt: {
    id: "crabClawBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture en crabClaw",
    description: "+6 défense, +12 endurance max, +0.6 régén. endurance/s.",
    statBonus: { defense: 6, stamina: 12, staminaRegen: 0.6 },
    stackable: false,
    price: 30,
  },
  blackBearPeltBelt: {
    id: "blackBearPeltBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture en blackBearPelt",
    description: "+7 défense, +14 endurance max, +0.7 régén. endurance/s.",
    statBonus: { defense: 7, stamina: 14, staminaRegen: 0.7 },
    stackable: false,
    price: 35,
  },
  turtleShellBelt: {
    id: "turtleShellBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture en turtleShell",
    description: "+8 défense, +16 endurance max, +0.8 régén. endurance/s.",
    statBonus: { defense: 8, stamina: 16, staminaRegen: 0.8 },
    stackable: false,
    price: 40,
  },
  greenMonsterScaleBelt: {
    id: "greenMonsterScaleBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture en greenMonsterScale",
    description: "+9 défense, +18 endurance max, +0.9 régén. endurance/s.",
    statBonus: { defense: 9, stamina: 18, staminaRegen: 0.9 },
    stackable: false,
    price: 45,
  },
  batWingsBelt: {
    id: "batWingsBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture en batWings",
    description: "+10 défense, +20 endurance max, +1.0 régén. endurance/s.",
    statBonus: { defense: 10, stamina: 20, staminaRegen: 1.0 },
    stackable: false,
    price: 50,
  },
  dragonScaleBelt: {
    id: "dragonScaleBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture en dragonScale",
    description: "+11 défense, +22 endurance max, +1.1 régén. endurance/s.",
    statBonus: { defense: 11, stamina: 22, staminaRegen: 1.1 },
    stackable: false,
    price: 55,
  },
  ghostEctoplasmBelt: {
    id: "ghostEctoplasmBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture en ghostEctoplasm",
    description: "+12 défense, +24 endurance max, +1.2 régén. endurance/s.",
    statBonus: { defense: 12, stamina: 24, staminaRegen: 1.2 },
    stackable: false,
    price: 60,
  },
  monsterCoreBelt: {
    id: "monsterCoreBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture en monsterCore",
    description: "+13 défense, +26 endurance max, +1.3 régén. endurance/s.",
    statBonus: { defense: 13, stamina: 26, staminaRegen: 1.3 },
    stackable: false,
    price: 65,
  },

  // ligne 3 - reinforced (textile/cuir)
  reinforcedBelt: {
    id: "reinforcedBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture renforcée",
    description: "+3 défense, +6 endurance max, +0.3 régén. endurance/s.",
    statBonus: { defense: 3, stamina: 6, staminaRegen: 0.3 },
    stackable: false,
    price: 8,
  },
  reinforcedSlimeBlobBelt: {
    id: "reinforcedSlimeBlobBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture en slimeBlob renforcée",
    description: "+4 défense, +8 endurance max, +0.4 régén. endurance/s.",
    statBonus: { defense: 4, stamina: 8, staminaRegen: 0.4 },
    stackable: false,
    price: 16,
  },
  reinforcedBearPeltBelt: {
    id: "reinforcedBearPeltBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture en bearPelt renforcée",
    description: "+5 défense, +10 endurance max, +0.5 régén. endurance/s.",
    statBonus: { defense: 5, stamina: 10, staminaRegen: 0.5 },
    stackable: false,
    price: 24,
  },
  reinforcedSpiderLegBelt: {
    id: "reinforcedSpiderLegBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture en spiderLeg renforcée",
    description: "+6 défense, +12 endurance max, +0.6 régén. endurance/s.",
    statBonus: { defense: 6, stamina: 12, staminaRegen: 0.6 },
    stackable: false,
    price: 32,
  },
  reinforcedGreyMonsterScaleBelt: {
    id: "reinforcedGreyMonsterScaleBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture en greyMonsterScale renforcée",
    description: "+7 défense, +14 endurance max, +0.7 régén. endurance/s.",
    statBonus: { defense: 7, stamina: 14, staminaRegen: 0.7 },
    stackable: false,
    price: 40,
  },
  reinforcedCrabClawBelt: {
    id: "reinforcedCrabClawBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture en crabClaw renforcée",
    description: "+8 défense, +16 endurance max, +0.8 régén. endurance/s.",
    statBonus: { defense: 8, stamina: 16, staminaRegen: 0.8 },
    stackable: false,
    price: 48,
  },
  reinforcedBlackBearPeltBelt: {
    id: "reinforcedBlackBearPeltBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture en blackBearPelt renforcée",
    description: "+9 défense, +18 endurance max, +0.9 régén. endurance/s.",
    statBonus: { defense: 9, stamina: 18, staminaRegen: 0.9 },
    stackable: false,
    price: 56,
  },
  reinforcedTurtleShellBelt: {
    id: "reinforcedTurtleShellBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture en turtleShell renforcée",
    description: "+10 défense, +20 endurance max, +1.0 régén. endurance/s.",
    statBonus: { defense: 10, stamina: 20, staminaRegen: 1.0 },
    stackable: false,
    price: 64,
  },
  reinforcedGreenMonsterScaleBelt: {
    id: "reinforcedGreenMonsterScaleBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture en greenMonsterScale renforcée",
    description: "+11 défense, +22 endurance max, +1.1 régén. endurance/s.",
    statBonus: { defense: 11, stamina: 22, staminaRegen: 1.1 },
    stackable: false,
    price: 72,
  },
  reinforcedBatWingsBelt: {
    id: "reinforcedBatWingsBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture en batWings renforcée",
    description: "+12 défense, +24 endurance max, +1.2 régén. endurance/s.",
    statBonus: { defense: 12, stamina: 24, staminaRegen: 1.2 },
    stackable: false,
    price: 80,
  },
  reinforcedDragonScaleBelt: {
    id: "reinforcedDragonScaleBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture en dragonScale renforcée",
    description: "+13 défense, +26 endurance max, +1.3 régén. endurance/s.",
    statBonus: { defense: 13, stamina: 26, staminaRegen: 1.3 },
    stackable: false,
    price: 88,
  },
  reinforcedGhostEctoplasmBelt: {
    id: "reinforcedGhostEctoplasmBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture en ghostEctoplasm renforcée",
    description: "+14 défense, +28 endurance max, +1.4 régén. endurance/s.",
    statBonus: { defense: 14, stamina: 28, staminaRegen: 1.4 },
    stackable: false,
    price: 96,
  },
  reinforcedMonsterCoreBelt: {
    id: "reinforcedMonsterCoreBelt",
    category: "equipment",
    slot: "belt",
    name: "Ceinture en monsterCore renforcée",
    description: "+15 défense, +30 endurance max, +1.5 régén. endurance/s.",
    statBonus: { defense: 15, stamina: 30, staminaRegen: 1.5 },
    stackable: false,
    price: 104,
  },

  // ===== Chapeau de mage - évolution classique (matériaux de monstres) - pas d'unlockLevel (craftable uniquement) =====
  mageHat: {
    id: "mageHat",
    category: "equipment",
    slot: "helmet",
    name: "Chapeau de mage de base",
    description: "+1 défense. Équipement d'entraînement de départ.",
    statBonus: { defense: 1 },
    archetypes: ["mage"],
    stackable: false,
    price: 2,
  },
  slimeBlobMageHat: {
    id: "slimeBlobMageHat",
    category: "equipment",
    slot: "helmet",
    name: "Chapeau de mage en bave de slime",
    description: "+1 défense.",
    statBonus: { defense: 1 },
    archetypes: ["mage"],
    stackable: false,
    price: 30,
  },
  bearPeltMageHat: {
    id: "bearPeltMageHat",
    category: "equipment",
    slot: "helmet",
    name: "Chapeau de mage en peau d'ours",
    description: "+2 défense.",
    statBonus: { defense: 2 },
    archetypes: ["mage"],
    stackable: false,
    price: 60,
  },
  spiderLegMageHat: {
    id: "spiderLegMageHat",
    category: "equipment",
    slot: "helmet",
    name: "Chapeau de mage en patte d'araignée",
    description: "+3 défense.",
    statBonus: { defense: 3 },
    archetypes: ["mage"],
    stackable: false,
    price: 100,
  },
  greyMonsterScaleMageHat: {
    id: "greyMonsterScaleMageHat",
    category: "equipment",
    slot: "helmet",
    name: "Chapeau de mage en écaille de monstre grise",
    description: "+4 défense.",
    statBonus: { defense: 4 },
    archetypes: ["mage"],
    stackable: false,
    price: 150,
  },
  crabClawMageHat: {
    id: "crabClawMageHat",
    category: "equipment",
    slot: "helmet",
    name: "Chapeau de mage en pince de crabe",
    description: "+5 défense.",
    statBonus: { defense: 5 },
    archetypes: ["mage"],
    stackable: false,
    price: 200,
  },
  blackBearPeltMageHat: {
    id: "blackBearPeltMageHat",
    category: "equipment",
    slot: "helmet",
    name: "Chapeau de mage en peau d'ours noir",
    description: "+6 défense.",
    statBonus: { defense: 6 },
    archetypes: ["mage"],
    stackable: false,
    price: 300,
  },
  turtleShellMageHat: {
    id: "turtleShellMageHat",
    category: "equipment",
    slot: "helmet",
    name: "Chapeau de mage en carapace de tortue",
    description: "+7 défense.",
    statBonus: { defense: 7 },
    archetypes: ["mage"],
    stackable: false,
    price: 400,
  },
  greenMonsterScaleMageHat: {
    id: "greenMonsterScaleMageHat",
    category: "equipment",
    slot: "helmet",
    name: "Chapeau de mage en écaille de monstre verte",
    description: "+8 défense.",
    statBonus: { defense: 8 },
    archetypes: ["mage"],
    stackable: false,
    price: 500,
  },
  batWingsMageHat: {
    id: "batWingsMageHat",
    category: "equipment",
    slot: "helmet",
    name: "Chapeau de mage en ailes de chauve-souris",
    description: "+9 défense.",
    statBonus: { defense: 9 },
    archetypes: ["mage"],
    stackable: false,
    price: 600,
  },
  dragonScaleMageHat: {
    id: "dragonScaleMageHat",
    category: "equipment",
    slot: "helmet",
    name: "Chapeau de mage en écaille de dragon",
    description: "+10 défense.",
    statBonus: { defense: 10 },
    archetypes: ["mage"],
    stackable: false,
    price: 700,
  },
  ghostEctoplasmMageHat: {
    id: "ghostEctoplasmMageHat",
    category: "equipment",
    slot: "helmet",
    name: "Chapeau de mage en ectoplasme de fantôme",
    description: "+11 défense.",
    statBonus: { defense: 11 },
    archetypes: ["mage"],
    stackable: false,
    price: 800,
  },
  monsterCoreMageHat: {
    id: "monsterCoreMageHat",
    category: "equipment",
    slot: "helmet",
    name: "Chapeau de mage en noyau de monstre",
    description: "+12 défense.",
    statBonus: { defense: 12 },
    archetypes: ["mage"],
    stackable: false,
    price: 900,
  },

  // ===== Chapeau de mage renforcé(e) - évolution (matériaux de monstres) - pas d'unlockLevel (craftable uniquement) =====
  reinforcedMageHat: {
    id: "reinforcedMageHat",
    category: "equipment",
    slot: "helmet",
    name: "Chapeau de mage renforcé(e) de base",
    description: "+3 défense.",
    statBonus: { defense: 3 },
    archetypes: ["mage"],
    stackable: false,
    price: 50,
  },
  reinforcedSlimeBlobMageHat: {
    id: "reinforcedSlimeBlobMageHat",
    category: "equipment",
    slot: "helmet",
    name: "Chapeau de mage renforcé(e) en bave de slime",
    description: "+3 défense.",
    statBonus: { defense: 3 },
    archetypes: ["mage"],
    stackable: false,
    price: 65,
  },
  reinforcedBearPeltMageHat: {
    id: "reinforcedBearPeltMageHat",
    category: "equipment",
    slot: "helmet",
    name: "Chapeau de mage renforcé(e) en peau d'ours",
    description: "+4 défense.",
    statBonus: { defense: 4 },
    archetypes: ["mage"],
    stackable: false,
    price: 80,
  },
  reinforcedSpiderLegMageHat: {
    id: "reinforcedSpiderLegMageHat",
    category: "equipment",
    slot: "helmet",
    name: "Chapeau de mage renforcé(e) en patte d'araignée",
    description: "+5 défense.",
    statBonus: { defense: 5 },
    archetypes: ["mage"],
    stackable: false,
    price: 120,
  },
  reinforcedGreyMonsterScaleMageHat: {
    id: "reinforcedGreyMonsterScaleMageHat",
    category: "equipment",
    slot: "helmet",
    name: "Chapeau de mage renforcé(e) en écaille de monstre grise",
    description: "+6 défense.",
    statBonus: { defense: 6 },
    archetypes: ["mage"],
    stackable: false,
    price: 180,
  },
  reinforcedCrabClawMageHat: {
    id: "reinforcedCrabClawMageHat",
    category: "equipment",
    slot: "helmet",
    name: "Chapeau de mage renforcé(e) en pince de crabe",
    description: "+7 défense.",
    statBonus: { defense: 7 },
    archetypes: ["mage"],
    stackable: false,
    price: 250,
  },
  reinforcedBlackBearPeltMageHat: {
    id: "reinforcedBlackBearPeltMageHat",
    category: "equipment",
    slot: "helmet",
    name: "Chapeau de mage renforcé(e) en peau d'ours noir",
    description: "+8 défense.",
    statBonus: { defense: 8 },
    archetypes: ["mage"],
    stackable: false,
    price: 350,
  },
  reinforcedTurtleShellMageHat: {
    id: "reinforcedTurtleShellMageHat",
    category: "equipment",
    slot: "helmet",
    name: "Chapeau de mage renforcé(e) en carapace de tortue",
    description: "+9 défense.",
    statBonus: { defense: 9 },
    archetypes: ["mage"],
    stackable: false,
    price: 450,
  },
  reinforcedGreenMonsterScaleMageHat: {
    id: "reinforcedGreenMonsterScaleMageHat",
    category: "equipment",
    slot: "helmet",
    name: "Chapeau de mage renforcé(e) en écaille de monstre verte",
    description: "+10 défense.",
    statBonus: { defense: 10 },
    archetypes: ["mage"],
    stackable: false,
    price: 550,
  },
  reinforcedBatWingsMageHat: {
    id: "reinforcedBatWingsMageHat",
    category: "equipment",
    slot: "helmet",
    name: "Chapeau de mage renforcé(e) en ailes de chauve-souris",
    description: "+11 défense.",
    statBonus: { defense: 11 },
    archetypes: ["mage"],
    stackable: false,
    price: 650,
  },
  reinforcedDragonScaleMageHat: {
    id: "reinforcedDragonScaleMageHat",
    category: "equipment",
    slot: "helmet",
    name: "Chapeau de mage renforcé(e) en écaille de dragon",
    description: "+12 défense.",
    statBonus: { defense: 12 },
    archetypes: ["mage"],
    stackable: false,
    price: 750,
  },
  reinforcedGhostEctoplasmMageHat: {
    id: "reinforcedGhostEctoplasmMageHat",
    category: "equipment",
    slot: "helmet",
    name: "Chapeau de mage renforcé(e) en ectoplasme de fantôme",
    description: "+13 défense.",
    statBonus: { defense: 13 },
    archetypes: ["mage"],
    stackable: false,
    price: 850,
  },
  reinforcedMonsterCoreMageHat: {
    id: "reinforcedMonsterCoreMageHat",
    category: "equipment",
    slot: "helmet",
    name: "Chapeau de mage renforcé(e) en noyau de monstre",
    description: "+14 défense.",
    statBonus: { defense: 14 },
    archetypes: ["mage"],
    stackable: false,
    price: 950,
  },

  // ===== Casque - évolution classique (matériaux de monstres) - pas d'unlockLevel (craftable uniquement) =====
  leatherHelmet: {
    id: "leatherHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque de base",
    description: "+1 défense. Équipement d'entraînement de départ.",
    statBonus: { defense: 1 },
    stackable: false,
    price: 2,
  },
  slimeBlobLeatherHelmet: {
    id: "slimeBlobLeatherHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque en bave de slime",
    description: "+1 défense.",
    statBonus: { defense: 1 },
    stackable: false,
    price: 30,
  },
  bearPeltLeatherHelmet: {
    id: "bearPeltLeatherHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque en peau d'ours",
    description: "+2 défense.",
    statBonus: { defense: 2 },
    stackable: false,
    price: 60,
  },
  spiderLegLeatherHelmet: {
    id: "spiderLegLeatherHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque en patte d'araignée",
    description: "+3 défense.",
    statBonus: { defense: 3 },
    stackable: false,
    price: 100,
  },
  greyMonsterScaleLeatherHelmet: {
    id: "greyMonsterScaleLeatherHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque en écaille de monstre grise",
    description: "+4 défense.",
    statBonus: { defense: 4 },
    stackable: false,
    price: 150,
  },
  crabClawLeatherHelmet: {
    id: "crabClawLeatherHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque en pince de crabe",
    description: "+5 défense.",
    statBonus: { defense: 5 },
    stackable: false,
    price: 200,
  },
  blackBearPeltLeatherHelmet: {
    id: "blackBearPeltLeatherHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque en peau d'ours noir",
    description: "+6 défense.",
    statBonus: { defense: 6 },
    stackable: false,
    price: 300,
  },
  turtleShellLeatherHelmet: {
    id: "turtleShellLeatherHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque en carapace de tortue",
    description: "+7 défense.",
    statBonus: { defense: 7 },
    stackable: false,
    price: 400,
  },
  greenMonsterScaleLeatherHelmet: {
    id: "greenMonsterScaleLeatherHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque en écaille de monstre verte",
    description: "+8 défense.",
    statBonus: { defense: 8 },
    stackable: false,
    price: 500,
  },
  batWingsLeatherHelmet: {
    id: "batWingsLeatherHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque en ailes de chauve-souris",
    description: "+9 défense.",
    statBonus: { defense: 9 },
    stackable: false,
    price: 600,
  },
  dragonScaleLeatherHelmet: {
    id: "dragonScaleLeatherHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque en écaille de dragon",
    description: "+10 défense.",
    statBonus: { defense: 10 },
    stackable: false,
    price: 700,
  },
  ghostEctoplasmLeatherHelmet: {
    id: "ghostEctoplasmLeatherHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque en ectoplasme de fantôme",
    description: "+11 défense.",
    statBonus: { defense: 11 },
    stackable: false,
    price: 800,
  },
  monsterCoreLeatherHelmet: {
    id: "monsterCoreLeatherHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque en noyau de monstre",
    description: "+12 défense.",
    statBonus: { defense: 12 },
    stackable: false,
    price: 900,
  },

  // ===== Casque renforcé(e) - évolution (matériaux de monstres) - pas d'unlockLevel (craftable uniquement) =====
  reinforcedLeatherHelmet: {
    id: "reinforcedLeatherHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque renforcé(e) de base",
    description: "+3 défense.",
    statBonus: { defense: 3 },
    stackable: false,
    price: 50,
  },
  reinforcedSlimeBlobLeatherHelmet: {
    id: "reinforcedSlimeBlobLeatherHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque renforcé(e) en bave de slime",
    description: "+3 défense.",
    statBonus: { defense: 3 },
    stackable: false,
    price: 65,
  },
  reinforcedBearPeltLeatherHelmet: {
    id: "reinforcedBearPeltLeatherHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque renforcé(e) en peau d'ours",
    description: "+4 défense.",
    statBonus: { defense: 4 },
    stackable: false,
    price: 80,
  },
  reinforcedSpiderLegLeatherHelmet: {
    id: "reinforcedSpiderLegLeatherHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque renforcé(e) en patte d'araignée",
    description: "+5 défense.",
    statBonus: { defense: 5 },
    stackable: false,
    price: 120,
  },
  reinforcedGreyMonsterScaleLeatherHelmet: {
    id: "reinforcedGreyMonsterScaleLeatherHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque renforcé(e) en écaille de monstre grise",
    description: "+6 défense.",
    statBonus: { defense: 6 },
    stackable: false,
    price: 180,
  },
  reinforcedCrabClawLeatherHelmet: {
    id: "reinforcedCrabClawLeatherHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque renforcé(e) en pince de crabe",
    description: "+7 défense.",
    statBonus: { defense: 7 },
    stackable: false,
    price: 250,
  },
  reinforcedBlackBearPeltLeatherHelmet: {
    id: "reinforcedBlackBearPeltLeatherHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque renforcé(e) en peau d'ours noir",
    description: "+8 défense.",
    statBonus: { defense: 8 },
    stackable: false,
    price: 350,
  },
  reinforcedTurtleShellLeatherHelmet: {
    id: "reinforcedTurtleShellLeatherHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque renforcé(e) en carapace de tortue",
    description: "+9 défense.",
    statBonus: { defense: 9 },
    stackable: false,
    price: 450,
  },
  reinforcedGreenMonsterScaleLeatherHelmet: {
    id: "reinforcedGreenMonsterScaleLeatherHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque renforcé(e) en écaille de monstre verte",
    description: "+10 défense.",
    statBonus: { defense: 10 },
    stackable: false,
    price: 550,
  },
  reinforcedBatWingsLeatherHelmet: {
    id: "reinforcedBatWingsLeatherHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque renforcé(e) en ailes de chauve-souris",
    description: "+11 défense.",
    statBonus: { defense: 11 },
    stackable: false,
    price: 650,
  },
  reinforcedDragonScaleLeatherHelmet: {
    id: "reinforcedDragonScaleLeatherHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque renforcé(e) en écaille de dragon",
    description: "+12 défense.",
    statBonus: { defense: 12 },
    stackable: false,
    price: 750,
  },
  reinforcedGhostEctoplasmLeatherHelmet: {
    id: "reinforcedGhostEctoplasmLeatherHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque renforcé(e) en ectoplasme de fantôme",
    description: "+13 défense.",
    statBonus: { defense: 13 },
    stackable: false,
    price: 850,
  },
  reinforcedMonsterCoreLeatherHelmet: {
    id: "reinforcedMonsterCoreLeatherHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque renforcé(e) en noyau de monstre",
    description: "+14 défense.",
    statBonus: { defense: 14 },
    stackable: false,
    price: 950,
  },

  // ===== Pantalon - ligne 1 : métal, évolution classique - pas d'unlockLevel (craftable uniquement) =====
  armorPants: {
    id: "armorPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en armure",
    description: "+1 défense. Équipement d'entraînement de départ.",
    statBonus: { defense: 1 },
    stackable: false,
    price: 2,
  },
  copperArmorPants: {
    id: "copperArmorPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en cuivre",
    description: "+1 défense.",
    statBonus: { defense: 1 },
    stackable: false,
    price: 30,
  },
  ironArmorPants: {
    id: "ironArmorPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en fer",
    description: "+2 défense.",
    statBonus: { defense: 2 },
    stackable: false,
    price: 60,
  },
  steelArmorPants: {
    id: "steelArmorPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en acier",
    description: "+3 défense.",
    statBonus: { defense: 3 },
    stackable: false,
    price: 100,
  },
  silverArmorPants: {
    id: "silverArmorPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en argent",
    description: "+4 défense.",
    statBonus: { defense: 4 },
    stackable: false,
    price: 150,
  },
  goldArmorPants: {
    id: "goldArmorPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en or",
    description: "+5 défense.",
    statBonus: { defense: 5 },
    stackable: false,
    price: 200,
  },
  platiniumArmorPants: {
    id: "platiniumArmorPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en platine",
    description: "+6 défense.",
    statBonus: { defense: 6 },
    stackable: false,
    price: 300,
  },
  cobaltArmorPants: {
    id: "cobaltArmorPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en cobalt",
    description: "+7 défense.",
    statBonus: { defense: 7 },
    stackable: false,
    price: 400,
  },
  adamantineArmorPants: {
    id: "adamantineArmorPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en adamantine",
    description: "+8 défense.",
    statBonus: { defense: 8 },
    stackable: false,
    price: 500,
  },
  crimsonArmorPants: {
    id: "crimsonArmorPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en cramoisi",
    description: "+9 défense.",
    statBonus: { defense: 9 },
    stackable: false,
    price: 600,
  },
  angelicArmorPants: {
    id: "angelicArmorPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en angélique",
    description: "+10 défense.",
    statBonus: { defense: 10 },
    stackable: false,
    price: 700,
  },
  fatefulArmorPants: {
    id: "fatefulArmorPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en fatidique",
    description: "+11 défense.",
    statBonus: { defense: 11 },
    stackable: false,
    price: 800,
  },
  novaArmorPants: {
    id: "novaArmorPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en nova",
    description: "+12 défense.",
    statBonus: { defense: 12 },
    stackable: false,
    price: 900,
  },

  // ===== Pantalon - ligne 1 : métal, renforcé =====
  reinforcedArmorPants: {
    id: "reinforcedArmorPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon renforcé",
    description: "+3 défense.",
    statBonus: { defense: 3 },
    stackable: false,
    price: 50,
  },
  reinforcedCopperArmorPants: {
    id: "reinforcedCopperArmorPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en cuivre renforcé",
    description: "+3 défense.",
    statBonus: { defense: 3 },
    stackable: false,
    price: 65,
  },
  reinforcedIronArmorPants: {
    id: "reinforcedIronArmorPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en fer renforcé",
    description: "+4 défense.",
    statBonus: { defense: 4 },
    stackable: false,
    price: 80,
  },
  reinforcedSteelArmorPants: {
    id: "reinforcedSteelArmorPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en acier renforcé",
    description: "+5 défense.",
    statBonus: { defense: 5 },
    stackable: false,
    price: 120,
  },
  reinforcedSilverArmorPants: {
    id: "reinforcedSilverArmorPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en argent renforcé",
    description: "+6 défense.",
    statBonus: { defense: 6 },
    stackable: false,
    price: 180,
  },
  reinforcedGoldArmorPants: {
    id: "reinforcedGoldArmorPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en or renforcé",
    description: "+7 défense.",
    statBonus: { defense: 7 },
    stackable: false,
    price: 250,
  },
  reinforcedPlatiniumArmorPants: {
    id: "reinforcedPlatiniumArmorPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en platine renforcé",
    description: "+8 défense.",
    statBonus: { defense: 8 },
    stackable: false,
    price: 350,
  },
  reinforcedCobaltArmorPants: {
    id: "reinforcedCobaltArmorPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en cobalt renforcé",
    description: "+9 défense.",
    statBonus: { defense: 9 },
    stackable: false,
    price: 450,
  },
  reinforcedAdamantineArmorPants: {
    id: "reinforcedAdamantineArmorPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en adamantine renforcé",
    description: "+10 défense.",
    statBonus: { defense: 10 },
    stackable: false,
    price: 550,
  },
  reinforcedCrimsonArmorPants: {
    id: "reinforcedCrimsonArmorPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en cramoisi renforcé",
    description: "+11 défense.",
    statBonus: { defense: 11 },
    stackable: false,
    price: 650,
  },
  reinforcedAngelicArmorPants: {
    id: "reinforcedAngelicArmorPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en angélique renforcé",
    description: "+12 défense.",
    statBonus: { defense: 12 },
    stackable: false,
    price: 750,
  },
  reinforcedFatefulArmorPants: {
    id: "reinforcedFatefulArmorPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en fatidique renforcé",
    description: "+13 défense.",
    statBonus: { defense: 13 },
    stackable: false,
    price: 850,
  },
  reinforcedNovaArmorPants: {
    id: "reinforcedNovaArmorPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en nova renforcé",
    description: "+14 défense.",
    statBonus: { defense: 14 },
    stackable: false,
    price: 950,
  },

  // ===== Pantalon - ligne 2 : textile/cuir, évolution classique (matériaux de monstres) =====
  pants: {
    id: "pants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en cuir",
    description: "+1 défense. Équipement d'entraînement de départ.",
    statBonus: { defense: 1 },
    stackable: false,
    price: 2,
  },
  slimeBlobPants: {
    id: "slimeBlobPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en bave de slime",
    description: "+1 défense.",
    statBonus: { defense: 1 },
    stackable: false,
    price: 30,
  },
  bearPeltPants: {
    id: "bearPeltPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en peau d'ours",
    description: "+2 défense.",
    statBonus: { defense: 2 },
    stackable: false,
    price: 60,
  },
  spiderLegPants: {
    id: "spiderLegPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en patte d'araignée",
    description: "+3 défense.",
    statBonus: { defense: 3 },
    stackable: false,
    price: 100,
  },
  greyMonsterScalePants: {
    id: "greyMonsterScalePants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en écaille de monstre grise",
    description: "+4 défense.",
    statBonus: { defense: 4 },
    stackable: false,
    price: 150,
  },
  crabClawPants: {
    id: "crabClawPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en pince de crabe",
    description: "+5 défense.",
    statBonus: { defense: 5 },
    stackable: false,
    price: 200,
  },
  blackBearPeltPants: {
    id: "blackBearPeltPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en peau d'ours noir",
    description: "+6 défense.",
    statBonus: { defense: 6 },
    stackable: false,
    price: 300,
  },
  turtleShellPants: {
    id: "turtleShellPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en carapace de tortue",
    description: "+7 défense.",
    statBonus: { defense: 7 },
    stackable: false,
    price: 400,
  },
  greenMonsterScalePants: {
    id: "greenMonsterScalePants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en écaille de monstre verte",
    description: "+8 défense.",
    statBonus: { defense: 8 },
    stackable: false,
    price: 500,
  },
  batWingsPants: {
    id: "batWingsPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en ailes de chauve-souris",
    description: "+9 défense.",
    statBonus: { defense: 9 },
    stackable: false,
    price: 600,
  },
  dragonScalePants: {
    id: "dragonScalePants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en écaille de dragon",
    description: "+10 défense.",
    statBonus: { defense: 10 },
    stackable: false,
    price: 700,
  },
  ghostEctoplasmPants: {
    id: "ghostEctoplasmPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en ectoplasme de fantôme",
    description: "+11 défense.",
    statBonus: { defense: 11 },
    stackable: false,
    price: 800,
  },
  monsterCorePants: {
    id: "monsterCorePants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en noyau de monstre",
    description: "+12 défense.",
    statBonus: { defense: 12 },
    stackable: false,
    price: 900,
  },

  // ===== Pantalon - ligne 3 : textile/cuir, renforcé =====
  reinforcedPants: {
    id: "reinforcedPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en cuir renforcé",
    description: "+3 défense.",
    statBonus: { defense: 3 },
    stackable: false,
    price: 50,
  },
  reinforcedSlimeBlobPants: {
    id: "reinforcedSlimeBlobPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en bave de slime renforcé",
    description: "+3 défense.",
    statBonus: { defense: 3 },
    stackable: false,
    price: 65,
  },
  reinforcedBearPeltPants: {
    id: "reinforcedBearPeltPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en peau d'ours renforcé",
    description: "+4 défense.",
    statBonus: { defense: 4 },
    stackable: false,
    price: 80,
  },
  reinforcedSpiderLegPants: {
    id: "reinforcedSpiderLegPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en patte d'araignée renforcé",
    description: "+5 défense.",
    statBonus: { defense: 5 },
    stackable: false,
    price: 120,
  },
  reinforcedGreyMonsterScalePants: {
    id: "reinforcedGreyMonsterScalePants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en écaille de monstre grise renforcé",
    description: "+6 défense.",
    statBonus: { defense: 6 },
    stackable: false,
    price: 180,
  },
  reinforcedCrabClawPants: {
    id: "reinforcedCrabClawPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en pince de crabe renforcé",
    description: "+7 défense.",
    statBonus: { defense: 7 },
    stackable: false,
    price: 250,
  },
  reinforcedBlackBearPeltPants: {
    id: "reinforcedBlackBearPeltPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en peau d'ours noir renforcé",
    description: "+8 défense.",
    statBonus: { defense: 8 },
    stackable: false,
    price: 350,
  },
  reinforcedTurtleShellPants: {
    id: "reinforcedTurtleShellPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en carapace de tortue renforcé",
    description: "+9 défense.",
    statBonus: { defense: 9 },
    stackable: false,
    price: 450,
  },
  reinforcedGreenMonsterScalePants: {
    id: "reinforcedGreenMonsterScalePants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en écaille de monstre verte renforcé",
    description: "+10 défense.",
    statBonus: { defense: 10 },
    stackable: false,
    price: 550,
  },
  reinforcedBatWingsPants: {
    id: "reinforcedBatWingsPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en ailes de chauve-souris renforcé",
    description: "+11 défense.",
    statBonus: { defense: 11 },
    stackable: false,
    price: 650,
  },
  reinforcedDragonScalePants: {
    id: "reinforcedDragonScalePants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en écaille de dragon renforcé",
    description: "+12 défense.",
    statBonus: { defense: 12 },
    stackable: false,
    price: 750,
  },
  reinforcedGhostEctoplasmPants: {
    id: "reinforcedGhostEctoplasmPants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en ectoplasme de fantôme renforcé",
    description: "+13 défense.",
    statBonus: { defense: 13 },
    stackable: false,
    price: 850,
  },
  reinforcedMonsterCorePants: {
    id: "reinforcedMonsterCorePants",
    category: "equipment",
    slot: "pants",
    name: "Pantalon en noyau de monstre renforcé",
    description: "+14 défense.",
    statBonus: { defense: 14 },
    stackable: false,
    price: 950,
  },
  // ring - hp - métal (13 items)
  hpRing: {
    id: "hpRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de vie",
    description: "+5 PV max.",
    statBonus: { maxHp: 5 },
    stackable: false,
    price: 7,
  },
  copperHpRing: {
    id: "copperHpRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de vie en copper",
    description: "+10 PV max.",
    statBonus: { maxHp: 10 },
    stackable: false,
    price: 14,
  },
  ironHpRing: {
    id: "ironHpRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de vie en iron",
    description: "+15 PV max.",
    statBonus: { maxHp: 15 },
    stackable: false,
    price: 21,
  },
  steelHpRing: {
    id: "steelHpRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de vie en steel",
    description: "+20 PV max.",
    statBonus: { maxHp: 20 },
    stackable: false,
    price: 28,
  },
  silverHpRing: {
    id: "silverHpRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de vie en silver",
    description: "+25 PV max.",
    statBonus: { maxHp: 25 },
    stackable: false,
    price: 35,
  },
  goldHpRing: {
    id: "goldHpRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de vie en gold",
    description: "+30 PV max.",
    statBonus: { maxHp: 30 },
    stackable: false,
    price: 42,
  },
  platiniumHpRing: {
    id: "platiniumHpRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de vie en platinium",
    description: "+35 PV max, +1.4 régén. PV/s.",
    statBonus: { maxHp: 35, hpRegen: 1.4 },
    stackable: false,
    price: 49,
  },
  cobaltHpRing: {
    id: "cobaltHpRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de vie en cobalt",
    description: "+40 PV max, +1.6 régén. PV/s.",
    statBonus: { maxHp: 40, hpRegen: 1.6 },
    stackable: false,
    price: 56,
  },
  adamantineHpRing: {
    id: "adamantineHpRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de vie en adamantine",
    description: "+45 PV max, +1.8 régén. PV/s.",
    statBonus: { maxHp: 45, hpRegen: 1.8 },
    stackable: false,
    price: 63,
  },
  crimsonHpRing: {
    id: "crimsonHpRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de vie en crimson",
    description: "+50 PV max, +2.0 régén. PV/s.",
    statBonus: { maxHp: 50, hpRegen: 2.0 },
    stackable: false,
    price: 70,
  },
  angelicHpRing: {
    id: "angelicHpRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de vie en angelic",
    description: "+55 PV max, +2.2 régén. PV/s.",
    statBonus: { maxHp: 55, hpRegen: 2.2 },
    stackable: false,
    price: 77,
  },
  fatefulHpRing: {
    id: "fatefulHpRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de vie en fateful",
    description: "+60 PV max, +2.4 régén. PV/s.",
    statBonus: { maxHp: 60, hpRegen: 2.4 },
    stackable: false,
    price: 84,
  },
  novaHpRing: {
    id: "novaHpRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de vie en nova",
    description: "+65 PV max, +2.6 régén. PV/s.",
    statBonus: { maxHp: 65, hpRegen: 2.6 },
    stackable: false,
    price: 91,
  },

  // ring - stamina - métal (13 items)
  staminaRing: {
    id: "staminaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau d'endurance",
    description: "+3 endurance max.",
    statBonus: { stamina: 3 },
    stackable: false,
    price: 7,
  },
  copperStaminaRing: {
    id: "copperStaminaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau d'endurance en copper",
    description: "+6 endurance max.",
    statBonus: { stamina: 6 },
    stackable: false,
    price: 14,
  },
  ironStaminaRing: {
    id: "ironStaminaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau d'endurance en iron",
    description: "+9 endurance max.",
    statBonus: { stamina: 9 },
    stackable: false,
    price: 21,
  },
  steelStaminaRing: {
    id: "steelStaminaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau d'endurance en steel",
    description: "+12 endurance max.",
    statBonus: { stamina: 12 },
    stackable: false,
    price: 28,
  },
  silverStaminaRing: {
    id: "silverStaminaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau d'endurance en silver",
    description: "+15 endurance max.",
    statBonus: { stamina: 15 },
    stackable: false,
    price: 35,
  },
  goldStaminaRing: {
    id: "goldStaminaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau d'endurance en gold",
    description: "+18 endurance max.",
    statBonus: { stamina: 18 },
    stackable: false,
    price: 42,
  },
  platiniumStaminaRing: {
    id: "platiniumStaminaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau d'endurance en platinium",
    description: "+21 endurance max, +0.84 régén. endurance/s.",
    statBonus: { stamina: 21, staminaRegen: 0.84 },
    stackable: false,
    price: 49,
  },
  cobaltStaminaRing: {
    id: "cobaltStaminaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau d'endurance en cobalt",
    description: "+24 endurance max, +0.96 régén. endurance/s.",
    statBonus: { stamina: 24, staminaRegen: 0.96 },
    stackable: false,
    price: 56,
  },
  adamantineStaminaRing: {
    id: "adamantineStaminaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau d'endurance en adamantine",
    description: "+27 endurance max, +1.08 régén. endurance/s.",
    statBonus: { stamina: 27, staminaRegen: 1.08 },
    stackable: false,
    price: 63,
  },
  crimsonStaminaRing: {
    id: "crimsonStaminaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau d'endurance en crimson",
    description: "+30 endurance max, +1.2 régén. endurance/s.",
    statBonus: { stamina: 30, staminaRegen: 1.2 },
    stackable: false,
    price: 70,
  },
  angelicStaminaRing: {
    id: "angelicStaminaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau d'endurance en angelic",
    description: "+33 endurance max, +1.32 régén. endurance/s.",
    statBonus: { stamina: 33, staminaRegen: 1.32 },
    stackable: false,
    price: 77,
  },
  fatefulStaminaRing: {
    id: "fatefulStaminaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau d'endurance en fateful",
    description: "+36 endurance max, +1.44 régén. endurance/s.",
    statBonus: { stamina: 36, staminaRegen: 1.44 },
    stackable: false,
    price: 84,
  },
  novaStaminaRing: {
    id: "novaStaminaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau d'endurance en nova",
    description: "+39 endurance max, +1.56 régén. endurance/s.",
    statBonus: { stamina: 39, staminaRegen: 1.56 },
    stackable: false,
    price: 91,
  },

  // ring - mana - métal (13 items)
  manaRing: {
    id: "manaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de mana",
    description: "+3 mana max.",
    statBonus: { mana: 3 },
    stackable: false,
    price: 7,
  },
  copperManaRing: {
    id: "copperManaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de mana en copper",
    description: "+6 mana max.",
    statBonus: { mana: 6 },
    stackable: false,
    price: 14,
  },
  ironManaRing: {
    id: "ironManaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de mana en iron",
    description: "+9 mana max.",
    statBonus: { mana: 9 },
    stackable: false,
    price: 21,
  },
  steelManaRing: {
    id: "steelManaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de mana en steel",
    description: "+12 mana max.",
    statBonus: { mana: 12 },
    stackable: false,
    price: 28,
  },
  silverManaRing: {
    id: "silverManaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de mana en silver",
    description: "+15 mana max.",
    statBonus: { mana: 15 },
    stackable: false,
    price: 35,
  },
  goldManaRing: {
    id: "goldManaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de mana en gold",
    description: "+18 mana max.",
    statBonus: { mana: 18 },
    stackable: false,
    price: 42,
  },
  platiniumManaRing: {
    id: "platiniumManaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de mana en platinium",
    description: "+21 mana max, +0.84 régén. mana/s.",
    statBonus: { mana: 21, manaRegen: 0.84 },
    stackable: false,
    price: 49,
  },
  cobaltManaRing: {
    id: "cobaltManaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de mana en cobalt",
    description: "+24 mana max, +0.96 régén. mana/s.",
    statBonus: { mana: 24, manaRegen: 0.96 },
    stackable: false,
    price: 56,
  },
  adamantineManaRing: {
    id: "adamantineManaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de mana en adamantine",
    description: "+27 mana max, +1.08 régén. mana/s.",
    statBonus: { mana: 27, manaRegen: 1.08 },
    stackable: false,
    price: 63,
  },
  crimsonManaRing: {
    id: "crimsonManaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de mana en crimson",
    description: "+30 mana max, +1.2 régén. mana/s.",
    statBonus: { mana: 30, manaRegen: 1.2 },
    stackable: false,
    price: 70,
  },
  angelicManaRing: {
    id: "angelicManaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de mana en angelic",
    description: "+33 mana max, +1.32 régén. mana/s.",
    statBonus: { mana: 33, manaRegen: 1.32 },
    stackable: false,
    price: 77,
  },
  fatefulManaRing: {
    id: "fatefulManaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de mana en fateful",
    description: "+36 mana max, +1.44 régén. mana/s.",
    statBonus: { mana: 36, manaRegen: 1.44 },
    stackable: false,
    price: 84,
  },
  novaManaRing: {
    id: "novaManaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de mana en nova",
    description: "+39 mana max, +1.56 régén. mana/s.",
    statBonus: { mana: 39, manaRegen: 1.56 },
    stackable: false,
    price: 91,
  },

  // ring - hp - bois (12 items)
  oakHpRing: {
    id: "oakHpRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de vie en oak",
    description: "+5 PV max.",
    statBonus: { maxHp: 5 },
    stackable: false,
    price: 7,
  },
  ashHpRing: {
    id: "ashHpRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de vie en ash",
    description: "+10 PV max.",
    statBonus: { maxHp: 10 },
    stackable: false,
    price: 14,
  },
  yewHpRing: {
    id: "yewHpRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de vie en yew",
    description: "+15 PV max.",
    statBonus: { maxHp: 15 },
    stackable: false,
    price: 21,
  },
  ebonyHpRing: {
    id: "ebonyHpRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de vie en ebony",
    description: "+20 PV max.",
    statBonus: { maxHp: 20 },
    stackable: false,
    price: 28,
  },
  petrifiedHpRing: {
    id: "petrifiedHpRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de vie en petrified",
    description: "+25 PV max.",
    statBonus: { maxHp: 25 },
    stackable: false,
    price: 35,
  },
  mistwoodHpRing: {
    id: "mistwoodHpRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de vie en mistwood",
    description: "+30 PV max.",
    statBonus: { maxHp: 30 },
    stackable: false,
    price: 42,
  },
  runewoodHpRing: {
    id: "runewoodHpRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de vie en runewood",
    description: "+35 PV max, +1.4 régén. PV/s.",
    statBonus: { maxHp: 35, hpRegen: 1.4 },
    stackable: false,
    price: 49,
  },
  skywoodHpRing: {
    id: "skywoodHpRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de vie en skywood",
    description: "+40 PV max, +1.6 régén. PV/s.",
    statBonus: { maxHp: 40, hpRegen: 1.6 },
    stackable: false,
    price: 56,
  },
  scarletwoodHpRing: {
    id: "scarletwoodHpRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de vie en scarletwood",
    description: "+45 PV max, +1.8 régén. PV/s.",
    statBonus: { maxHp: 45, hpRegen: 1.8 },
    stackable: false,
    price: 63,
  },
  sacredHpRing: {
    id: "sacredHpRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de vie en sacred",
    description: "+50 PV max, +2.0 régén. PV/s.",
    statBonus: { maxHp: 50, hpRegen: 2.0 },
    stackable: false,
    price: 70,
  },
  eternalHpRing: {
    id: "eternalHpRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de vie en eternal",
    description: "+55 PV max, +2.2 régén. PV/s.",
    statBonus: { maxHp: 55, hpRegen: 2.2 },
    stackable: false,
    price: 77,
  },
  starwoodHpRing: {
    id: "starwoodHpRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de vie en starwood",
    description: "+60 PV max, +2.4 régén. PV/s.",
    statBonus: { maxHp: 60, hpRegen: 2.4 },
    stackable: false,
    price: 84,
  },

  // ring - stamina - bois (12 items)
  oakStaminaRing: {
    id: "oakStaminaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau d'endurance en oak",
    description: "+3 endurance max.",
    statBonus: { stamina: 3 },
    stackable: false,
    price: 7,
  },
  ashStaminaRing: {
    id: "ashStaminaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau d'endurance en ash",
    description: "+6 endurance max.",
    statBonus: { stamina: 6 },
    stackable: false,
    price: 14,
  },
  yewStaminaRing: {
    id: "yewStaminaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau d'endurance en yew",
    description: "+9 endurance max.",
    statBonus: { stamina: 9 },
    stackable: false,
    price: 21,
  },
  ebonyStaminaRing: {
    id: "ebonyStaminaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau d'endurance en ebony",
    description: "+12 endurance max.",
    statBonus: { stamina: 12 },
    stackable: false,
    price: 28,
  },
  petrifiedStaminaRing: {
    id: "petrifiedStaminaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau d'endurance en petrified",
    description: "+15 endurance max.",
    statBonus: { stamina: 15 },
    stackable: false,
    price: 35,
  },
  mistwoodStaminaRing: {
    id: "mistwoodStaminaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau d'endurance en mistwood",
    description: "+18 endurance max.",
    statBonus: { stamina: 18 },
    stackable: false,
    price: 42,
  },
  runewoodStaminaRing: {
    id: "runewoodStaminaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau d'endurance en runewood",
    description: "+21 endurance max, +0.84 régén. endurance/s.",
    statBonus: { stamina: 21, staminaRegen: 0.84 },
    stackable: false,
    price: 49,
  },
  skywoodStaminaRing: {
    id: "skywoodStaminaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau d'endurance en skywood",
    description: "+24 endurance max, +0.96 régén. endurance/s.",
    statBonus: { stamina: 24, staminaRegen: 0.96 },
    stackable: false,
    price: 56,
  },
  scarletwoodStaminaRing: {
    id: "scarletwoodStaminaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau d'endurance en scarletwood",
    description: "+27 endurance max, +1.08 régén. endurance/s.",
    statBonus: { stamina: 27, staminaRegen: 1.08 },
    stackable: false,
    price: 63,
  },
  sacredStaminaRing: {
    id: "sacredStaminaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau d'endurance en sacred",
    description: "+30 endurance max, +1.2 régén. endurance/s.",
    statBonus: { stamina: 30, staminaRegen: 1.2 },
    stackable: false,
    price: 70,
  },
  eternalStaminaRing: {
    id: "eternalStaminaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau d'endurance en eternal",
    description: "+33 endurance max, +1.32 régén. endurance/s.",
    statBonus: { stamina: 33, staminaRegen: 1.32 },
    stackable: false,
    price: 77,
  },
  starwoodStaminaRing: {
    id: "starwoodStaminaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau d'endurance en starwood",
    description: "+36 endurance max, +1.44 régén. endurance/s.",
    statBonus: { stamina: 36, staminaRegen: 1.44 },
    stackable: false,
    price: 84,
  },

  // ring - mana - bois (12 items)
  oakManaRing: {
    id: "oakManaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de mana en oak",
    description: "+3 mana max.",
    statBonus: { mana: 3 },
    stackable: false,
    price: 7,
  },
  ashManaRing: {
    id: "ashManaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de mana en ash",
    description: "+6 mana max.",
    statBonus: { mana: 6 },
    stackable: false,
    price: 14,
  },
  yewManaRing: {
    id: "yewManaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de mana en yew",
    description: "+9 mana max.",
    statBonus: { mana: 9 },
    stackable: false,
    price: 21,
  },
  ebonyManaRing: {
    id: "ebonyManaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de mana en ebony",
    description: "+12 mana max.",
    statBonus: { mana: 12 },
    stackable: false,
    price: 28,
  },
  petrifiedManaRing: {
    id: "petrifiedManaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de mana en petrified",
    description: "+15 mana max.",
    statBonus: { mana: 15 },
    stackable: false,
    price: 35,
  },
  mistwoodManaRing: {
    id: "mistwoodManaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de mana en mistwood",
    description: "+18 mana max.",
    statBonus: { mana: 18 },
    stackable: false,
    price: 42,
  },
  runewoodManaRing: {
    id: "runewoodManaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de mana en runewood",
    description: "+21 mana max, +0.84 régén. mana/s.",
    statBonus: { mana: 21, manaRegen: 0.84 },
    stackable: false,
    price: 49,
  },
  skywoodManaRing: {
    id: "skywoodManaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de mana en skywood",
    description: "+24 mana max, +0.96 régén. mana/s.",
    statBonus: { mana: 24, manaRegen: 0.96 },
    stackable: false,
    price: 56,
  },
  scarletwoodManaRing: {
    id: "scarletwoodManaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de mana en scarletwood",
    description: "+27 mana max, +1.08 régén. mana/s.",
    statBonus: { mana: 27, manaRegen: 1.08 },
    stackable: false,
    price: 63,
  },
  sacredManaRing: {
    id: "sacredManaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de mana en sacred",
    description: "+30 mana max, +1.2 régén. mana/s.",
    statBonus: { mana: 30, manaRegen: 1.2 },
    stackable: false,
    price: 70,
  },
  eternalManaRing: {
    id: "eternalManaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de mana en eternal",
    description: "+33 mana max, +1.32 régén. mana/s.",
    statBonus: { mana: 33, manaRegen: 1.32 },
    stackable: false,
    price: 77,
  },
  starwoodManaRing: {
    id: "starwoodManaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de mana en starwood",
    description: "+36 mana max, +1.44 régén. mana/s.",
    statBonus: { mana: 36, manaRegen: 1.44 },
    stackable: false,
    price: 84,
  },

  // necklace - hp - métal (12 items)
  hpNecklace: {
    id: "hpNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de vie",
    description: "+5 PV max.",
    statBonus: { maxHp: 5 },
    stackable: false,
    price: 7,
  },
  ironHpNecklace: {
    id: "ironHpNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de vie en iron",
    description: "+10 PV max.",
    statBonus: { maxHp: 10 },
    stackable: false,
    price: 14,
  },
  steelHpNecklace: {
    id: "steelHpNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de vie en steel",
    description: "+15 PV max.",
    statBonus: { maxHp: 15 },
    stackable: false,
    price: 21,
  },
  silverHpNecklace: {
    id: "silverHpNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de vie en silver",
    description: "+20 PV max.",
    statBonus: { maxHp: 20 },
    stackable: false,
    price: 28,
  },
  goldHpNecklace: {
    id: "goldHpNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de vie en gold",
    description: "+25 PV max.",
    statBonus: { maxHp: 25 },
    stackable: false,
    price: 35,
  },
  platiniumHpNecklace: {
    id: "platiniumHpNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de vie en platinium",
    description: "+30 PV max.",
    statBonus: { maxHp: 30 },
    stackable: false,
    price: 42,
  },
  cobaltHpNecklace: {
    id: "cobaltHpNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de vie en cobalt",
    description: "+35 PV max, +1.4 régén. PV/s.",
    statBonus: { maxHp: 35, hpRegen: 1.4 },
    stackable: false,
    price: 49,
  },
  adamantineHpNecklace: {
    id: "adamantineHpNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de vie en adamantine",
    description: "+40 PV max, +1.6 régén. PV/s.",
    statBonus: { maxHp: 40, hpRegen: 1.6 },
    stackable: false,
    price: 56,
  },
  crimsonHpNecklace: {
    id: "crimsonHpNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de vie en crimson",
    description: "+45 PV max, +1.8 régén. PV/s.",
    statBonus: { maxHp: 45, hpRegen: 1.8 },
    stackable: false,
    price: 63,
  },
  angelicHpNecklace: {
    id: "angelicHpNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de vie en angelic",
    description: "+50 PV max, +2.0 régén. PV/s.",
    statBonus: { maxHp: 50, hpRegen: 2.0 },
    stackable: false,
    price: 70,
  },
  fatefulHpNecklace: {
    id: "fatefulHpNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de vie en fateful",
    description: "+55 PV max, +2.2 régén. PV/s.",
    statBonus: { maxHp: 55, hpRegen: 2.2 },
    stackable: false,
    price: 77,
  },
  novaHpNecklace: {
    id: "novaHpNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de vie en nova",
    description: "+60 PV max, +2.4 régén. PV/s.",
    statBonus: { maxHp: 60, hpRegen: 2.4 },
    stackable: false,
    price: 84,
  },

  // necklace - stamina - métal (13 items)
  staminaNecklace: {
    id: "staminaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier d'endurance",
    description: "+3 endurance max.",
    statBonus: { stamina: 3 },
    stackable: false,
    price: 7,
  },
  copperStaminaNecklace: {
    id: "copperStaminaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier d'endurance en copper",
    description: "+6 endurance max.",
    statBonus: { stamina: 6 },
    stackable: false,
    price: 14,
  },
  ironStaminaNecklace: {
    id: "ironStaminaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier d'endurance en iron",
    description: "+9 endurance max.",
    statBonus: { stamina: 9 },
    stackable: false,
    price: 21,
  },
  steelStaminaNecklace: {
    id: "steelStaminaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier d'endurance en steel",
    description: "+12 endurance max.",
    statBonus: { stamina: 12 },
    stackable: false,
    price: 28,
  },
  silverStaminaNecklace: {
    id: "silverStaminaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier d'endurance en silver",
    description: "+15 endurance max.",
    statBonus: { stamina: 15 },
    stackable: false,
    price: 35,
  },
  goldStaminaNecklace: {
    id: "goldStaminaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier d'endurance en gold",
    description: "+18 endurance max.",
    statBonus: { stamina: 18 },
    stackable: false,
    price: 42,
  },
  platiniumStaminaNecklace: {
    id: "platiniumStaminaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier d'endurance en platinium",
    description: "+21 endurance max, +0.84 régén. endurance/s.",
    statBonus: { stamina: 21, staminaRegen: 0.84 },
    stackable: false,
    price: 49,
  },
  cobaltStaminaNecklace: {
    id: "cobaltStaminaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier d'endurance en cobalt",
    description: "+24 endurance max, +0.96 régén. endurance/s.",
    statBonus: { stamina: 24, staminaRegen: 0.96 },
    stackable: false,
    price: 56,
  },
  adamantineStaminaNecklace: {
    id: "adamantineStaminaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier d'endurance en adamantine",
    description: "+27 endurance max, +1.08 régén. endurance/s.",
    statBonus: { stamina: 27, staminaRegen: 1.08 },
    stackable: false,
    price: 63,
  },
  crimsonStaminaNecklace: {
    id: "crimsonStaminaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier d'endurance en crimson",
    description: "+30 endurance max, +1.2 régén. endurance/s.",
    statBonus: { stamina: 30, staminaRegen: 1.2 },
    stackable: false,
    price: 70,
  },
  angelicStaminaNecklace: {
    id: "angelicStaminaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier d'endurance en angelic",
    description: "+33 endurance max, +1.32 régén. endurance/s.",
    statBonus: { stamina: 33, staminaRegen: 1.32 },
    stackable: false,
    price: 77,
  },
  fatefulStaminaNecklace: {
    id: "fatefulStaminaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier d'endurance en fateful",
    description: "+36 endurance max, +1.44 régén. endurance/s.",
    statBonus: { stamina: 36, staminaRegen: 1.44 },
    stackable: false,
    price: 84,
  },
  novaStaminaNecklace: {
    id: "novaStaminaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier d'endurance en nova",
    description: "+39 endurance max, +1.56 régén. endurance/s.",
    statBonus: { stamina: 39, staminaRegen: 1.56 },
    stackable: false,
    price: 91,
  },

  // necklace - mana - métal (13 items)
  manaNecklace: {
    id: "manaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de mana",
    description: "+3 mana max.",
    statBonus: { mana: 3 },
    stackable: false,
    price: 7,
  },
  copperManaNecklace: {
    id: "copperManaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de mana en copper",
    description: "+6 mana max.",
    statBonus: { mana: 6 },
    stackable: false,
    price: 14,
  },
  ironManaNecklace: {
    id: "ironManaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de mana en iron",
    description: "+9 mana max.",
    statBonus: { mana: 9 },
    stackable: false,
    price: 21,
  },
  steelManaNecklace: {
    id: "steelManaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de mana en steel",
    description: "+12 mana max.",
    statBonus: { mana: 12 },
    stackable: false,
    price: 28,
  },
  silverManaNecklace: {
    id: "silverManaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de mana en silver",
    description: "+15 mana max.",
    statBonus: { mana: 15 },
    stackable: false,
    price: 35,
  },
  goldManaNecklace: {
    id: "goldManaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de mana en gold",
    description: "+18 mana max.",
    statBonus: { mana: 18 },
    stackable: false,
    price: 42,
  },
  platiniumManaNecklace: {
    id: "platiniumManaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de mana en platinium",
    description: "+21 mana max, +0.84 régén. mana/s.",
    statBonus: { mana: 21, manaRegen: 0.84 },
    stackable: false,
    price: 49,
  },
  cobaltManaNecklace: {
    id: "cobaltManaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de mana en cobalt",
    description: "+24 mana max, +0.96 régén. mana/s.",
    statBonus: { mana: 24, manaRegen: 0.96 },
    stackable: false,
    price: 56,
  },
  adamantineManaNecklace: {
    id: "adamantineManaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de mana en adamantine",
    description: "+27 mana max, +1.08 régén. mana/s.",
    statBonus: { mana: 27, manaRegen: 1.08 },
    stackable: false,
    price: 63,
  },
  crimsonManaNecklace: {
    id: "crimsonManaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de mana en crimson",
    description: "+30 mana max, +1.2 régén. mana/s.",
    statBonus: { mana: 30, manaRegen: 1.2 },
    stackable: false,
    price: 70,
  },
  angelicManaNecklace: {
    id: "angelicManaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de mana en angelic",
    description: "+33 mana max, +1.32 régén. mana/s.",
    statBonus: { mana: 33, manaRegen: 1.32 },
    stackable: false,
    price: 77,
  },
  fatefulManaNecklace: {
    id: "fatefulManaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de mana en fateful",
    description: "+36 mana max, +1.44 régén. mana/s.",
    statBonus: { mana: 36, manaRegen: 1.44 },
    stackable: false,
    price: 84,
  },
  novaManaNecklace: {
    id: "novaManaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de mana en nova",
    description: "+39 mana max, +1.56 régén. mana/s.",
    statBonus: { mana: 39, manaRegen: 1.56 },
    stackable: false,
    price: 91,
  },

  // necklace - hp - matériau (12 items)
  slimeBlobHpNecklace: {
    id: "slimeBlobHpNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de vie en slimeBlob",
    description: "+5 PV max.",
    statBonus: { maxHp: 5 },
    stackable: false,
    price: 7,
  },
  bearPeltHpNecklace: {
    id: "bearPeltHpNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de vie en bearPelt",
    description: "+10 PV max.",
    statBonus: { maxHp: 10 },
    stackable: false,
    price: 14,
  },
  spiderLegHpNecklace: {
    id: "spiderLegHpNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de vie en spiderLeg",
    description: "+15 PV max.",
    statBonus: { maxHp: 15 },
    stackable: false,
    price: 21,
  },
  greyMonsterScaleHpNecklace: {
    id: "greyMonsterScaleHpNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de vie en greyMonsterScale",
    description: "+20 PV max.",
    statBonus: { maxHp: 20 },
    stackable: false,
    price: 28,
  },
  crabClawHpNecklace: {
    id: "crabClawHpNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de vie en crabClaw",
    description: "+25 PV max.",
    statBonus: { maxHp: 25 },
    stackable: false,
    price: 35,
  },
  blackBearPeltHpNecklace: {
    id: "blackBearPeltHpNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de vie en blackBearPelt",
    description: "+30 PV max.",
    statBonus: { maxHp: 30 },
    stackable: false,
    price: 42,
  },
  turtleShellHpNecklace: {
    id: "turtleShellHpNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de vie en turtleShell",
    description: "+35 PV max, +1.4 régén. PV/s.",
    statBonus: { maxHp: 35, hpRegen: 1.4 },
    stackable: false,
    price: 49,
  },
  greenMonsterScaleHpNecklace: {
    id: "greenMonsterScaleHpNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de vie en greenMonsterScale",
    description: "+40 PV max, +1.6 régén. PV/s.",
    statBonus: { maxHp: 40, hpRegen: 1.6 },
    stackable: false,
    price: 56,
  },
  batWingsHpNecklace: {
    id: "batWingsHpNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de vie en batWings",
    description: "+45 PV max, +1.8 régén. PV/s.",
    statBonus: { maxHp: 45, hpRegen: 1.8 },
    stackable: false,
    price: 63,
  },
  dragonScaleHpNecklace: {
    id: "dragonScaleHpNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de vie en dragonScale",
    description: "+50 PV max, +2.0 régén. PV/s.",
    statBonus: { maxHp: 50, hpRegen: 2.0 },
    stackable: false,
    price: 70,
  },
  ghostEctoplasmHpNecklace: {
    id: "ghostEctoplasmHpNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de vie en ghostEctoplasm",
    description: "+55 PV max, +2.2 régén. PV/s.",
    statBonus: { maxHp: 55, hpRegen: 2.2 },
    stackable: false,
    price: 77,
  },
  monsterCoreHpNecklace: {
    id: "monsterCoreHpNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de vie en monsterCore",
    description: "+60 PV max, +2.4 régén. PV/s.",
    statBonus: { maxHp: 60, hpRegen: 2.4 },
    stackable: false,
    price: 84,
  },

  // necklace - stamina - matériau (12 items)
  slimeBlobStaminaNecklace: {
    id: "slimeBlobStaminaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier d'endurance en slimeBlob",
    description: "+3 endurance max.",
    statBonus: { stamina: 3 },
    stackable: false,
    price: 7,
  },
  bearPeltStaminaNecklace: {
    id: "bearPeltStaminaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier d'endurance en bearPelt",
    description: "+6 endurance max.",
    statBonus: { stamina: 6 },
    stackable: false,
    price: 14,
  },
  spiderLegStaminaNecklace: {
    id: "spiderLegStaminaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier d'endurance en spiderLeg",
    description: "+9 endurance max.",
    statBonus: { stamina: 9 },
    stackable: false,
    price: 21,
  },
  greyMonsterScaleStaminaNecklace: {
    id: "greyMonsterScaleStaminaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier d'endurance en greyMonsterScale",
    description: "+12 endurance max.",
    statBonus: { stamina: 12 },
    stackable: false,
    price: 28,
  },
  crabClawStaminaNecklace: {
    id: "crabClawStaminaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier d'endurance en crabClaw",
    description: "+15 endurance max.",
    statBonus: { stamina: 15 },
    stackable: false,
    price: 35,
  },
  blackBearPeltStaminaNecklace: {
    id: "blackBearPeltStaminaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier d'endurance en blackBearPelt",
    description: "+18 endurance max.",
    statBonus: { stamina: 18 },
    stackable: false,
    price: 42,
  },
  turtleShellStaminaNecklace: {
    id: "turtleShellStaminaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier d'endurance en turtleShell",
    description: "+21 endurance max, +0.84 régén. endurance/s.",
    statBonus: { stamina: 21, staminaRegen: 0.84 },
    stackable: false,
    price: 49,
  },
  greenMonsterScaleStaminaNecklace: {
    id: "greenMonsterScaleStaminaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier d'endurance en greenMonsterScale",
    description: "+24 endurance max, +0.96 régén. endurance/s.",
    statBonus: { stamina: 24, staminaRegen: 0.96 },
    stackable: false,
    price: 56,
  },
  batWingsStaminaNecklace: {
    id: "batWingsStaminaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier d'endurance en batWings",
    description: "+27 endurance max, +1.08 régén. endurance/s.",
    statBonus: { stamina: 27, staminaRegen: 1.08 },
    stackable: false,
    price: 63,
  },
  dragonScaleStaminaNecklace: {
    id: "dragonScaleStaminaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier d'endurance en dragonScale",
    description: "+30 endurance max, +1.2 régén. endurance/s.",
    statBonus: { stamina: 30, staminaRegen: 1.2 },
    stackable: false,
    price: 70,
  },
  ghostEctoplasmStaminaNecklace: {
    id: "ghostEctoplasmStaminaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier d'endurance en ghostEctoplasm",
    description: "+33 endurance max, +1.32 régén. endurance/s.",
    statBonus: { stamina: 33, staminaRegen: 1.32 },
    stackable: false,
    price: 77,
  },
  monsterCoreStaminaNecklace: {
    id: "monsterCoreStaminaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier d'endurance en monsterCore",
    description: "+36 endurance max, +1.44 régén. endurance/s.",
    statBonus: { stamina: 36, staminaRegen: 1.44 },
    stackable: false,
    price: 84,
  },

  // necklace - mana - matériau (12 items)
  slimeBlobManaNecklace: {
    id: "slimeBlobManaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de mana en slimeBlob",
    description: "+3 mana max.",
    statBonus: { mana: 3 },
    stackable: false,
    price: 7,
  },
  bearPeltManaNecklace: {
    id: "bearPeltManaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de mana en bearPelt",
    description: "+6 mana max.",
    statBonus: { mana: 6 },
    stackable: false,
    price: 14,
  },
  spiderLegManaNecklace: {
    id: "spiderLegManaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de mana en spiderLeg",
    description: "+9 mana max.",
    statBonus: { mana: 9 },
    stackable: false,
    price: 21,
  },
  greyMonsterScaleManaNecklace: {
    id: "greyMonsterScaleManaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de mana en greyMonsterScale",
    description: "+12 mana max.",
    statBonus: { mana: 12 },
    stackable: false,
    price: 28,
  },
  crabClawManaNecklace: {
    id: "crabClawManaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de mana en crabClaw",
    description: "+15 mana max.",
    statBonus: { mana: 15 },
    stackable: false,
    price: 35,
  },
  blackBearPeltManaNecklace: {
    id: "blackBearPeltManaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de mana en blackBearPelt",
    description: "+18 mana max.",
    statBonus: { mana: 18 },
    stackable: false,
    price: 42,
  },
  turtleShellManaNecklace: {
    id: "turtleShellManaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de mana en turtleShell",
    description: "+21 mana max, +0.84 régén. mana/s.",
    statBonus: { mana: 21, manaRegen: 0.84 },
    stackable: false,
    price: 49,
  },
  greenMonsterScaleManaNecklace: {
    id: "greenMonsterScaleManaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de mana en greenMonsterScale",
    description: "+24 mana max, +0.96 régén. mana/s.",
    statBonus: { mana: 24, manaRegen: 0.96 },
    stackable: false,
    price: 56,
  },
  batWingsManaNecklace: {
    id: "batWingsManaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de mana en batWings",
    description: "+27 mana max, +1.08 régén. mana/s.",
    statBonus: { mana: 27, manaRegen: 1.08 },
    stackable: false,
    price: 63,
  },
  dragonScaleManaNecklace: {
    id: "dragonScaleManaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de mana en dragonScale",
    description: "+30 mana max, +1.2 régén. mana/s.",
    statBonus: { mana: 30, manaRegen: 1.2 },
    stackable: false,
    price: 70,
  },
  ghostEctoplasmManaNecklace: {
    id: "ghostEctoplasmManaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de mana en ghostEctoplasm",
    description: "+33 mana max, +1.32 régén. mana/s.",
    statBonus: { mana: 33, manaRegen: 1.32 },
    stackable: false,
    price: 77,
  },
  monsterCoreManaNecklace: {
    id: "monsterCoreManaNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de mana en monsterCore",
    description: "+36 mana max, +1.44 régén. mana/s.",
    statBonus: { mana: 36, manaRegen: 1.44 },
    stackable: false,
    price: 84,
  },

  woodenArrow: {
    id: "woodenArrow",
    category: "ammo",
    slot: "quiver",
    name: "Flèche en bois",
    description: "+1 dégât à distance tant que des flèches sont encochées.",
    statBonus: { rangedDamage: 1 },
    stackable: true,
    archetypes: ["archer"],
    price: 1,
    inflictsEffect: {
      type: "slow",
      kind: "modifier", // <-- le champ qui manquait, decide TOUT le comportement
      chance: 1,
      statModifiers: { moveSpeedPercent: -0.4 }, // -40% de vitesse
      durationMs: 2500, // duree en ms, PAS ticks/tickIntervalMs/damagePerTick (ca c'est la forme DOT)
    },
  },
  bigWoodenArrow: {
    id: "bigWoodenArrow",
    category: "ammo",
    slot: "quiver",
    name: "Flèche en bois géante",
    description: "+5 dégât à distance tant que des flèches sont encochées.",
    statBonus: { rangedDamage: 5 },
    stackable: true,
    archetypes: ["archer"],
    price: 15,
    // inflictsEffect: {
    //   type: "slow",
    //   kind: "modifier", // <-- le champ qui manquait, decide TOUT le comportement
    //   chance: 1,
    //   statModifiers: { moveSpeedPercent: -0.4 }, // -40% de vitesse
    //   durationMs: 2500, // duree en ms, PAS ticks/tickIntervalMs/damagePerTick (ca c'est la forme DOT)
    // },
  },
  woodenCrossbowBolt: {
    id: "woodenCrossbowBolt",
    category: "ammo",
    slot: "quiver",
    name: "Carreau en bois",
    description:
      "+1 dégât à distance tant que des carreaux sont disponibles. Munition de l'arbalète uniquement.",
    statBonus: { rangedDamage: 1 },
    stackable: true,
    archetypes: ["archer", "voleur"],
    price: 1,
  },
  ironArrow: {
    id: "ironArrow",
    category: "ammo",
    slot: "quiver",
    name: "Flèche en fer",
    description: "+7 dégât à distance tant que des flèches sont encochées.",
    statBonus: { rangedDamage: 7 },
    stackable: true,
    archetypes: ["archer"],
    price: 12,
  },
  ironCrossbowBolt: {
    id: "ironCrossbowBolt",
    category: "ammo",
    slot: "quiver",
    name: "Carreau de fer",
    description:
      "+5 dégât à distance tant que des carreaux sont encochés. Munition de l'arbalète uniquement.",
    statBonus: { rangedDamage: 5 },
    stackable: true,
    archetypes: ["archer", "voleur"],
    price: 10,
  },

  acidArrow: {
    id: "acidArrow",
    category: "ammo",
    slot: "quiver",
    name: "Flèche d'acide",
    description: "+15 dégât à distance tant que des flèches sont encochées.",
    statBonus: { rangedDamage: 15 },
    stackable: true,
    archetypes: ["archer"],
    inflictsEffect: {
      // chance d'infliger un effet par coup - PAS de cooldown ici
      type: "acid",
      kind: "dot",
      chance: 0.3,
      damagePerTick: 3,
      tickIntervalMs: 1000,
      ticks: 3,
    },
    price: 45,
  },
  acidCrossbowBolt: {
    id: "acidCrossbowBolt",
    category: "ammo",
    slot: "quiver",
    name: "Carreau d'acide",
    description:
      "+12 dégât à distance et +2 de distance d'attaque tant que des carreaux sont encochés. Munition de l'arbalète uniquement.",
    statBonus: { rangedDamage: 12, rangedRange: 2 },
    stackable: true,
    archetypes: ["archer", "voleur"],
    price: 43,
  },

  /** Tools **/

  woodenPickaxe: {
    id: "woodenPickaxe",
    category: "equipment",
    slot: "tool",
    name: "Pioche en bois",
    description: "Outil de minage basique. Niveau 1.",
    toolType: "pickaxe",
    toolTier: 1,
    stackable: false,
    price: 20,
  },
  copperPickaxe: {
    id: "copperPickaxe",
    category: "equipment",
    slot: "tool",
    name: "Pioche en cuivre",
    description: "Outil de minage intermédiaire. Niveau 2.",
    toolType: "pickaxe",
    toolTier: 2,
    stackable: false,
    price: 40,
  },
  ironPickaxe: {
    id: "ironPickaxe",
    category: "equipment",
    slot: "tool",
    name: "Pioche en fer",
    description: "Outil de minage robuste. Niveau 3.",
    toolType: "pickaxe",
    toolTier: 3,
    stackable: false,
    price: 60,
  },
  silverPickaxe: {
    id: "silverPickaxe",
    category: "equipment",
    slot: "tool",
    name: "Pioche en argent",
    description: "Outil de minage avancé. Niveau 4.",
    toolType: "pickaxe",
    toolTier: 4,
    stackable: false,
    price: 80,
  },
  steelPickaxe: {
    id: "steelPickaxe",
    category: "equipment",
    slot: "tool",
    name: "Pioche en acier",
    description: "Outil de minage solide. Niveau 5.",
    toolType: "pickaxe",
    toolTier: 5,
    stackable: false,
    price: 90,
  },
  goldPickaxe: {
    id: "goldPickaxe",
    category: "equipment",
    slot: "tool",
    name: "Pioche en or",
    description: "Outil de minage expert. Niveau 6.",
    toolType: "pickaxe",
    toolTier: 6,
    stackable: false,
    price: 100,
  },
  platiniumPickaxe: {
    id: "platiniumPickaxe",
    category: "equipment",
    slot: "tool",
    name: "Pioche en platine",
    description: "Outil de minage maître. Niveau 7.",
    toolType: "pickaxe",
    toolTier: 7,
    stackable: false,
    price: 120,
  },
  cobaltPickaxe: {
    id: "cobaltPickaxe",
    category: "equipment",
    slot: "tool",
    name: "Pioche en cobalt",
    description: "Outil de minage légendaire. Niveau 8.",
    toolType: "pickaxe",
    toolTier: 8,
    stackable: false,
    price: 150,
  },
  adamantinePickaxe: {
    id: "adamantinePickaxe",
    category: "equipment",
    slot: "tool",
    name: "Pioche en adamantine",
    description: "Outil de minage mythique. Niveau 9.",
    toolType: "pickaxe",
    toolTier: 9,
    stackable: false,
    price: 200,
  },
  crimsonPickaxe: {
    id: "crimsonPickaxe",
    category: "equipment",
    slot: "tool",
    name: "Pioche en cramoisi",
    description: "Outil de minage ultime. Niveau 10.",
    toolType: "pickaxe",
    toolTier: 10,
    stackable: false,
    price: 250,
  },
  angelicPickaxe: {
    id: "angelicPickaxe",
    category: "equipment",
    slot: "tool",
    name: "Pioche angélique",
    description: "Outil de minage divin. Niveau 11.",
    toolType: "pickaxe",
    toolTier: 11,
    stackable: false,
    price: 300,
  },
  fatefulPickaxe: {
    id: "fatefulPickaxe",
    category: "equipment",
    slot: "tool",
    name: "Pioche fatale",
    description: "Outil de minage du destin. Niveau 12.",
    toolType: "pickaxe",
    toolTier: 12,
    stackable: false,
    price: 400,
  },
  novaPickaxe: {
    id: "novaPickaxe",
    category: "equipment",
    slot: "tool",
    name: "Pioche nova",
    description: "Outil de minage cosmique. Niveau 13.",
    toolType: "pickaxe",
    toolTier: 13,
    stackable: false,
    price: 500,
  },

  woodenAxe: {
    id: "woodenAxe",
    category: "equipment",
    slot: "tool",
    name: "Hache en bois",
    description: "Outil de coupe basique. Niveau 1.",
    toolType: "axe",
    toolTier: 1,
    stackable: false,
    price: 20,
  },
  copperAxe: {
    id: "copperAxe",
    category: "equipment",
    slot: "tool",
    name: "Hache en cuivre",
    description: "Outil de coupe basique. Niveau 2.",
    toolType: "axe",
    toolTier: 2,
    stackable: false,
    price: 30,
  },
  ironAxe: {
    id: "ironAxe",
    category: "equipment",
    slot: "tool",
    name: "Hache en fer",
    description: "Outil de coupe basique. Niveau 3.",
    toolType: "axe",
    toolTier: 3,
    stackable: false,
    price: 50,
  },

  silverAxe: {
    id: "silverAxe",
    category: "equipment",
    slot: "tool",
    name: "Hache en argent",
    description: "Outil de coupe avancé. Niveau 4.",
    toolType: "axe",
    toolTier: 4,
    stackable: false,
    price: 80,
  },
  steelAxe: {
    id: "steelAxe",
    category: "equipment",
    slot: "tool",
    name: "Hache en acier",
    description: "Outil de coupe avancé. Niveau 5.",
    toolType: "axe",
    toolTier: 5,
    stackable: false,
    price: 120,
  },
  goldAxe: {
    id: "goldAxe",
    category: "equipment",
    slot: "tool",
    name: "Hache en or",
    description: "Outil de coupe avancé. Niveau 6.",
    toolType: "axe",
    toolTier: 6,
    stackable: false,
    price: 150,
  },
  platiniumAxe: {
    id: "platiniumAxe",
    category: "equipment",
    slot: "tool",
    name: "Hache en platine",
    description: "Outil de coupe légendaire. Niveau 7.",
    toolType: "axe",
    toolTier: 7,
    stackable: false,
    price: 200,
  },
  cobaltAxe: {
    id: "cobaltAxe",
    category: "equipment",
    slot: "tool",
    name: "Hache en cobalt",
    description: "Outil de coupe légendaire. Niveau 8.",
    toolType: "axe",
    toolTier: 8,
    stackable: false,
    price: 250,
  },
  adamantineAxe: {
    id: "adamantineAxe",
    category: "equipment",
    slot: "tool",
    name: "Hache en adamantine",
    description: "Outil de coupe mythique. Niveau 9.",
    toolType: "axe",
    toolTier: 9,
    stackable: false,
    price: 300,
  },
  crimsonAxe: {
    id: "crimsonAxe",
    category: "equipment",
    slot: "tool",
    name: "Hache en cramoisi",
    description: "Outil de coupe ultime. Niveau 10.",
    toolType: "axe",
    toolTier: 10,
    stackable: false,
    price: 350,
  },
  angelicAxe: {
    id: "angelicAxe",
    category: "equipment",
    slot: "tool",
    name: "Hache angélique",
    description: "Outil de coupe divin. Niveau 11.",
    toolType: "axe",
    toolTier: 11,
    stackable: false,
    price: 400,
  },
  fatefulAxe: {
    id: "fatefulAxe",
    category: "equipment",
    slot: "tool",
    name: "Hache fatale",
    description: "Outil de coupe du destin. Niveau 12.",
    toolType: "axe",
    toolTier: 12,
    stackable: false,
    price: 450,
  },
  novaAxe: {
    id: "novaAxe",
    category: "equipment",
    slot: "tool",
    name: "Hache nova",
    description: "Outil de coupe cosmique. Niveau 13.",
    toolType: "axe",
    toolTier: 13,
    stackable: false,
    price: 500,
  },

  /** Gemmes de socket **/
  // Gemmes socketables sur les armes/armures ayant des sockets libres
  // (cf. gemSlots tire aleatoirement a la creation de l'objet dans
  // gemSockets.js). category "gem" : simples objets empilables tant
  // qu'ils ne sont pas socketes (comme un consommable classique). Une
  // fois socketee dans un objet instancie, la gemme est retiree de
  // l'inventaire (consommee definitivement) et son effet
  // (permanentModifiers + inflictsEffect) devient permanent sur CET
  // exemplaire d'objet (cf. socketGem dans gemSockets.js).
  // gemFamily reutilise directement les types d'effet de statut deja
  // existants (burn/bleed/acid/slow/stun) - sert aussi a determiner le
  // bonus de combo (actif quand tous les sockets d'un objet sont de la
  // meme famille). Valeurs d'exemple, a ajuster selon l'equilibrage.
  burnGem: {
    id: "burnGem",
    category: "gem",
    name: "Gemme de braise",
    description:
      "+10% des dégâts de base au corps à corps de l'arme. Chance d'infliger une brûlure (dégâts sur la durée).",
    gemFamily: "burn",
    gemUsage: "combat",
    percentModifiers: { meleeDamage: 0.1 },
    inflictsEffect: {
      type: "burn",
      kind: "dot",
      chance: 1,
      damagePerTick: 2,
      tickIntervalMs: 1000,
      durationMs: 4000,
    },
    stackable: true,
    price: 80,
  },
  bleedGem: {
    id: "bleedGem",
    category: "gem",
    name: "Gemme d'épine",
    description:
      "+10% des dégâts de base à distance de l'arme. Chance d'infliger un saignement (dégâts sur la durée).",
    gemFamily: "bleed",
    gemUsage: "combat",
    percentModifiers: { rangedDamage: 0.1 },
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 0.25,
      damagePerTick: 2,
      tickIntervalMs: 1000,
      durationMs: 4000,
    },
    stackable: true,
    price: 80,
  },
  acidGem: {
    id: "acidGem",
    category: "gem",
    name: "Gemme corrosive",
    description:
      "+10% de la défense de base de l'objet. Chance d'infliger de l'acide (dégâts sur la durée).",
    gemFamily: "acid",
    gemUsage: "combat",
    percentModifiers: { defense: 0.1 },
    inflictsEffect: {
      type: "acid",
      kind: "dot",
      chance: 0.25,
      damagePerTick: 2,
      tickIntervalMs: 1000,
      durationMs: 4000,
    },
    stackable: true,
    price: 80,
  },
  slowGem: {
    id: "slowGem",
    category: "gem",
    name: "Gemme de givre",
    description:
      "+10% des PV max de base de l'objet. Chance de ralentir l'ennemi touché.",
    gemFamily: "slow",
    gemUsage: "combat",
    percentModifiers: { maxHp: 0.1 },
    inflictsEffect: {
      type: "slow",
      kind: "modifier",
      chance: 0.25,
      statModifiers: { moveSpeedPercent: -0.3 },
      durationMs: 2000,
    },
    stackable: true,
    price: 80,
  },
  stunGem: {
    id: "stunGem",
    category: "gem",
    name: "Gemme de foudre",
    description:
      "+10% de la vitesse de déplacement de base de l'objet. Chance d'étourdir l'ennemi touché.",
    gemFamily: "stun",
    gemUsage: "combat",
    percentModifiers: { moveSpeed: 0.1 },
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.15,
      statModifiers: { stunned: true },
      durationMs: 1200,
    },
    stackable: true,
    price: 100,
  },

  // Gemmes de combat REACTIVES - a la difference des 5 ci-dessus
  // (inflictsEffect, declenchees quand le JOUEUR porte un coup), celles-ci
  // se declenchent quand le JOUEUR EN ENCAISSE un (reactiveEffect),
  // typiquement pensees pour une armure/botte plutot qu'une arme - meme
  // gemUsage "combat" (aucune restriction supplementaire par slot), mais
  // consommees par ai.js (attaque ennemie sur le joueur) plutot que par
  // playerCombat.js. Meme principe de bonus de stat permanent en %
  // (percentModifiers) que les gemmes ci-dessus.
  hasteGem: {
    id: "hasteGem",
    category: "gem",
    name: "Gemme de vif-argent",
    description:
      "+10% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de gagner un bref regain de vitesse de déplacement.",
    gemFamily: "surge",
    gemUsage: "combat",
    percentModifiers: { defense: 0.1 },
    reactiveEffect: {
      type: "haste",
      kind: "modifier",
      chance: 0.25,
      statModifiers: { moveSpeedPercent: 0.3 },
      durationMs: 2000,
    },
    stackable: true,
    price: 90,
  },
  repelGem: {
    id: "repelGem",
    category: "gem",
    name: "Gemme de répulsion",
    description:
      "+10% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup au corps à corps, de repousser l'assaillant.",
    gemFamily: "repel",
    gemUsage: "combat",
    percentModifiers: { defense: 0.1 },
    reactiveEffect: {
      type: "repel",
      kind: "knockback",
      chance: 0.25,
      distance: 48,
    },
    stackable: true,
    price: 90,
  },

  // Gemmes "d'ability reactive" : meme principe que hasteGem/repelGem
  // ci-dessus (reactiveEffect declenche quand le JOUEUR encaisse un coup),
  // mais au lieu de coder un effet ad hoc, kind: "ability" + abilityId
  // declenche directement l'ability correspondante de ABILITY_DEFS via
  // triggerAbilityEffect (abilities.js) - l'ability garde tous ses
  // parametres propres (rayon, duree, degats...), la gemme ne fait que
  // fournir la chance% et un cooldown de gemme independant
  // (reactiveEffect.cooldownMs) pour eviter le spam, cf. ai.js. Gratuites
  // (pas de cout mana/stamina) et fonctionnent meme si l'ability
  // correspondante n'a pas ete debloquee par le joueur.
  slowWardGem: {
    id: "slowWardGem",
    category: "gem",
    name: "Gemme de ralentissement",
    description:
      "+10% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de ralentir fortement tous les ennemis proches.",
    gemFamily: "slow",
    gemUsage: "combat",
    percentModifiers: { defense: 0.1 },
    reactiveEffect: {
      type: "slow",
      kind: "ability",
      abilityId: "slow",
      chance: 0.3,
      cooldownMs: 7000,
    },
    stackable: true,
    price: 100,
  },
  stunWardGem: {
    id: "stunWardGem",
    category: "gem",
    name: "Gemme d'étourdissement",
    description:
      "+10% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, d'étourdir tous les ennemis proches.",
    gemFamily: "stunWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.1 },
    reactiveEffect: {
      type: "stun",
      kind: "ability",
      abilityId: "stun",
      chance: 0.2,
      cooldownMs: 12000,
    },
    stackable: true,
    price: 130,
  },
  randomTeleportGem: {
    id: "randomTeleportGem",
    category: "gem",
    name: "Gemme de fuite",
    description:
      "+10% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de te téléporter instantanément vers un point aléatoire de l'étage.",
    gemFamily: "teleportWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.1 },
    reactiveEffect: {
      type: "randomTeleport",
      kind: "ability",
      abilityId: "randomTeleport",
      chance: 1, // a modifier selon l'équilibrage du jeu
      cooldownMs: 14000,
    },
    stackable: true,
    price: 130,
  },
  staticStormWardGem: {
    id: "staticStormWardGem",
    category: "gem",
    name: "Gemme de tempête statique",
    description:
      "+10% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de déclencher une tempête électrique autour de toi.",
    gemFamily: "staticStormWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.1 },
    reactiveEffect: {
      type: "staticStorm",
      kind: "ability",
      abilityId: "staticStorm",
      chance: 0.2,
      cooldownMs: 12000,
    },
    stackable: true,
    price: 120,
  },
  thornsWardGem: {
    id: "thornsWardGem",
    category: "gem",
    name: "Gemme d'épines",
    description:
      "+10% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de faire jaillir des épines empoisonnées devant toi.",
    gemFamily: "thornsWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.1 },
    reactiveEffect: {
      type: "thorns",
      kind: "ability",
      abilityId: "thorns",
      chance: 0.3,
      cooldownMs: 5000,
    },
    stackable: true,
    price: 100,
  },
  sporesWardGem: {
    id: "sporesWardGem",
    category: "gem",
    name: "Gemme de spores",
    description:
      "+10% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de libérer un nuage de spores engourdissant les ennemis proches.",
    gemFamily: "sporesWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.1 },
    reactiveEffect: {
      type: "spores",
      kind: "ability",
      abilityId: "spores",
      chance: 0.3,
      cooldownMs: 7000,
    },
    stackable: true,
    price: 100,
  },
  curseWardGem: {
    id: "curseWardGem",
    category: "gem",
    name: "Gemme de malédiction",
    description:
      "+10% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de maudire les ennemis proches et réduire leurs dégâts.",
    gemFamily: "curseWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.1 },
    reactiveEffect: {
      type: "curse",
      kind: "ability",
      abilityId: "curse",
      chance: 0.2,
      cooldownMs: 11000,
    },
    stackable: true,
    price: 120,
  },
  riposteGem: {
    id: "riposteGem",
    category: "gem",
    name: "Gemme de riposte",
    description:
      "+10% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de renvoyer une partie des dégâts subis pendant un court instant.",
    gemFamily: "riposte",
    gemUsage: "combat",
    percentModifiers: { defense: 0.1 },
    reactiveEffect: {
      type: "riposte",
      kind: "ability",
      abilityId: "riposte",
      chance: 0.25,
      cooldownMs: 9000,
    },
    stackable: true,
    price: 120,
  },
  parryGem: {
    id: "parryGem",
    category: "gem",
    name: "Gemme de parade",
    description:
      "+10% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de réduire fortement les dégâts subis pendant un court instant.",
    gemFamily: "parry",
    gemUsage: "combat",
    percentModifiers: { defense: 0.1 },
    reactiveEffect: {
      type: "parry",
      kind: "ability",
      abilityId: "parry",
      chance: 0.25,
      cooldownMs: 7000,
    },
    stackable: true,
    price: 120,
  },
  freezeWardGem: {
    id: "freezeWardGem",
    category: "gem",
    name: "Gemme de gel",
    description:
      "+10% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de projeter un éclat de glace qui gèle les ennemis touchés.",
    gemFamily: "freezeWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.1 },
    reactiveEffect: {
      type: "freeze",
      kind: "ability",
      abilityId: "freeze",
      chance: 0.25,
      cooldownMs: 6000,
    },
    stackable: true,
    price: 110,
  },
  rootWardGem: {
    id: "rootWardGem",
    category: "gem",
    name: "Gemme de racines",
    description:
      "+10% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, d'enraciner tous les ennemis proches.",
    gemFamily: "rootWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.1 },
    reactiveEffect: {
      type: "root",
      kind: "ability",
      abilityId: "root",
      chance: 0.3,
      cooldownMs: 8000,
    },
    stackable: true,
    price: 100,
  },
  shockwaveGem: {
    id: "shockwaveGem",
    category: "gem",
    name: "Gemme d'onde de choc",
    description:
      "+10% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de déclencher une onde de choc qui blesse et repousse les ennemis proches.",
    gemFamily: "shockwave",
    gemUsage: "combat",
    percentModifiers: { defense: 0.1 },
    reactiveEffect: {
      type: "shockwave",
      kind: "ability",
      abilityId: "shockwave",
      chance: 0.2,
      cooldownMs: 7000,
    },
    stackable: true,
    price: 130,
  },

  // Gemmes d'outil (minage/bûcheronnage) - reservees aux objets de slot
  // "tool" (pioches/haches, cf. gemSockets.socketGem qui refuse tout
  // croisement avec les gemmes de combat ci-dessus, et inversement).
  // Aucun permanentModifiers/inflictsEffect ici (ca n'a pas de sens sur un
  // outil) : l'effet passe par toolEffect, consomme par exploration.js via
  // resolveInstanceToolEffectSources (gemSockets.js).
  miningSpeedGem: {
    id: "miningSpeedGem",
    category: "gem",
    name: "Gemme de célérité",
    description:
      "Une fois socketée sur un outil : chance qu'un coup de récolte n'entame pas le temps de récupération (récolte plus rapide).",
    gemFamily: "haste",
    gemUsage: "tool",
    toolEffect: { type: "fastHarvest", chance: 0.25 },
    stackable: true,
    price: 80,
  },
  miningYieldGem: {
    id: "miningYieldGem",
    category: "gem",
    name: "Gemme d'abondance",
    description:
      "Une fois socketée sur un outil : chance d'obtenir une ressource supplémentaire en minant/récoltant.",
    gemFamily: "yield",
    gemUsage: "tool",
    toolEffect: { type: "bonusYield", chance: 0.25 },
    stackable: true,
    price: 80,
  },
  miningLuckGem: {
    id: "miningLuckGem",
    category: "gem",
    name: "Gemme de fortune",
    description:
      "Une fois socketée sur un outil : augmente la chance de trouvaille rare en minant/récoltant.",
    gemFamily: "luck",
    gemUsage: "tool",
    toolEffect: { type: "rareLuck", bonusChance: 0.15 },
    stackable: true,
    price: 100,
  },
  /** === NOUVELLES GEMMES : effets supplémentaires (T1 à T5) === **/
  poisonGem: {
    id: "poisonGem",
    category: "gem",
    name: "Gemme de venin",
    description:
      "+10% des dégâts de base au corps à corps de l'arme. Chance d'empoisonner l'ennemi touché (dégâts sur la durée).",
    gemFamily: "poison",
    gemUsage: "combat",
    percentModifiers: { meleeDamage: 0.1 },
    inflictsEffect: {
      type: "poison",
      kind: "dot",
      chance: 0.25,
      damagePerTick: 2,
      tickIntervalMs: 1000,
      durationMs: 4000,
    },
    stackable: true,
    price: 90,
  },
  poisonGemT2: {
    id: "poisonGemT2",
    category: "gem",
    name: "Gemme de venin polie",
    description:
      "+15% des dégâts de base au corps à corps de l'arme. Chance d'empoisonner l'ennemi touché (dégâts sur la durée).",
    gemFamily: "poison",
    gemUsage: "combat",
    percentModifiers: { meleeDamage: 0.15 },
    inflictsEffect: {
      type: "poison",
      kind: "dot",
      chance: 0.3,
      damagePerTick: 3,
      tickIntervalMs: 1000,
      durationMs: 4500,
    },
    stackable: true,
    price: 180,
  },
  poisonGemT3: {
    id: "poisonGemT3",
    category: "gem",
    name: "Gemme de venin pure",
    description:
      "+20% des dégâts de base au corps à corps de l'arme. Chance d'empoisonner l'ennemi touché (dégâts sur la durée).",
    gemFamily: "poison",
    gemUsage: "combat",
    percentModifiers: { meleeDamage: 0.2 },
    inflictsEffect: {
      type: "poison",
      kind: "dot",
      chance: 0.35,
      damagePerTick: 4,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    stackable: true,
    price: 360,
  },
  poisonGemT4: {
    id: "poisonGemT4",
    category: "gem",
    name: "Gemme de venin radieuse",
    description:
      "+30% des dégâts de base au corps à corps de l'arme. Chance d'empoisonner l'ennemi touché (dégâts sur la durée).",
    gemFamily: "poison",
    gemUsage: "combat",
    percentModifiers: { meleeDamage: 0.3 },
    inflictsEffect: {
      type: "poison",
      kind: "dot",
      chance: 0.4,
      damagePerTick: 6,
      tickIntervalMs: 1000,
      durationMs: 5500,
    },
    stackable: true,
    price: 720,
  },
  poisonGemT5: {
    id: "poisonGemT5",
    category: "gem",
    name: "Gemme de venin parfaite",
    description:
      "+40% des dégâts de base au corps à corps de l'arme. Chance d'empoisonner l'ennemi touché (dégâts sur la durée).",
    gemFamily: "poison",
    gemUsage: "combat",
    percentModifiers: { meleeDamage: 0.4 },
    inflictsEffect: {
      type: "poison",
      kind: "dot",
      chance: 0.45,
      damagePerTick: 8,
      tickIntervalMs: 1000,
      durationMs: 6000,
    },
    stackable: true,
    price: 1440,
  },
  weakenGem: {
    id: "weakenGem",
    category: "gem",
    name: "Gemme de faiblesse",
    description:
      "+10% des PV max de base de l'objet. Chance d'affaiblir l'ennemi touché : ses dégâts sont réduits un moment.",
    gemFamily: "weaken",
    gemUsage: "combat",
    percentModifiers: { maxHp: 0.1 },
    inflictsEffect: {
      type: "curse",
      kind: "modifier",
      chance: 0.25,
      statModifiers: { damagePercent: -0.2 },
      durationMs: 3000,
    },
    stackable: true,
    price: 100,
  },
  weakenGemT2: {
    id: "weakenGemT2",
    category: "gem",
    name: "Gemme de faiblesse polie",
    description:
      "+15% des PV max de base de l'objet. Chance d'affaiblir l'ennemi touché : ses dégâts sont réduits un moment.",
    gemFamily: "weaken",
    gemUsage: "combat",
    percentModifiers: { maxHp: 0.15 },
    inflictsEffect: {
      type: "curse",
      kind: "modifier",
      chance: 0.3,
      statModifiers: { damagePercent: -0.25 },
      durationMs: 3500,
    },
    stackable: true,
    price: 200,
  },
  weakenGemT3: {
    id: "weakenGemT3",
    category: "gem",
    name: "Gemme de faiblesse pure",
    description:
      "+20% des PV max de base de l'objet. Chance d'affaiblir l'ennemi touché : ses dégâts sont réduits un moment.",
    gemFamily: "weaken",
    gemUsage: "combat",
    percentModifiers: { maxHp: 0.2 },
    inflictsEffect: {
      type: "curse",
      kind: "modifier",
      chance: 0.35,
      statModifiers: { damagePercent: -0.3 },
      durationMs: 4000,
    },
    stackable: true,
    price: 400,
  },
  weakenGemT4: {
    id: "weakenGemT4",
    category: "gem",
    name: "Gemme de faiblesse radieuse",
    description:
      "+30% des PV max de base de l'objet. Chance d'affaiblir l'ennemi touché : ses dégâts sont réduits un moment.",
    gemFamily: "weaken",
    gemUsage: "combat",
    percentModifiers: { maxHp: 0.3 },
    inflictsEffect: {
      type: "curse",
      kind: "modifier",
      chance: 0.4,
      statModifiers: { damagePercent: -0.35 },
      durationMs: 4500,
    },
    stackable: true,
    price: 800,
  },
  weakenGemT5: {
    id: "weakenGemT5",
    category: "gem",
    name: "Gemme de faiblesse parfaite",
    description:
      "+40% des PV max de base de l'objet. Chance d'affaiblir l'ennemi touché : ses dégâts sont réduits un moment.",
    gemFamily: "weaken",
    gemUsage: "combat",
    percentModifiers: { maxHp: 0.4 },
    inflictsEffect: {
      type: "curse",
      kind: "modifier",
      chance: 0.45,
      statModifiers: { damagePercent: -0.4 },
      durationMs: 5000,
    },
    stackable: true,
    price: 1600,
  },
  rootGem: {
    id: "rootGem",
    category: "gem",
    name: "Gemme d'entrave",
    description:
      "+10% de la défense de base de l'objet. Chance d'enraciner l'ennemi touché : il ne peut plus bouger un moment.",
    gemFamily: "root",
    gemUsage: "combat",
    percentModifiers: { defense: 0.1 },
    inflictsEffect: {
      type: "root",
      kind: "modifier",
      chance: 0.15,
      statModifiers: {},
      durationMs: 1500,
    },
    stackable: true,
    price: 100,
  },
  rootGemT2: {
    id: "rootGemT2",
    category: "gem",
    name: "Gemme d'entrave polie",
    description:
      "+15% de la défense de base de l'objet. Chance d'enraciner l'ennemi touché : il ne peut plus bouger un moment.",
    gemFamily: "root",
    gemUsage: "combat",
    percentModifiers: { defense: 0.15 },
    inflictsEffect: {
      type: "root",
      kind: "modifier",
      chance: 0.17,
      statModifiers: {},
      durationMs: 1800,
    },
    stackable: true,
    price: 200,
  },
  rootGemT3: {
    id: "rootGemT3",
    category: "gem",
    name: "Gemme d'entrave pure",
    description:
      "+20% de la défense de base de l'objet. Chance d'enraciner l'ennemi touché : il ne peut plus bouger un moment.",
    gemFamily: "root",
    gemUsage: "combat",
    percentModifiers: { defense: 0.2 },
    inflictsEffect: {
      type: "root",
      kind: "modifier",
      chance: 0.19,
      statModifiers: {},
      durationMs: 2100,
    },
    stackable: true,
    price: 400,
  },
  rootGemT4: {
    id: "rootGemT4",
    category: "gem",
    name: "Gemme d'entrave radieuse",
    description:
      "+30% de la défense de base de l'objet. Chance d'enraciner l'ennemi touché : il ne peut plus bouger un moment.",
    gemFamily: "root",
    gemUsage: "combat",
    percentModifiers: { defense: 0.3 },
    inflictsEffect: {
      type: "root",
      kind: "modifier",
      chance: 0.22,
      statModifiers: {},
      durationMs: 2400,
    },
    stackable: true,
    price: 800,
  },
  rootGemT5: {
    id: "rootGemT5",
    category: "gem",
    name: "Gemme d'entrave parfaite",
    description:
      "+40% de la défense de base de l'objet. Chance d'enraciner l'ennemi touché : il ne peut plus bouger un moment.",
    gemFamily: "root",
    gemUsage: "combat",
    percentModifiers: { defense: 0.4 },
    inflictsEffect: {
      type: "root",
      kind: "modifier",
      chance: 0.25,
      statModifiers: {},
      durationMs: 2700,
    },
    stackable: true,
    price: 1600,
  },

  /** === PALIERS SUPÉRIEURS (T2 à T5) des gemmes offensives existantes === **/
  burnGemT2: {
    id: "burnGemT2",
    category: "gem",
    name: "Gemme de braise polie",
    description:
      "+15% des dégâts de base au corps à corps de l'arme. Chance d'infliger une brûlure (dégâts sur la durée).",
    gemFamily: "burn",
    gemUsage: "combat",
    percentModifiers: { meleeDamage: 0.15 },
    inflictsEffect: {
      type: "burn",
      kind: "dot",
      chance: 0.3,
      damagePerTick: 3,
      tickIntervalMs: 1000,
      durationMs: 4500,
    },
    stackable: true,
    price: 160,
  },
  burnGemT3: {
    id: "burnGemT3",
    category: "gem",
    name: "Gemme de braise pure",
    description:
      "+20% des dégâts de base au corps à corps de l'arme. Chance d'infliger une brûlure (dégâts sur la durée).",
    gemFamily: "burn",
    gemUsage: "combat",
    percentModifiers: { meleeDamage: 0.2 },
    inflictsEffect: {
      type: "burn",
      kind: "dot",
      chance: 0.35,
      damagePerTick: 4,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    stackable: true,
    price: 320,
  },
  burnGemT4: {
    id: "burnGemT4",
    category: "gem",
    name: "Gemme de braise radieuse",
    description:
      "+30% des dégâts de base au corps à corps de l'arme. Chance d'infliger une brûlure (dégâts sur la durée).",
    gemFamily: "burn",
    gemUsage: "combat",
    percentModifiers: { meleeDamage: 0.3 },
    inflictsEffect: {
      type: "burn",
      kind: "dot",
      chance: 0.4,
      damagePerTick: 6,
      tickIntervalMs: 1000,
      durationMs: 5500,
    },
    stackable: true,
    price: 640,
  },
  burnGemT5: {
    id: "burnGemT5",
    category: "gem",
    name: "Gemme de braise parfaite",
    description:
      "+40% des dégâts de base au corps à corps de l'arme. Chance d'infliger une brûlure (dégâts sur la durée).",
    gemFamily: "burn",
    gemUsage: "combat",
    percentModifiers: { meleeDamage: 0.4 },
    inflictsEffect: {
      type: "burn",
      kind: "dot",
      chance: 0.45,
      damagePerTick: 8,
      tickIntervalMs: 1000,
      durationMs: 6000,
    },
    stackable: true,
    price: 1280,
  },
  bleedGemT2: {
    id: "bleedGemT2",
    category: "gem",
    name: "Gemme d'épine polie",
    description:
      "+15% des dégâts de base à distance de l'arme. Chance d'infliger un saignement (dégâts sur la durée).",
    gemFamily: "bleed",
    gemUsage: "combat",
    percentModifiers: { rangedDamage: 0.15 },
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 0.3,
      damagePerTick: 3,
      tickIntervalMs: 1000,
      durationMs: 4500,
    },
    stackable: true,
    price: 160,
  },
  bleedGemT3: {
    id: "bleedGemT3",
    category: "gem",
    name: "Gemme d'épine pure",
    description:
      "+20% des dégâts de base à distance de l'arme. Chance d'infliger un saignement (dégâts sur la durée).",
    gemFamily: "bleed",
    gemUsage: "combat",
    percentModifiers: { rangedDamage: 0.2 },
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 0.35,
      damagePerTick: 4,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    stackable: true,
    price: 320,
  },
  bleedGemT4: {
    id: "bleedGemT4",
    category: "gem",
    name: "Gemme d'épine radieuse",
    description:
      "+30% des dégâts de base à distance de l'arme. Chance d'infliger un saignement (dégâts sur la durée).",
    gemFamily: "bleed",
    gemUsage: "combat",
    percentModifiers: { rangedDamage: 0.3 },
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 0.4,
      damagePerTick: 6,
      tickIntervalMs: 1000,
      durationMs: 5500,
    },
    stackable: true,
    price: 640,
  },
  bleedGemT5: {
    id: "bleedGemT5",
    category: "gem",
    name: "Gemme d'épine parfaite",
    description:
      "+40% des dégâts de base à distance de l'arme. Chance d'infliger un saignement (dégâts sur la durée).",
    gemFamily: "bleed",
    gemUsage: "combat",
    percentModifiers: { rangedDamage: 0.4 },
    inflictsEffect: {
      type: "bleed",
      kind: "dot",
      chance: 0.45,
      damagePerTick: 8,
      tickIntervalMs: 1000,
      durationMs: 6000,
    },
    stackable: true,
    price: 1280,
  },
  acidGemT2: {
    id: "acidGemT2",
    category: "gem",
    name: "Gemme corrosive polie",
    description:
      "+15% de la défense de base de l'objet. Chance d'infliger de l'acide (dégâts sur la durée).",
    gemFamily: "acid",
    gemUsage: "combat",
    percentModifiers: { defense: 0.15 },
    inflictsEffect: {
      type: "acid",
      kind: "dot",
      chance: 0.3,
      damagePerTick: 3,
      tickIntervalMs: 1000,
      durationMs: 4500,
    },
    stackable: true,
    price: 160,
  },
  acidGemT3: {
    id: "acidGemT3",
    category: "gem",
    name: "Gemme corrosive pure",
    description:
      "+20% de la défense de base de l'objet. Chance d'infliger de l'acide (dégâts sur la durée).",
    gemFamily: "acid",
    gemUsage: "combat",
    percentModifiers: { defense: 0.2 },
    inflictsEffect: {
      type: "acid",
      kind: "dot",
      chance: 0.35,
      damagePerTick: 4,
      tickIntervalMs: 1000,
      durationMs: 5000,
    },
    stackable: true,
    price: 320,
  },
  acidGemT4: {
    id: "acidGemT4",
    category: "gem",
    name: "Gemme corrosive radieuse",
    description:
      "+30% de la défense de base de l'objet. Chance d'infliger de l'acide (dégâts sur la durée).",
    gemFamily: "acid",
    gemUsage: "combat",
    percentModifiers: { defense: 0.3 },
    inflictsEffect: {
      type: "acid",
      kind: "dot",
      chance: 0.4,
      damagePerTick: 6,
      tickIntervalMs: 1000,
      durationMs: 5500,
    },
    stackable: true,
    price: 640,
  },
  acidGemT5: {
    id: "acidGemT5",
    category: "gem",
    name: "Gemme corrosive parfaite",
    description:
      "+40% de la défense de base de l'objet. Chance d'infliger de l'acide (dégâts sur la durée).",
    gemFamily: "acid",
    gemUsage: "combat",
    percentModifiers: { defense: 0.4 },
    inflictsEffect: {
      type: "acid",
      kind: "dot",
      chance: 0.45,
      damagePerTick: 8,
      tickIntervalMs: 1000,
      durationMs: 6000,
    },
    stackable: true,
    price: 1280,
  },
  slowGemT2: {
    id: "slowGemT2",
    category: "gem",
    name: "Gemme de givre polie",
    description:
      "+15% des PV max de base de l'objet. Chance de ralentir l'ennemi touché.",
    gemFamily: "slow",
    gemUsage: "combat",
    percentModifiers: { maxHp: 0.15 },
    inflictsEffect: {
      type: "slow",
      kind: "modifier",
      chance: 0.3,
      statModifiers: { moveSpeedPercent: -0.35 },
      durationMs: 2500,
    },
    stackable: true,
    price: 160,
  },
  slowGemT3: {
    id: "slowGemT3",
    category: "gem",
    name: "Gemme de givre pure",
    description:
      "+20% des PV max de base de l'objet. Chance de ralentir l'ennemi touché.",
    gemFamily: "slow",
    gemUsage: "combat",
    percentModifiers: { maxHp: 0.2 },
    inflictsEffect: {
      type: "slow",
      kind: "modifier",
      chance: 0.35,
      statModifiers: { moveSpeedPercent: -0.4 },
      durationMs: 3000,
    },
    stackable: true,
    price: 320,
  },
  slowGemT4: {
    id: "slowGemT4",
    category: "gem",
    name: "Gemme de givre radieuse",
    description:
      "+30% des PV max de base de l'objet. Chance de ralentir l'ennemi touché.",
    gemFamily: "slow",
    gemUsage: "combat",
    percentModifiers: { maxHp: 0.3 },
    inflictsEffect: {
      type: "slow",
      kind: "modifier",
      chance: 0.4,
      statModifiers: { moveSpeedPercent: -0.45 },
      durationMs: 3500,
    },
    stackable: true,
    price: 640,
  },
  slowGemT5: {
    id: "slowGemT5",
    category: "gem",
    name: "Gemme de givre parfaite",
    description:
      "+40% des PV max de base de l'objet. Chance de ralentir l'ennemi touché.",
    gemFamily: "slow",
    gemUsage: "combat",
    percentModifiers: { maxHp: 0.4 },
    inflictsEffect: {
      type: "slow",
      kind: "modifier",
      chance: 0.45,
      statModifiers: { moveSpeedPercent: -0.5 },
      durationMs: 4000,
    },
    stackable: true,
    price: 1280,
  },
  stunGemT2: {
    id: "stunGemT2",
    category: "gem",
    name: "Gemme de foudre polie",
    description:
      "+15% de la vitesse de déplacement de base de l'objet. Chance d'étourdir l'ennemi touché.",
    gemFamily: "stun",
    gemUsage: "combat",
    percentModifiers: { moveSpeed: 0.15 },
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.17,
      statModifiers: { stunned: true },
      durationMs: 1300,
    },
    stackable: true,
    price: 200,
  },
  stunGemT3: {
    id: "stunGemT3",
    category: "gem",
    name: "Gemme de foudre pure",
    description:
      "+20% de la vitesse de déplacement de base de l'objet. Chance d'étourdir l'ennemi touché.",
    gemFamily: "stun",
    gemUsage: "combat",
    percentModifiers: { moveSpeed: 0.2 },
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.19,
      statModifiers: { stunned: true },
      durationMs: 1400,
    },
    stackable: true,
    price: 400,
  },
  stunGemT4: {
    id: "stunGemT4",
    category: "gem",
    name: "Gemme de foudre radieuse",
    description:
      "+30% de la vitesse de déplacement de base de l'objet. Chance d'étourdir l'ennemi touché.",
    gemFamily: "stun",
    gemUsage: "combat",
    percentModifiers: { moveSpeed: 0.3 },
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.22,
      statModifiers: { stunned: true },
      durationMs: 1500,
    },
    stackable: true,
    price: 800,
  },
  stunGemT5: {
    id: "stunGemT5",
    category: "gem",
    name: "Gemme de foudre parfaite",
    description:
      "+40% de la vitesse de déplacement de base de l'objet. Chance d'étourdir l'ennemi touché.",
    gemFamily: "stun",
    gemUsage: "combat",
    percentModifiers: { moveSpeed: 0.4 },
    inflictsEffect: {
      type: "stun",
      kind: "modifier",
      chance: 0.25,
      statModifiers: { stunned: true },
      durationMs: 1600,
    },
    stackable: true,
    price: 1600,
  },

  /** === PALIERS SUPÉRIEURS (T2 à T5) des gemmes réactives === **/
  hasteGemT2: {
    id: "hasteGemT2",
    category: "gem",
    name: "Gemme de vif-argent polie",
    description:
      "+15% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de gagner un bref regain de vitesse de déplacement.",
    gemFamily: "surge",
    gemUsage: "combat",
    percentModifiers: { defense: 0.15 },
    reactiveEffect: {
      type: "haste",
      kind: "modifier",
      chance: 0.28,
      statModifiers: { moveSpeedPercent: 0.35 },
      durationMs: 2500,
    },
    stackable: true,
    price: 180,
  },
  hasteGemT3: {
    id: "hasteGemT3",
    category: "gem",
    name: "Gemme de vif-argent pure",
    description:
      "+20% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de gagner un bref regain de vitesse de déplacement.",
    gemFamily: "surge",
    gemUsage: "combat",
    percentModifiers: { defense: 0.2 },
    reactiveEffect: {
      type: "haste",
      kind: "modifier",
      chance: 0.31,
      statModifiers: { moveSpeedPercent: 0.4 },
      durationMs: 3000,
    },
    stackable: true,
    price: 360,
  },
  hasteGemT4: {
    id: "hasteGemT4",
    category: "gem",
    name: "Gemme de vif-argent radieuse",
    description:
      "+30% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de gagner un bref regain de vitesse de déplacement.",
    gemFamily: "surge",
    gemUsage: "combat",
    percentModifiers: { defense: 0.3 },
    reactiveEffect: {
      type: "haste",
      kind: "modifier",
      chance: 0.34,
      statModifiers: { moveSpeedPercent: 0.45 },
      durationMs: 3500,
    },
    stackable: true,
    price: 720,
  },
  hasteGemT5: {
    id: "hasteGemT5",
    category: "gem",
    name: "Gemme de vif-argent parfaite",
    description:
      "+40% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de gagner un bref regain de vitesse de déplacement.",
    gemFamily: "surge",
    gemUsage: "combat",
    percentModifiers: { defense: 0.4 },
    reactiveEffect: {
      type: "haste",
      kind: "modifier",
      chance: 0.37,
      statModifiers: { moveSpeedPercent: 0.5 },
      durationMs: 4000,
    },
    stackable: true,
    price: 1440,
  },
  repelGemT2: {
    id: "repelGemT2",
    category: "gem",
    name: "Gemme de répulsion polie",
    description:
      "+15% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup au corps à corps, de repousser l'assaillant.",
    gemFamily: "repel",
    gemUsage: "combat",
    percentModifiers: { defense: 0.15 },
    reactiveEffect: {
      type: "repel",
      kind: "knockback",
      chance: 0.28,
      distance: 64,
    },
    stackable: true,
    price: 180,
  },
  repelGemT3: {
    id: "repelGemT3",
    category: "gem",
    name: "Gemme de répulsion pure",
    description:
      "+20% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup au corps à corps, de repousser l'assaillant.",
    gemFamily: "repel",
    gemUsage: "combat",
    percentModifiers: { defense: 0.2 },
    reactiveEffect: {
      type: "repel",
      kind: "knockback",
      chance: 0.31,
      distance: 80,
    },
    stackable: true,
    price: 360,
  },
  repelGemT4: {
    id: "repelGemT4",
    category: "gem",
    name: "Gemme de répulsion radieuse",
    description:
      "+30% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup au corps à corps, de repousser l'assaillant.",
    gemFamily: "repel",
    gemUsage: "combat",
    percentModifiers: { defense: 0.3 },
    reactiveEffect: {
      type: "repel",
      kind: "knockback",
      chance: 0.34,
      distance: 96,
    },
    stackable: true,
    price: 720,
  },
  repelGemT5: {
    id: "repelGemT5",
    category: "gem",
    name: "Gemme de répulsion parfaite",
    description:
      "+40% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup au corps à corps, de repousser l'assaillant.",
    gemFamily: "repel",
    gemUsage: "combat",
    percentModifiers: { defense: 0.4 },
    reactiveEffect: {
      type: "repel",
      kind: "knockback",
      chance: 0.37,
      distance: 112,
    },
    stackable: true,
    price: 1440,
  },
  slowWardGemT2: {
    id: "slowWardGemT2",
    category: "gem",
    name: "Gemme de ralentissement polie",
    description:
      "+15% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de ralentir fortement tous les ennemis proches.",
    gemFamily: "slow",
    gemUsage: "combat",
    percentModifiers: { defense: 0.15 },
    reactiveEffect: {
      type: "slow",
      kind: "ability",
      abilityId: "slow",
      chance: 0.35,
      cooldownMs: 6300,
    },
    stackable: true,
    price: 200,
  },
  slowWardGemT3: {
    id: "slowWardGemT3",
    category: "gem",
    name: "Gemme de ralentissement pure",
    description:
      "+20% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de ralentir fortement tous les ennemis proches.",
    gemFamily: "slow",
    gemUsage: "combat",
    percentModifiers: { defense: 0.2 },
    reactiveEffect: {
      type: "slow",
      kind: "ability",
      abilityId: "slow",
      chance: 0.39,
      cooldownMs: 5600,
    },
    stackable: true,
    price: 400,
  },
  slowWardGemT4: {
    id: "slowWardGemT4",
    category: "gem",
    name: "Gemme de ralentissement radieuse",
    description:
      "+30% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de ralentir fortement tous les ennemis proches.",
    gemFamily: "slow",
    gemUsage: "combat",
    percentModifiers: { defense: 0.3 },
    reactiveEffect: {
      type: "slow",
      kind: "ability",
      abilityId: "slow",
      chance: 0.45,
      cooldownMs: 4900,
    },
    stackable: true,
    price: 800,
  },
  slowWardGemT5: {
    id: "slowWardGemT5",
    category: "gem",
    name: "Gemme de ralentissement parfaite",
    description:
      "+40% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de ralentir fortement tous les ennemis proches.",
    gemFamily: "slow",
    gemUsage: "combat",
    percentModifiers: { defense: 0.4 },
    reactiveEffect: {
      type: "slow",
      kind: "ability",
      abilityId: "slow",
      chance: 0.51,
      cooldownMs: 4200,
    },
    stackable: true,
    price: 1600,
  },
  stunWardGemT2: {
    id: "stunWardGemT2",
    category: "gem",
    name: "Gemme d'étourdissement polie",
    description:
      "+15% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, d'étourdir tous les ennemis proches.",
    gemFamily: "stunWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.15 },
    reactiveEffect: {
      type: "stun",
      kind: "ability",
      abilityId: "stun",
      chance: 0.23,
      cooldownMs: 10800,
    },
    stackable: true,
    price: 260,
  },
  stunWardGemT3: {
    id: "stunWardGemT3",
    category: "gem",
    name: "Gemme d'étourdissement pure",
    description:
      "+20% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, d'étourdir tous les ennemis proches.",
    gemFamily: "stunWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.2 },
    reactiveEffect: {
      type: "stun",
      kind: "ability",
      abilityId: "stun",
      chance: 0.26,
      cooldownMs: 9600,
    },
    stackable: true,
    price: 520,
  },
  stunWardGemT4: {
    id: "stunWardGemT4",
    category: "gem",
    name: "Gemme d'étourdissement radieuse",
    description:
      "+30% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, d'étourdir tous les ennemis proches.",
    gemFamily: "stunWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.3 },
    reactiveEffect: {
      type: "stun",
      kind: "ability",
      abilityId: "stun",
      chance: 0.3,
      cooldownMs: 8400,
    },
    stackable: true,
    price: 1040,
  },
  stunWardGemT5: {
    id: "stunWardGemT5",
    category: "gem",
    name: "Gemme d'étourdissement parfaite",
    description:
      "+40% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, d'étourdir tous les ennemis proches.",
    gemFamily: "stunWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.4 },
    reactiveEffect: {
      type: "stun",
      kind: "ability",
      abilityId: "stun",
      chance: 0.34,
      cooldownMs: 7200,
    },
    stackable: true,
    price: 2080,
  },
  randomTeleportGemT2: {
    id: "randomTeleportGemT2",
    category: "gem",
    name: "Gemme de fuite polie",
    description:
      "+15% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de te téléporter instantanément vers un point aléatoire de l'étage.",
    gemFamily: "teleportWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.15 },
    reactiveEffect: {
      type: "randomTeleport",
      kind: "ability",
      abilityId: "randomTeleport",
      chance: 0.29,
      cooldownMs: 12600,
    },
    stackable: true,
    price: 260,
  },
  randomTeleportGemT3: {
    id: "randomTeleportGemT3",
    category: "gem",
    name: "Gemme de fuite pure",
    description:
      "+20% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de te téléporter instantanément vers un point aléatoire de l'étage.",
    gemFamily: "teleportWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.2 },
    reactiveEffect: {
      type: "randomTeleport",
      kind: "ability",
      abilityId: "randomTeleport",
      chance: 0.33,
      cooldownMs: 11200,
    },
    stackable: true,
    price: 520,
  },
  randomTeleportGemT4: {
    id: "randomTeleportGemT4",
    category: "gem",
    name: "Gemme de fuite radieuse",
    description:
      "+30% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de te téléporter instantanément vers un point aléatoire de l'étage.",
    gemFamily: "teleportWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.3 },
    reactiveEffect: {
      type: "randomTeleport",
      kind: "ability",
      abilityId: "randomTeleport",
      chance: 0.38,
      cooldownMs: 9800,
    },
    stackable: true,
    price: 1040,
  },
  randomTeleportGemT5: {
    id: "randomTeleportGemT5",
    category: "gem",
    name: "Gemme de fuite parfaite",
    description:
      "+40% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de te téléporter instantanément vers un point aléatoire de l'étage.",
    gemFamily: "teleportWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.4 },
    reactiveEffect: {
      type: "randomTeleport",
      kind: "ability",
      abilityId: "randomTeleport",
      chance: 0.43,
      cooldownMs: 8400,
    },
    stackable: true,
    price: 2080,
  },
  staticStormWardGemT2: {
    id: "staticStormWardGemT2",
    category: "gem",
    name: "Gemme de tempête statique polie",
    description:
      "+15% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de déclencher une tempête électrique autour de toi.",
    gemFamily: "staticStormWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.15 },
    reactiveEffect: {
      type: "staticStorm",
      kind: "ability",
      abilityId: "staticStorm",
      chance: 0.23,
      cooldownMs: 10800,
    },
    stackable: true,
    price: 240,
  },
  staticStormWardGemT3: {
    id: "staticStormWardGemT3",
    category: "gem",
    name: "Gemme de tempête statique pure",
    description:
      "+20% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de déclencher une tempête électrique autour de toi.",
    gemFamily: "staticStormWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.2 },
    reactiveEffect: {
      type: "staticStorm",
      kind: "ability",
      abilityId: "staticStorm",
      chance: 0.26,
      cooldownMs: 9600,
    },
    stackable: true,
    price: 480,
  },
  staticStormWardGemT4: {
    id: "staticStormWardGemT4",
    category: "gem",
    name: "Gemme de tempête statique radieuse",
    description:
      "+30% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de déclencher une tempête électrique autour de toi.",
    gemFamily: "staticStormWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.3 },
    reactiveEffect: {
      type: "staticStorm",
      kind: "ability",
      abilityId: "staticStorm",
      chance: 0.3,
      cooldownMs: 8400,
    },
    stackable: true,
    price: 960,
  },
  staticStormWardGemT5: {
    id: "staticStormWardGemT5",
    category: "gem",
    name: "Gemme de tempête statique parfaite",
    description:
      "+40% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de déclencher une tempête électrique autour de toi.",
    gemFamily: "staticStormWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.4 },
    reactiveEffect: {
      type: "staticStorm",
      kind: "ability",
      abilityId: "staticStorm",
      chance: 0.34,
      cooldownMs: 7200,
    },
    stackable: true,
    price: 1920,
  },
  thornsWardGemT2: {
    id: "thornsWardGemT2",
    category: "gem",
    name: "Gemme d'épines polie",
    description:
      "+15% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de faire jaillir des épines empoisonnées devant toi.",
    gemFamily: "thornsWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.15 },
    reactiveEffect: {
      type: "thorns",
      kind: "ability",
      abilityId: "thorns",
      chance: 0.35,
      cooldownMs: 4500,
    },
    stackable: true,
    price: 200,
  },
  thornsWardGemT3: {
    id: "thornsWardGemT3",
    category: "gem",
    name: "Gemme d'épines pure",
    description:
      "+20% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de faire jaillir des épines empoisonnées devant toi.",
    gemFamily: "thornsWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.2 },
    reactiveEffect: {
      type: "thorns",
      kind: "ability",
      abilityId: "thorns",
      chance: 0.39,
      cooldownMs: 4000,
    },
    stackable: true,
    price: 400,
  },
  thornsWardGemT4: {
    id: "thornsWardGemT4",
    category: "gem",
    name: "Gemme d'épines radieuse",
    description:
      "+30% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de faire jaillir des épines empoisonnées devant toi.",
    gemFamily: "thornsWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.3 },
    reactiveEffect: {
      type: "thorns",
      kind: "ability",
      abilityId: "thorns",
      chance: 0.45,
      cooldownMs: 3500,
    },
    stackable: true,
    price: 800,
  },
  thornsWardGemT5: {
    id: "thornsWardGemT5",
    category: "gem",
    name: "Gemme d'épines parfaite",
    description:
      "+40% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de faire jaillir des épines empoisonnées devant toi.",
    gemFamily: "thornsWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.4 },
    reactiveEffect: {
      type: "thorns",
      kind: "ability",
      abilityId: "thorns",
      chance: 0.51,
      cooldownMs: 3000,
    },
    stackable: true,
    price: 1600,
  },
  sporesWardGemT2: {
    id: "sporesWardGemT2",
    category: "gem",
    name: "Gemme de spores polie",
    description:
      "+15% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de libérer un nuage de spores engourdissant les ennemis proches.",
    gemFamily: "sporesWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.15 },
    reactiveEffect: {
      type: "spores",
      kind: "ability",
      abilityId: "spores",
      chance: 0.35,
      cooldownMs: 6300,
    },
    stackable: true,
    price: 200,
  },
  sporesWardGemT3: {
    id: "sporesWardGemT3",
    category: "gem",
    name: "Gemme de spores pure",
    description:
      "+20% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de libérer un nuage de spores engourdissant les ennemis proches.",
    gemFamily: "sporesWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.2 },
    reactiveEffect: {
      type: "spores",
      kind: "ability",
      abilityId: "spores",
      chance: 0.39,
      cooldownMs: 5600,
    },
    stackable: true,
    price: 400,
  },
  sporesWardGemT4: {
    id: "sporesWardGemT4",
    category: "gem",
    name: "Gemme de spores radieuse",
    description:
      "+30% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de libérer un nuage de spores engourdissant les ennemis proches.",
    gemFamily: "sporesWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.3 },
    reactiveEffect: {
      type: "spores",
      kind: "ability",
      abilityId: "spores",
      chance: 0.45,
      cooldownMs: 4900,
    },
    stackable: true,
    price: 800,
  },
  sporesWardGemT5: {
    id: "sporesWardGemT5",
    category: "gem",
    name: "Gemme de spores parfaite",
    description:
      "+40% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de libérer un nuage de spores engourdissant les ennemis proches.",
    gemFamily: "sporesWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.4 },
    reactiveEffect: {
      type: "spores",
      kind: "ability",
      abilityId: "spores",
      chance: 0.51,
      cooldownMs: 4200,
    },
    stackable: true,
    price: 1600,
  },
  curseWardGemT2: {
    id: "curseWardGemT2",
    category: "gem",
    name: "Gemme de malédiction polie",
    description:
      "+15% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de maudire les ennemis proches et réduire leurs dégâts.",
    gemFamily: "curseWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.15 },
    reactiveEffect: {
      type: "curse",
      kind: "ability",
      abilityId: "curse",
      chance: 0.23,
      cooldownMs: 9900,
    },
    stackable: true,
    price: 240,
  },
  curseWardGemT3: {
    id: "curseWardGemT3",
    category: "gem",
    name: "Gemme de malédiction pure",
    description:
      "+20% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de maudire les ennemis proches et réduire leurs dégâts.",
    gemFamily: "curseWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.2 },
    reactiveEffect: {
      type: "curse",
      kind: "ability",
      abilityId: "curse",
      chance: 0.26,
      cooldownMs: 8800,
    },
    stackable: true,
    price: 480,
  },
  curseWardGemT4: {
    id: "curseWardGemT4",
    category: "gem",
    name: "Gemme de malédiction radieuse",
    description:
      "+30% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de maudire les ennemis proches et réduire leurs dégâts.",
    gemFamily: "curseWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.3 },
    reactiveEffect: {
      type: "curse",
      kind: "ability",
      abilityId: "curse",
      chance: 0.3,
      cooldownMs: 7700,
    },
    stackable: true,
    price: 960,
  },
  curseWardGemT5: {
    id: "curseWardGemT5",
    category: "gem",
    name: "Gemme de malédiction parfaite",
    description:
      "+40% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de maudire les ennemis proches et réduire leurs dégâts.",
    gemFamily: "curseWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.4 },
    reactiveEffect: {
      type: "curse",
      kind: "ability",
      abilityId: "curse",
      chance: 0.34,
      cooldownMs: 6600,
    },
    stackable: true,
    price: 1920,
  },
  riposteGemT2: {
    id: "riposteGemT2",
    category: "gem",
    name: "Gemme de riposte polie",
    description:
      "+15% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de renvoyer une partie des dégâts subis pendant un court instant.",
    gemFamily: "riposte",
    gemUsage: "combat",
    percentModifiers: { defense: 0.15 },
    reactiveEffect: {
      type: "riposte",
      kind: "ability",
      abilityId: "riposte",
      chance: 0.29,
      cooldownMs: 8100,
    },
    stackable: true,
    price: 240,
  },
  riposteGemT3: {
    id: "riposteGemT3",
    category: "gem",
    name: "Gemme de riposte pure",
    description:
      "+20% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de renvoyer une partie des dégâts subis pendant un court instant.",
    gemFamily: "riposte",
    gemUsage: "combat",
    percentModifiers: { defense: 0.2 },
    reactiveEffect: {
      type: "riposte",
      kind: "ability",
      abilityId: "riposte",
      chance: 0.33,
      cooldownMs: 7200,
    },
    stackable: true,
    price: 480,
  },
  riposteGemT4: {
    id: "riposteGemT4",
    category: "gem",
    name: "Gemme de riposte radieuse",
    description:
      "+30% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de renvoyer une partie des dégâts subis pendant un court instant.",
    gemFamily: "riposte",
    gemUsage: "combat",
    percentModifiers: { defense: 0.3 },
    reactiveEffect: {
      type: "riposte",
      kind: "ability",
      abilityId: "riposte",
      chance: 0.38,
      cooldownMs: 6300,
    },
    stackable: true,
    price: 960,
  },
  riposteGemT5: {
    id: "riposteGemT5",
    category: "gem",
    name: "Gemme de riposte parfaite",
    description:
      "+40% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de renvoyer une partie des dégâts subis pendant un court instant.",
    gemFamily: "riposte",
    gemUsage: "combat",
    percentModifiers: { defense: 0.4 },
    reactiveEffect: {
      type: "riposte",
      kind: "ability",
      abilityId: "riposte",
      chance: 0.43,
      cooldownMs: 5400,
    },
    stackable: true,
    price: 1920,
  },
  parryGemT2: {
    id: "parryGemT2",
    category: "gem",
    name: "Gemme de parade polie",
    description:
      "+15% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de réduire fortement les dégâts subis pendant un court instant.",
    gemFamily: "parry",
    gemUsage: "combat",
    percentModifiers: { defense: 0.15 },
    reactiveEffect: {
      type: "parry",
      kind: "ability",
      abilityId: "parry",
      chance: 0.29,
      cooldownMs: 6300,
    },
    stackable: true,
    price: 240,
  },
  parryGemT3: {
    id: "parryGemT3",
    category: "gem",
    name: "Gemme de parade pure",
    description:
      "+20% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de réduire fortement les dégâts subis pendant un court instant.",
    gemFamily: "parry",
    gemUsage: "combat",
    percentModifiers: { defense: 0.2 },
    reactiveEffect: {
      type: "parry",
      kind: "ability",
      abilityId: "parry",
      chance: 0.33,
      cooldownMs: 5600,
    },
    stackable: true,
    price: 480,
  },
  parryGemT4: {
    id: "parryGemT4",
    category: "gem",
    name: "Gemme de parade radieuse",
    description:
      "+30% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de réduire fortement les dégâts subis pendant un court instant.",
    gemFamily: "parry",
    gemUsage: "combat",
    percentModifiers: { defense: 0.3 },
    reactiveEffect: {
      type: "parry",
      kind: "ability",
      abilityId: "parry",
      chance: 0.38,
      cooldownMs: 4900,
    },
    stackable: true,
    price: 960,
  },
  parryGemT5: {
    id: "parryGemT5",
    category: "gem",
    name: "Gemme de parade parfaite",
    description:
      "+40% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de réduire fortement les dégâts subis pendant un court instant.",
    gemFamily: "parry",
    gemUsage: "combat",
    percentModifiers: { defense: 0.4 },
    reactiveEffect: {
      type: "parry",
      kind: "ability",
      abilityId: "parry",
      chance: 0.43,
      cooldownMs: 4200,
    },
    stackable: true,
    price: 1920,
  },
  freezeWardGemT2: {
    id: "freezeWardGemT2",
    category: "gem",
    name: "Gemme de gel polie",
    description:
      "+15% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de projeter un éclat de glace qui gèle les ennemis touchés.",
    gemFamily: "freezeWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.15 },
    reactiveEffect: {
      type: "freeze",
      kind: "ability",
      abilityId: "freeze",
      chance: 0.29,
      cooldownMs: 5400,
    },
    stackable: true,
    price: 220,
  },
  freezeWardGemT3: {
    id: "freezeWardGemT3",
    category: "gem",
    name: "Gemme de gel pure",
    description:
      "+20% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de projeter un éclat de glace qui gèle les ennemis touchés.",
    gemFamily: "freezeWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.2 },
    reactiveEffect: {
      type: "freeze",
      kind: "ability",
      abilityId: "freeze",
      chance: 0.33,
      cooldownMs: 4800,
    },
    stackable: true,
    price: 440,
  },
  freezeWardGemT4: {
    id: "freezeWardGemT4",
    category: "gem",
    name: "Gemme de gel radieuse",
    description:
      "+30% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de projeter un éclat de glace qui gèle les ennemis touchés.",
    gemFamily: "freezeWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.3 },
    reactiveEffect: {
      type: "freeze",
      kind: "ability",
      abilityId: "freeze",
      chance: 0.38,
      cooldownMs: 4200,
    },
    stackable: true,
    price: 880,
  },
  freezeWardGemT5: {
    id: "freezeWardGemT5",
    category: "gem",
    name: "Gemme de gel parfaite",
    description:
      "+40% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de projeter un éclat de glace qui gèle les ennemis touchés.",
    gemFamily: "freezeWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.4 },
    reactiveEffect: {
      type: "freeze",
      kind: "ability",
      abilityId: "freeze",
      chance: 0.43,
      cooldownMs: 3600,
    },
    stackable: true,
    price: 1760,
  },
  rootWardGemT2: {
    id: "rootWardGemT2",
    category: "gem",
    name: "Gemme de racines polie",
    description:
      "+15% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, d'enraciner tous les ennemis proches.",
    gemFamily: "rootWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.15 },
    reactiveEffect: {
      type: "root",
      kind: "ability",
      abilityId: "root",
      chance: 0.35,
      cooldownMs: 7200,
    },
    stackable: true,
    price: 200,
  },
  rootWardGemT3: {
    id: "rootWardGemT3",
    category: "gem",
    name: "Gemme de racines pure",
    description:
      "+20% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, d'enraciner tous les ennemis proches.",
    gemFamily: "rootWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.2 },
    reactiveEffect: {
      type: "root",
      kind: "ability",
      abilityId: "root",
      chance: 0.39,
      cooldownMs: 6400,
    },
    stackable: true,
    price: 400,
  },
  rootWardGemT4: {
    id: "rootWardGemT4",
    category: "gem",
    name: "Gemme de racines radieuse",
    description:
      "+30% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, d'enraciner tous les ennemis proches.",
    gemFamily: "rootWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.3 },
    reactiveEffect: {
      type: "root",
      kind: "ability",
      abilityId: "root",
      chance: 0.45,
      cooldownMs: 5600,
    },
    stackable: true,
    price: 800,
  },
  rootWardGemT5: {
    id: "rootWardGemT5",
    category: "gem",
    name: "Gemme de racines parfaite",
    description:
      "+40% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, d'enraciner tous les ennemis proches.",
    gemFamily: "rootWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.4 },
    reactiveEffect: {
      type: "root",
      kind: "ability",
      abilityId: "root",
      chance: 0.51,
      cooldownMs: 4800,
    },
    stackable: true,
    price: 1600,
  },
  shockwaveGemT2: {
    id: "shockwaveGemT2",
    category: "gem",
    name: "Gemme d'onde de choc polie",
    description:
      "+15% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de déclencher une onde de choc qui blesse et repousse les ennemis proches.",
    gemFamily: "shockwave",
    gemUsage: "combat",
    percentModifiers: { defense: 0.15 },
    reactiveEffect: {
      type: "shockwave",
      kind: "ability",
      abilityId: "shockwave",
      chance: 0.23,
      cooldownMs: 6300,
    },
    stackable: true,
    price: 260,
  },
  shockwaveGemT3: {
    id: "shockwaveGemT3",
    category: "gem",
    name: "Gemme d'onde de choc pure",
    description:
      "+20% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de déclencher une onde de choc qui blesse et repousse les ennemis proches.",
    gemFamily: "shockwave",
    gemUsage: "combat",
    percentModifiers: { defense: 0.2 },
    reactiveEffect: {
      type: "shockwave",
      kind: "ability",
      abilityId: "shockwave",
      chance: 0.26,
      cooldownMs: 5600,
    },
    stackable: true,
    price: 520,
  },
  shockwaveGemT4: {
    id: "shockwaveGemT4",
    category: "gem",
    name: "Gemme d'onde de choc radieuse",
    description:
      "+30% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de déclencher une onde de choc qui blesse et repousse les ennemis proches.",
    gemFamily: "shockwave",
    gemUsage: "combat",
    percentModifiers: { defense: 0.3 },
    reactiveEffect: {
      type: "shockwave",
      kind: "ability",
      abilityId: "shockwave",
      chance: 0.3,
      cooldownMs: 4900,
    },
    stackable: true,
    price: 1040,
  },
  shockwaveGemT5: {
    id: "shockwaveGemT5",
    category: "gem",
    name: "Gemme d'onde de choc parfaite",
    description:
      "+40% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de déclencher une onde de choc qui blesse et repousse les ennemis proches.",
    gemFamily: "shockwave",
    gemUsage: "combat",
    percentModifiers: { defense: 0.4 },
    reactiveEffect: {
      type: "shockwave",
      kind: "ability",
      abilityId: "shockwave",
      chance: 0.34,
      cooldownMs: 4200,
    },
    stackable: true,
    price: 2080,
  },

  /** === PALIERS SUPÉRIEURS (T2 à T5) des gemmes d'outil === **/
  miningSpeedGemT2: {
    id: "miningSpeedGemT2",
    category: "gem",
    name: "Gemme de célérité polie",
    description:
      "Une fois socketée sur un outil : chance qu'un coup de récolte n'entame pas le temps de récupération (récolte plus rapide).",
    gemFamily: "haste",
    gemUsage: "tool",
    toolEffect: { type: "fastHarvest", chance: 0.3 },
    stackable: true,
    price: 160,
  },
  miningSpeedGemT3: {
    id: "miningSpeedGemT3",
    category: "gem",
    name: "Gemme de célérité pure",
    description:
      "Une fois socketée sur un outil : chance qu'un coup de récolte n'entame pas le temps de récupération (récolte plus rapide).",
    gemFamily: "haste",
    gemUsage: "tool",
    toolEffect: { type: "fastHarvest", chance: 0.35 },
    stackable: true,
    price: 320,
  },
  miningSpeedGemT4: {
    id: "miningSpeedGemT4",
    category: "gem",
    name: "Gemme de célérité radieuse",
    description:
      "Une fois socketée sur un outil : chance qu'un coup de récolte n'entame pas le temps de récupération (récolte plus rapide).",
    gemFamily: "haste",
    gemUsage: "tool",
    toolEffect: { type: "fastHarvest", chance: 0.4 },
    stackable: true,
    price: 640,
  },
  miningSpeedGemT5: {
    id: "miningSpeedGemT5",
    category: "gem",
    name: "Gemme de célérité parfaite",
    description:
      "Une fois socketée sur un outil : chance qu'un coup de récolte n'entame pas le temps de récupération (récolte plus rapide).",
    gemFamily: "haste",
    gemUsage: "tool",
    toolEffect: { type: "fastHarvest", chance: 0.45 },
    stackable: true,
    price: 1280,
  },
  miningYieldGemT2: {
    id: "miningYieldGemT2",
    category: "gem",
    name: "Gemme d'abondance polie",
    description:
      "Une fois socketée sur un outil : chance d'obtenir une ressource supplémentaire en minant/récoltant.",
    gemFamily: "yield",
    gemUsage: "tool",
    toolEffect: { type: "bonusYield", chance: 0.3 },
    stackable: true,
    price: 160,
  },
  miningYieldGemT3: {
    id: "miningYieldGemT3",
    category: "gem",
    name: "Gemme d'abondance pure",
    description:
      "Une fois socketée sur un outil : chance d'obtenir une ressource supplémentaire en minant/récoltant.",
    gemFamily: "yield",
    gemUsage: "tool",
    toolEffect: { type: "bonusYield", chance: 0.35 },
    stackable: true,
    price: 320,
  },
  miningYieldGemT4: {
    id: "miningYieldGemT4",
    category: "gem",
    name: "Gemme d'abondance radieuse",
    description:
      "Une fois socketée sur un outil : chance d'obtenir une ressource supplémentaire en minant/récoltant.",
    gemFamily: "yield",
    gemUsage: "tool",
    toolEffect: { type: "bonusYield", chance: 0.4 },
    stackable: true,
    price: 640,
  },
  miningYieldGemT5: {
    id: "miningYieldGemT5",
    category: "gem",
    name: "Gemme d'abondance parfaite",
    description:
      "Une fois socketée sur un outil : chance d'obtenir une ressource supplémentaire en minant/récoltant.",
    gemFamily: "yield",
    gemUsage: "tool",
    toolEffect: { type: "bonusYield", chance: 0.45 },
    stackable: true,
    price: 1280,
  },
  miningLuckGemT2: {
    id: "miningLuckGemT2",
    category: "gem",
    name: "Gemme de fortune polie",
    description:
      "Une fois socketée sur un outil : augmente la chance de trouvaille rare en minant/récoltant.",
    gemFamily: "luck",
    gemUsage: "tool",
    toolEffect: { type: "rareLuck", bonusChance: 0.2 },
    stackable: true,
    price: 200,
  },
  miningLuckGemT3: {
    id: "miningLuckGemT3",
    category: "gem",
    name: "Gemme de fortune pure",
    description:
      "Une fois socketée sur un outil : augmente la chance de trouvaille rare en minant/récoltant.",
    gemFamily: "luck",
    gemUsage: "tool",
    toolEffect: { type: "rareLuck", bonusChance: 0.25 },
    stackable: true,
    price: 400,
  },
  miningLuckGemT4: {
    id: "miningLuckGemT4",
    category: "gem",
    name: "Gemme de fortune radieuse",
    description:
      "Une fois socketée sur un outil : augmente la chance de trouvaille rare en minant/récoltant.",
    gemFamily: "luck",
    gemUsage: "tool",
    toolEffect: { type: "rareLuck", bonusChance: 0.3 },
    stackable: true,
    price: 800,
  },
  miningLuckGemT5: {
    id: "miningLuckGemT5",
    category: "gem",
    name: "Gemme de fortune parfaite",
    description:
      "Une fois socketée sur un outil : augmente la chance de trouvaille rare en minant/récoltant.",
    gemFamily: "luck",
    gemUsage: "tool",
    toolEffect: { type: "rareLuck", bonusChance: 0.35 },
    stackable: true,
    price: 1600,
  },
  frostNovaGem: {
    id: "frostNovaGem",
    category: "gem",
    name: "Gemme de nova glaciale",
    description:
      "+10% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de geler l'air autour de toi et de ralentir les ennemis proches.",
    gemFamily: "frostNovaWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.1 },
    reactiveEffect: {
      type: "frostNova",
      kind: "ability",
      abilityId: "frostNova",
      chance: 0.25,
      cooldownMs: 9000,
    },
    stackable: true,
    price: 120,
  },
  frostNovaGemT2: {
    id: "frostNovaGemT2",
    category: "gem",
    name: "Gemme de nova glaciale polie",
    description:
      "+15% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de geler l'air autour de toi et de ralentir les ennemis proches.",
    gemFamily: "frostNovaWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.15 },
    reactiveEffect: {
      type: "frostNova",
      kind: "ability",
      abilityId: "frostNova",
      chance: 0.29,
      cooldownMs: 8100,
    },
    stackable: true,
    price: 240,
  },
  frostNovaGemT3: {
    id: "frostNovaGemT3",
    category: "gem",
    name: "Gemme de nova glaciale pure",
    description:
      "+20% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de geler l'air autour de toi et de ralentir les ennemis proches.",
    gemFamily: "frostNovaWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.2 },
    reactiveEffect: {
      type: "frostNova",
      kind: "ability",
      abilityId: "frostNova",
      chance: 0.33,
      cooldownMs: 7200,
    },
    stackable: true,
    price: 480,
  },
  frostNovaGemT4: {
    id: "frostNovaGemT4",
    category: "gem",
    name: "Gemme de nova glaciale radieuse",
    description:
      "+30% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de geler l'air autour de toi et de ralentir les ennemis proches.",
    gemFamily: "frostNovaWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.3 },
    reactiveEffect: {
      type: "frostNova",
      kind: "ability",
      abilityId: "frostNova",
      chance: 0.38,
      cooldownMs: 6300,
    },
    stackable: true,
    price: 960,
  },
  frostNovaGemT5: {
    id: "frostNovaGemT5",
    category: "gem",
    name: "Gemme de nova glaciale parfaite",
    description:
      "+40% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de geler l'air autour de toi et de ralentir les ennemis proches.",
    gemFamily: "frostNovaWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.4 },
    reactiveEffect: {
      type: "frostNova",
      kind: "ability",
      abilityId: "frostNova",
      chance: 0.43,
      cooldownMs: 5400,
    },
    stackable: true,
    price: 1920,
  },
  icePrisonGem: {
    id: "icePrisonGem",
    category: "gem",
    name: "Gemme de prison de glace",
    description:
      "+10% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, d'emprisonner les ennemis proches dans la glace.",
    gemFamily: "icePrisonWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.1 },
    reactiveEffect: {
      type: "icePrison",
      kind: "ability",
      abilityId: "icePrison",
      chance: 0.2,
      cooldownMs: 12000,
    },
    stackable: true,
    price: 130,
  },
  icePrisonGemT2: {
    id: "icePrisonGemT2",
    category: "gem",
    name: "Gemme de prison de glace polie",
    description:
      "+15% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, d'emprisonner les ennemis proches dans la glace.",
    gemFamily: "icePrisonWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.15 },
    reactiveEffect: {
      type: "icePrison",
      kind: "ability",
      abilityId: "icePrison",
      chance: 0.23,
      cooldownMs: 10800,
    },
    stackable: true,
    price: 260,
  },
  icePrisonGemT3: {
    id: "icePrisonGemT3",
    category: "gem",
    name: "Gemme de prison de glace pure",
    description:
      "+20% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, d'emprisonner les ennemis proches dans la glace.",
    gemFamily: "icePrisonWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.2 },
    reactiveEffect: {
      type: "icePrison",
      kind: "ability",
      abilityId: "icePrison",
      chance: 0.26,
      cooldownMs: 9600,
    },
    stackable: true,
    price: 520,
  },
  icePrisonGemT4: {
    id: "icePrisonGemT4",
    category: "gem",
    name: "Gemme de prison de glace radieuse",
    description:
      "+30% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, d'emprisonner les ennemis proches dans la glace.",
    gemFamily: "icePrisonWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.3 },
    reactiveEffect: {
      type: "icePrison",
      kind: "ability",
      abilityId: "icePrison",
      chance: 0.3,
      cooldownMs: 8400,
    },
    stackable: true,
    price: 1040,
  },
  icePrisonGemT5: {
    id: "icePrisonGemT5",
    category: "gem",
    name: "Gemme de prison de glace parfaite",
    description:
      "+40% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, d'emprisonner les ennemis proches dans la glace.",
    gemFamily: "icePrisonWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.4 },
    reactiveEffect: {
      type: "icePrison",
      kind: "ability",
      abilityId: "icePrison",
      chance: 0.34,
      cooldownMs: 7200,
    },
    stackable: true,
    price: 2080,
  },
  fireNovaGem: {
    id: "fireNovaGem",
    category: "gem",
    name: "Gemme de nova ardente",
    description:
      "+10% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de libérer une nova de feu qui brûle les ennemis proches.",
    gemFamily: "fireNovaWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.1 },
    reactiveEffect: {
      type: "fireNova",
      kind: "ability",
      abilityId: "fireNova",
      chance: 0.2,
      cooldownMs: 10000,
    },
    stackable: true,
    price: 130,
  },
  fireNovaGemT2: {
    id: "fireNovaGemT2",
    category: "gem",
    name: "Gemme de nova ardente polie",
    description:
      "+15% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de libérer une nova de feu qui brûle les ennemis proches.",
    gemFamily: "fireNovaWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.15 },
    reactiveEffect: {
      type: "fireNova",
      kind: "ability",
      abilityId: "fireNova",
      chance: 0.23,
      cooldownMs: 9000,
    },
    stackable: true,
    price: 260,
  },
  fireNovaGemT3: {
    id: "fireNovaGemT3",
    category: "gem",
    name: "Gemme de nova ardente pure",
    description:
      "+20% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de libérer une nova de feu qui brûle les ennemis proches.",
    gemFamily: "fireNovaWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.2 },
    reactiveEffect: {
      type: "fireNova",
      kind: "ability",
      abilityId: "fireNova",
      chance: 0.26,
      cooldownMs: 8000,
    },
    stackable: true,
    price: 520,
  },
  fireNovaGemT4: {
    id: "fireNovaGemT4",
    category: "gem",
    name: "Gemme de nova ardente radieuse",
    description:
      "+30% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de libérer une nova de feu qui brûle les ennemis proches.",
    gemFamily: "fireNovaWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.3 },
    reactiveEffect: {
      type: "fireNova",
      kind: "ability",
      abilityId: "fireNova",
      chance: 0.3,
      cooldownMs: 7000,
    },
    stackable: true,
    price: 1040,
  },
  fireNovaGemT5: {
    id: "fireNovaGemT5",
    category: "gem",
    name: "Gemme de nova ardente parfaite",
    description:
      "+40% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de libérer une nova de feu qui brûle les ennemis proches.",
    gemFamily: "fireNovaWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.4 },
    reactiveEffect: {
      type: "fireNova",
      kind: "ability",
      abilityId: "fireNova",
      chance: 0.34,
      cooldownMs: 6000,
    },
    stackable: true,
    price: 2080,
  },
  whirlwindGem: {
    id: "whirlwindGem",
    category: "gem",
    name: "Gemme de tourbillon",
    description:
      "+10% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de tournoyer et de frapper tous les ennemis proches.",
    gemFamily: "whirlwindWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.1 },
    reactiveEffect: {
      type: "whirlwind",
      kind: "ability",
      abilityId: "whirlwind",
      chance: 0.25,
      cooldownMs: 8000,
    },
    stackable: true,
    price: 120,
  },
  whirlwindGemT2: {
    id: "whirlwindGemT2",
    category: "gem",
    name: "Gemme de tourbillon polie",
    description:
      "+15% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de tournoyer et de frapper tous les ennemis proches.",
    gemFamily: "whirlwindWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.15 },
    reactiveEffect: {
      type: "whirlwind",
      kind: "ability",
      abilityId: "whirlwind",
      chance: 0.29,
      cooldownMs: 7200,
    },
    stackable: true,
    price: 240,
  },
  whirlwindGemT3: {
    id: "whirlwindGemT3",
    category: "gem",
    name: "Gemme de tourbillon pure",
    description:
      "+20% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de tournoyer et de frapper tous les ennemis proches.",
    gemFamily: "whirlwindWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.2 },
    reactiveEffect: {
      type: "whirlwind",
      kind: "ability",
      abilityId: "whirlwind",
      chance: 0.33,
      cooldownMs: 6400,
    },
    stackable: true,
    price: 480,
  },
  whirlwindGemT4: {
    id: "whirlwindGemT4",
    category: "gem",
    name: "Gemme de tourbillon radieuse",
    description:
      "+30% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de tournoyer et de frapper tous les ennemis proches.",
    gemFamily: "whirlwindWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.3 },
    reactiveEffect: {
      type: "whirlwind",
      kind: "ability",
      abilityId: "whirlwind",
      chance: 0.38,
      cooldownMs: 5600,
    },
    stackable: true,
    price: 960,
  },
  whirlwindGemT5: {
    id: "whirlwindGemT5",
    category: "gem",
    name: "Gemme de tourbillon parfaite",
    description:
      "+40% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de tournoyer et de frapper tous les ennemis proches.",
    gemFamily: "whirlwindWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.4 },
    reactiveEffect: {
      type: "whirlwind",
      kind: "ability",
      abilityId: "whirlwind",
      chance: 0.43,
      cooldownMs: 4800,
    },
    stackable: true,
    price: 1920,
  },
  smiteGem: {
    id: "smiteGem",
    category: "gem",
    name: "Gemme de châtiment",
    description:
      "+10% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, d'infliger un châtiment sacré aux ennemis proches.",
    gemFamily: "smiteWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.1 },
    reactiveEffect: {
      type: "smite",
      kind: "ability",
      abilityId: "smite",
      chance: 0.2,
      cooldownMs: 11000,
    },
    stackable: true,
    price: 130,
  },
  smiteGemT2: {
    id: "smiteGemT2",
    category: "gem",
    name: "Gemme de châtiment polie",
    description:
      "+15% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, d'infliger un châtiment sacré aux ennemis proches.",
    gemFamily: "smiteWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.15 },
    reactiveEffect: {
      type: "smite",
      kind: "ability",
      abilityId: "smite",
      chance: 0.23,
      cooldownMs: 9900,
    },
    stackable: true,
    price: 260,
  },
  smiteGemT3: {
    id: "smiteGemT3",
    category: "gem",
    name: "Gemme de châtiment pure",
    description:
      "+20% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, d'infliger un châtiment sacré aux ennemis proches.",
    gemFamily: "smiteWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.2 },
    reactiveEffect: {
      type: "smite",
      kind: "ability",
      abilityId: "smite",
      chance: 0.26,
      cooldownMs: 8800,
    },
    stackable: true,
    price: 520,
  },
  smiteGemT4: {
    id: "smiteGemT4",
    category: "gem",
    name: "Gemme de châtiment radieuse",
    description:
      "+30% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, d'infliger un châtiment sacré aux ennemis proches.",
    gemFamily: "smiteWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.3 },
    reactiveEffect: {
      type: "smite",
      kind: "ability",
      abilityId: "smite",
      chance: 0.3,
      cooldownMs: 7700,
    },
    stackable: true,
    price: 1040,
  },
  smiteGemT5: {
    id: "smiteGemT5",
    category: "gem",
    name: "Gemme de châtiment parfaite",
    description:
      "+40% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, d'infliger un châtiment sacré aux ennemis proches.",
    gemFamily: "smiteWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.4 },
    reactiveEffect: {
      type: "smite",
      kind: "ability",
      abilityId: "smite",
      chance: 0.34,
      cooldownMs: 6600,
    },
    stackable: true,
    price: 2080,
  },
  arcaneBurstGem: {
    id: "arcaneBurstGem",
    category: "gem",
    name: "Gemme d'explosion arcanique",
    description:
      "+10% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de déclencher une explosion arcanique autour de toi.",
    gemFamily: "arcaneBurstWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.1 },
    reactiveEffect: {
      type: "arcaneExplosion",
      kind: "ability",
      abilityId: "arcaneExplosion",
      chance: 0.2,
      cooldownMs: 11000,
    },
    stackable: true,
    price: 130,
  },
  arcaneBurstGemT2: {
    id: "arcaneBurstGemT2",
    category: "gem",
    name: "Gemme d'explosion arcanique polie",
    description:
      "+15% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de déclencher une explosion arcanique autour de toi.",
    gemFamily: "arcaneBurstWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.15 },
    reactiveEffect: {
      type: "arcaneExplosion",
      kind: "ability",
      abilityId: "arcaneExplosion",
      chance: 0.23,
      cooldownMs: 9900,
    },
    stackable: true,
    price: 260,
  },
  arcaneBurstGemT3: {
    id: "arcaneBurstGemT3",
    category: "gem",
    name: "Gemme d'explosion arcanique pure",
    description:
      "+20% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de déclencher une explosion arcanique autour de toi.",
    gemFamily: "arcaneBurstWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.2 },
    reactiveEffect: {
      type: "arcaneExplosion",
      kind: "ability",
      abilityId: "arcaneExplosion",
      chance: 0.26,
      cooldownMs: 8800,
    },
    stackable: true,
    price: 520,
  },
  arcaneBurstGemT4: {
    id: "arcaneBurstGemT4",
    category: "gem",
    name: "Gemme d'explosion arcanique radieuse",
    description:
      "+30% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de déclencher une explosion arcanique autour de toi.",
    gemFamily: "arcaneBurstWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.3 },
    reactiveEffect: {
      type: "arcaneExplosion",
      kind: "ability",
      abilityId: "arcaneExplosion",
      chance: 0.3,
      cooldownMs: 7700,
    },
    stackable: true,
    price: 1040,
  },
  arcaneBurstGemT5: {
    id: "arcaneBurstGemT5",
    category: "gem",
    name: "Gemme d'explosion arcanique parfaite",
    description:
      "+40% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de déclencher une explosion arcanique autour de toi.",
    gemFamily: "arcaneBurstWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.4 },
    reactiveEffect: {
      type: "arcaneExplosion",
      kind: "ability",
      abilityId: "arcaneExplosion",
      chance: 0.34,
      cooldownMs: 6600,
    },
    stackable: true,
    price: 2080,
  },
  chainLightningGem: {
    id: "chainLightningGem",
    category: "gem",
    name: "Gemme d'éclair en chaîne",
    description:
      "+10% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de déchaîner un éclair qui rebondit d'ennemi en ennemi.",
    gemFamily: "chainLightningWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.1 },
    reactiveEffect: {
      type: "chainLightningSpell",
      kind: "ability",
      abilityId: "chainLightningSpell",
      chance: 0.2,
      cooldownMs: 10000,
    },
    stackable: true,
    price: 140,
  },
  chainLightningGemT2: {
    id: "chainLightningGemT2",
    category: "gem",
    name: "Gemme d'éclair en chaîne polie",
    description:
      "+15% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de déchaîner un éclair qui rebondit d'ennemi en ennemi.",
    gemFamily: "chainLightningWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.15 },
    reactiveEffect: {
      type: "chainLightningSpell",
      kind: "ability",
      abilityId: "chainLightningSpell",
      chance: 0.23,
      cooldownMs: 9000,
    },
    stackable: true,
    price: 280,
  },
  chainLightningGemT3: {
    id: "chainLightningGemT3",
    category: "gem",
    name: "Gemme d'éclair en chaîne pure",
    description:
      "+20% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de déchaîner un éclair qui rebondit d'ennemi en ennemi.",
    gemFamily: "chainLightningWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.2 },
    reactiveEffect: {
      type: "chainLightningSpell",
      kind: "ability",
      abilityId: "chainLightningSpell",
      chance: 0.26,
      cooldownMs: 8000,
    },
    stackable: true,
    price: 560,
  },
  chainLightningGemT4: {
    id: "chainLightningGemT4",
    category: "gem",
    name: "Gemme d'éclair en chaîne radieuse",
    description:
      "+30% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de déchaîner un éclair qui rebondit d'ennemi en ennemi.",
    gemFamily: "chainLightningWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.3 },
    reactiveEffect: {
      type: "chainLightningSpell",
      kind: "ability",
      abilityId: "chainLightningSpell",
      chance: 0.3,
      cooldownMs: 7000,
    },
    stackable: true,
    price: 1120,
  },
  chainLightningGemT5: {
    id: "chainLightningGemT5",
    category: "gem",
    name: "Gemme d'éclair en chaîne parfaite",
    description:
      "+40% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de déchaîner un éclair qui rebondit d'ennemi en ennemi.",
    gemFamily: "chainLightningWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.4 },
    reactiveEffect: {
      type: "chainLightningSpell",
      kind: "ability",
      abilityId: "chainLightningSpell",
      chance: 0.34,
      cooldownMs: 6000,
    },
    stackable: true,
    price: 2240,
  },
  vortexGem: {
    id: "vortexGem",
    category: "gem",
    name: "Gemme de vortex",
    description:
      "+10% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de créer un vortex qui attire et blesse les ennemis.",
    gemFamily: "vortexWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.1 },
    reactiveEffect: {
      type: "vortex",
      kind: "ability",
      abilityId: "vortex",
      chance: 0.2,
      cooldownMs: 12000,
    },
    stackable: true,
    price: 140,
  },
  vortexGemT2: {
    id: "vortexGemT2",
    category: "gem",
    name: "Gemme de vortex polie",
    description:
      "+15% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de créer un vortex qui attire et blesse les ennemis.",
    gemFamily: "vortexWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.15 },
    reactiveEffect: {
      type: "vortex",
      kind: "ability",
      abilityId: "vortex",
      chance: 0.23,
      cooldownMs: 10800,
    },
    stackable: true,
    price: 280,
  },
  vortexGemT3: {
    id: "vortexGemT3",
    category: "gem",
    name: "Gemme de vortex pure",
    description:
      "+20% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de créer un vortex qui attire et blesse les ennemis.",
    gemFamily: "vortexWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.2 },
    reactiveEffect: {
      type: "vortex",
      kind: "ability",
      abilityId: "vortex",
      chance: 0.26,
      cooldownMs: 9600,
    },
    stackable: true,
    price: 560,
  },
  vortexGemT4: {
    id: "vortexGemT4",
    category: "gem",
    name: "Gemme de vortex radieuse",
    description:
      "+30% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de créer un vortex qui attire et blesse les ennemis.",
    gemFamily: "vortexWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.3 },
    reactiveEffect: {
      type: "vortex",
      kind: "ability",
      abilityId: "vortex",
      chance: 0.3,
      cooldownMs: 8400,
    },
    stackable: true,
    price: 1120,
  },
  vortexGemT5: {
    id: "vortexGemT5",
    category: "gem",
    name: "Gemme de vortex parfaite",
    description:
      "+40% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de créer un vortex qui attire et blesse les ennemis.",
    gemFamily: "vortexWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.4 },
    reactiveEffect: {
      type: "vortex",
      kind: "ability",
      abilityId: "vortex",
      chance: 0.34,
      cooldownMs: 7200,
    },
    stackable: true,
    price: 2240,
  },
  quakeGem: {
    id: "quakeGem",
    category: "gem",
    name: "Gemme de séisme",
    description:
      "+10% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de provoquer un séisme qui étourdit les ennemis proches.",
    gemFamily: "quakeWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.1 },
    reactiveEffect: {
      type: "localizedQuake",
      kind: "ability",
      abilityId: "localizedQuake",
      chance: 0.2,
      cooldownMs: 12000,
    },
    stackable: true,
    price: 130,
  },
  quakeGemT2: {
    id: "quakeGemT2",
    category: "gem",
    name: "Gemme de séisme polie",
    description:
      "+15% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de provoquer un séisme qui étourdit les ennemis proches.",
    gemFamily: "quakeWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.15 },
    reactiveEffect: {
      type: "localizedQuake",
      kind: "ability",
      abilityId: "localizedQuake",
      chance: 0.23,
      cooldownMs: 10800,
    },
    stackable: true,
    price: 260,
  },
  quakeGemT3: {
    id: "quakeGemT3",
    category: "gem",
    name: "Gemme de séisme pure",
    description:
      "+20% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de provoquer un séisme qui étourdit les ennemis proches.",
    gemFamily: "quakeWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.2 },
    reactiveEffect: {
      type: "localizedQuake",
      kind: "ability",
      abilityId: "localizedQuake",
      chance: 0.26,
      cooldownMs: 9600,
    },
    stackable: true,
    price: 520,
  },
  quakeGemT4: {
    id: "quakeGemT4",
    category: "gem",
    name: "Gemme de séisme radieuse",
    description:
      "+30% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de provoquer un séisme qui étourdit les ennemis proches.",
    gemFamily: "quakeWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.3 },
    reactiveEffect: {
      type: "localizedQuake",
      kind: "ability",
      abilityId: "localizedQuake",
      chance: 0.3,
      cooldownMs: 8400,
    },
    stackable: true,
    price: 1040,
  },
  quakeGemT5: {
    id: "quakeGemT5",
    category: "gem",
    name: "Gemme de séisme parfaite",
    description:
      "+40% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de provoquer un séisme qui étourdit les ennemis proches.",
    gemFamily: "quakeWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.4 },
    reactiveEffect: {
      type: "localizedQuake",
      kind: "ability",
      abilityId: "localizedQuake",
      chance: 0.34,
      cooldownMs: 7200,
    },
    stackable: true,
    price: 2080,
  },
  blizzardGem: {
    id: "blizzardGem",
    category: "gem",
    name: "Gemme de blizzard",
    description:
      "+10% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de faire se lever un blizzard qui gèle la zone autour de toi.",
    gemFamily: "blizzardWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.1 },
    reactiveEffect: {
      type: "blizzard",
      kind: "ability",
      abilityId: "blizzard",
      chance: 0.15,
      cooldownMs: 15000,
    },
    stackable: true,
    price: 150,
  },
  blizzardGemT2: {
    id: "blizzardGemT2",
    category: "gem",
    name: "Gemme de blizzard polie",
    description:
      "+15% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de faire se lever un blizzard qui gèle la zone autour de toi.",
    gemFamily: "blizzardWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.15 },
    reactiveEffect: {
      type: "blizzard",
      kind: "ability",
      abilityId: "blizzard",
      chance: 0.17,
      cooldownMs: 13500,
    },
    stackable: true,
    price: 300,
  },
  blizzardGemT3: {
    id: "blizzardGemT3",
    category: "gem",
    name: "Gemme de blizzard pure",
    description:
      "+20% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de faire se lever un blizzard qui gèle la zone autour de toi.",
    gemFamily: "blizzardWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.2 },
    reactiveEffect: {
      type: "blizzard",
      kind: "ability",
      abilityId: "blizzard",
      chance: 0.2,
      cooldownMs: 12000,
    },
    stackable: true,
    price: 600,
  },
  blizzardGemT4: {
    id: "blizzardGemT4",
    category: "gem",
    name: "Gemme de blizzard radieuse",
    description:
      "+30% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de faire se lever un blizzard qui gèle la zone autour de toi.",
    gemFamily: "blizzardWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.3 },
    reactiveEffect: {
      type: "blizzard",
      kind: "ability",
      abilityId: "blizzard",
      chance: 0.22,
      cooldownMs: 10500,
    },
    stackable: true,
    price: 1200,
  },
  blizzardGemT5: {
    id: "blizzardGemT5",
    category: "gem",
    name: "Gemme de blizzard parfaite",
    description:
      "+40% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de faire se lever un blizzard qui gèle la zone autour de toi.",
    gemFamily: "blizzardWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.4 },
    reactiveEffect: {
      type: "blizzard",
      kind: "ability",
      abilityId: "blizzard",
      chance: 0.26,
      cooldownMs: 9000,
    },
    stackable: true,
    price: 2400,
  },
  firePoolGem: {
    id: "firePoolGem",
    category: "gem",
    name: "Gemme de flaque ardente",
    description:
      "+10% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de faire jaillir une flaque de feu qui brûle les ennemis.",
    gemFamily: "firePoolWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.1 },
    reactiveEffect: {
      type: "firePool",
      kind: "ability",
      abilityId: "firePool",
      chance: 0.2,
      cooldownMs: 11000,
    },
    stackable: true,
    price: 130,
  },
  firePoolGemT2: {
    id: "firePoolGemT2",
    category: "gem",
    name: "Gemme de flaque ardente polie",
    description:
      "+15% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de faire jaillir une flaque de feu qui brûle les ennemis.",
    gemFamily: "firePoolWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.15 },
    reactiveEffect: {
      type: "firePool",
      kind: "ability",
      abilityId: "firePool",
      chance: 0.23,
      cooldownMs: 9900,
    },
    stackable: true,
    price: 260,
  },
  firePoolGemT3: {
    id: "firePoolGemT3",
    category: "gem",
    name: "Gemme de flaque ardente pure",
    description:
      "+20% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de faire jaillir une flaque de feu qui brûle les ennemis.",
    gemFamily: "firePoolWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.2 },
    reactiveEffect: {
      type: "firePool",
      kind: "ability",
      abilityId: "firePool",
      chance: 0.26,
      cooldownMs: 8800,
    },
    stackable: true,
    price: 520,
  },
  firePoolGemT4: {
    id: "firePoolGemT4",
    category: "gem",
    name: "Gemme de flaque ardente radieuse",
    description:
      "+30% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de faire jaillir une flaque de feu qui brûle les ennemis.",
    gemFamily: "firePoolWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.3 },
    reactiveEffect: {
      type: "firePool",
      kind: "ability",
      abilityId: "firePool",
      chance: 0.3,
      cooldownMs: 7700,
    },
    stackable: true,
    price: 1040,
  },
  firePoolGemT5: {
    id: "firePoolGemT5",
    category: "gem",
    name: "Gemme de flaque ardente parfaite",
    description:
      "+40% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de faire jaillir une flaque de feu qui brûle les ennemis.",
    gemFamily: "firePoolWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.4 },
    reactiveEffect: {
      type: "firePool",
      kind: "ability",
      abilityId: "firePool",
      chance: 0.34,
      cooldownMs: 6600,
    },
    stackable: true,
    price: 2080,
  },
  shortDashGem: {
    id: "shortDashGem",
    category: "gem",
    name: "Gemme d'esquive",
    description:
      "+10% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de te téléporter sur une courte distance dans ta direction.",
    gemFamily: "dashWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.1 },
    reactiveEffect: {
      type: "shortDash",
      kind: "ability",
      abilityId: "shortDash",
      chance: 0.25,
      cooldownMs: 8000,
    },
    stackable: true,
    price: 100,
  },
  shortDashGemT2: {
    id: "shortDashGemT2",
    category: "gem",
    name: "Gemme d'esquive polie",
    description:
      "+15% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de te téléporter sur une courte distance dans ta direction.",
    gemFamily: "dashWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.15 },
    reactiveEffect: {
      type: "shortDash",
      kind: "ability",
      abilityId: "shortDash",
      chance: 0.29,
      cooldownMs: 7200,
    },
    stackable: true,
    price: 200,
  },
  shortDashGemT3: {
    id: "shortDashGemT3",
    category: "gem",
    name: "Gemme d'esquive pure",
    description:
      "+20% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de te téléporter sur une courte distance dans ta direction.",
    gemFamily: "dashWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.2 },
    reactiveEffect: {
      type: "shortDash",
      kind: "ability",
      abilityId: "shortDash",
      chance: 0.33,
      cooldownMs: 6400,
    },
    stackable: true,
    price: 400,
  },
  shortDashGemT4: {
    id: "shortDashGemT4",
    category: "gem",
    name: "Gemme d'esquive radieuse",
    description:
      "+30% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de te téléporter sur une courte distance dans ta direction.",
    gemFamily: "dashWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.3 },
    reactiveEffect: {
      type: "shortDash",
      kind: "ability",
      abilityId: "shortDash",
      chance: 0.38,
      cooldownMs: 5600,
    },
    stackable: true,
    price: 800,
  },
  shortDashGemT5: {
    id: "shortDashGemT5",
    category: "gem",
    name: "Gemme d'esquive parfaite",
    description:
      "+40% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de te téléporter sur une courte distance dans ta direction.",
    gemFamily: "dashWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.4 },
    reactiveEffect: {
      type: "shortDash",
      kind: "ability",
      abilityId: "shortDash",
      chance: 0.43,
      cooldownMs: 4800,
    },
    stackable: true,
    price: 1600,
  },
  stealthGem: {
    id: "stealthGem",
    category: "gem",
    name: "Gemme d'ombre",
    description:
      "+10% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de disparaître dans l'ombre (furtivité temporaire).",
    gemFamily: "stealthWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.1 },
    reactiveEffect: {
      type: "stealth",
      kind: "ability",
      abilityId: "stealth",
      chance: 0.2,
      cooldownMs: 14000,
    },
    stackable: true,
    price: 130,
  },
  stealthGemT2: {
    id: "stealthGemT2",
    category: "gem",
    name: "Gemme d'ombre polie",
    description:
      "+15% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de disparaître dans l'ombre (furtivité temporaire).",
    gemFamily: "stealthWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.15 },
    reactiveEffect: {
      type: "stealth",
      kind: "ability",
      abilityId: "stealth",
      chance: 0.23,
      cooldownMs: 12600,
    },
    stackable: true,
    price: 260,
  },
  stealthGemT3: {
    id: "stealthGemT3",
    category: "gem",
    name: "Gemme d'ombre pure",
    description:
      "+20% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de disparaître dans l'ombre (furtivité temporaire).",
    gemFamily: "stealthWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.2 },
    reactiveEffect: {
      type: "stealth",
      kind: "ability",
      abilityId: "stealth",
      chance: 0.26,
      cooldownMs: 11200,
    },
    stackable: true,
    price: 520,
  },
  stealthGemT4: {
    id: "stealthGemT4",
    category: "gem",
    name: "Gemme d'ombre radieuse",
    description:
      "+30% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de disparaître dans l'ombre (furtivité temporaire).",
    gemFamily: "stealthWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.3 },
    reactiveEffect: {
      type: "stealth",
      kind: "ability",
      abilityId: "stealth",
      chance: 0.3,
      cooldownMs: 9800,
    },
    stackable: true,
    price: 1040,
  },
  stealthGemT5: {
    id: "stealthGemT5",
    category: "gem",
    name: "Gemme d'ombre parfaite",
    description:
      "+40% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de disparaître dans l'ombre (furtivité temporaire).",
    gemFamily: "stealthWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.4 },
    reactiveEffect: {
      type: "stealth",
      kind: "ability",
      abilityId: "stealth",
      chance: 0.34,
      cooldownMs: 8400,
    },
    stackable: true,
    price: 2080,
  },
  manaShieldGem: {
    id: "manaShieldGem",
    category: "gem",
    name: "Gemme d'égide",
    description:
      "+10% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de dresser une armure de mana qui renforce ta défense.",
    gemFamily: "manaShieldWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.1 },
    reactiveEffect: {
      type: "manaShield",
      kind: "ability",
      abilityId: "manaShield",
      chance: 0.25,
      cooldownMs: 10000,
    },
    stackable: true,
    price: 120,
  },
  manaShieldGemT2: {
    id: "manaShieldGemT2",
    category: "gem",
    name: "Gemme d'égide polie",
    description:
      "+15% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de dresser une armure de mana qui renforce ta défense.",
    gemFamily: "manaShieldWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.15 },
    reactiveEffect: {
      type: "manaShield",
      kind: "ability",
      abilityId: "manaShield",
      chance: 0.29,
      cooldownMs: 9000,
    },
    stackable: true,
    price: 240,
  },
  manaShieldGemT3: {
    id: "manaShieldGemT3",
    category: "gem",
    name: "Gemme d'égide pure",
    description:
      "+20% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de dresser une armure de mana qui renforce ta défense.",
    gemFamily: "manaShieldWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.2 },
    reactiveEffect: {
      type: "manaShield",
      kind: "ability",
      abilityId: "manaShield",
      chance: 0.33,
      cooldownMs: 8000,
    },
    stackable: true,
    price: 480,
  },
  manaShieldGemT4: {
    id: "manaShieldGemT4",
    category: "gem",
    name: "Gemme d'égide radieuse",
    description:
      "+30% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de dresser une armure de mana qui renforce ta défense.",
    gemFamily: "manaShieldWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.3 },
    reactiveEffect: {
      type: "manaShield",
      kind: "ability",
      abilityId: "manaShield",
      chance: 0.38,
      cooldownMs: 7000,
    },
    stackable: true,
    price: 960,
  },
  manaShieldGemT5: {
    id: "manaShieldGemT5",
    category: "gem",
    name: "Gemme d'égide parfaite",
    description:
      "+40% de la défense de base de l'objet une fois socketée. Chance, en encaissant un coup, de dresser une armure de mana qui renforce ta défense.",
    gemFamily: "manaShieldWard",
    gemUsage: "combat",
    percentModifiers: { defense: 0.4 },
    reactiveEffect: {
      type: "manaShield",
      kind: "ability",
      abilityId: "manaShield",
      chance: 0.43,
      cooldownMs: 6000,
    },
    stackable: true,
    price: 1920,
  },

  /** Parchemins d'amélioration **/
  socketPerforationScroll: {
    id: "socketPerforationScroll",
    category: "socketPerforation",
    name: "Parchemin de perforation",
    description:
      "Tente d'ajouter un socket supplémentaire à un équipement (jusqu'au maximum de son palier). Consomme aussi un matériau du même palier que la cible. Risque d'échec : perte des matériaux sans effet sur l'objet, ou très rarement destruction totale de celui-ci.",
    stackable: true,
    price: 150,
  },
  gemExtractionScroll: {
    id: "gemExtractionScroll",
    category: "gemExtraction",
    name: "Parchemin d'extraction",
    description:
      "Libère un socket d'un équipement en détruisant la gemme qui y est insérée (pour en insérer une autre à la place). Consomme aussi un matériau du même palier que la cible. Risque très faible que l'objet entier soit détruit dans l'opération.",
    loreText:
      "Les archivistes de la Tour Grise notaient déjà ce procédé il y a trois siècles : « séparer la pierre de sa monture sans la briser demande moins de force que de patience ». ...",
    stackable: true,
    price: 150,
  },

  /** Potions **/

  healthPotion: {
    id: "healthPotion",
    category: "consumable",
    name: "Petite potion de soin",
    description: "Restaure 30 PV à l'usage.",
    effect: { heal: 30 },
    stackable: true,
    price: 15, // vendable en boutique - cf. shopGenerator.js. Absent = jamais en vente (or, objets de quete)
  },
  mediumHealthPotion: {
    id: "mediumHealthPotion",
    category: "consumable",
    name: "Moyenne potion de soin",
    description: "Restaure 60 PV à l'usage.",
    effect: { heal: 60 },
    stackable: true,
    price: 50,
  },
  largeHealthPotion: {
    id: "largeHealthPotion",
    category: "consumable",
    name: "Grande potion de soin",
    description: "Restaure 120 PV à l'usage.",
    effect: { heal: 120 },
    stackable: true,
    price: 200,
  },
  manaPotion: {
    id: "manaPotion",
    category: "consumable",
    name: "Petite potion de mana",
    description: "Restaure 20 PM à l'usage.",
    effect: { mana: 20 },
    stackable: true,
    archetypes: ["mage"],
    price: 12,
  },
  mediumManaPotion: {
    id: "mediumManaPotion",
    category: "consumable",
    name: "Moyenne potion de mana",
    description: "Restaure 40 PM à l'usage.",
    effect: { mana: 40 },
    stackable: true,
    archetypes: ["mage"],
    price: 45,
  },
  largeManaPotion: {
    id: "largeManaPotion",
    category: "consumable",
    name: "Grande potion de mana",
    description: "Restaure 80 PM à l'usage.",
    effect: { mana: 80 },
    stackable: true,
    archetypes: ["mage"],
    price: 150,
  },
  staminaPotion: {
    id: "staminaPotion",
    category: "consumable",
    name: "Potion de stamina",
    description: "Restaure 40 points de stamina à l'usage.",
    effect: { stamina: 40 },
    stackable: true,
    price: 20,
  },
  mediumStaminaPotion: {
    id: "mediumStaminaPotion",
    category: "consumable",
    name: "Moyenne potion de stamina",
    description: "Restaure 80 points de stamina à l'usage.",
    effect: { stamina: 80 },
    stackable: true,
    price: 50,
  },
  largeStaminaPotion: {
    id: "largeStaminaPotion",
    category: "consumable",
    name: "Grande potion de stamina",
    description: "Restaure 120 points de stamina à l'usage.",
    effect: { stamina: 120 },
    stackable: true,
    price: 120,
  },
  hybridPotion: {
    id: "hybridPotion",
    category: "consumable",
    name: "Élixir hybride",
    description: "Restaure 25 PV, 25 mana et 25 stamina à l'usage.",
    effect: { heal: 25, mana: 25, stamina: 25 },
    stackable: true,
    price: 70,
  },
  mediumHybridPotion: {
    id: "mediumHybridPotion",
    category: "consumable",
    name: "Moyen élixir hybride",
    description: "Restaure 50 PV, 50 mana et 50 stamina à l'usage.",
    effect: { heal: 50, mana: 50, stamina: 50 },
    stackable: true,
    price: 100,
  },
  largeHybridPotion: {
    id: "largeHybridPotion",
    category: "consumable",
    name: "Grand élixir hybride",
    description: "Restaure 100 PV, 100 mana et 100 stamina à l'usage.",
    effect: { heal: 100, mana: 100, stamina: 100 },
    stackable: true,
    price: 200,
  },

  strengthPotion: {
    id: "strengthPotion",
    category: "consumable",
    name: "Potion de force",
    description:
      "Augmente tes dégâts au corps à corps pendant un court moment.",
    effect: {
      buff: { statModifiers: { meleeDamagePercent: 0.4 }, durationMs: 30000 },
    },
    stackable: true,
    price: 35,
  },
  mediumStrengthPotion: {
    id: "mediumStrengthPotion",
    category: "consumable",
    name: "Moyenne potion de force",
    description:
      "Augmente tes dégâts au corps à corps pendant un court moment.",
    effect: {
      buff: { statModifiers: { meleeDamagePercent: 0.6 }, durationMs: 40000 },
    },
    stackable: true,
    price: 50,
  },
  largeStrengthPotion: {
    id: "largeStrengthPotion",
    category: "consumable",
    name: "Grande potion de force",
    description:
      "Augmente tes dégâts au corps à corps pendant un court moment.",
    effect: {
      buff: { statModifiers: { meleeDamagePercent: 0.8 }, durationMs: 50000 },
    },
    stackable: true,
    price: 70,
  },
  swiftnessPotion: {
    id: "swiftnessPotion",
    category: "consumable",
    name: "Potion de vitesse",
    description: "Augmente ta vitesse de déplacement pendant un court moment.",
    effect: {
      buff: { statModifiers: { moveSpeedPercent: 0.5 }, durationMs: 20000 },
    },
    stackable: true,
    price: 30,
  },
  mediumSwiftnessPotion: {
    id: "mediumSwiftnessPotion",
    category: "consumable",
    name: "Moyenne potion de vitesse",
    description: "Augmente ta vitesse de déplacement pendant un court moment.",
    effect: {
      buff: { statModifiers: { moveSpeedPercent: 0.7 }, durationMs: 30000 },
    },
    stackable: true,
    price: 45,
  },
  largeSwiftnessPotion: {
    id: "largeSwiftnessPotion",
    category: "consumable",
    name: "Grande potion de vitesse",
    description: "Augmente ta vitesse de déplacement pendant un court moment.",
    effect: {
      buff: { statModifiers: { moveSpeedPercent: 0.9 }, durationMs: 40000 },
    },
    stackable: true,
    price: 60,
  },
  fortifyPotion: {
    id: "fortifyPotion",
    category: "consumable",
    name: "Potion de fortification",
    description: "Augmente fortement ta défense pendant un court moment.",
    effect: {
      buff: { statModifiers: { defensePercent: 0.5 }, durationMs: 25000 },
    },
    stackable: true,
    price: 40,
  },
  mediumFortifyPotion: {
    id: "mediumFortifyPotion",
    category: "consumable",
    name: "Moyenne potion de fortification",
    description: "Augmente fortement ta défense pendant un court moment.",
    effect: {
      buff: { statModifiers: { defensePercent: 0.7 }, durationMs: 30000 },
    },
    stackable: true,
    price: 55,
  },
  largeFortifyPotion: {
    id: "largeFortifyPotion",
    category: "consumable",
    name: "Grande potion de fortification",
    description: "Augmente fortement ta défense pendant un court moment.",
    effect: {
      buff: { statModifiers: { defensePercent: 0.9 }, durationMs: 40000 },
    },
    stackable: true,
    price: 70,
  },
  weaknessPotion: {
    id: "weaknessPotion",
    category: "consumable",
    name: "Fiole trouble",
    description: "Son effet est incertain...",
    effect: {
      buff: {
        statModifiers: { meleeDamagePercent: -0.3, rangedDamagePercent: -0.3 },
        durationMs: 15000,
      },
    },
    stackable: true,
    price: 5,
  },
  mediumWeaknessPotion: {
    id: "mediumWeaknessPotion",
    category: "consumable",
    name: "Moyenne fiole trouble",
    description: "Son effet est incertain...",
    effect: {
      buff: {
        statModifiers: { meleeDamagePercent: -0.5, rangedDamagePercent: -0.5 },
        durationMs: 20000,
      },
    },
    stackable: true,
    price: 10,
  },
  largeWeaknessPotion: {
    id: "largeWeaknessPotion",
    category: "consumable",
    name: "Grande fiole trouble",
    description: "Son effet est incertain...",
    effect: {
      buff: {
        statModifiers: { meleeDamagePercent: -0.7, rangedDamagePercent: -0.7 },
        durationMs: 25000,
      },
    },
    stackable: true,
    price: 15,
  },

  /** Objets explosifs */
  fireVial: {
    id: "fireVial",
    category: "consumable",
    name: "Fiole de feu",
    description: "Explose en une boule de feu au contact (20 dégâts).",
    stackable: true,
    price: 15,
    effect: {
      combat: {
        effectType: "aoe",
        damage: 20,
        damageType: "fire",
        radius: 65,
        inflictsEffect: {
          type: "burn",
          kind: "dot",
          chance: 0.4,
          damagePerTick: 3,
          tickIntervalMs: 1000,
          ticks: 3,
        },
      },
    },
  },
  mediumFireVial: {
    id: "mediumFireVial",
    category: "consumable",
    name: "Fiole de feu moyenne",
    description: "Explose en une boule de feu au contact (30 dégâts).",
    stackable: true,
    price: 25,
    effect: {
      combat: {
        effectType: "aoe",
        damage: 30,
        damageType: "fire",
        radius: 70,
        inflictsEffect: {
          type: "burn",
          kind: "dot",
          chance: 0.5,
          damagePerTick: 4,
          tickIntervalMs: 1000,
          ticks: 3,
        },
      },
    },
  },
  largeFireVial: {
    id: "largeFireVial",
    category: "consumable",
    name: "Grande fiole de feu",
    description: "Explose en une boule de feu au contact (40 dégâts).",
    stackable: true,
    price: 35,
    effect: {
      combat: {
        effectType: "aoe",
        damage: 40,
        damageType: "fire",
        radius: 75,
        inflictsEffect: {
          type: "burn",
          kind: "dot",
          chance: 0.6,
          damagePerTick: 5,
          tickIntervalMs: 1000,
          ticks: 3,
        },
      },
    },
  },
  acidVial: {
    id: "acidVial",
    category: "consumable",
    name: "Fiole d'acide",
    description: "Libère un nuage toxique autour de toi (14 dégâts).",
    stackable: true,
    price: 15,
    effect: {
      combat: {
        effectType: "aoe",
        damage: 14,
        damageType: "poison",
        radius: 60,
        inflictsEffect: {
          type: "acid",
          kind: "dot",
          chance: 0.6,
          damagePerTick: 4,
          tickIntervalMs: 1000,
          ticks: 3,
        },
      },
    },
  },
  mediumAcidVial: {
    id: "mediumAcidVial",
    category: "consumable",
    name: "Fiole d'acide moyenne",
    description: "Libère un nuage toxique autour de toi (20 dégâts).",
    stackable: true,
    price: 25,
    effect: {
      combat: {
        effectType: "aoe",
        damage: 20,
        damageType: "poison",
        radius: 65,
        inflictsEffect: {
          type: "acid",
          kind: "dot",
          chance: 0.6,
          damagePerTick: 5,
          tickIntervalMs: 1000,
          ticks: 3,
        },
      },
    },
  },
  largeAcidVial: {
    id: "largeAcidVial",
    category: "consumable",
    name: "Grande fiole d'acide",
    description: "Libère un nuage toxique autour de toi (30 dégâts).",
    stackable: true,
    price: 35,
    effect: {
      combat: {
        effectType: "aoe",
        damage: 30,
        damageType: "poison",
        radius: 70,
        inflictsEffect: {
          type: "acid",
          kind: "dot",
          chance: 0.6,
          damagePerTick: 6,
          tickIntervalMs: 1000,
          ticks: 3,
        },
      },
    },
  },
  stunBomb: {
    id: "stunBomb",
    category: "consumable",
    name: "Bombe étourdissante",
    description: "Étourdit tous les ennemis proches (10 dégâts).",
    stackable: true,
    price: 18,
    effect: {
      combat: {
        effectType: "aoeStun",
        damage: 10,
        radius: 60,
        durationMs: 1500,
      },
    },
  },
  mediumStunBomb: {
    id: "mediumStunBomb",
    category: "consumable",
    name: "Bombe étourdissante moyenne",
    description: "Étourdit tous les ennemis proches (15 dégâts).",
    stackable: true,
    price: 28,
    effect: {
      combat: {
        effectType: "aoeStun",
        damage: 15,
        radius: 65,
        durationMs: 2000,
      },
    },
  },
  largeStunBomb: {
    id: "largeStunBomb",
    category: "consumable",
    name: "Grande bombe étourdissante",
    description: "Étourdit tous les ennemis proches (20 dégâts).",
    stackable: true,
    price: 40,
    effect: {
      combat: {
        effectType: "aoeStun",
        damage: 20,
        radius: 70,
        durationMs: 2500,
      },
    },
  },

  leatherArmor: {
    id: "leatherArmor",
    category: "equipment",
    slot: "armor",
    name: "Armure de cuir",
    description: "+3 défense.",
    statBonus: { defense: 3 },
    stackable: false,
    price: 45,
  },

  gold: {
    id: "gold",
    category: "currency",
    name: "Or",
    description: "Monnaie du jeu.",
    stackable: true,
  },

  //enchanted weapons with scroll

  flamingSword: {
    id: "flamingSword",
    category: "equipment",
    slot: "mainHand",
    name: "Épée enflammée",
    description: "Une épée forgée dans les flammes.",
    statBonus: { meleeDamage: 5 }, // bonus de stat classique, deja utilise partout
    damageType: "fire", // type de degats (resistances elementaires)
    varianceDice: "1d6", // variance aleatoire sur les degats
    inflictsEffect: {
      // chance d'infliger un effet par coup - PAS de cooldown ici
      type: "burn",
      kind: "dot",
      chance: 0.3,
      damagePerTick: 3,
      tickIntervalMs: 1000,
      ticks: 3,
    },
    stackable: false,
  },

  //scroll
  blanckScroll: {
    id: "blankScroll",
    category: "craftingMaterial",
    name: "Parchemin vierge",
    description:
      "Un parchemin vierge, utilisé pour créer de nouveaux sorts. Il peut également permettre de créer des grimoires.",
    stackable: true,
    price: 10,
  },

  grimoire: {
    id: "grimoire",
    category: "craftingMaterial",
    name: "Grimoire vierge",
    description:
      "Un grimoire vierge, utilisé pour créer de nouvelles compétences.",
    stackable: true,
  },

  fireballScroll: {
    id: "fireballScroll",
    category: "abilityScroll",
    name: "Parchemin : Boule de feu",
    description: "Apprend la compétence Boule de feu (consomme du mana).",
    grantsAbility: "fireball",
    stackable: true,
    price: 80,
  },
  whirlwindScroll: {
    id: "whirlwindScroll",
    category: "abilityScroll",
    name: "Parchemin : Tourbillon",
    description: "Apprend la compétence Tourbillon (consomme de la stamina).",
    grantsAbility: "whirlwind",
    stackable: true,
    price: 90,
  },
  hasteScroll: {
    id: "hasteScroll",
    category: "abilityScroll",
    name: "Parchemin : Hâte",
    description: "Apprend la compétence Hâte (consomme de la stamina).",
    grantsAbility: "haste",
    stackable: true,
    price: 85,
  },
  slowScroll: {
    id: "slowScroll",
    category: "abilityScroll",
    name: "Parchemin : Lenteur",
    description: "Apprend la compétence Lenteur (consomme du mana).",
    grantsAbility: "slow",
    stackable: true,
    price: 75,
  },
  flameWeaponScroll: {
    id: "flameWeaponScroll",
    category: "abilityScroll",
    name: "Parchemin : Lame enflammée",
    description:
      "Apprend la compétence Lame enflammée (consomme de la stamina).",
    grantsAbility: "flameWeapon",
    stackable: true,
    price: 95,
  },
  flamingEdgeScroll: {
    id: "flamingEdgeScroll",
    category: "abilityScroll",
    name: "Parchemin : Tranchant flamboyant",
    description: "Apprend l'enchantement Tranchant flamboyant.",
    grantsAbility: "flamingEdge",
    stackable: false,
    price: 45,
  },
  glacialEdgeScroll: {
    id: "glacialEdgeScroll",
    category: "abilityScroll",
    name: "Parchemin : Tranchant glacial",
    description: "Apprend l'enchantement Tranchant glacial.",
    grantsAbility: "glacialEdge",
    stackable: false,
    price: 45,
  },
  shockingEdgeScroll: {
    id: "shockingEdgeScroll",
    category: "abilityScroll",
    name: "Parchemin : Tranchant foudroyant",
    description: "Apprend l'enchantement Tranchant foudroyant.",
    grantsAbility: "shockingEdge",
    stackable: false,
    price: 45,
  },
  venomEdgeScroll: {
    id: "venomEdgeScroll",
    category: "abilityScroll",
    name: "Parchemin : Tranchant empoisonné",
    description: "Apprend l'enchantement Tranchant empoisonné.",
    grantsAbility: "venomEdge",
    stackable: false,
    price: 45,
  },
  holyEdgeScroll: {
    id: "holyEdgeScroll",
    category: "abilityScroll",
    name: "Parchemin : Tranchant sacré",
    description: "Apprend l'enchantement Tranchant sacré.",
    grantsAbility: "holyEdge",
    stackable: false,
    price: 45,
  },
  shadowEdgeScroll: {
    id: "shadowEdgeScroll",
    category: "abilityScroll",
    name: "Parchemin : Tranchant ténébreux",
    description: "Apprend l'enchantement Tranchant ténébreux.",
    grantsAbility: "shadowEdge",
    stackable: false,
    price: 45,
  },
  flameWallScroll: {
    id: "flameWallScroll",
    category: "abilityScroll",
    name: "Parchemin : Mur de flammes",
    description:
      "Apprend la compétence Mur de flammes (consomme de la stamina).",
    grantsAbility: "flameWall",
    stackable: true,
    price: 100,
  },
  detectTrapsScroll: {
    id: "detectTrapsScroll",
    category: "abilityScroll",
    name: "Parchemin : Détection de pièges",
    description: "Apprend la compétence Détection de pièges.",
    grantsAbility: "detectTraps",
    stackable: false,
    price: 40, // optionnel - retire ce champ si tu veux qu'il ne soit JAMAIS achetable en boutique (cf. getPurchasableItemIds, base uniquement sur la presence de price)
  },

  detectSecretsScroll: {
    id: "detectSecretsScroll",
    category: "abilityScroll",
    name: "Parchemin : Détection des secrets",
    description: "Apprend la compétence Détection des secrets.",
    grantsAbility: "detectSecrets",
    stackable: false,
    price: 45,
  },

  loreScroll1: {
    id: "loreScroll1",
    category: "loreScroll",
    name: "lore ARPG",
    description: "le lore de ce ARPG",
    loreText:
      "Ce parchemin contient des informations détaillées sur l'histoire et le lore de ce ARPG.",
    grantsAbility: "lore",
    stackable: false,
    unique: true,
    price: 50,
  },

  // recipes
  flamingSwordRecipe: {
    id: "flamingSwordRecipe",
    category: "recipeScroll",
    name: "Plan : Épée enflammée",
    description: "Apprend la recette de l'épée enflammée.",
    grantsRecipe: "flamingSwordRecipe",
    stackable: true,
    price: 70,
  },
  healthPotionRecipe: {
    id: "healthPotionRecipe",
    category: "recipeScroll",
    name: "Plan : Potion de soin",
    description: "Apprend la recette de la potion de soin.",
    grantsRecipe: "healthPotionRecipe",
    stackable: true,
    price: 50,
  },

  healingAuraScroll: {
    id: "healingAuraScroll",
    category: "abilityScroll",
    name: "Parchemin : Soin de zone",
    description:
      "Apprend la compétence Soin de zone (consomme du mana). Soigne aussi tes invocations et les alliés escortés.",
    grantsAbility: "healingAura",
    stackable: true,
    price: 110,
  },
  woodenArrowRecipe: {
    id: "woodenArrowRecipe",
    category: "recipeScroll",
    name: "Plan : Flèche en bois",
    description: "Apprend la recette de la flèche en bois.",
    grantsRecipe: "woodenArrowRecipe",
    stackable: true,
    price: 40,
  },
  bigWoodenArrowRecipe: {
    id: "bigWoodenArrowRecipe",
    category: "recipeScroll",
    name: "Plan : Flèche en bois géante",
    description: "Apprend la recette de la flèche en bois géante.",
    grantsRecipe: "bigWoodenArrowRecipe",
    stackable: true,
    price: 80,
  },
  summonAngryBrownMushroomScroll: {
    id: "summonAngryBrownMushroomScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Champignon brun en colère",
    description:
      "Apprend la compétence Invocation : Champignon brun en colère.",
    grantsAbility: "summonAngryBrownMushroom",
    stackable: false,
  },
  summonGnomeGreenScroll: {
    id: "summonGnomeGreenScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Gnome Vert",
    description: "Apprend la compétence Invocation : Gnome Vert.",
    grantsAbility: "summonGnomeGreen",
    stackable: false,
  },
  summonAngryTrentScroll: {
    id: "summonAngryTrentScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Ronchêne",
    description: "Apprend la compétence Invocation : Ronchêne.",
    grantsAbility: "summonAngryTrent",
    stackable: false,
  },

  summonKnifedBatScroll: {
    id: "summonKnifedBatScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Chauve-souris poignardée",
    description: "Apprend la compétence Invocation : Chauve-souris poignardée.",
    grantsAbility: "summonKnifedBat",
    stackable: false,
  },

  summonDeer1Scroll: {
    id: "summonDeer1Scroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Cerf",
    description: "Apprend la compétence Invocation : Cerf.",
    grantsAbility: "summonDeer1",
    stackable: false,
  },

  // craft material
  deerAntler: {
    id: "deerAntler",
    category: "craftingMaterial",
    name: "Bois de cerf",
    description: "Un bois de cerf robuste, utilisé en artisanat.",
    stackable: true,
    price: 8,
  },
  mushroom: {
    id: "mushroom",
    category: "craftingMaterial",
    name: "Champignon",
    description: "Un champignon commun, utilisé en artisanat.",
    stackable: true,
    price: 5,
  },
  branch: {
    id: "branch",
    category: "craftingMaterial",
    name: "Branche",
    description: "Une branche solide, utilisée en artisanat.",
    stackable: true,
    price: 5,
  },
  waterLily: {
    id: "waterLily",
    category: "craftingMaterial",
    name: "Nénuphar",
    description: "Une plante aquatique utilisée en artisanat.",
    stackable: true,
    price: 5,
  },
  berries: {
    id: "berries",
    category: "craftingMaterial",
    name: "Baies",
    description: "Des baies sauvages, utilisées en artisanat.",
    stackable: true,
    price: 5,
  },
  honeycomb: {
    id: "honeycomb",
    category: "craftingMaterial",
    name: "Rayon de miel",
    description: "Un rayon de miel, utilisé en artisanat.",
    stackable: true,
    price: 10,
  },

  vegetalFiber: {
    id: "vegetalFiber",
    category: "craftingMaterial",
    name: "Fibre végétale",
    description: "Une fibre végétale, utilisée en artisanat.",
    stackable: true,
    price: 5,
  },
  copperOre: {
    id: "copperOre",
    category: "craftingMaterial",
    name: "Minerai de cuivre",
    description: "Un morceau de minerai brut, utilisé en artisanat.",
    stackable: true,
    price: 5,
  },
  ironOre: {
    id: "ironOre",
    category: "craftingMaterial",
    name: "Minerai de fer",
    description: "Un morceau de minerai brut, utilisé en artisanat.",
    stackable: true,
    price: 8,
  },
  silverOre: {
    id: "silverOre",
    category: "craftingMaterial",
    name: "Minerai d'argent",
    description: "Un morceau de minerai précieux, utilisé en artisanat.",
    stackable: true,
    price: 15,
  },
  coalOre: {
    id: "coalOre",
    category: "craftingMaterial",
    name: "Minerai de charbon",
    description: "Un morceau de minerai de charbon, utilisé en artisanat.",
    stackable: true,
    price: 5,
  },
  goldOre: {
    id: "goldOre",
    category: "craftingMaterial",
    name: "Minerai d'or",
    description: "Un morceau de minerai rare, utilisé en artisanat.",
    stackable: true,
    price: 25,
  },
  platiniumOre: {
    id: "platiniumOre",
    category: "craftingMaterial",
    name: "Minerai de platine",
    description: "Un morceau de minerai rare, utilisé en artisanat.",
    stackable: true,
    price: 30,
  },
  cobaltOre: {
    id: "cobaltOre",
    category: "craftingMaterial",
    name: "Minerai de cobalt",
    description: "Un morceau de minerai rare, utilisé en artisanat.",
    stackable: true,
    price: 35,
  },
  adamantineOre: {
    id: "adamantineOre",
    category: "craftingMaterial",
    name: "Minerai d'adamantine",
    description: "Un morceau de minerai très rare, utilisé en artisanat.",
    stackable: true,
    price: 50,
  },
  crimsonOre: {
    id: "crimsonOre",
    category: "craftingMaterial",
    name: "Minerai cramoisi",
    description: "Un morceau de minerai très rare, utilisé en artisanat.",
    stackable: true,
    price: 60,
  },
  angelicOre: {
    id: "angelicOre",
    category: "craftingMaterial",
    name: "Minerai angélique",
    description:
      "Un morceau de minerai extrêmement rare, utilisé en artisanat.",
    stackable: true,
    price: 80,
  },
  fatefulOre: {
    id: "fatefulOre",
    category: "craftingMaterial",
    name: "Minerai fatidique",
    description:
      "Un morceau de minerai extrêmement rare, utilisé en artisanat.",
    stackable: true,
    price: 100,
  },
  novaOre: {
    id: "novaOre",
    category: "craftingMaterial",
    name: "Minerai nova",
    description:
      "Un morceau de minerai extrêmement rare, utilisé en artisanat.",
    stackable: true,
    price: 120,
  },
  copperIngot: {
    id: "copperIngot",
    category: "craftingMaterial",
    name: "Lingot de cuivre",
    description: "Un lingot de cuivre raffiné, utilisé en artisanat.",
    stackable: true,
    price: 10,
  },

  ironIngot: {
    id: "ironIngot",
    category: "craftingMaterial",
    name: "Lingot de fer",
    description: "Un lingot de fer raffiné, utilisé en artisanat.",
    stackable: true,
    price: 15,
  },
  silverIngot: {
    id: "silverIngot",
    category: "craftingMaterial",
    name: "Lingot d'argent",
    description: "Un lingot d'argent raffiné, utilisé en artisanat.",
    stackable: true,
    price: 30,
  },
  steelIngot: {
    id: "steelIngot",
    category: "craftingMaterial",
    name: "Lingot d'acier",
    description: "Un lingot d'acier raffiné, utilisé en artisanat.",
    stackable: true,
    price: 40,
  },
  goldIngot: {
    id: "goldIngot",
    category: "craftingMaterial",
    name: "Lingot d'or",
    description: "Un lingot d'or raffiné, utilisé en artisanat.",
    stackable: true,
    price: 50,
  },
  platiniumIngot: {
    id: "platiniumIngot",
    category: "craftingMaterial",
    name: "Lingot de platine",
    description: "Un lingot de platine raffiné, utilisé en artisanat.",
    stackable: true,
    price: 60,
  },
  cobaltIngot: {
    id: "cobaltIngot",
    category: "craftingMaterial",
    name: "Lingot de cobalt",
    description: "Un lingot de cobalt raffiné, utilisé en artisanat.",
    stackable: true,
    price: 70,
  },
  adamantineIngot: {
    id: "adamantineIngot",
    category: "craftingMaterial",
    name: "Lingot d'adamantine",
    description: "Un lingot d'adamantine raffiné, utilisé en artisanat.",
    stackable: true,
    price: 90,
  },
  crimsonIngot: {
    id: "crimsonIngot",
    category: "craftingMaterial",
    name: "Lingot cramoisi",
    description: "Un lingot cramoisi raffiné, utilisé en artisanat.",
    stackable: true,
    price: 100,
  },
  angelicIngot: {
    id: "angelicIngot",
    category: "craftingMaterial",
    name: "Lingot angélique",
    description: "Un lingot angélique raffiné, utilisé en artisanat.",
    stackable: true,
    price: 120,
  },
  fatefulIngot: {
    id: "fatefulIngot",
    category: "craftingMaterial",
    name: "Lingot fatidique",
    description: "Un lingot fatidique raffiné, utilisé en artisanat.",
    stackable: true,
    price: 140,
  },
  novaIngot: {
    id: "novaIngot",
    category: "craftingMaterial",
    name: "Lingot nova",
    description: "Un lingot nova raffiné, utilisé en artisanat.",
    stackable: true,
    price: 160,
  },
  whetstone: {
    id: "whetstone",
    category: "craftingMaterial",
    name: "Pierre à aiguiser",
    description: "Une pierre utilisée pour aiguiser les lames.",
    stackable: true,
    price: 10,
  },
  foolsGold: {
    id: "foolsGold",
    category: "craftingMaterial",
    name: "Pyrite",
    description: "Un minéral trompeur ressemblant à de l'or.",
    stackable: true,
    price: 8,
  },
  moonStone: {
    id: "moonStone",
    category: "craftingMaterial",
    name: "Pierre de lune",
    description: "Une pierre précieuse avec une lueur mystérieuse.",
    stackable: true,
    price: 20,
  },
  diamond: {
    id: "diamond",
    category: "craftingMaterial",
    name: "Diamant",
    description: "Une pierre précieuse extrêmement dure et brillante.",
    stackable: true,
    price: 50,
  },
  smokyQuartz: {
    id: "smokyQuartz",
    category: "craftingMaterial",
    name: "Quartz fumé",
    description: "Un quartz avec une teinte fumée.",
    stackable: true,
    price: 10,
  },
  ruby: {
    id: "ruby",
    category: "craftingMaterial",
    name: "Rubis",
    description: "Une pierre précieuse rouge éclatante.",
    stackable: true,
    price: 50,
  },
  aquaMarine: {
    id: "aquaMarine",
    category: "craftingMaterial",
    name: "Aigue-marine",
    description: "Une pierre précieuse bleue claire.",
    stackable: true,
    price: 40,
  },
  peridot: {
    id: "peridot",
    category: "craftingMaterial",
    name: "Péridot",
    description: "Une pierre précieuse verte éclatante.",
    stackable: true,
    price: 30,
  },
  oakWood: {
    id: "oakWood",
    category: "craftingMaterial",
    name: "Bois de chêne",
    description:
      "Un bois robuste et commun, utilisé pour les arcs les plus simples.",
    stackable: true,
  },
  ashWood: {
    id: "ashWood",
    category: "craftingMaterial",
    name: "Bois de frêne",
    description: "Souple et résistant, prisé pour les hampes de flèches.",
    stackable: true,
  },
  yewWood: {
    id: "yewWood",
    category: "craftingMaterial",
    name: "Bois d'if",
    description: "Le bois traditionnel des grands arcs de chasse.",
    stackable: true,
  },
  ebonyWood: {
    id: "ebonyWood",
    category: "craftingMaterial",
    name: "Bois d'ébène",
    description: "Dense et sombre, presque aussi solide que du métal léger.",
    stackable: true,
  },
  petrifiedWood: {
    id: "petrifiedWood",
    category: "craftingMaterial",
    name: "Bois pétrifié",
    description:
      "Un bois ancien, durci par le temps jusqu'à la dureté de la pierre.",
    stackable: true,
  },
  mistwood: {
    id: "mistwood",
    category: "craftingMaterial",
    name: "Bois de brume",
    description:
      "Un bois étrangement léger, presque translucide dans la pénombre.",
    stackable: true,
  },
  runewood: {
    id: "runewood",
    category: "craftingMaterial",
    name: "Bois runique",
    description: "Des motifs anciens courent naturellement dans son grain.",
    stackable: true,
  },
  skywood: {
    id: "skywood",
    category: "craftingMaterial",
    name: "Bois céleste",
    description: "Un bois rare qui semble capter la lumière du ciel.",
    stackable: true,
  },
  scarletwood: {
    id: "scarletwood",
    category: "craftingMaterial",
    name: "Bois écarlate",
    description: "Une teinte rouge profonde traverse toute sa fibre.",
    stackable: true,
  },
  sacredWood: {
    id: "sacredWood",
    category: "craftingMaterial",
    name: "Bois sacré",
    description: "Un bois vénéré, réputé pour ne jamais pourrir.",
    stackable: true,
  },
  eternalWood: {
    id: "eternalWood",
    category: "craftingMaterial",
    name: "Bois éternel",
    description: "On dit qu'il ne cesse jamais vraiment de croître.",
    stackable: true,
  },
  starwood: {
    id: "starwood",
    category: "craftingMaterial",
    name: "Bois stellaire",
    description:
      "Un bois d'une origine incertaine, parcouru de reflets scintillants.",
    stackable: true,
  },
  resin: {
    id: "resin",
    category: "craftingMaterial",
    name: "Résine",
    description: "Une résine collante, utile en artisanat.",
    stackable: true,
    price: 10,
  },

  // unique monster cores
  angryBrownMushroomCore: {
    id: "angryBrownMushroomCore",
    category: "craftingMaterial",
    name: "Noyau de champignon brun en colère",
    description:
      "Le cœur d'un champignon brun en colère, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  gnomeGreenCore: {
    id: "gnomeGreenCore",
    category: "craftingMaterial",
    name: "Noyau de gnome vert",
    description: "Le cœur d'un gnome vert, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  gnomeClaw: {
    id: "gnomeClaw",
    category: "craftingMaterial",
    name: "Griffe de gnome",
    description: "Une griffe provenant d'un gnome, utilisée pour l'artisanat.",
    stackable: true,
  },
  gnomeFurTuft: {
    id: "gnomeFurTuft",
    category: "craftingMaterial",
    name: "Touffe de cheveux de gnome",
    description:
      "Une petite touffe de cheveux provenant d'un gnome, utilisée pour l'artisanat.",
    stackable: true,
  },

  angryTrentCore: {
    id: "angryTrentCore",
    category: "craftingMaterial",
    name: "Noyau de Ronchêne",
    description: "Le cœur d'un Ronchêne, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  woodAngryTrent: {
    id: "woodAngryTrent",
    category: "craftingMaterial",
    name: "Bois de Ronchêne",
    description: "Du bois provenant d'un Ronchêne, utilisé pour l'artisanat.",
    stackable: true,
  },

  knifedBatCore: {
    id: "knifedBatCore",
    category: "craftingMaterial",
    name: "Noyau de chauve-souris poignardée",
    description:
      "Le cœur d'une chauve-souris poignardée, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  batWings: {
    id: "batWings",
    category: "craftingMaterial",
    name: "Aile de chauve-souris",
    description:
      "Une aile provenant d'une chauve-souris, utilisée pour l'artisanat.",
    stackable: true,
  },
  batFur: {
    id: "batFur",
    category: "craftingMaterial",
    name: "Fourrure de chauve-souris",
    description:
      "De la fourrure provenant d'une chauve-souris, utilisée pour l'artisanat.",
    stackable: true,
  },
  batEye: {
    id: "batEye",
    category: "craftingMaterial",
    name: "Œil de chauve-souris",
    description:
      "Un œil provenant d'une chauve-souris, utilisé pour l'artisanat.",
    stackable: true,
  },
  deerHoof: {
    id: "deerHoof",
    category: "craftingMaterial",
    name: "Patte de cerf",
    description: "Une patte provenant d'un cerf, utilisée pour l'artisanat.",
    stackable: true,
  },
  deerMeat: {
    id: "deerMeat",
    category: "craftingMaterial",
    name: "Viande de cerf",
    description: "De la viande provenant d'un cerf, utilisée pour l'artisanat.",
    stackable: true,
  },
  deerBone: {
    id: "deerBone",
    category: "craftingMaterial",
    name: "Os de cerf",
    description: "Un os provenant d'un cerf, utilisé pour l'artisanat.",
    stackable: true,
  },
  deer1Core: {
    id: "deer1Core",
    category: "craftingMaterial",
    name: "Noyau de cerf",
    description: "Le cœur d'un cerf, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },

  // exemple d'objet de quete - existe dans le monde, mais aucune quete
  // de type "en rapporter N" ne sait encore l'exploiter (cf. commentaire
  // en tete de fichier)
  ancientRelic: {
    id: "ancientRelic",
    category: "questItem",
    name: "Relique ancienne",
    description: "Un artefact qui semble important.",
    stackable: false,
  },
  redChest: {
    id: "redChest",
    category: "questItem",
    name: "Coffre rouge",
    description: "Un coffre verrouillé.",
    stackable: false,
  },
  alricMerchandise: {
    id: "alricMerchandise",
    category: "questItem",
    name: "Marchandise d'Alric",
    description: "Des objets appartenant à Alric.",
    stackable: true,
  },
  greenApple: {
    id: "greenApple",
    category: "questItem",
    name: "Pomme verte",
    description: "Une pomme fraîche et juteuse.",
    stackable: false,
  },
  sealedPackage: {
    id: "sealedPackage",
    category: "questItem",
    name: "Colis scellé",
    description: "À livrer à son destinataire, sans l'ouvrir.",
    stackable: false,
  },
  orangeMushroom: {
    id: "orangeMushroom",
    category: "questItem",
    name: "Champignon orange",
    description: "Un champignon rare.",
    stackable: true,
    price: 5,
  },
  steelPickaxeBrenn: {
    id: "steelPickaxeBrenn",
    category: "questItem",
    name: "Pioche en acier de Brenn",
    description:
      "Une pioche en acier appartenant à Brenn. Inutilisable par quiconque qui n'est pas Brenn.",
    stackable: false,
    unique: true,
  },
  mudGolemCore: {
    id: "mudGolemCore",
    category: "craftingMaterial",
    name: "Noyau de golem de boue",
    description: "Le cœur d'un golem de boue, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonMudGolemScroll: {
    id: "summonMudGolemScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Golem de boue",
    description: "Apprend la compétence Invocation : Golem de boue.",
    grantsAbility: "summonMudGolem",
    stackable: false,
  },
  redBeetleCore: {
    id: "redBeetleCore",
    category: "craftingMaterial",
    name: "Noyau de insecte Rouge",
    description: "Le cœur d'un insecte rouge, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonRedBeetleScroll: {
    id: "summonRedBeetleScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Insecte Rouge",
    description: "Apprend la compétence Invocation : Insecte Rouge.",
    grantsAbility: "summonRedBeetle",
    stackable: false,
  },
  pinkOgreCore: {
    id: "pinkOgreCore",
    category: "craftingMaterial",
    name: "Noyau de ogre rose",
    description: "Le cœur d'un ogre rose, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonPinkOgreScroll: {
    id: "summonPinkOgreScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Ogre rose",
    description: "Apprend la compétence Invocation : Ogre rose.",
    grantsAbility: "summonPinkOgre",
    stackable: false,
  },
  batCore: {
    id: "batCore",
    category: "craftingMaterial",
    name: "Noyau de chauve-souris",
    description: "Le cœur d'une chauve-souris, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonBatScroll: {
    id: "summonBatScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Chauve-souris",
    description: "Apprend la compétence Invocation : Chauve-souris.",
    grantsAbility: "summonBat",
    stackable: false,
  },
  brownBearCore: {
    id: "brownBearCore",
    category: "craftingMaterial",
    name: "Noyau d'ours brun",
    description: "Le cœur d'un ours brun, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonBrownBearScroll: {
    id: "summonBrownBearScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Ours brun",
    description: "Apprend la compétence Invocation : Ours brun.",
    grantsAbility: "summonBrownBear",
    stackable: false,
  },
  beeCore: {
    id: "beeCore",
    category: "craftingMaterial",
    name: "Noyau de abeille",
    description: "Le cœur d'une abeille, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonBeeScroll: {
    id: "summonBeeScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Abeille",
    description: "Apprend la compétence Invocation : Abeille.",
    grantsAbility: "summonBee",
    stackable: false,
  },
  bigtickCore: {
    id: "bigtickCore",
    category: "craftingMaterial",
    name: "Noyau de gros tique",
    description: "Le cœur d'un gros tique, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonBigtickScroll: {
    id: "summonBigtickScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Gros tique",
    description: "Apprend la compétence Invocation : Gros tique.",
    grantsAbility: "summonBigtick",
    stackable: false,
  },
  blackdragonCore: {
    id: "blackdragonCore",
    category: "craftingMaterial",
    name: "Noyau de dragon noir",
    description: "Le cœur d'un dragon noir, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonBlackdragonScroll: {
    id: "summonBlackdragonScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Dragon noir",
    description: "Apprend la compétence Invocation : Dragon noir.",
    grantsAbility: "summonBlackdragon",
    stackable: false,
  },
  darkelfCore: {
    id: "darkelfCore",
    category: "craftingMaterial",
    name: "Noyau de elfe noir",
    description: "Le cœur d'un elfe noir, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonDarkelfScroll: {
    id: "summonDarkelfScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Elfe noir",
    description: "Apprend la compétence Invocation : Elfe noir.",
    grantsAbility: "summonDarkelf",
    stackable: false,
  },
  demonDragonCore: {
    id: "demonDragonCore",
    category: "craftingMaterial",
    name: "Noyau de dragon démon",
    description: "Le cœur d'un dragon démon, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonDemonDragonScroll: {
    id: "summonDemonDragonScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Dragon démon",
    description: "Apprend la compétence Invocation : Dragon démon.",
    grantsAbility: "summonDemonDragon",
    stackable: false,
  },
  dwarfCore: {
    id: "dwarfCore",
    category: "craftingMaterial",
    name: "Noyau de nain",
    description: "Le cœur d'un nain, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonDwarfScroll: {
    id: "summonDwarfScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Nain",
    description: "Apprend la compétence Invocation : Nain.",
    grantsAbility: "summonDwarf",
    stackable: false,
  },
  fantasy1Core: {
    id: "fantasy1Core",
    category: "craftingMaterial",
    name: "Noyau de fantasy 1",
    description: "Le cœur d'un fantasy 1, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonFantasy1Scroll: {
    id: "summonFantasy1Scroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Fantasy 1",
    description: "Apprend la compétence Invocation : Fantasy 1.",
    grantsAbility: "summonFantasy1",
    stackable: false,
  },
  fantasy2Core: {
    id: "fantasy2Core",
    category: "craftingMaterial",
    name: "Noyau de fantasy 2",
    description: "Le cœur d'un fantasy 2, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonFantasy2Scroll: {
    id: "summonFantasy2Scroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Fantasy 2",
    description: "Apprend la compétence Invocation : Fantasy 2.",
    grantsAbility: "summonFantasy2",
    stackable: false,
  },
  fantasy3Core: {
    id: "fantasy3Core",
    category: "craftingMaterial",
    name: "Noyau de fantasy 3",
    description: "Le cœur d'un fantasy 3, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonFantasy3Scroll: {
    id: "summonFantasy3Scroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Fantasy 3",
    description: "Apprend la compétence Invocation : Fantasy 3.",
    grantsAbility: "summonFantasy3",
    stackable: false,
  },
  fantasy4Core: {
    id: "fantasy4Core",
    category: "craftingMaterial",
    name: "Noyau de fantasy 4",
    description: "Le cœur d'un fantasy 4, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonFantasy4Scroll: {
    id: "summonFantasy4Scroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Fantasy 4",
    description: "Apprend la compétence Invocation : Fantasy 4.",
    grantsAbility: "summonFantasy4",
    stackable: false,
  },
  orqueGreenCore: {
    id: "orqueGreenCore",
    category: "craftingMaterial",
    name: "Noyau d'orque verte",
    description: "Le cœur d'une orque verte, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonOrqueGreenScroll: {
    id: "summonOrqueGreenScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Orque Verte",
    description: "Apprend la compétence Invocation : Orque Verte.",
    grantsAbility: "summonOrqueGreen",
    stackable: false,
  },
  redWarriorMushroomCore: {
    id: "redWarriorMushroomCore",
    category: "craftingMaterial",
    name: "Noyau de guerrier champignon rouge",
    description:
      "Le cœur d'un guerrier champignon rouge, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonredWarriorMushroomScroll: {
    id: "summonredWarriorMushroomScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Guerrier Champignon Rouge",
    description:
      "Apprend la compétence Invocation : Guerrier Champignon Rouge.",
    grantsAbility: "summonredWarriorMushroom",
    stackable: false,
  },
  fantasy7Core: {
    id: "fantasy7Core",
    category: "craftingMaterial",
    name: "Noyau de fantasy 7",
    description: "Le cœur d'un fantasy 7, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonFantasy7Scroll: {
    id: "summonFantasy7Scroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Fantasy 7",
    description: "Apprend la compétence Invocation : Fantasy 7.",
    grantsAbility: "summonFantasy7",
    stackable: false,
  },
  massecailleBlueCore: {
    id: "massecailleBlueCore",
    category: "craftingMaterial",
    name: "Noyau de Massecaille Bleu",
    description: "Le cœur d'un Massecaille Bleu, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonMassecailleBlueScroll: {
    id: "summonMassecailleBlueScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Massecaille Bleu",
    description: "Apprend la compétence Invocation : Massecaille Bleu.",
    grantsAbility: "summonMassecailleBlue",
    stackable: false,
  },
  fantasy9Core: {
    id: "fantasy9Core",
    category: "craftingMaterial",
    name: "Noyau de fantasy 9",
    description: "Le cœur d'un fantasy 9, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonFantasy9Scroll: {
    id: "summonFantasy9Scroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Fantasy 9",
    description: "Apprend la compétence Invocation : Fantasy 9.",
    grantsAbility: "summonFantasy9",
    stackable: false,
  },
  knightJauneRougeCore: {
    id: "knightJauneRougeCore",
    category: "craftingMaterial",
    name: "Noyau de knight Jaune Rouge",
    description: "Le cœur d'un knight Jaune Rouge, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonKnightJauneRougeScroll: {
    id: "summonKnightJauneRougeScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Knight Jaune Rouge",
    description: "Apprend la compétence Invocation : Knight Jaune Rouge.",
    grantsAbility: "summonKnightJauneRouge",
    stackable: false,
  },
  gargoyleCore: {
    id: "gargoyleCore",
    category: "craftingMaterial",
    name: "Noyau de gargouille",
    description: "Le cœur d'une gargouille, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonGargoyleScroll: {
    id: "summonGargoyleScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Gargouille",
    description: "Apprend la compétence Invocation : Gargouille.",
    grantsAbility: "summonGargoyle",
    stackable: false,
  },
  ghostCore: {
    id: "ghostCore",
    category: "craftingMaterial",
    name: "Noyau de fantome",
    description: "Le cœur d'un fantome, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonGhostScroll: {
    id: "summonGhostScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Fantome",
    description: "Apprend la compétence Invocation : Fantome.",
    grantsAbility: "summonGhost",
    stackable: false,
  },
  gnomeFouCore: {
    id: "gnomeFouCore",
    category: "craftingMaterial",
    name: "Noyau de gnome fou",
    description: "Le cœur d'un gnome fou, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonGnomeFouScroll: {
    id: "summonGnomeFouScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Gnome fou",
    description: "Apprend la compétence Invocation : Gnome fou.",
    grantsAbility: "summonGnomeFou",
    stackable: false,
  },
  golemCore: {
    id: "golemCore",
    category: "craftingMaterial",
    name: "Noyau de golem",
    description: "Le cœur d'un golem, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonGolemScroll: {
    id: "summonGolemScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Golem",
    description: "Apprend la compétence Invocation : Golem.",
    grantsAbility: "summonGolem",
    stackable: false,
  },
  gorillaCore: {
    id: "gorillaCore",
    category: "craftingMaterial",
    name: "Noyau de gorille",
    description: "Le cœur d'un gorille, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonGorillaScroll: {
    id: "summonGorillaScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Gorille",
    description: "Apprend la compétence Invocation : Gorille.",
    grantsAbility: "summonGorilla",
    stackable: false,
  },
  greendragonCore: {
    id: "greendragonCore",
    category: "craftingMaterial",
    name: "Noyau de dragon vert",
    description: "Le cœur d'un dragon vert, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonGreendragonScroll: {
    id: "summonGreendragonScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Dragon vert",
    description: "Apprend la compétence Invocation : Dragon vert.",
    grantsAbility: "summonGreendragon",
    stackable: false,
  },
  ogreBrownCore: {
    id: "ogreBrownCore",
    category: "craftingMaterial",
    name: "Noyau de gourdogre — Clan Fangebois",
    description:
      "Le cœur d'un gourdogre du Clan Fangebois, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonOgreBrownScroll: {
    id: "summonOgreBrownScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Gourdogre — Clan Fangebois",
    description:
      "Apprend la compétence Invocation : Gourdogre — Clan Fangebois.",
    grantsAbility: "summonOgreBrown",
    stackable: false,
  },
  redbeetleCore: {
    id: "redbeetleCore",
    category: "craftingMaterial",
    name: "Noyau de insecte rouge",
    description: "Le cœur d'un insecte rouge, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonRedbeetleScroll: {
    id: "summonRedbeetleScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Insecte rouge",
    description: "Apprend la compétence Invocation : Insecte rouge.",
    grantsAbility: "summonRedbeetle",
    stackable: false,
  },
  robot1Core: {
    id: "robot1Core",
    category: "craftingMaterial",
    name: "Noyau de robot1",
    description: "Le cœur d'un robot1, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonRobot1Scroll: {
    id: "summonRobot1Scroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Robot1",
    description: "Apprend la compétence Invocation : Robot1.",
    grantsAbility: "summonRobot1",
    stackable: false,
  },
  robot2Core: {
    id: "robot2Core",
    category: "craftingMaterial",
    name: "Noyau de robot2",
    description: "Le cœur d'un robot2, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonRobot2Scroll: {
    id: "summonRobot2Scroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Robot2",
    description: "Apprend la compétence Invocation : Robot2.",
    grantsAbility: "summonRobot2",
    stackable: false,
  },
  robot3Core: {
    id: "robot3Core",
    category: "craftingMaterial",
    name: "Noyau de robot3",
    description: "Le cœur d'un robot3, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonRobot3Scroll: {
    id: "summonRobot3Scroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Robot3",
    description: "Apprend la compétence Invocation : Robot3.",
    grantsAbility: "summonRobot3",
    stackable: false,
  },
  robot4Core: {
    id: "robot4Core",
    category: "craftingMaterial",
    name: "Noyau de robot4",
    description: "Le cœur d'un robot4, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonRobot4Scroll: {
    id: "summonRobot4Scroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Robot4",
    description: "Apprend la compétence Invocation : Robot4.",
    grantsAbility: "summonRobot4",
    stackable: false,
  },
  robot5Core: {
    id: "robot5Core",
    category: "craftingMaterial",
    name: "Noyau de robot5",
    description: "Le cœur d'un robot5, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonRobot5Scroll: {
    id: "summonRobot5Scroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Robot5",
    description: "Apprend la compétence Invocation : Robot5.",
    grantsAbility: "summonRobot5",
    stackable: false,
  },
  robot6Core: {
    id: "robot6Core",
    category: "craftingMaterial",
    name: "Noyau de robot6",
    description: "Le cœur d'un robot6, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonRobot6Scroll: {
    id: "summonRobot6Scroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Robot6",
    description: "Apprend la compétence Invocation : Robot6.",
    grantsAbility: "summonRobot6",
    stackable: false,
  },
  robot7Core: {
    id: "robot7Core",
    category: "craftingMaterial",
    name: "Noyau de robot7",
    description: "Le cœur d'un robot7, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonRobot7Scroll: {
    id: "summonRobot7Scroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Robot7",
    description: "Apprend la compétence Invocation : Robot7.",
    grantsAbility: "summonRobot7",
    stackable: false,
  },
  robot8Core: {
    id: "robot8Core",
    category: "craftingMaterial",
    name: "Noyau de robot8",
    description: "Le cœur d'un robot8, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonRobot8Scroll: {
    id: "summonRobot8Scroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Robot8",
    description: "Apprend la compétence Invocation : Robot8.",
    grantsAbility: "summonRobot8",
    stackable: false,
  },
  robot9Core: {
    id: "robot9Core",
    category: "craftingMaterial",
    name: "Noyau de robot9",
    description: "Le cœur d'un robot9, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonRobot9Scroll: {
    id: "summonRobot9Scroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Robot9",
    description: "Apprend la compétence Invocation : Robot9.",
    grantsAbility: "summonRobot9",
    stackable: false,
  },
  robot10Core: {
    id: "robot10Core",
    category: "craftingMaterial",
    name: "Noyau de robot10",
    description: "Le cœur d'un robot10, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonRobot10Scroll: {
    id: "summonRobot10Scroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Robot10",
    description: "Apprend la compétence Invocation : Robot10.",
    grantsAbility: "summonRobot10",
    stackable: false,
  },
  skeletonCore: {
    id: "skeletonCore",
    category: "craftingMaterial",
    name: "Noyau de squelette",
    description: "Le cœur d'un squelette, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonSkeletonScroll: {
    id: "summonSkeletonScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Squelette",
    description: "Apprend la compétence Invocation : Squelette.",
    grantsAbility: "summonSkeleton",
    stackable: false,
  },
  skeletonkingCore: {
    id: "skeletonkingCore",
    category: "craftingMaterial",
    name: "Noyau de roi Squelette",
    description: "Le cœur d'un roi squelette, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonSkeletonkingScroll: {
    id: "summonSkeletonkingScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Roi Squelette",
    description: "Apprend la compétence Invocation : Roi Squelette.",
    grantsAbility: "summonSkeletonking",
    stackable: false,
  },
  greenSlimeCore: {
    id: "greenSlimeCore",
    category: "craftingMaterial",
    name: "Noyau de blob vert",
    description: "Le cœur d'un blob vert, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonGreenSlimeScroll: {
    id: "summonGreenSlimeScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Blob vert",
    description: "Apprend la compétence Invocation : Blob vert.",
    grantsAbility: "summonGreenSlime",
    stackable: false,
  },
  spiderCore: {
    id: "spiderCore",
    category: "craftingMaterial",
    name: "Noyau de araignée",
    description: "Le cœur d'une araignée, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonSpiderScroll: {
    id: "summonSpiderScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Tarentule",
    description: "Apprend la compétence Invocation : Tarentule.",
    grantsAbility: "summonSpider",
    stackable: false,
  },
  yellowSlimeCore: {
    id: "yellowSlimeCore",
    category: "craftingMaterial",
    name: "Noyau de blob jaune",
    description: "Le cœur d'un blob jaune, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonYellowSlimeScroll: {
    id: "summonYellowSlimeScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Blob jaune",
    description: "Apprend la compétence Invocation : Blob jaune.",
    grantsAbility: "summonYellowSlime",
    stackable: false,
  },
  blueSlimeCore: {
    id: "blueSlimeCore",
    category: "craftingMaterial",
    name: "Noyau de blob bleu",
    description: "Le cœur d'un blob bleu, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonBlueSlimeScroll: {
    id: "summonBlueSlimeScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Blob bleu",
    description: "Apprend la compétence Invocation : Blob bleu.",
    grantsAbility: "summonBlueSlime",
    stackable: false,
  },
  purpleSlimeCore: {
    id: "purpleSlimeCore",
    category: "craftingMaterial",
    name: "Noyau de blob violet",
    description: "Le cœur d'un blob violet, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonPurpleSlimeScroll: {
    id: "summonPurpleSlimeScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Blob violet",
    description: "Apprend la compétence Invocation : Blob violet.",
    grantsAbility: "summonPurpleSlime",
    stackable: false,
  },
  yellowWarriorMushroomCore: {
    id: "yellowWarriorMushroomCore",
    category: "craftingMaterial",
    name: "Noyau de guerrier Champignon Jaune",
    description:
      "Le cœur d'un guerrier champignon jaune, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonYellowWarriorMushroomScroll: {
    id: "summonYellowWarriorMushroomScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Guerrier Champignon Jaune",
    description:
      "Apprend la compétence Invocation : Guerrier Champignon Jaune.",
    grantsAbility: "summonYellowWarriorMushroom",
    stackable: false,
  },
  blueWarriorMushroomCore: {
    id: "blueWarriorMushroomCore",
    category: "craftingMaterial",
    name: "Noyau de guerrier Champignon Bleu",
    description:
      "Le cœur d'un guerrier chamignon bleu, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonBlueWarriorMushroomScroll: {
    id: "summonBlueWarriorMushroomScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Guerrier Champignon Bleu",
    description: "Apprend la compétence Invocation : Guerrier Champignon Bleu.",
    grantsAbility: "summonBlueWarriorMushroom",
    stackable: false,
  },
  greenWarriorMushroomCore: {
    id: "greenWarriorMushroomCore",
    category: "craftingMaterial",
    name: "Noyau de guerrier Champignon Vert",
    description:
      "Le cœur d'un guerrier champignon vert, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonGreenWarriorMushroomScroll: {
    id: "summonGreenWarriorMushroomScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Guerrier Champignon Vert",
    description: "Apprend la compétence Invocation : Guerrier Champignon Vert.",
    grantsAbility: "summonGreenWarriorMushroom",
    stackable: false,
  },
  purpleWarriorMushroomCore: {
    id: "purpleWarriorMushroomCore",
    category: "craftingMaterial",
    name: "Noyau de guerrier Champignon Violet",
    description:
      "Le cœur d'un guerrier champignon violet, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonPurpleWarriorMushroomScroll: {
    id: "summonPurpleWarriorMushroomScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Guerrier Champignon Violet",
    description:
      "Apprend la compétence Invocation : Guerrier Champignon Violet.",
    grantsAbility: "summonPurpleWarriorMushroom",
    stackable: false,
  },
  blackBearCore: {
    id: "blackBearCore",
    category: "craftingMaterial",
    name: "Noyau de ours noir",
    description: "Le cœur d'un ours noir, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  blackBearFurTuft: {
    id: "blackBearFurTuft",
    category: "craftingMaterial",
    name: "Touffe de fourrure d'ours noir",
    description:
      "Une touffe de fourrure d'ours noir, utilisée pour l'artisanat.",
    stackable: true,
  },
  blackBearPelt: {
    id: "blackBearPelt",
    category: "craftingMaterial",
    name: "Peau d'ours noir",
    description: "La peau d'un ours noir, utilisée pour l'artisanat.",
    stackable: true,
    unique: false,
  },
  summonBlackBearScroll: {
    id: "summonBlackBearScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Ours noir",
    description: "Apprend la compétence Invocation : Ours noir.",
    grantsAbility: "summonBlackBear",
    stackable: false,
  },
  whiteBearCore: {
    id: "whiteBearCore",
    category: "craftingMaterial",
    name: "Noyau de ours Blanc",
    description: "Le cœur d'un ours blanc, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonWhiteBearScroll: {
    id: "summonWhiteBearScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Ours Blanc",
    description: "Apprend la compétence Invocation : Ours Blanc.",
    grantsAbility: "summonWhiteBear",
    stackable: false,
  },
  knightBleuArgentCore: {
    id: "knightBleuArgentCore",
    category: "craftingMaterial",
    name: "Noyau de chevalier Bleu Argent",
    description:
      "Le cœur d'un chevalier bleu argent, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonKnightBleuArgentScroll: {
    id: "summonKnightBleuArgentScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Chevalier Bleu Argent",
    description: "Apprend la compétence Invocation : Chevalier Bleu Argent.",
    grantsAbility: "summonKnightBleuArgent",
    stackable: false,
  },
  knightNoirCramoisiCore: {
    id: "knightNoirCramoisiCore",
    category: "craftingMaterial",
    name: "Noyau de chevalier Noir Cramoisi",
    description:
      "Le cœur d'un chevalier noir cramoisi, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonKnightNoirCramoisiScroll: {
    id: "summonKnightNoirCramoisiScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Chevalier Noir Cramoisi",
    description: "Apprend la compétence Invocation : Chevalier Noir Cramoisi.",
    grantsAbility: "summonKnightNoirCramoisi",
    stackable: false,
  },
  knightVertOrCore: {
    id: "knightVertOrCore",
    category: "craftingMaterial",
    name: "Noyau de chevalier Vert or",
    description: "Le cœur d'un chevalier vert or, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonKnightVertOrScroll: {
    id: "summonKnightVertOrScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Chevalier Vert or",
    description: "Apprend la compétence Invocation : Chevalier Vert or.",
    grantsAbility: "summonKnightVertOr",
    stackable: false,
  },
  knightVioletArgentCore: {
    id: "knightVioletArgentCore",
    category: "craftingMaterial",
    name: "Noyau de chevalier Violet Argent",
    description:
      "Le cœur d'un chevalier violet argent, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonKnightVioletArgentScroll: {
    id: "summonKnightVioletArgentScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Chevalier Violet Argent",
    description: "Apprend la compétence Invocation : Chevalier Violet Argent.",
    grantsAbility: "summonKnightVioletArgent",
    stackable: false,
  },
  massecaillePurpleCore: {
    id: "massecaillePurpleCore",
    category: "craftingMaterial",
    name: "Noyau de massecaille Violet",
    description: "Le cœur d'un massecaille violet, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonMassecaillePurpleScroll: {
    id: "summonMassecaillePurpleScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Massecaille Violet",
    description: "Apprend la compétence Invocation : Massecaille Violet.",
    grantsAbility: "summonMassecaillePurple",
    stackable: false,
  },
  massecailleGreenCore: {
    id: "massecailleGreenCore",
    category: "craftingMaterial",
    name: "Noyau de massecaille Vert",
    description: "Le cœur d'un massecaille vert, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonMassecailleGreenScroll: {
    id: "summonMassecailleGreenScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Massecaille Vert",
    description: "Apprend la compétence Invocation : Massecaille Vert.",
    grantsAbility: "summonMassecailleGreen",
    stackable: false,
  },
  massecailleRedCore: {
    id: "massecailleRedCore",
    category: "craftingMaterial",
    name: "Noyau de massecaille Rouge",
    description: "Le cœur d'un massecaille rouge, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonMassecailleRedScroll: {
    id: "summonMassecailleRedScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Massecaille Rouge",
    description: "Apprend la compétence Invocation : Massecaille Rouge.",
    grantsAbility: "summonMassecailleRed",
    stackable: false,
  },
  massecailleYellowCore: {
    id: "massecailleYellowCore",
    category: "craftingMaterial",
    name: "Noyau de massecaille Jaune",
    description: "Le cœur d'un massecaille jaune, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonMassecailleYellowScroll: {
    id: "summonMassecailleYellowScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Massecaille Jaune",
    description: "Apprend la compétence Invocation : Massecaille Jaune.",
    grantsAbility: "summonMassecailleYellow",
    stackable: false,
  },
  orqueBlackCore: {
    id: "orqueBlackCore",
    category: "craftingMaterial",
    name: "Noyau de orque en noir",
    description: "Le cœur d'une orque en noir, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonOrqueBlackScroll: {
    id: "summonOrqueBlackScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Orque en noir",
    description: "Apprend la compétence Invocation : Orque en noir.",
    grantsAbility: "summonOrqueBlack",
    stackable: false,
  },
  orqueYellowCore: {
    id: "orqueYellowCore",
    category: "craftingMaterial",
    name: "Noyau de orque en jaune",
    description: "Le cœur d'une orque en jaune, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonOrqueYellowScroll: {
    id: "summonOrqueYellowScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Orque en jaune",
    description: "Apprend la compétence Invocation : Orque en jaune.",
    grantsAbility: "summonOrqueYellow",
    stackable: false,
  },
  orqueBlueCore: {
    id: "orqueBlueCore",
    category: "craftingMaterial",
    name: "Noyau de orque en bleu",
    description: "Le cœur d'une orque en bleu, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonOrqueBlueScroll: {
    id: "summonOrqueBlueScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Orque en bleu",
    description: "Apprend la compétence Invocation : Orque en bleu.",
    grantsAbility: "summonOrqueBlue",
    stackable: false,
  },
  orqueRedCore: {
    id: "orqueRedCore",
    category: "craftingMaterial",
    name: "Noyau de orque en rouge",
    description: "Le cœur d'une orque en rouge, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonOrqueRedScroll: {
    id: "summonOrqueRedScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Orque en rouge",
    description: "Apprend la compétence Invocation : Orque en rouge.",
    grantsAbility: "summonOrqueRed",
    stackable: false,
  },
  orquePurpleCore: {
    id: "orquePurpleCore",
    category: "craftingMaterial",
    name: "Noyau de orque en violet",
    description: "Le cœur d'une orque en violet, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonOrquePurpleScroll: {
    id: "summonOrquePurpleScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Orque en violet",
    description: "Apprend la compétence Invocation : Orque en violet.",
    grantsAbility: "summonOrquePurple",
    stackable: false,
  },
  orqueGreyCore: {
    id: "orqueGreyCore",
    category: "craftingMaterial",
    name: "Noyau de orque en gris",
    description: "Le cœur d'une orque en gris, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  orqueGreyFurTuft: {
    id: "orqueGreyFurTuft",
    category: "craftingMaterial",
    name: "Touffe de fourrure d'orque gris",
    description:
      "Une touffe de fourrure d'orque gris, utilisée pour l'artisanat.",
    stackable: false,
  },
  summonOrqueGreyScroll: {
    id: "summonOrqueGreyScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Orque en gris",
    description: "Apprend la compétence Invocation : Orque en gris.",
    grantsAbility: "summonOrqueGrey",
    stackable: false,
  },
  trollBlueCore: {
    id: "trollBlueCore",
    category: "craftingMaterial",
    name: "Noyau de troll Bleu",
    description: "Le cœur d'un troll bleu, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  trollBlueFurTuft: {
    id: "trollBlueFurTuft",
    category: "craftingMaterial",
    name: "Touffe de fourrure de troll bleu",
    description:
      "Une touffe de fourrure de troll bleu, utilisée pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonTrollBlueScroll: {
    id: "summonTrollBlueScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Troll Bleu",
    description: "Apprend la compétence Invocation : Troll Bleu.",
    grantsAbility: "summonTrollBlue",
    stackable: false,
  },
  trollGrisCore: {
    id: "trollGrisCore",
    category: "craftingMaterial",
    name: "Noyau de troll Gris",
    description: "Le cœur d'un troll gris, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  trollGrisFurTuft: {
    id: "trollGrisFurTuft",
    category: "craftingMaterial",
    name: "Touffe de fourrure de troll gris",
    description:
      "Une touffe de fourrure de troll gris, utilisée pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonTrollGrisScroll: {
    id: "summonTrollGrisScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Troll Gris",
    description: "Apprend la compétence Invocation : Troll Gris.",
    grantsAbility: "summonTrollGris",
    stackable: false,
  },
  trollRoseCore: {
    id: "trollRoseCore",
    category: "craftingMaterial",
    name: "Noyau de troll rose",
    description: "Le cœur d'une troll rose, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  trollRoseFurTuft: {
    id: "trollRoseFurTuft",
    category: "craftingMaterial",
    name: "Touffe de fourrure de troll rose",
    description:
      "Une touffe de fourrure de troll rose, utilisée pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonTrollRoseScroll: {
    id: "summonTrollRoseScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Troll rose",
    description: "Apprend la compétence Invocation : Troll rose.",
    grantsAbility: "summonTrollRose",
    stackable: false,
  },
  trollRougeCore: {
    id: "trollRougeCore",
    category: "craftingMaterial",
    name: "Noyau de troll Rouge",
    description: "Le cœur d'un troll rouge, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  trollRougeFurTuft: {
    id: "trollRougeFurTuft",
    category: "craftingMaterial",
    name: "Touffe de fourrure de troll rouge",
    description:
      "Une touffe de fourrure de troll rouge, utilisée pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonTrollRougeScroll: {
    id: "summonTrollRougeScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Troll Rouge",
    description: "Apprend la compétence Invocation : Troll Rouge.",
    grantsAbility: "summonTrollRouge",
    stackable: false,
  },
  trollVertCore: {
    id: "trollVertCore",
    category: "craftingMaterial",
    name: "Noyau de troll vert",
    description: "Le cœur d'un troll vert, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  trollVertFurTuft: {
    id: "trollVertFurTuft",
    category: "craftingMaterial",
    name: "Touffe de fourrure de troll vert",
    description:
      "Une touffe de fourrure de troll vert, utilisée pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonTrollVertScroll: {
    id: "summonTrollVertScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Troll vert",
    description: "Apprend la compétence Invocation : Troll vert.",
    grantsAbility: "summonTrollVert",
    stackable: false,
  },
  trollVioletCore: {
    id: "trollVioletCore",
    category: "craftingMaterial",
    name: "Noyau de troll Violet",
    description: "Le cœur d'une troll  violette, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  trollVioletFurTuft: {
    id: "trollVioletFurTuft",
    category: "craftingMaterial",
    name: "Touffe de fourrure de troll violet",
    description:
      "Une touffe de fourrure de troll violet, utilisée pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonTrollVioletScroll: {
    id: "summonTrollVioletScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Troll Violet",
    description: "Apprend la compétence Invocation : Troll Violet.",
    grantsAbility: "summonTrollViolet",
    stackable: false,
  },
  warlockRedCore: {
    id: "warlockRedCore",
    category: "craftingMaterial",
    name: "Noyau de sorcier Rouge",
    description: "Le cœur d'un sorcier rouge, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonWarlockRedScroll: {
    id: "summonWarlockRedScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Sorcier Rouge",
    description: "Apprend la compétence Invocation : Sorcier Rouge.",
    grantsAbility: "summonWarlockRed",
    stackable: false,
  },
  warlockWhiteCore: {
    id: "warlockWhiteCore",
    category: "craftingMaterial",
    name: "Noyau de sorcier Blanc",
    description: "Le cœur d'un sorcier blanc, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonWarlockWhiteScroll: {
    id: "summonWarlockWhiteScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Sorcier Blanc",
    description: "Apprend la compétence Invocation : Sorcier Blanc.",
    grantsAbility: "summonWarlockWhite",
    stackable: false,
  },
  warlockGreenCore: {
    id: "warlockGreenCore",
    category: "craftingMaterial",
    name: "Noyau de sorcier Vert",
    description: "Le cœur d'un sorcier vert, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonWarlockGreenScroll: {
    id: "summonWarlockGreenScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Sorcier Vert",
    description: "Apprend la compétence Invocation : Sorcier Vert.",
    grantsAbility: "summonWarlockGreen",
    stackable: false,
  },
  warlockBlueCore: {
    id: "warlockBlueCore",
    category: "craftingMaterial",
    name: "Noyau de sorcier Bleu",
    description: "Le cœur d'un sorcier bleu, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonWarlockBlueScroll: {
    id: "summonWarlockBlueScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Sorcier Bleu",
    description: "Apprend la compétence Invocation : Sorcier Bleu.",
    grantsAbility: "summonWarlockBlue",
    stackable: false,
  },
  warlockBlackCore: {
    id: "warlockBlackCore",
    category: "craftingMaterial",
    name: "Noyau de sorcier Noir",
    description: "Le cœur d'un sorcier noir, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonWarlockBlackScroll: {
    id: "summonWarlockBlackScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Sorcier Noir",
    description: "Apprend la compétence Invocation : Sorcier Noir.",
    grantsAbility: "summonWarlockBlack",
    stackable: false,
  },
  warlockPurpleCore: {
    id: "warlockPurpleCore",
    category: "craftingMaterial",
    name: "Noyau de sorcier Violet",
    description: "Le cœur d'un sorcier violet, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonWarlockPurpleScroll: {
    id: "summonWarlockPurpleScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Sorcier Violet",
    description: "Apprend la compétence Invocation : Sorcier Violet.",
    grantsAbility: "summonWarlockPurple",
    stackable: false,
  },

  golemEauCore: {
    id: "golemEauCore",
    category: "craftingMaterial",
    name: "Noyau de golem d'eau",
    description: "Le cœur d'un golem d'eau, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonGolemEauScroll: {
    id: "summonGolemEauScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Golem d'eau",
    description: "Apprend la compétence Invocation : Golem d'eau.",
    grantsAbility: "summonGolemEau",
    stackable: false,
  },
  golemFeuCore: {
    id: "golemFeuCore",
    category: "craftingMaterial",
    name: "Noyau de golem de feu",
    description: "Le cœur d'un golem de feu, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonGolemFeuScroll: {
    id: "summonGolemFeuScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Golem de feu",
    description: "Apprend la compétence Invocation : Golem de feu.",
    grantsAbility: "summonGolemFeu",
    stackable: false,
  },
  golemFoudreCore: {
    id: "golemFoudreCore",
    category: "craftingMaterial",
    name: "Noyau de golem de foudre",
    description: "Le cœur d'un golem de foudre, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonGolemFoudreScroll: {
    id: "summonGolemFoudreScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Golem de foudre",
    description: "Apprend la compétence Invocation : Golem de foudre.",
    grantsAbility: "summonGolemFoudre",
    stackable: false,
  },
  golemGlaceCore: {
    id: "golemGlaceCore",
    category: "craftingMaterial",
    name: "Noyau de golem de glace",
    description: "Le cœur d'un golem de glace, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonGolemGlaceScroll: {
    id: "summonGolemGlaceScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Golem de glace",
    description: "Apprend la compétence Invocation : Golem de glace.",
    grantsAbility: "summonGolemGlace",
    stackable: false,
  },
  golemOmbreCore: {
    id: "golemOmbreCore",
    category: "craftingMaterial",
    name: "Noyau de golem d'ombre",
    description: "Le cœur d'un golem d'ombre, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonGolemOmbreScroll: {
    id: "summonGolemOmbreScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Golem d'ombre",
    description: "Apprend la compétence Invocation : Golem d'ombre.",
    grantsAbility: "summonGolemOmbre",
    stackable: false,
  },
  zombiequeenBossCore: {
    id: "zombiequeenBossCore",
    category: "craftingMaterial",
    name: "Noyau de reine Zombie",
    description: "Le cœur d'une reine zombie, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonZombiequeenBossScroll: {
    id: "summonZombiequeenBossScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Reine Zombie",
    description: "Apprend la compétence Invocation : Reine Zombie.",
    grantsAbility: "summonZombiequeenBoss",
    stackable: false,
  },
  zombiequeenCore: {
    id: "zombiequeenCore",
    category: "craftingMaterial",
    name: "Noyau de reine Zombie",
    description: "Le cœur d'une reine zombie, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonZombiequeenScroll: {
    id: "summonZombiequeenScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Reine Zombie",
    description: "Apprend la compétence Invocation : Reine Zombie.",
    grantsAbility: "summonZombiequeen",
    stackable: false,
  },
  beequeenBossCore: {
    id: "beequeenBossCore",
    category: "craftingMaterial",
    name: "Noyau de reine des abeilles",
    description: "Le cœur d'une reine des abeilles, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonBeequeenBossScroll: {
    id: "summonBeequeenBossScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Reine des abeilles",
    description: "Apprend la compétence Invocation : Reine des abeilles.",
    grantsAbility: "summonBeequeenBoss",
    stackable: false,
  },
  beequeenCore: {
    id: "beequeenCore",
    category: "craftingMaterial",
    name: "Noyau de abeille Royale",
    description: "Le cœur d'une abeille royale, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonBeequeenScroll: {
    id: "summonBeequeenScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Abeille Royale",
    description: "Apprend la compétence Invocation : Abeille Royale.",
    grantsAbility: "summonBeequeen",
    stackable: false,
  },

  // ===================================================================
  // ===== Ajouts du 24/09 (cf. CHANTIERS.md) - objets manquants     =====
  // ===== referencés par craftingRecipes.js mais absents d'ici       =====
  // ===================================================================

  // --- Chaîne de casques en métal (wood -> nova), miroir exact des stats
  //     de la chaîne "armor" (chest) au même palier - manquait entièrement.
  woodenHelmet: {
    id: "woodenHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque en bois",
    description: "+1 défense. Casque d'entraînement de départ.",
    statBonus: { defense: 1 },
    stackable: false,
    price: 2,
  },
  copperHelmet: {
    id: "copperHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque en cuivre",
    description: "+2 défense.",
    statBonus: { defense: 2 },
    stackable: false,
    price: 30,
  },
  ironHelmet: {
    id: "ironHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque en fer",
    description: "+3 défense.",
    statBonus: { defense: 3 },
    stackable: false,
    price: 60,
  },
  silverHelmet: {
    id: "silverHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque en argent",
    description: "+5 défense.",
    statBonus: { defense: 5 },
    stackable: false,
    price: 150,
  },
  steelHelmet: {
    id: "steelHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque en acier",
    description: "+4 défense.",
    statBonus: { defense: 4 },
    stackable: false,
    price: 100,
  },
  goldHelmet: {
    id: "goldHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque en or",
    description: "+6 défense.",
    statBonus: { defense: 6 },
    stackable: false,
    price: 200,
  },
  platiniumHelmet: {
    id: "platiniumHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque en platine",
    description: "+7 défense.",
    statBonus: { defense: 7 },
    stackable: false,
    price: 300,
  },
  cobaltHelmet: {
    id: "cobaltHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque en cobalt",
    description: "+8 défense.",
    statBonus: { defense: 8 },
    stackable: false,
    price: 400,
  },
  adamantineHelmet: {
    id: "adamantineHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque en adamantine",
    description: "+9 défense.",
    statBonus: { defense: 9 },
    stackable: false,
    price: 500,
  },
  crimsonHelmet: {
    id: "crimsonHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque en cramoisi",
    description: "+10 défense.",
    statBonus: { defense: 10 },
    stackable: false,
    price: 600,
  },
  angelicHelmet: {
    id: "angelicHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque en angélique",
    description: "+11 défense.",
    statBonus: { defense: 11 },
    stackable: false,
    price: 700,
  },
  fatefulHelmet: {
    id: "fatefulHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque en fatidique",
    description: "+12 défense.",
    statBonus: { defense: 12 },
    stackable: false,
    price: 800,
  },
  novaHelmet: {
    id: "novaHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque en nova",
    description: "+13 défense.",
    statBonus: { defense: 13 },
    stackable: false,
    price: 900,
  },
  reinforcedWoodenHelmet: {
    id: "reinforcedWoodenHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque en bois renforcé",
    description: "+3 défense.",
    statBonus: { defense: 3 },
    stackable: false,
    price: 50,
  },
  reinforcedCopperHelmet: {
    id: "reinforcedCopperHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque en cuivre renforcé",
    description: "+4 défense.",
    statBonus: { defense: 4 },
    stackable: false,
    price: 65,
  },
  reinforcedIronHelmet: {
    id: "reinforcedIronHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque en fer renforcé",
    description: "+5 défense.",
    statBonus: { defense: 5 },
    stackable: false,
    price: 80,
  },
  reinforcedSilverHelmet: {
    id: "reinforcedSilverHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque en argent renforcé",
    description: "+7 défense.",
    statBonus: { defense: 7 },
    stackable: false,
    price: 180,
  },
  reinforcedSteelHelmet: {
    id: "reinforcedSteelHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque en acier renforcé",
    description: "+6 défense.",
    statBonus: { defense: 6 },
    stackable: false,
    price: 120,
  },
  reinforcedGoldHelmet: {
    id: "reinforcedGoldHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque en or renforcé",
    description: "+8 défense.",
    statBonus: { defense: 8 },
    stackable: false,
    price: 250,
  },
  reinforcedPlatiniumHelmet: {
    id: "reinforcedPlatiniumHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque en platine renforcé",
    description: "+9 défense.",
    statBonus: { defense: 9 },
    stackable: false,
    price: 350,
  },
  reinforcedCobaltHelmet: {
    id: "reinforcedCobaltHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque en cobalt renforcé",
    description: "+10 défense.",
    statBonus: { defense: 10 },
    stackable: false,
    price: 450,
  },
  reinforcedAdamantineHelmet: {
    id: "reinforcedAdamantineHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque en adamantine renforcé",
    description: "+11 défense.",
    statBonus: { defense: 11 },
    stackable: false,
    price: 550,
  },
  reinforcedCrimsonHelmet: {
    id: "reinforcedCrimsonHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque en cramoisi renforcé",
    description: "+12 défense.",
    statBonus: { defense: 12 },
    stackable: false,
    price: 650,
  },
  reinforcedAngelicHelmet: {
    id: "reinforcedAngelicHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque en angélique renforcé",
    description: "+13 défense.",
    statBonus: { defense: 13 },
    stackable: false,
    price: 750,
  },
  reinforcedFatefulHelmet: {
    id: "reinforcedFatefulHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque en fatidique renforcé",
    description: "+14 défense.",
    statBonus: { defense: 14 },
    stackable: false,
    price: 850,
  },
  reinforcedNovaHelmet: {
    id: "reinforcedNovaHelmet",
    category: "equipment",
    slot: "helmet",
    name: "Casque en nova renforcé",
    description: "+15 défense.",
    statBonus: { defense: 15 },
    stackable: false,
    price: 950,
  },

  // --- Matériaux de monstre manquants (utilisés comme matériau de montée
  //     de palier des armures dans craftingRecipes.js) - même famille que
  //     blackBearPelt, qui existe déjà.
  slimeBlob: {
    id: "slimeBlob",
    category: "craftingMaterial",
    name: "Blob de slime",
    description: "Un résidu gluant de slime, utilisé pour l'artisanat.",
    stackable: true,
  },
  bearPelt: {
    id: "bearPelt",
    category: "craftingMaterial",
    name: "Peau d'ours",
    description: "La peau d'un ours, utilisée pour l'artisanat.",
    stackable: true,
  },
  spiderLeg: {
    id: "spiderLeg",
    category: "craftingMaterial",
    name: "Patte d'araignée",
    description: "Une patte d'araignée, utilisée pour l'artisanat.",
    stackable: true,
  },
  greyMonsterScale: {
    id: "greyMonsterScale",
    category: "craftingMaterial",
    name: "Écaille de monstre grise",
    description: "Une écaille de monstre grise, utilisée pour l'artisanat.",
    stackable: true,
  },
  crabClaw: {
    id: "crabClaw",
    category: "craftingMaterial",
    name: "Pince de crabe",
    description: "Une pince de crabe, utilisée pour l'artisanat.",
    stackable: true,
  },
  turtleShell: {
    id: "turtleShell",
    category: "craftingMaterial",
    name: "Carapace de tortue",
    description: "Une carapace de tortue, utilisée pour l'artisanat.",
    stackable: true,
  },
  greenMonsterScale: {
    id: "greenMonsterScale",
    category: "craftingMaterial",
    name: "Écaille de monstre verte",
    description: "Une écaille de monstre verte, utilisée pour l'artisanat.",
    stackable: true,
  },
  dragonScale: {
    id: "dragonScale",
    category: "craftingMaterial",
    name: "Écaille de dragon",
    description: "Une écaille de dragon, utilisée pour l'artisanat.",
    stackable: true,
  },
  ghostEctoplasm: {
    id: "ghostEctoplasm",
    category: "craftingMaterial",
    name: "Ectoplasme de fantôme",
    description:
      "L'ectoplasme résiduel d'un fantôme, utilisé pour l'artisanat.",
    stackable: true,
  },
  monsterCore: {
    id: "monsterCore",
    category: "craftingMaterial",
    name: "Noyau de monstre",
    description: "Le noyau d'énergie d'un monstre, utilisé pour l'artisanat.",
    stackable: true,
  },
  furTuft: {
    id: "furTuft",
    category: "craftingMaterial",
    name: "Touffe de fourrure",
    description: "Une touffe de fourrure, utilisée pour l'artisanat.",
    stackable: true,
  },

  // --- Bois tardifs manquants (les objets finaux comme mistwoodBow
  //     existent déjà, seul le matériau manquait).
  mistWood: {
    id: "mistWood",
    category: "craftingMaterial",
    name: "Bois de brume",
    description:
      "Un bois léger et vaporeux, presque translucide dans la brume.",
    stackable: true,
  },
  runeWood: {
    id: "runeWood",
    category: "craftingMaterial",
    name: "Bois runique",
    description: "Un bois gravé de runes anciennes, empli de magie latente.",
    stackable: true,
  },
  skyWood: {
    id: "skyWood",
    category: "craftingMaterial",
    name: "Bois du ciel",
    description: "Un bois extrêmement léger, comme porté par le vent.",
    stackable: true,
  },
  scarletWood: {
    id: "scarletWood",
    category: "craftingMaterial",
    name: "Bois écarlate",
    description: "Un bois teinté d'un rouge profond, réputé pour sa dureté.",
    stackable: true,
  },
  starWood: {
    id: "starWood",
    category: "craftingMaterial",
    name: "Bois d'étoile",
    description:
      "Un bois rarissime, scintillant comme de la poussière d'étoile.",
    stackable: true,
  },

  // --- Matériaux de craft de base manquants (utilisés par 24 recettes
  //     manuelles de scrolls/potions/bombes/enchantements).
  wood: {
    id: "wood",
    category: "craftingMaterial",
    name: "Bois",
    description: "Un morceau de bois, utilisé en artisanat.",
    stackable: true,
    price: 5,
  },
  paper: {
    id: "paper",
    category: "craftingMaterial",
    name: "Papier",
    description: "Une feuille de papier, utilisée en artisanat.",
    stackable: true,
    price: 5,
  },
  pepper: {
    id: "pepper",
    category: "craftingMaterial",
    name: "Poivre",
    description: "Du poivre, utilisé en artisanat.",
    stackable: true,
    price: 5,
  },
  sulfur: {
    id: "sulfur",
    category: "craftingMaterial",
    name: "Soufre",
    description: "Un morceau de soufre, utilisé en artisanat.",
    stackable: true,
    price: 5,
  },
  holyWater: {
    id: "holyWater",
    category: "craftingMaterial",
    name: "Eau bénite",
    description: "De l'eau bénite, utilisée en artisanat.",
    stackable: true,
    price: 5,
  },
  shadowEssence: {
    id: "shadowEssence",
    category: "craftingMaterial",
    name: "Essence d'ombre",
    description: "Une essence d'ombre, utilisée en artisanat.",
    stackable: true,
    price: 5,
  },
  fireCrystal: {
    id: "fireCrystal",
    category: "craftingMaterial",
    name: "Cristal de feu",
    description: "Un cristal empli d'énergie de feu, utilisé en artisanat.",
    stackable: true,
    price: 5,
  },
  iceCrystal: {
    id: "iceCrystal",
    category: "craftingMaterial",
    name: "Cristal de glace",
    description: "Un cristal empli d'énergie de glace, utilisé en artisanat.",
    stackable: true,
    price: 5,
  },
  lightningShard: {
    id: "lightningShard",
    category: "craftingMaterial",
    name: "Éclat de foudre",
    description: "Un éclat chargé d'énergie électrique, utilisé en artisanat.",
    stackable: true,
    price: 5,
  },
  featherRoot: {
    id: "featherRoot",
    category: "craftingMaterial",
    name: "Racine-plume",
    description: "Une racine légère comme une plume, utilisée en artisanat.",
    stackable: true,
    price: 5,
  },
  frogLeg: {
    id: "frogLeg",
    category: "craftingMaterial",
    name: "Cuisse de grenouille",
    description: "Une cuisse de grenouille, utilisée en artisanat.",
    stackable: true,
    price: 5,
  },
  slimeCore: {
    id: "slimeCore",
    category: "craftingMaterial",
    name: "Noyau de slime",
    description: "Le noyau d'énergie d'un slime, utilisé en artisanat.",
    stackable: true,
    price: 5,
  },

  // --- Objets isolés manquants (armes/armures/bijoux référencés par une
  //     recette ou une liste de palier mais jamais définis ici).
  copperHpNecklace: {
    id: "copperHpNecklace",
    category: "equipment",
    slot: "necklace",
    name: "Collier de vie en copper",
    description: "+10 PV max.",
    statBonus: { maxHp: 10 },
    stackable: false,
    price: 14,
  },
  platiniumSpear: {
    id: "platiniumSpear",
    category: "equipment",
    slot: "mainHand",
    twoHanded: true,
    grantsRanged: false,
    name: "Lance en platine",
    description: "+12 dégât au corps à corps. +3 de distance d'attaque.",
    statBonus: { meleeDamage: 12, meleeRange: 3 },
    stackable: false,
    archetypes: ["guerrier"],
    price: 400,
  },
  crimsonDagger: {
    id: "crimsonDagger",
    category: "equipment",
    slot: "mainHand",
    name: "Dague cramoisie",
    description: "Dague de base cramoisie.",
    statBonus: { meleeDamage: 20 },
    stackable: false,
    archetypes: ["voleur", "assassin"],
    price: 215,
  },
  reinforcedMageRobe: {
    id: "reinforcedMageRobe",
    category: "equipment",
    slot: "armor",
    name: "Robe de mage de base renforcée",
    description: "+3 défense.",
    statBonus: { defense: 3 },
    archetypes: ["mage"],
    stackable: false,
    price: 50,
  },
  reinforcedWoodenShield: {
    id: "reinforcedWoodenShield",
    category: "equipment",
    slot: "offHand",
    isShield: true,
    twoHanded: false,
    grantsRanged: false,
    name: "Bouclier en bois renforcé",
    description: "+3 défense.",
    statBonus: { defense: 3 },
    archetypes: ["guerrier"],
    stackable: false,
    price: 50,
  },
  sacredwoodHpRing: {
    id: "sacredwoodHpRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de vie en sacredwood",
    description: "+55 PV max, +2.2 régén. PV/s.",
    statBonus: { maxHp: 55, hpRegen: 2.2 },
    stackable: false,
    price: 77,
  },
  sacredwoodStaminaRing: {
    id: "sacredwoodStaminaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau d'endurance en sacredwood",
    description: "+33 endurance max, +1.32 régén. endurance/s.",
    statBonus: { stamina: 33, staminaRegen: 1.32 },
    stackable: false,
    price: 77,
  },
  sacredwoodManaRing: {
    id: "sacredwoodManaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de mana en sacredwood",
    description: "+33 mana max, +1.32 régén. mana/s.",
    statBonus: { mana: 33, manaRegen: 1.32 },
    stackable: false,
    price: 77,
  },
  eternalwoodHpRing: {
    id: "eternalwoodHpRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de vie en eternalwood",
    description: "+60 PV max, +2.4 régén. PV/s.",
    statBonus: { maxHp: 60, hpRegen: 2.4 },
    stackable: false,
    price: 84,
  },
  eternalwoodStaminaRing: {
    id: "eternalwoodStaminaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau d'endurance en eternalwood",
    description: "+36 endurance max, +1.44 régén. endurance/s.",
    statBonus: { stamina: 36, staminaRegen: 1.44 },
    stackable: false,
    price: 84,
  },
  eternalwoodManaRing: {
    id: "eternalwoodManaRing",
    category: "equipment",
    slot: "ring",
    name: "Anneau de mana en eternalwood",
    description: "+36 mana max, +1.44 régén. mana/s.",
    statBonus: { mana: 36, manaRegen: 1.44 },
    stackable: false,
    price: 84,
  },

  // --- Parité avec itemTypes.js (serveur) : ces deux objets sont
  //     lootables/achetables côté serveur mais n'avaient pas de miroir
  //     ici, contrairement à l'invariant documenté en tête de fichier.
  //     Copié tel quel depuis itemTypes.js.
  crossbow: {
    id: "crossbow",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: true,
    requiresAmmo: ["woodenCrossbowBolt", "ironCrossbowBolt"],
    name: "Arbalète",
    description: "+3 dégâts à distance. Se manie a une main.",
    statBonus: { rangedDamage: 3, rangedRange: 5 },
    stackable: false,
    archetypes: ["archer", "voleur"],
    unlockLevel: 2,
  },
  reinforcedSword: {
    id: "reinforcedSword",
    category: "equipment",
    slot: "mainHand",
    twoHanded: false,
    grantsRanged: false,
    name: "Épée renforcée",
    description: "+2 dégât au corps à corps. Arme d'entraînement avancée.",
    statBonus: { meleeDamage: 2 },
    stackable: false,
    archetypes: ["guerrier"],
    inflictsEffect: {
      type: "bleed",
      chance: 0.9,
      damagePerTick: 1,
      tickIntervalMs: 1000,
      ticks: 3,
    },
  },
  grayGriblinCore: {
    id: "grayGriblinCore",
    category: "craftingMaterial",
    name: "Noyau de griblin",
    description: "Le cœur d'un griblin, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonGrayGriblinScroll: {
    id: "summonGrayGriblinScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Griblin",
    description: "Apprend la compétence Invocation : Griblin.",
    grantsAbility: "summonGrayGriblin",
    stackable: false,
  },
  greenGriblinCore: {
    id: "greenGriblinCore",
    category: "craftingMaterial",
    name: "Noyau de griblin Emeraude",
    description: "Le cœur d'un griblin émeraude, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonGreenGriblinScroll: {
    id: "summonGreenGriblinScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Griblin Emeraude",
    description: "Apprend la compétence Invocation : Griblin Emeraude.",
    grantsAbility: "summonGreenGriblin",
    stackable: false,
  },
  redGriblinCore: {
    id: "redGriblinCore",
    category: "craftingMaterial",
    name: "Noyau de griblin Ecarlate",
    description: "Le cœur d'un griblin écarlate, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonRedGriblinScroll: {
    id: "summonRedGriblinScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Griblin Ecarlate",
    description: "Apprend la compétence Invocation : Griblin Ecarlate.",
    grantsAbility: "summonRedGriblin",
    stackable: false,
  },
  blueGriblinCore: {
    id: "blueGriblinCore",
    category: "craftingMaterial",
    name: "Noyau de grinblin Azur",
    description: "Le cœur d'un griblin azur, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonBlueGriblinScroll: {
    id: "summonBlueGriblinScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Grinblin Azur",
    description: "Apprend la compétence Invocation : Grinblin Azur.",
    grantsAbility: "summonBlueGriblin",
    stackable: false,
  },
  purpleGriblinCore: {
    id: "purpleGriblinCore",
    category: "craftingMaterial",
    name: "Noyau de griblin Améthyste",
    description: "Le cœur d'un griblin améthyste, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonPurpleGriblinScroll: {
    id: "summonPurpleGriblinScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Griblin Améthyste",
    description: "Apprend la compétence Invocation : Griblin Améthyste.",
    grantsAbility: "summonPurpleGriblin",
    stackable: false,
  },
  goldGriblinCore: {
    id: "goldGriblinCore",
    category: "craftingMaterial",
    name: "Noyau de griblin Doré",
    description: "Le cœur d'un griblin doré, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonGoldGriblinScroll: {
    id: "summonGoldGriblinScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Griblin Doré",
    description: "Apprend la compétence Invocation : Griblin Doré.",
    grantsAbility: "summonGoldGriblin",
    stackable: false,
  },
  redManditaureCore: {
    id: "redManditaureCore",
    category: "craftingMaterial",
    name: "Noyau de manditaure écarlate",
    description: "Le cœur d'un manditaure écarlate, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonRedManditaureScroll: {
    id: "summonRedManditaureScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Manditaure écarlate",
    description: "Apprend la compétence Invocation : Manditaure écarlate.",
    grantsAbility: "summonRedManditaure",
    stackable: false,
  },
  blueManditaureCore: {
    id: "blueManditaureCore",
    category: "craftingMaterial",
    name: "Noyau de manditaure azur",
    description: "Le cœur d'un manditaure azur, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonBlueManditaureScroll: {
    id: "summonBlueManditaureScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Manditaure azur",
    description: "Apprend la compétence Invocation : Manditaure azur.",
    grantsAbility: "summonBlueManditaure",
    stackable: false,
  },
  greenManditaureCore: {
    id: "greenManditaureCore",
    category: "craftingMaterial",
    name: "Noyau de manditaure émeraude",
    description: "Le cœur d'un manditaure émeraude, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonGreenManditaureScroll: {
    id: "summonGreenManditaureScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Manditaure émeraude",
    description: "Apprend la compétence Invocation : Manditaure émeraude.",
    grantsAbility: "summonGreenManditaure",
    stackable: false,
  },
  purpleManditaureCore: {
    id: "purpleManditaureCore",
    category: "craftingMaterial",
    name: "Noyau de manditaure améthyste",
    description:
      "Le cœur d'une manditaure améthyste, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonPurpleManditaureScroll: {
    id: "summonPurpleManditaureScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Manditaure améthyste",
    description: "Apprend la compétence Invocation : Manditaure améthyste.",
    grantsAbility: "summonPurpleManditaure",
    stackable: false,
  },
  yellowManditaureCore: {
    id: "yellowManditaureCore",
    category: "craftingMaterial",
    name: "Noyau de manditaure doré",
    description: "Le cœur d'un manditaure doré, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonYellowManditaureScroll: {
    id: "summonYellowManditaureScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Manditaure doré",
    description: "Apprend la compétence Invocation : Manditaure doré.",
    grantsAbility: "summonYellowManditaure",
    stackable: false,
  },
  blackManditaureCore: {
    id: "blackManditaureCore",
    category: "craftingMaterial",
    name: "Noyau de manditaure noir",
    description: "Le cœur d'un manditaure noir, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonBlackManditaureScroll: {
    id: "summonBlackManditaureScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Manditaure noir",
    description: "Apprend la compétence Invocation : Manditaure noir.",
    grantsAbility: "summonBlackManditaure",
    stackable: false,
  },
  purpleSpectrelinCore: {
    id: "purpleSpectrelinCore",
    category: "craftingMaterial",
    name: "Noyau de spectrelin",
    description: "Le cœur d'un spectrelin, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonPurpleSpectrelinScroll: {
    id: "summonPurpleSpectrelinScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Spectrelin",
    description: "Apprend la compétence Invocation : Spectrelin.",
    grantsAbility: "summonPurpleSpectrelin",
    stackable: false,
  },
  orangeSpectrelinCore: {
    id: "orangeSpectrelinCore",
    category: "craftingMaterial",
    name: "Noyau de brumelin",
    description: "Le cœur d'un brumelin, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonOrangeSpectrelinScroll: {
    id: "summonOrangeSpectrelinScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Brumelin",
    description: "Apprend la compétence Invocation : Brumelin.",
    grantsAbility: "summonOrangeSpectrelin",
    stackable: false,
  },
  cyanSpectrelinCore: {
    id: "cyanSpectrelinCore",
    category: "craftingMaterial",
    name: "Noyau de glaselin",
    description: "Le cœur d'un glaselin, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonCyanSpectrelinScroll: {
    id: "summonCyanSpectrelinScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Glaselin",
    description: "Apprend la compétence Invocation : Glaselin.",
    grantsAbility: "summonCyanSpectrelin",
    stackable: false,
  },
  pinkSpectrelinCore: {
    id: "pinkSpectrelinCore",
    category: "craftingMaterial",
    name: "Noyau de hanteline",
    description: "Le cœur d'une hanteline, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonPinkSpectrelinScroll: {
    id: "summonPinkSpectrelinScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Hanteline",
    description: "Apprend la compétence Invocation : Hanteline.",
    grantsAbility: "summonPinkSpectrelin",
    stackable: false,
  },
  silverSpectrelinCore: {
    id: "silverSpectrelinCore",
    category: "craftingMaterial",
    name: "Noyau de ombrelin",
    description: "Le cœur d'un ombrelin, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonSilverSpectrelinScroll: {
    id: "summonSilverSpectrelinScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Ombrelin",
    description: "Apprend la compétence Invocation : Ombrelin.",
    grantsAbility: "summonSilverSpectrelin",
    stackable: false,
  },
  bronzeSpectrelinCore: {
    id: "bronzeSpectrelinCore",
    category: "craftingMaterial",
    name: "Noyau de echolin",
    description: "Le cœur d'un écholin, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonBronzeSpectrelinScroll: {
    id: "summonBronzeSpectrelinScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Echolin",
    description: "Apprend la compétence Invocation : Echolin.",
    grantsAbility: "summonBronzeSpectrelin",
    stackable: false,
  },
  greenGemmacrabeCore: {
    id: "greenGemmacrabeCore",
    category: "craftingMaterial",
    name: "Noyau de gemmacrabe",
    description: "Le cœur d'un gemmacrabe, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonGreenGemmacrabeScroll: {
    id: "summonGreenGemmacrabeScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Gemmacrabe",
    description: "Apprend la compétence Invocation : Gemmacrabe.",
    grantsAbility: "summonGreenGemmacrabe",
    stackable: false,
  },
  redGemmacrabeCore: {
    id: "redGemmacrabeCore",
    category: "craftingMaterial",
    name: "Noyau de braisecrabe",
    description: "Le cœur d'un braisecrabe, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonRedGemmacrabeScroll: {
    id: "summonRedGemmacrabeScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Braisecrabe",
    description: "Apprend la compétence Invocation : Braisecrabe.",
    grantsAbility: "summonRedGemmacrabe",
    stackable: false,
  },
  blueGemmacrabeCore: {
    id: "blueGemmacrabeCore",
    category: "craftingMaterial",
    name: "Noyau de abyssacrabe",
    description: "Le cœur d'un abyssacrabe, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonBlueGemmacrabeScroll: {
    id: "summonBlueGemmacrabeScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Abyssacrabe",
    description: "Apprend la compétence Invocation : Abyssacrabe.",
    grantsAbility: "summonBlueGemmacrabe",
    stackable: false,
  },
  yellowGemmacrabeCore: {
    id: "yellowGemmacrabeCore",
    category: "craftingMaterial",
    name: "Noyau de ambracrabre",
    description: "Le cœur d'un ambracrabe, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonYellowGemmacrabeScroll: {
    id: "summonYellowGemmacrabeScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Ambracrabre",
    description: "Apprend la compétence Invocation : Ambracrabre.",
    grantsAbility: "summonYellowGemmacrabe",
    stackable: false,
  },
  orangeGemmacrabeCore: {
    id: "orangeGemmacrabeCore",
    category: "craftingMaterial",
    name: "Noyau de corailcrabe",
    description: "Le cœur d'un corailcrabe, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonOrangeGemmacrabeScroll: {
    id: "summonOrangeGemmacrabeScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Corailcrabe",
    description: "Apprend la compétence Invocation : Corailcrabe.",
    grantsAbility: "summonOrangeGemmacrabe",
    stackable: false,
  },
  whiteGemmacrabeCore: {
    id: "whiteGemmacrabeCore",
    category: "craftingMaterial",
    name: "Noyau de nacrabe",
    description: "Le cœur d'un nacrabe, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonWhiteGemmacrabeScroll: {
    id: "summonWhiteGemmacrabeScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Nacrabe",
    description: "Apprend la compétence Invocation : Nacrabe.",
    grantsAbility: "summonWhiteGemmacrabe",
    stackable: false,
  },
  blueGrayVesperinCore: {
    id: "blueGrayVesperinCore",
    category: "craftingMaterial",
    name: "Noyau de vesperin",
    description: "Le cœur d'un vesperin, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonBlueGrayVesperinScroll: {
    id: "summonBlueGrayVesperinScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Vesperin",
    description: "Apprend la compétence Invocation : Vesperin.",
    grantsAbility: "summonBlueGrayVesperin",
    stackable: false,
  },
  silverVesperinCore: {
    id: "silverVesperinCore",
    category: "craftingMaterial",
    name: "Noyau de astrelin",
    description: "Le cœur d'un astrelin, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonSilverVesperinScroll: {
    id: "summonSilverVesperinScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Astrelin",
    description: "Apprend la compétence Invocation : Astrelin.",
    grantsAbility: "summonSilverVesperin",
    stackable: false,
  },
  goldVesperinCore: {
    id: "goldVesperinCore",
    category: "craftingMaterial",
    name: "Noyau de solelin",
    description: "Le cœur d'un solelin, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonGoldVesperinScroll: {
    id: "summonGoldVesperinScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Solelin",
    description: "Apprend la compétence Invocation : Solelin.",
    grantsAbility: "summonGoldVesperin",
    stackable: false,
  },
  copperVesperinCore: {
    id: "copperVesperinCore",
    category: "craftingMaterial",
    name: "Noyau de forgelin",
    description: "Le cœur d'un forgelin, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonCopperVesperinScroll: {
    id: "summonCopperVesperinScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Forgelin",
    description: "Apprend la compétence Invocation : Forgelin.",
    grantsAbility: "summonCopperVesperin",
    stackable: false,
  },
  steelVesperinCore: {
    id: "steelVesperinCore",
    category: "craftingMaterial",
    name: "Noyau de lamelin",
    description: "Le cœur d'un lamelin, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonSteelVesperinScroll: {
    id: "summonSteelVesperinScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Lamelin",
    description: "Apprend la compétence Invocation : Lamelin.",
    grantsAbility: "summonSteelVesperin",
    stackable: false,
  },
  emeraldVesperinCore: {
    id: "emeraldVesperinCore",
    category: "craftingMaterial",
    name: "Noyau de jadelin",
    description: "Le cœur d'un jadelin, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonEmeraldVesperinScroll: {
    id: "summonEmeraldVesperinScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Jadelin",
    description: "Apprend la compétence Invocation : Jadelin.",
    grantsAbility: "summonEmeraldVesperin",
    stackable: false,
  },
  orangeTikorinCore: {
    id: "orangeTikorinCore",
    category: "craftingMaterial",
    name: "Noyau de tikorin",
    description: "Le cœur d'un tikorin, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonOrangeTikorinScroll: {
    id: "summonOrangeTikorinScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Tikorin",
    description: "Apprend la compétence Invocation : Tikorin.",
    grantsAbility: "summonOrangeTikorin",
    stackable: false,
  },
  lightOakTikorinCore: {
    id: "lightOakTikorinCore",
    category: "craftingMaterial",
    name: "Noyau de clarorin",
    description: "Le cœur d'un clarorin, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonLightOakTikorinScroll: {
    id: "summonLightOakTikorinScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Clarorin",
    description: "Apprend la compétence Invocation : Clarorin.",
    grantsAbility: "summonLightOakTikorin",
    stackable: false,
  },
  walnutTikorinCore: {
    id: "walnutTikorinCore",
    category: "craftingMaterial",
    name: "Noyau de racorin",
    description: "Le cœur d'un racorin, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonWalnutTikorinScroll: {
    id: "summonWalnutTikorinScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Racorin",
    description: "Apprend la compétence Invocation : Racorin.",
    grantsAbility: "summonWalnutTikorin",
    stackable: false,
  },
  mahoganyTikorinCore: {
    id: "mahoganyTikorinCore",
    category: "craftingMaterial",
    name: "Noyau de braisorin",
    description: "Le cœur d'un braisorin, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonMahoganyTikorinScroll: {
    id: "summonMahoganyTikorinScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Braisorin",
    description: "Apprend la compétence Invocation : Braisorin.",
    grantsAbility: "summonMahoganyTikorin",
    stackable: false,
  },
  ebonyTikorinCore: {
    id: "ebonyTikorinCore",
    category: "craftingMaterial",
    name: "Noyau de noctorin",
    description: "Le cœur d'un noctorin, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonEbonyTikorinScroll: {
    id: "summonEbonyTikorinScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Noctorin",
    description: "Apprend la compétence Invocation : Noctorin.",
    grantsAbility: "summonEbonyTikorin",
    stackable: false,
  },
  goldenBeechTikorinCore: {
    id: "goldenBeechTikorinCore",
    category: "craftingMaterial",
    name: "Noyau de auréorin",
    description: "Le cœur d'un auréorin, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonGoldenBeechTikorinScroll: {
    id: "summonGoldenBeechTikorinScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Auréorin",
    description: "Apprend la compétence Invocation : Auréorin.",
    grantsAbility: "summonGoldenBeechTikorin",
    stackable: false,
  },
  pinkLimacorneCore: {
    id: "pinkLimacorneCore",
    category: "craftingMaterial",
    name: "Noyau de limacorne",
    description: "Le cœur d'une limacorne, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonPinkLimacorneScroll: {
    id: "summonPinkLimacorneScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Limacorne",
    description: "Apprend la compétence Invocation : Limacorne.",
    grantsAbility: "summonPinkLimacorne",
    stackable: false,
  },
  deepBlueLimacorneCore: {
    id: "deepBlueLimacorneCore",
    category: "craftingMaterial",
    name: "Noyau de gouffrorne",
    description: "Le cœur d'une gouffrorne, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonDeepBlueLimacorneScroll: {
    id: "summonDeepBlueLimacorneScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Gouffrorne",
    description: "Apprend la compétence Invocation : Gouffrorne.",
    grantsAbility: "summonDeepBlueLimacorne",
    stackable: false,
  },
  turquoiseLimacorneCore: {
    id: "turquoiseLimacorneCore",
    category: "craftingMaterial",
    name: "Noyau de réciforne",
    description: "Le cœur d'une réciforne, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonTurquoiseLimacorneScroll: {
    id: "summonTurquoiseLimacorneScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Réciforne",
    description: "Apprend la compétence Invocation : Réciforne.",
    grantsAbility: "summonTurquoiseLimacorne",
    stackable: false,
  },
  algaeGreenLimacorneCore: {
    id: "algaeGreenLimacorneCore",
    category: "craftingMaterial",
    name: "Noyau de algorne",
    description: "Le cœur d'une algorne, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonAlgaeGreenLimacorneScroll: {
    id: "summonAlgaeGreenLimacorneScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Algorne",
    description: "Apprend la compétence Invocation : Algorne.",
    grantsAbility: "summonAlgaeGreenLimacorne",
    stackable: false,
  },
  coralLimacorneCore: {
    id: "coralLimacorneCore",
    category: "craftingMaterial",
    name: "Noyau de corallorne",
    description: "Le cœur d'une corallorne, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonCoralLimacorneScroll: {
    id: "summonCoralLimacorneScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Corallorne",
    description: "Apprend la compétence Invocation : Corallorne.",
    grantsAbility: "summonCoralLimacorne",
    stackable: false,
  },
  pearlLimacorneCore: {
    id: "pearlLimacorneCore",
    category: "craftingMaterial",
    name: "Noyau de perlorne",
    description: "Le cœur d'une perlorne, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonPearlLimacorneScroll: {
    id: "summonPearlLimacorneScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Perlorne",
    description: "Apprend la compétence Invocation : Perlorne.",
    grantsAbility: "summonPearlLimacorne",
    stackable: false,
  },
  craquelinCore: {
    id: "craquelinCore",
    category: "craftingMaterial",
    name: "Noyau de craquelin",
    description: "Le cœur d'un craquelin, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonCraquelinScroll: {
    id: "summonCraquelinScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Craquelin",
    description: "Apprend la compétence Invocation : Craquelin.",
    grantsAbility: "summonCraquelin",
    stackable: false,
  },
  craqueglaceCore: {
    id: "craqueglaceCore",
    category: "craftingMaterial",
    name: "Noyau de craqueglace",
    description: "Le cœur d'un craqueglace, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonCraqueglaceScroll: {
    id: "summonCraqueglaceScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Craqueglace",
    description: "Apprend la compétence Invocation : Craqueglace.",
    grantsAbility: "summonCraqueglace",
    stackable: false,
  },
  craqueveninCore: {
    id: "craqueveninCore",
    category: "craftingMaterial",
    name: "Noyau de craquevenin",
    description: "Le cœur d'un craquevenin, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonCraqueveninScroll: {
    id: "summonCraqueveninScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Craquevenin",
    description: "Apprend la compétence Invocation : Craquevenin.",
    grantsAbility: "summonCraquevenin",
    stackable: false,
  },
  craquarcaneCore: {
    id: "craquarcaneCore",
    category: "craftingMaterial",
    name: "Noyau de craquarcane",
    description: "Le cœur d'un craquarcane, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonCraquarcaneScroll: {
    id: "summonCraquarcaneScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Craquarcane",
    description: "Apprend la compétence Invocation : Craquarcane.",
    grantsAbility: "summonCraquarcane",
    stackable: false,
  },
  craquorCore: {
    id: "craquorCore",
    category: "craftingMaterial",
    name: "Noyau de craquor",
    description: "Le cœur d'un craquor, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonCraquorScroll: {
    id: "summonCraquorScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Craquor",
    description: "Apprend la compétence Invocation : Craquor.",
    grantsAbility: "summonCraquor",
    stackable: false,
  },
  craquombreCore: {
    id: "craquombreCore",
    category: "craftingMaterial",
    name: "Noyau de craquombre",
    description: "Le cœur d'un craquombre, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonCraquombreScroll: {
    id: "summonCraquombreScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Craquombre",
    description: "Apprend la compétence Invocation : Craquombre.",
    grantsAbility: "summonCraquombre",
    stackable: false,
  },
  redSlimeCore: {
    id: "redSlimeCore",
    category: "craftingMaterial",
    name: "Noyau de blob rouge",
    description: "Le cœur d'un blob rouge, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonRedSlimeScroll: {
    id: "summonRedSlimeScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Blob rouge",
    description: "Apprend la compétence Invocation : Blob rouge.",
    grantsAbility: "summonRedSlime",
    stackable: false,
  },
  orangeSlimeCore: {
    id: "orangeSlimeCore",
    category: "craftingMaterial",
    name: "Noyau de blob orange",
    description: "Le cœur d'un blob orange, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonOrangeSlimeScroll: {
    id: "summonOrangeSlimeScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Blob orange",
    description: "Apprend la compétence Invocation : Blob orange.",
    grantsAbility: "summonOrangeSlime",
    stackable: false,
  },
  skyBlueSlimeCore: {
    id: "skyBlueSlimeCore",
    category: "craftingMaterial",
    name: "Noyau de blob bleu ciel",
    description: "Le cœur d'un blob bleu ciel, utilisé pour l'artisanat.",
    stackable: false,
    unique: true,
  },
  summonSkyBlueSlimeScroll: {
    id: "summonSkyBlueSlimeScroll",
    category: "abilityScroll",
    name: "Parchemin : Invocation : Blob bleu ciel",
    description: "Apprend la compétence Invocation : Blob bleu ciel.",
    grantsAbility: "summonSkyBlueSlime",
    stackable: false,
  },
};

/**
 * Résout la définition d'un objet, avec repli propre si le client ne
 * connaît pas encore une clé envoyée par le serveur (ex: nouvel objet
 * ajouté côté serveur avant que ce fichier soit mis à jour) - n'empêche
 * jamais l'objet d'exister dans l'inventaire, juste son affichage et ses
 * effets (repli sans statBonus/effect : l'objet ne fait juste rien de
 * spécial tant qu'il n'est pas correctement défini ici).
 */
export function resolveItemDef(itemId) {
  return (
    ITEM_DEFS[itemId] || {
      id: itemId,
      category: "unknown",
      name: itemId,
      description: "",
      stackable: true,
    }
  );
}
